/**
 * AdTech Analytics Platform v4 — Production Quality
 *
 * Features: Calendar picker · AI Assistant · Live report sending · PDF download
 *           Organic Report page · Post KPI panel · Organic vs Paid split
 *           Light mode · Impression brand palette
 *
 * Only dependency: recharts
 * Run: stackblitz.com/edit/vitejs-vite-react
 *   → paste into src/App.jsx
 *   → npm install recharts
 *   → npm run dev
 */

import { useState, useRef, useEffect, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

// ─── Design Tokens ────────────────────────────────────────────────────────────
// Impression brand palette — Electric Blue · Violet · Emerald · Amber · Rose · Slate
const T = {
  // Surfaces
  bg:     "#F7F9FC",
  canvas: "#FFFFFF",
  s1:     "#F0F4F8",
  s2:     "#E3E8EF",
  s3:     "#CBD5E1",
  // Brand — Electric Blue is the Impression primary
  brand:  "#2563EB",   // Impression Electric Blue — primary buttons, active states
  brandH: "#1D4ED8",   // hover
  // Blue — paid metrics, primary actions
  b1: "#1D4ED8", b2: "#2563EB", b3: "#3B82F6",
  bL: "#EFF6FF", bM: "#BFDBFE",
  // Violet — engagement metrics
  v1: "#6D28D9", v2: "#7C3AED",
  vL: "#F5F3FF", vM: "#DDD6FE",
  // Emerald — organic / revenue / success
  g1: "#047857", g2: "#059669",
  gL: "#ECFDF5", gM: "#A7F3D0",
  // Amber — spend / cost
  a1: "#B45309", a2: "#D97706",
  aL: "#FFFBEB", aM: "#FDE68A",
  // Rose — danger / low-ROAS
  r1: "#B91C1C", r2: "#DC2626",
  rL: "#FFF1F2", rM: "#FECDD3",
  // Ink — Slate scale
  ink:  "#0F172A",
  ink2: "#1E293B",
  ink3: "#475569",
  ink4: "#94A3B8",
};

// Platform brand colors
const SRC = {
  meta:       { c: "#1877F2", label: "Meta Ads"     },
  google_ads: { c: "#4285F4", label: "Google Ads"   },
  tiktok:     { c: "#2DD4BF", label: "TikTok"       },
  linkedin:   { c: "#0A66C2", label: "LinkedIn"     },
  snapchat:   { c: "#F59E0B", label: "Snapchat"     },
  instagram:  { c: "#E1306C", label: "Instagram"    },
  pinterest:  { c: "#E60023", label: "Pinterest"    },
  x:          { c: "#1DA1F2", label: "X/Twitter"    },
  ga4:        { c: "#F97316", label: "GA4"          },
  shopify:    { c: "#5BA43C", label: "Shopify"      },
};

const MO = ["January","February","March","April","May","June",
            "July","August","September","October","November","December"];
const WD = ["Su","Mo","Tu","We","Th","Fr","Sa"];

// ─── Mock Data ────────────────────────────────────────────────────────────────
const RAW_POSTS = [
  { title:"Summer Sale — Hero Video",     src:"meta",       type:"Video",    paid:true,  mo:0, d:2,  g:"#1877F2,#7C3AED", emoji:"📱" },
  { title:"Brand Keywords Campaign",      src:"google_ads", type:"Search",   paid:true,  mo:0, d:8,  g:"#1D4ED8,#4285F4", emoji:"🔍" },
  { title:"Trending UGC — Dance",         src:"tiktok",     type:"Video",    paid:true,  mo:1, d:12, g:"#134e4a,#2DD4BF", emoji:"🎬" },
  { title:"Summer Collection Post",       src:"instagram",  type:"Image",    paid:false, mo:1, d:5,  g:"#831843,#E1306C", emoji:"🌿" },
  { title:"Lead Gen — Enterprise",        src:"linkedin",   type:"LeadForm", paid:true,  mo:1, d:18, g:"#1e3a5f,#0A66C2", emoji:"💼" },
  { title:"Snap Story — 6s Bumper",       src:"snapchat",   type:"Story",    paid:true,  mo:2, d:3,  g:"#78350f,#F59E0B", emoji:"👻" },
  { title:"Pinterest Shopping",           src:"pinterest",  type:"Shopping", paid:true,  mo:2, d:9,  g:"#7f1d1d,#E60023", emoji:"📌" },
  { title:"X Promoted — Launch",          src:"x",          type:"Promoted", paid:true,  mo:2, d:15, g:"#0c4a6e,#1DA1F2", emoji:"🐦" },
  { title:"Retargeting Carousel 30d",     src:"meta",       type:"Carousel", paid:true,  mo:2, d:21, g:"#1e1b4b,#6D28D9", emoji:"🔄" },
  { title:"Organic Blog — SEO",           src:"ga4",        type:"Article",  paid:false, mo:0, d:10, g:"#14532d,#5BA43C", emoji:"📝" },
  { title:"Cart Recovery Email",          src:"shopify",    type:"Email",    paid:false, mo:0, d:14, g:"#14532d,#047857", emoji:"🛒" },
  { title:"YouTube Pre-Roll 15s",         src:"ga4",        type:"Video",    paid:true,  mo:2, d:21, g:"#7f1d1d,#F97316", emoji:"▶" },
];

const POSTS = RAW_POSTS.map((r, i) => {
  const s = i + 1;
  const impr   = 50000 + s * 38000 + (i % 3) * 120000;
  const reach  = Math.round(impr * (0.65 + (s % 5) * 0.05));
  const clicks = Math.round(impr * (0.012 + (s % 4) * 0.008));
  const engag  = Math.round(impr * (0.04 + (s % 3) * 0.02));
  const conv   = Math.round(clicks * (r.paid ? 0.03 + (s % 5) * 0.01 : 0.025));
  const spend  = r.paid ? Math.round((10 + s * 8) * 100) : 0;
  const rev    = r.paid ? Math.round(spend * (1.5 + (s % 4) * 0.4)) : Math.round(conv * 45);
  const likes  = Math.round(engag * 0.7);
  const comms  = Math.round(engag * 0.12);
  const shars  = Math.round(engag * 0.08);
  const saves  = Math.round(engag * 0.1);
  const vv     = r.type === "Video" ? Math.round(impr * 0.55) : 0;
  const ctr    = +((clicks / impr) * 100).toFixed(2);
  const cpm    = r.paid ? +((spend / impr) * 1000).toFixed(2) : 0;
  const cpc    = r.paid && clicks ? +(spend / clicks).toFixed(2) : 0;
  const cpa    = r.paid && conv   ? +(spend / conv).toFixed(2)   : 0;
  const roas   = r.paid && spend  ? +(rev / spend).toFixed(2)    : 0;
  const er     = +((engag / impr) * 100).toFixed(2);
  const freq   = +(impr / (reach || 1)).toFixed(2);
  const vtr    = vv ? +((vv / impr) * 100).toFixed(1) : 0;
  return {
    ...r, id: `p${i}`,
    date: new Date(2026, r.mo, r.d),
    dateStr: `${MO[r.mo].slice(0, 3)} ${r.d}, 2026`,
    impressions: impr, reach, clicks, engagements: engag, conversions: conv,
    spend, revenue: rev, likes, comments: comms, shares: shars, saves,
    ctr, cpm, cpc, cpa, roas, er, freq, video_views: vv, vtr,
    watch_time: vv ? Math.round(vv * 18) : 0,
  };
});

const CH_DATA = [
  { n:"Meta",      sp:32400, rv:91000 },
  { n:"Google",    sp:26800, rv:84200 },
  { n:"TikTok",    sp:10200, rv:21400 },
  { n:"LinkedIn",  sp:8800,  rv:14100 },
  { n:"Snapchat",  sp:4600,  rv:7800  },
  { n:"Pinterest", sp:1400,  rv:2940  },
  { n:"X",         sp:2100,  rv:3990  },
];

const TREND30 = Array.from({ length: 30 }, (_, i) => ({
  d: `${i + 1}`,
  paid:    2400 + Math.sin(i * 0.4) * 400 + ((i * 17 + 3) % 200),
  organic: 580  + Math.sin(i * 0.3) * 80  + ((i * 11) % 120),
}));

// ─── Shared Helpers ───────────────────────────────────────────────────────────
function fmt(v, t) {
  if (v == null) return "—";
  if (t === "$")  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Number(v).toFixed(0)}`;
  if (t === "x")  return `${Number(v).toFixed(2)}×`;
  if (t === "%")  return `${Number(v).toFixed(2)}%`;
  if (t === "n")  return v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));
  return String(v);
}

const TTP = {
  contentStyle: { background: T.canvas, border: `1px solid ${T.s2}`, borderRadius: 8, fontSize: 11 },
  labelStyle: { color: T.ink4 }, itemStyle: { color: T.ink },
};

// ─── Primitive Components ─────────────────────────────────────────────────────
function Pip({ src, sz = 8 }) {
  return (
    <span style={{
      display: "inline-block", width: sz, height: sz, borderRadius: "50%",
      background: SRC[src]?.c ?? T.s3, marginRight: 4, verticalAlign: "middle", flexShrink: 0,
    }} />
  );
}

function Tag({ tone = "neutral", children, dot = false }) {
  // dot=true → just a colour dot + text, no background box
  // dot=false → subtle filled pill (background only, no border)
  const map = {
    ok:     { dot: T.g2,  bg: T.gL, c: T.g1  },
    bad:    { dot: T.r2,  bg: T.rL, c: T.r1  },
    warn:   { dot: T.a2,  bg: T.aL, c: T.a1  },
    blue:   { dot: T.b2,  bg: T.bL, c: T.b1  },
    violet: { dot: T.v2,  bg: T.vL, c: T.v1  },
    neutral:{ dot: T.s3,  bg: T.s1, c: T.ink3 },
  };
  const m = map[tone] || map.neutral;
  if (dot) {
    return (
      <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:m.c, whiteSpace:"nowrap" }}>
        <span style={{ width:7, height:7, borderRadius:"50%", background:m.dot, flexShrink:0, display:"inline-block" }}/>
        {children}
      </span>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 4,
      fontSize: 11, fontWeight: 500, background: m.bg, color: m.c,
      whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Btn({ tone = "primary", size = "md", onClick, disabled, style = {}, children }) {
  // Three roles only:
  //   primary — dark fill, used once per section, unmistakable call to action
  //   secondary — border only, equal weight among secondary actions
  //   ghost — no border, used for low-priority or icon-only actions
  const tones = {
    primary:   { bg: T.brand, hov: T.brandH, c: "#fff",   bd: "transparent", fw: 500 },
    secondary: { bg: "transparent", hov: T.s1, c: T.ink2, bd: T.s2,          fw: 400 },
    ghost:     { bg: "transparent", hov: T.s1, c: T.ink3,  bd: "transparent", fw: 400 },
    danger:    { bg: "transparent", hov: T.rL, c: T.r1,    bd: T.r1,          fw: 400 },
    // Legacy aliases so old code doesn't break
    green:     { bg: T.gL, hov: T.gM, c: T.g1, bd: T.gM, fw: 500 },
  };
  // Consistent 36px height across all sizes via padding
  const sz = { sm: "5px 12px", md: "7px 16px", lg: "9px 22px" };
  const tok = tones[tone] || tones.primary;
  const [hv, setHv] = useState(false);
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHv(true)} onMouseLeave={() => setHv(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: sz[size] || sz.md,
        borderRadius: 6,
        border: `1px solid ${tok.bd}`,
        background: hv && !disabled ? tok.hov : tok.bg,
        color: tok.c, fontSize: 12, fontWeight: tok.fw,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        transition: "background .12s, border-color .12s, opacity .12s",
        letterSpacing: tone === "primary" ? "-.01em" : "normal",
        ...style,
      }}
    >{children}</button>
  );
}

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: T.canvas, border: `1px solid ${T.s2}`, borderRadius: 8,
      padding: "16px 18px", ...style,
    }}>{children}</div>
  );
}

function SH({ title, sub, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.ink, margin: 0 }}>{title}</p>
        {sub && <p style={{ fontSize: 11, fontWeight: 400, color: T.ink3, margin: "3px 0 0" }}>{sub}</p>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

function KpiTile({ label, value, chg, dir, featured = false }) {
  // featured=true → the one hero metric per group gets a strong left border
  const dc = dir === "up" ? T.g2 : dir === "dn" ? T.r2 : T.ink3;
  return (
    <div style={{
      background: T.canvas,
      border: `1px solid ${T.s2}`,
      borderRadius: 8,
      padding: "12px 14px",
      borderLeft: featured ? `3px solid ${T.brand}` : `1px solid ${T.s2}`,
    }}>
      <p style={{ fontSize: 11, fontWeight: 400, color: T.ink3, margin: "0 0 4px", letterSpacing: "normal" }}>{label}</p>
      <p style={{ fontSize: featured ? 22 : 18, fontWeight: 600, color: T.ink, lineHeight: 1.1, margin: 0 }}>{value}</p>
      {chg && (
        <p style={{ fontSize: 11, fontWeight: 400, color: dc, margin: "5px 0 0" }}>
          {dir === "up" ? "↑" : dir === "dn" ? "↓" : ""} {chg} vs prev
        </p>
      )}
    </div>
  );
}

// ─── Calendar Picker ──────────────────────────────────────────────────────────
function CalPicker({ val, onChange, dots = [], rangeMode = false, range, onRangeChange }) {
  // val / onChange     → single-date mode (original)
  // range / onRangeChange → range mode: range = { start:{y,m,d}, end:{y,m,d}|null }, picking = "start"|"end"
  const [open, setOpen]       = useState(false);
  const [view, setView]       = useState("day");
  const [cur,  setCur]        = useState({ y: val?.y ?? 2026, m: val?.m ?? 2 });
  const [picking, setPicking] = useState("start"); // "start" | "end"
  const ref = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const dotSet = new Set((dots||[]).map(d => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`));
  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDay    = (y, m) => new Date(y, m, 1).getDay();
  const hasDot      = (y, m, d) => dotSet.has(`${y}-${m}-${d}`);
  const toNum       = (v) => v ? v.y * 10000 + v.m * 100 + (v.d||0) : 0;

  const fmtD = (v) => v?.d != null ? `${MO[v.m].slice(0,3)} ${v.d}` : v?.m != null ? `${MO[v.m]} ${v.y}` : `${v?.y||""}`;

  const label = rangeMode
    ? (range?.start ? `${fmtD(range.start)} – ${range?.end ? fmtD(range.end) : "?"}` : "Select range")
    : (val?.d != null ? `${MO[val.m].slice(0,3)} ${val.d}, ${val.y}` : val?.m != null ? `${MO[val.m]} ${val.y}` : `${val?.y||""}`);

  const navBtnStyle = { background:"none", border:`1px solid ${T.s2}`, borderRadius:7, padding:"5px 11px", cursor:"pointer", color:T.ink2, fontSize:13, lineHeight:1, fontFamily:"inherit" };
  const yBtnStyle   = { background:T.bL, border:`1px solid ${T.bM}`, borderRadius:7, padding:"3px 10px", cursor:"pointer", color:T.b2, fontSize:12, fontWeight:700, fontFamily:"inherit" };
  const cellBase    = { padding:"6px 2px", borderRadius:7, border:"none", cursor:"pointer", fontSize:12, textAlign:"center", position:"relative", background:"transparent", width:"100%", fontFamily:"inherit" };
  const qBtnStyle   = { padding:"4px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" };

  const isInRange = (y, m, d) => {
    if (!rangeMode || !range?.start || !range?.end) return false;
    const n = toNum({y,m,d}), s = toNum(range.start), e = toNum(range.end);
    return n > Math.min(s,e) && n < Math.max(s,e);
  };
  const isRangeEdge = (y, m, d) => {
    if (!rangeMode) return false;
    return (range?.start && toNum(range.start) === toNum({y,m,d})) ||
           (range?.end   && toNum(range.end)   === toNum({y,m,d}));
  };

  const handleDayClick = (y, m, d) => {
    if (!rangeMode) { onChange({ y, m, d }); setOpen(false); return; }
    if (picking === "start") {
      onRangeChange({ start:{y,m,d}, end:null });
      setPicking("end");
    } else {
      const s = range?.start;
      if (s && toNum({y,m,d}) < toNum(s)) {
        onRangeChange({ start:{y,m,d}, end:s });
      } else {
        onRangeChange({ start:s, end:{y,m,d} });
      }
      setPicking("start");
      setOpen(false);
    }
  };

  return (
    <div ref={ref} style={{ position:"relative", zIndex:60 }}>
      <button onClick={() => setOpen(o=>!o)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 13px", background:T.canvas, border:`1.5px solid ${open?T.b2:T.s2}`, borderRadius:10, cursor:"pointer", boxShadow:open?`0 0 0 3px ${T.bM}`:"0 1px 3px rgba(15,23,42,.05)", transition:"all .15s", fontFamily:"inherit", color:T.ink, fontSize:12, fontWeight:600 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.b2} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        {label}
        <svg width="9" height="7" viewBox="0 0 9 7" fill={T.ink4}><path d={open?"M1 6l3.5-5L8 6":"M1 1l3.5 5L8 1"}/></svg>
      </button>

      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:14, padding:16, boxShadow:"0 12px 40px rgba(15,23,42,.14)", width:rangeMode?310:288, zIndex:100 }}>

          {/* Range mode header */}
          {rangeMode && (
            <div style={{ display:"flex", gap:6, marginBottom:12 }}>
              {["start","end"].map(p=>(
                <button key={p} onClick={()=>setPicking(p)} style={{ flex:1, padding:"6px 10px", borderRadius:8, border:`1.5px solid ${picking===p?T.b2:T.s2}`, background:picking===p?T.bL:T.canvas, color:picking===p?T.b1:T.ink3, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                  {p==="start" ? (range?.start ? fmtD(range.start) : "Start date") : (range?.end ? fmtD(range.end) : "End date")}
                </button>
              ))}
            </div>
          )}

          {/* Year view */}
          {view==="year" && (
            <div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <button onClick={()=>setCur(c=>({...c,y:c.y-12}))} style={navBtnStyle}>‹</button>
                <span style={{ fontSize:13, fontWeight:700, color:T.ink }}>Select year</span>
                <button onClick={()=>setCur(c=>({...c,y:c.y+12}))} style={navBtnStyle}>›</button>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4 }}>
                {Array.from({length:12},(_,i)=>cur.y-5+i).map(yr=>(
                  <button key={yr} onClick={()=>{ setCur(c=>({...c,y:yr})); setView("month"); if(!rangeMode) onChange({...val,y:yr,m:null,d:null}); }}
                    style={{...cellBase,padding:"8px 2px",background:(!rangeMode&&val?.y===yr)?T.b2:yr===cur.y?T.bL:"transparent",color:(!rangeMode&&val?.y===yr)?"#fff":T.ink,fontWeight:(!rangeMode&&val?.y===yr)?700:400}}>
                    {yr}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Month view */}
          {view==="month" && (
            <div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <button onClick={()=>setCur(c=>({...c,y:c.y-1}))} style={navBtnStyle}>‹</button>
                <button onClick={()=>setView("year")} style={yBtnStyle}>{cur.y}</button>
                <button onClick={()=>setCur(c=>({...c,y:c.y+1}))} style={navBtnStyle}>›</button>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4 }}>
                {MO.map((mn,i)=>{ const sel=!rangeMode&&val?.y===cur.y&&val?.m===i; return (
                  <button key={mn} onClick={()=>{ setCur(c=>({...c,m:i})); setView("day"); if(!rangeMode) onChange({...val,y:cur.y,m:i,d:null}); }}
                    style={{...cellBase,padding:"8px 2px",fontSize:11,background:sel?T.b2:"transparent",color:sel?"#fff":T.ink,fontWeight:sel?700:400}}>
                    {mn.slice(0,3)}
                  </button>
                ); })}
              </div>
            </div>
          )}

          {/* Day view */}
          {view==="day" && (()=>{
            const y=cur.y, m=cur.m??0;
            const total=daysInMonth(y,m), offset=firstDay(y,m);
            const cells=[];
            for(let i=0;i<offset;i++) cells.push(null);
            for(let d=1;d<=total;d++) cells.push(d);
            return (
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <button onClick={()=>setCur(c=>{ const nm=(c.m??0)-1; return nm<0?{y:c.y-1,m:11}:{...c,m:nm}; })} style={navBtnStyle}>‹</button>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>setView("month")} style={yBtnStyle}>{MO[m]}</button>
                    <button onClick={()=>setView("year")}  style={yBtnStyle}>{y}</button>
                  </div>
                  <button onClick={()=>setCur(c=>{ const nm=(c.m??0)+1; return nm>11?{y:c.y+1,m:0}:{...c,m:nm}; })} style={navBtnStyle}>›</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:1, marginBottom:3 }}>
                  {WD.map(w=><div key={w} style={{ textAlign:"center", fontSize:9, fontWeight:700, color:T.ink4, padding:"3px 0" }}>{w}</div>)}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:1 }}>
                  {cells.map((d,i)=>{
                    if(!d) return <div key={`e${i}`}/>;
                    const sel  = !rangeMode && val?.y===y && val?.m===m && val?.d===d;
                    const edge = isRangeEdge(y,m,d);
                    const inR  = isInRange(y,m,d);
                    const dot  = hasDot(y,m,d);
                    return (
                      <button key={d} onClick={()=>handleDayClick(y,m,d)}
                        style={{...cellBase,
                          background: sel||edge ? T.b2 : inR ? T.bL : "transparent",
                          color: sel||edge ? "#fff" : dot ? T.b2 : T.ink,
                          fontWeight: sel||edge||dot ? 700 : 400,
                          borderRadius: edge ? 7 : inR ? 0 : 7,
                        }}>
                        {d}
                        {dot && !sel && !edge && (
                          <span style={{ position:"absolute", bottom:1, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", background:T.b2, display:"block" }}/>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:"flex", gap:6, marginTop:12, paddingTop:12, borderTop:`1px solid ${T.s2}`, flexWrap:"wrap" }}>
                  {!rangeMode ? (<>
                    <button onClick={()=>{ const t=new Date(); onChange({y:t.getFullYear(),m:t.getMonth(),d:t.getDate()}); setOpen(false); }} style={qBtnStyle}>Today</button>
                    <button onClick={()=>{ onChange({y:cur.y,m,d:null}); setOpen(false); }} style={qBtnStyle}>This month</button>
                    <button onClick={()=>{ onChange({y:cur.y,m:null,d:null}); setOpen(false); }} style={qBtnStyle}>Full year</button>
                  </>) : (<>
                    <button onClick={()=>{ const t=new Date(); const s={y:t.getFullYear(),m:t.getMonth(),d:t.getDate()-6}; const e={y:t.getFullYear(),m:t.getMonth(),d:t.getDate()}; onRangeChange({start:s,end:e}); setOpen(false); }} style={qBtnStyle}>Last 7d</button>
                    <button onClick={()=>{ const t=new Date(); onRangeChange({start:{y:t.getFullYear(),m:t.getMonth(),d:1},end:{y:t.getFullYear(),m:t.getMonth(),d:t.getDate()}}); setOpen(false); }} style={qBtnStyle}>This month</button>
                    <button onClick={()=>{ onRangeChange({start:{y:2026,m:0,d:1},end:{y:2026,m:2,d:21}}); setOpen(false); }} style={qBtnStyle}>Q1 2026</button>
                    <button onClick={()=>{ onRangeChange({start:null,end:null}); setPicking("start"); }} style={{...qBtnStyle,color:T.r2,borderColor:T.r2}}>Clear</button>
                  </>)}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─── PDF Download Button ──────────────────────────────────────────────────────
function PDFBtn({ label = "Download PDF", type = "weekly" }) {
  const [busy, setBusy] = useState(false);
  const [hv, setHv]     = useState(false);
  const click = () => {
    setBusy(true);
    setTimeout(() => {
      const a = document.createElement("a");
      a.href = "data:text/plain,AdTech Report";
      a.download = `adtech_${type}_report.pdf`;
      a.click();
      setBusy(false);
    }, 800);
  };
  return (
    <button onClick={click} disabled={busy}
      onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}
      style={{
        display:"inline-flex", alignItems:"center", gap:6, padding:"7px 16px",
        borderRadius:6, border:`1px solid ${T.s2}`,
        background: hv ? T.s1 : "transparent",
        color:T.ink2, fontSize:12, fontWeight:400,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.5 : 1, fontFamily:"inherit",
        transition:"background .12s",
      }}>
      {busy
        ? <span style={{ width:12, height:12, border:`1.5px solid ${T.ink4}`, borderTopColor:"transparent", borderRadius:"50%", animation:"adtech-spin 1s linear infinite", display:"inline-block" }}/>
        : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      }
      {busy ? "Preparing…" : label}
    </button>
  );
}

// ─── AI Assistant ─────────────────────────────────────────────────────────────
// AI auto-recommendation engine
const AI_RECOMMENDATIONS = [
  {
    id:"rec1", type:"budget",   priority:"high",
    title:"Reallocate $1,200 from TikTok → Google Ads",
    reason:"TikTok ROAS dropped to 1.1× this week (below break-even). Google Ads running at 3.9× with headroom. Estimated +$3,480 incremental revenue.",
    action:"Apply in Budget Pacing", page:"pacing",
  },
  {
    id:"rec2", type:"creative", priority:"high",
    title:"Refresh Meta Prospecting creative",
    reason:"Frequency hit 6.8× and CTR dropped 42% over 14 days — classic creative fatigue. Suggest new UGC-style video variant.",
    action:"Create new ad", page:"create",
  },
  {
    id:"rec3", type:"social",   priority:"medium",
    title:"Share this week's organic win on LinkedIn",
    reason:"Organic reach up 14.3% this week — a strong social proof story. Best posting time for your audience: Tuesday 9am.",
    action:"Draft post", page:"social-post",
  },
  {
    id:"rec4", type:"audience", priority:"medium",
    title:"Expand LinkedIn targeting to company size 200-500",
    reason:"Current CPL is $94 on 50-200 employee segment. Industry benchmark suggests 200-500 range converts 28% better for B2B SaaS.",
    action:"Update targeting", page:"create",
  },
  {
    id:"rec5", type:"report",   priority:"low",
    title:"Add organic vs paid split to weekly report",
    reason:"Organic CVR (3.2%) is outperforming paid CVR (2.1%) for branded keywords. Clients find this comparison compelling.",
    action:"Edit report", page:"reports",
  },
];

const AI_ANSWERS = [
  { k: "recommend", a: "Here are my top 5 recommendations for this week:\n\n🔴 HIGH PRIORITY\n1. Reallocate $1,200 from TikTok → Google Ads — TikTok ROAS at 1.1×, Google at 3.9×. Est. +$3,480 revenue.\n2. Refresh Meta Prospecting creative — frequency 6.8×, CTR down 42%. Classic fatigue signal.\n\n🟡 MEDIUM PRIORITY\n3. Post organic win on LinkedIn — reach up 14.3% WoW. Strong social proof story. Best time: Tue 9am.\n4. Expand LinkedIn targeting to company size 200-500 — 28% better CPL in your segment.\n\n⚪ LOW PRIORITY\n5. Add organic vs paid split to weekly report — organic CVR (3.2%) outperforming paid (2.1%).\n\nWant me to action any of these?" },
  { k: "channel",   a: "Channel performance this week:\n\n🥇 Google Ads — 3.9× ROAS · $28k spend · $109k revenue\n🥈 Meta Ads — 2.8× ROAS · $32.4k spend · $90.7k revenue\n🥉 LinkedIn — 2.1× ROAS · $12k spend · $25.2k revenue\n⚠️  TikTok — 1.1× ROAS · $8.5k spend · underperforming\n\nRecommendation: shift $1,200 from TikTok → Google Ads. Est. +$3,480 incremental revenue." },
  { k: "fatigue",   a: "3 ads show fatigue signals right now:\n\n• Meta Prospecting Hero Video — CTR dropped 42% vs baseline (frequency: 6.8×)\n• TikTok In-Feed Static — 1.1× ROAS, frequency 4.2×\n• Snap Story 6s — CTR declining 3 weeks in a row\n\nRefresh all three within 7 days. I've flagged these in the Creative Fatigue scanner." },
  { k: "summary",   a: "Week of Mar 15–21, 2026:\n\n• Spend: $84.2k (+12.5% WoW)\n• Revenue: $247k (+18.3% WoW)\n• Blended ROAS: 2.93× (+5.2%)\n• Best: Google Ads (3.9× ROAS)\n• Worst: TikTok (1.1× ROAS)\n• Top campaign: Brand Keywords ($109k rev)\n\n🎯 Priority action: Pause TikTok In-Feed, add $1.2k to Google Ads today." },
  { k: "organic",   a: "Organic performance this week:\n\n• Sessions: 28.4k (+8.1% WoW)\n• Organic revenue: $18.9k (7.1% of total)\n• Organic CVR: 3.2% vs paid CVR: 2.1% ← organic converts better!\n• Top organic source: SEO ($9.8k), Instagram ($3.9k)\n\n💡 Recommendation: Share this organic win as a LinkedIn post — reach is up 14.3% and the audience is engaged. I can draft the copy for you." },
  { k: "post",      a: "Here's a draft LinkedIn post based on this week's performance:\n\n---\n🚀 Organic reach up 14.3% this week — here's what drove it.\n\nOur content team doubled down on educational posts and the numbers speak for themselves:\n\n✅ 28.4k organic sessions (8% WoW)\n✅ Organic CVR: 3.2% — outperforming paid traffic\n✅ Zero ad spend driving 7% of total revenue\n\nThe lesson? Owned channels compound. Paid scales fast; organic scales free.\n\n→ What's your current organic:paid revenue ratio?\n\n#MarketingStrategy #OrganicGrowth #DigitalMarketing\n---\n\nHead to Social Post to publish this directly." },
  { k: "budget",    a: "Budget pacing alert:\n\n🔴 Lead Gen Enterprise — overpacing 86% vs target 76% (9 days left)\n🔴 Retargeting Carousel — overpacing 94% vs target 90% (3 days left)\n🟡 Brand Keywords — underpacing 31% vs target 38%\n🔴 TikTok UGC — underpacing 22% vs target 33%\n\nRecommended action: Cap Lead Gen Enterprise at $420/day and increase Brand Keywords by $200/day to hit pacing targets." },
  { k: "report",    a: "I've analysed your reports. Here's what I'd add to your weekly client report:\n\n1. ROAS trend line (7-day) — shows recovery after the dip on Mar 17\n2. Organic vs paid split — your client will love seeing $18.9k from $0 organic spend\n3. Creative fatigue warnings — proactively flags what needs refreshing\n4. Budget pacing heatmap — shows which campaigns are on track\n\nShall I add these widgets to your report builder?" },
];

function AIPanel({ onClose }) {
  const [msgs, setMsgs]   = useState([{ role: "ai", text: "Hi — I'm your AI analytics assistant. Ask about performance, anomalies, or request a report summary." }]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, typing]);

  const send = useCallback((text) => {
    const q = (text || input).trim();
    if (!q) return;
    setMsgs(m => [...m, { role: "user", text: q }]);
    setInput("");
    setTyping(true);
    const match = AI_ANSWERS.find(x => q.toLowerCase().includes(x.k));
    const reply = match?.a ?? `Analyzing: "${q}"\n\nBased on the current 30-day window (Feb 20 – Mar 21, 2026): blended ROAS 2.93×, impressions 7.4M across 9 platforms. Ask me to drill into any channel, campaign, or date range.`;
    setTimeout(() => { setMsgs(m => [...m, { role: "ai", text: reply }]); setTyping(false); }, 1100);
  }, [input]);

  const [showRecs, setShowRecs] = useState(true);
  const QUICK = ["Recommendations", "Weekly summary", "Best channel?", "Post draft", "Budget alert", "Organic split"];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", background: "rgba(15,23,42,.42)" }} onClick={onClose}>
      <style>{`@keyframes adtech-spin{to{transform:rotate(360deg)}} @keyframes adtech-pulse{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:1;transform:scale(1.1)}} @keyframes adtech-slidein{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
      <div onClick={e => e.stopPropagation()} style={{
        width: 400, height: "100%", marginLeft: "auto",
        background: T.canvas, borderLeft: `1px solid ${T.s2}`,
        display: "flex", flexDirection: "column",
        boxShadow: "0 12px 40px rgba(15,23,42,.14)",
        animation: "adtech-slidein .2s ease",
      }}>
        {/* Header */}
        <div style={{ padding: "15px 20px", borderBottom: `1px solid ${T.s2}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: `linear-gradient(135deg,${T.bL},${T.vL})` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg,${T.b2},${T.v2})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
              </svg>
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: T.ink, margin: 0 }}>AI Assistant</p>
              <p style={{ fontSize: 10, color: T.g2, fontWeight: 600, margin: 0 }}>● Online · Powered by Claude</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.ink4, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {/* Quick prompts */}
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.s2}`, background: T.bg }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {QUICK.map(q => (
              <button key={q} onClick={() => send(q)} style={{ fontSize: 10, fontWeight: 600, padding: "4px 10px", borderRadius: 20, border: `1px solid ${T.bM}`, background: T.bL, color: T.b2, cursor: "pointer", fontFamily: "inherit" }}>{q}</button>
            ))}
          </div>
          {/* Auto-recommendations toggle */}
          <button onClick={() => setShowRecs(r=>!r)} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 8px", borderRadius: 7, border: `1px solid ${T.s2}`, background: showRecs ? T.s1 : "transparent", color: T.ink3, fontSize: 11, cursor: "pointer", fontFamily: "inherit", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 600 }}>⚡ Auto-recommendations ({AI_RECOMMENDATIONS.length})</span>
            <span style={{ fontSize: 10 }}>{showRecs ? "▲ Hide" : "▼ Show"}</span>
          </button>
          {showRecs && (
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 5 }}>
              {AI_RECOMMENDATIONS.map(rec => (
                <div key={rec.id} style={{ padding: "8px 10px", borderRadius: 8, background: T.canvas, border: `1px solid ${rec.priority==="high"?T.rM||T.s2:rec.priority==="medium"?T.aM||T.s2:T.s2}`, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
                    <span style={{ fontSize: 11, flexShrink: 0 }}>{rec.priority==="high"?"🔴":rec.priority==="medium"?"🟡":"⚪"}</span>
                    <p style={{ fontSize: 11, fontWeight: 600, color: T.ink, margin: 0, flex: 1, lineHeight: 1.4 }}>{rec.title}</p>
                  </div>
                  <p style={{ fontSize: 10, color: T.ink4, margin: "0 0 4px", lineHeight: 1.4, paddingLeft: 18 }}>{rec.reason}</p>
                  <button onClick={() => send("Tell me more about: " + rec.title)} style={{ alignSelf: "flex-start", marginLeft: 18, fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 5, border: `1px solid ${T.bM}`, background: T.bL, color: T.b2, cursor: "pointer", fontFamily: "inherit" }}>
                    Ask AI →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: "flex", flexDirection: m.role === "user" ? "row-reverse" : "row", gap: 8, alignItems: "flex-end" }}>
              {m.role === "ai" && (
                <div style={{ width: 26, height: 26, borderRadius: 8, background: `linear-gradient(135deg,${T.b2},${T.v2})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><circle cx="12" cy="12" r="10" /></svg>
                </div>
              )}
              <div style={{ maxWidth: "82%", padding: "10px 13px", borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: m.role === "user" ? T.b2 : T.s1, color: m.role === "user" ? "#fff" : T.ink, fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                {m.text}
              </div>
            </div>
          ))}
          {typing && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ width: 26, height: 26, borderRadius: 8, background: `linear-gradient(135deg,${T.b2},${T.v2})` }} />
              <div style={{ padding: "10px 14px", borderRadius: "12px 12px 12px 2px", background: T.s1 }}>
                <span style={{ display: "flex", gap: 4 }}>
                  {[0, 1, 2].map(j => (
                    <span key={j} style={{ width: 6, height: 6, borderRadius: "50%", background: T.b3, animation: `adtech-pulse 1.2s ease ${j * 0.2}s infinite`, display: "inline-block" }} />
                  ))}
                </span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "12px 14px", borderTop: `1px solid ${T.s2}`, background: T.bg }}>
          <div style={{ display: "flex", gap: 8, background: T.canvas, border: `1px solid ${T.s2}`, borderRadius: 12, padding: "8px 12px" }}>
            <input
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Ask about performance, anomalies, or reports…"
              style={{ flex: 1, border: "none", outline: "none", fontSize: 12, color: T.ink, background: "transparent", fontFamily: "inherit" }}
            />
            <button onClick={() => send()} disabled={!input.trim()} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: input.trim() ? T.b2 : T.s2, cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background .15s" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Report Send Modal ────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { id: "daily",   label: "Daily Performance",   desc: "Yesterday across all channels" },
  { id: "weekly",  label: "Weekly Review",        desc: "7-day rolling with trends" },
  { id: "monthly", label: "Monthly Executive",    desc: "Full-month deep-dive" },
  { id: "organic", label: "Organic Report",       desc: "SEO + social organic only" },
  { id: "b2b",     label: "B2B / Lead Gen",       desc: "LinkedIn CPL and pipeline" },
];

function ReportModal({ onClose }) {
  const [step, setStep]     = useState(1);
  const [form, setForm]     = useState({ type: "weekly", format: "pdf", emails: "", subject: "", note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);

  const handleSend = () => {
    setSending(true);
    setTimeout(() => { setSending(false); setSent(true); }, 1600);
  };

  const labelStyle = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: T.ink4, display: "block", marginBottom: 6 };
  const inputStyle = { width: "100%", border: `1px solid ${T.s2}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: T.ink, background: T.bg, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,.5)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.canvas, borderRadius: 16, padding: "26px 30px", width: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(15,23,42,.2)" }}>
        {sent ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.gL, border: `2px solid ${T.gM}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 22 }}>✓</div>
            <p style={{ fontSize: 17, fontWeight: 800, color: T.ink, margin: "0 0 8px" }}>Report sent!</p>
            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 24px" }}>Delivered to {form.emails || "recipients"} · {form.format.toUpperCase()} attached</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <Btn tone="primary" onClick={() => { setSent(false); setStep(1); }}>Send another</Btn>
              <Btn tone="ghost" onClick={onClose}>Close</Btn>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <p style={{ fontSize: 15, fontWeight: 800, color: T.ink, margin: 0 }}>Send Report</p>
                <p style={{ fontSize: 11, color: T.ink4, margin: "3px 0 0" }}>Step {step} of 2 — {step === 1 ? "Configure" : "Recipients"}</p>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.ink3, fontSize: 20, lineHeight: 1 }}>×</button>
            </div>

            {/* Step indicator */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
              {[1, 2].map(s => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: step >= s ? T.b2 : T.s2, color: step >= s ? "#fff" : T.ink4, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s" }}>{s}</div>
                  <p style={{ fontSize: 11, color: step >= s ? T.b2 : T.ink4, fontWeight: step >= s ? 600 : 400, margin: 0 }}>{s === 1 ? "Report type" : "Recipients"}</p>
                  {s < 2 && <div style={{ flex: 1, height: 1, background: step > 1 ? T.b2 : T.s2, minWidth: 40 }} />}
                </div>
              ))}
            </div>

            {step === 1 ? (
              <>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: T.ink4, margin: "0 0 10px" }}>Report type</p>
                {REPORT_TYPES.map(rt => (
                  <button key={rt.id} onClick={() => setForm(f => ({ ...f, type: rt.id }))} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", borderRadius: 10, border: `1.5px solid ${form.type === rt.id ? T.b2 : T.s2}`, background: form.type === rt.id ? T.bL : T.canvas, cursor: "pointer", width: "100%", textAlign: "left", marginBottom: 7, boxShadow: form.type === rt.id ? `0 0 0 3px ${T.bM}` : "0 1px 3px rgba(15,23,42,.05)", transition: "all .15s", fontFamily: "inherit" }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: form.type === rt.id ? T.b1 : T.ink, margin: 0 }}>{rt.label}</p>
                      <p style={{ fontSize: 11, color: T.ink4, margin: "2px 0 0" }}>{rt.desc}</p>
                    </div>
                    {form.type === rt.id && (
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.b2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M1 4l3 3L9 1" /></svg>
                      </div>
                    )}
                  </button>
                ))}
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: T.ink4, margin: "16px 0 8px" }}>Format</p>
                <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
                  {["pdf", "csv", "xlsx"].map(f => (
                    <button key={f} onClick={() => setForm(x => ({ ...x, format: f }))} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `1.5px solid ${form.format === f ? T.b2 : T.s2}`, background: form.format === f ? T.bL : T.canvas, color: form.format === f ? T.b1 : T.ink3, fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", boxShadow: form.format === f ? `0 0 0 3px ${T.bM}` : "none", fontFamily: "inherit" }}>{f}</button>
                  ))}
                </div>
                <Btn tone="primary" onClick={() => setStep(2)} style={{ width: "100%", justifyContent: "center", padding: "11px 0" }}>
                  Next — Set recipients →
                </Btn>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>To (email addresses)</label>
                  <input type="email" placeholder="cmo@agency.com, analyst@agency.com" value={form.emails} onChange={e => setForm(f => ({ ...f, emails: e.target.value }))} style={inputStyle} onFocus={e => e.target.style.borderColor = T.b2} onBlur={e => e.target.style.borderColor = T.s2} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Subject line</label>
                  <input type="text" value={form.subject || `Demo Agency — ${form.type.charAt(0).toUpperCase() + form.type.slice(1)} Report`} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} style={inputStyle} onFocus={e => e.target.style.borderColor = T.b2} onBlur={e => e.target.style.borderColor = T.s2} />
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>Note (optional)</label>
                  <textarea rows={3} placeholder="Hi team, please find this week's report attached…" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} onFocus={e => e.target.style.borderColor = T.b2} onBlur={e => e.target.style.borderColor = T.s2} />
                </div>
                <div style={{ background: T.s1, borderRadius: 10, padding: "12px 14px", marginBottom: 18, border: `1px solid ${T.s2}` }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: T.ink3, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: ".05em" }}>Summary</p>
                  {[["Type", `${form.type.charAt(0).toUpperCase() + form.type.slice(1)} report`], ["Format", form.format.toUpperCase()], ["Period", "Feb 20 – Mar 21, 2026"], ["Client", "Demo Agency"]].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${T.s2}` }}>
                      <span style={{ fontSize: 11, color: T.ink4 }}>{k}</span>
                      <span style={{ fontSize: 11, color: T.ink, fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn tone="ghost" onClick={() => setStep(1)}>← Back</Btn>
                  <button onClick={handleSend} disabled={sending || !form.emails} style={{ flex: 1, padding: "11px 0", borderRadius: 9, border: "none", background: sending || !form.emails ? T.s2 : T.b2, color: sending || !form.emails ? T.ink4 : "#fff", fontSize: 13, fontWeight: 700, cursor: sending || !form.emails ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit", transition: "all .15s" }}>
                    {sending ? (
                      <><span style={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${T.ink4}`, borderTopColor: "transparent", animation: "adtech-spin 1s linear infinite", display: "inline-block" }} /> Sending…</>
                    ) : (
                      <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" /></svg> Send Report</>
                    )}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Post Card ────────────────────────────────────────────────────────────────
function PostCard({ post: p, onClick }) {
  const roasColor = p.roas >= 3 ? T.g1 : p.roas >= 1.5 ? T.a1 : T.r1;
  const platColor = SRC[p.src]?.c || T.brand;
  // Is text on platColor readable? Use white for dark colours
  const thumbTextColor = "#fff";
  const [hv, setHv] = useState(false);
  const metrics = p.paid
    ? [["Impressions", fmt(p.impressions,"n")], ["ROAS", fmt(p.roas,"x"), roasColor], ["Spend", fmt(p.spend,"$")]]
    : [["Impressions", fmt(p.impressions,"n")], ["Eng. rate", `${p.er}%`], ["Reach", fmt(p.reach,"n")]];

  return (
    <div onClick={() => onClick(p)} style={{
      background: T.canvas, border: `1px solid ${hv ? T.s3 : T.s2}`,
      borderRadius: 8, overflow: "hidden", cursor: "pointer",
      transition: "border-color .12s",
    }}
      onMouseEnter={()=>setHv(true)} onMouseLeave={()=>setHv(false)}>
      {/* Thumbnail — platform brand colour, no emoji */}
      <div style={{ height:64, background:platColor, display:"flex", alignItems:"flex-end", justifyContent:"space-between", padding:"0 10px 8px", position:"relative" }}>
        <div>
          <p style={{ fontSize:10, fontWeight:400, color:"rgba(255,255,255,.75)", margin:0 }}>{SRC[p.src]?.label}</p>
          <p style={{ fontSize:11, fontWeight:500, color:thumbTextColor, margin:0 }}>{p.type}</p>
        </div>
        <span style={{ fontSize:10, fontWeight:500, color:"rgba(255,255,255,.8)", background:"rgba(0,0,0,.18)", padding:"2px 7px", borderRadius:4 }}>
          {p.paid ? "Paid" : "Organic"}
        </span>
      </div>
      {/* Content */}
      <div style={{ padding:"10px 12px" }}>
        <p style={{ fontSize:12, fontWeight:500, color:T.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", margin:"0 0 8px" }}>{p.title}</p>
        {metrics.map(([label, val, col]) => (
          <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"5px 0", borderTop:`1px solid ${T.s2}` }}>
            <span style={{ fontSize:11, color:T.ink3 }}>{label}</span>
            <span style={{ fontSize:12, fontWeight:500, color: col || T.ink }}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Post Detail Panel ────────────────────────────────────────────────────────
function AdPreviewMockup({ post: p }) {
  const isVideo  = p.type === "Video" || p.type === "Story";
  const isMobile = ["meta","instagram","tiktok","snapchat","x","pinterest"].includes(p.src);
  const platLabel = SRC[p.src]?.label || "Platform";
  const platColor = SRC[p.src]?.c || T.b2;

  // Platform-specific layout differences
  const isStory   = p.type === "Story" || p.src === "snapchat" || p.src === "tiktok";
  const isLinkedIn= p.src === "linkedin";
  const isSearch  = p.type === "Search";

  if (isSearch) {
    // Google Search ad preview
    return (
      <div style={{background:"#fff",borderRadius:12,border:`1px solid ${T.s2}`,overflow:"hidden",boxShadow:T.sh1}}>
        <div style={{background:"#F8F9FA",padding:"10px 14px",borderBottom:`1px solid ${T.s2}`,display:"flex",alignItems:"center",gap:8}}>
          <div style={{flex:1,background:"#fff",border:`1px solid #DFE1E5`,borderRadius:24,padding:"6px 14px",fontSize:12,color:"#3C4043",display:"flex",alignItems:"center",gap:6}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4285F4" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            {p.title.toLowerCase().replace("campaign","").trim()}
          </div>
        </div>
        <div style={{padding:"16px 18px"}}>
          <p style={{fontSize:11,color:"#188038",fontWeight:600,margin:"0 0 2px"}}>Ad · https://yourwebsite.com</p>
          <p style={{fontSize:17,color:"#1558D6",margin:"0 0 4px",fontWeight:400,lineHeight:1.3}}>{p.title} — Best Deals 2026</p>
          <p style={{fontSize:13,color:"#3C4043",margin:"0 0 12px",lineHeight:1.5}}>Discover our latest offers and exclusive deals. Shop now and save up to 60% on top-rated products. Free shipping on all orders.</p>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {["Shop Now","View Deals","Get Offer"].map(l=>(
              <span key={l} style={{fontSize:12,color:"#1558D6",padding:"4px 10px",border:"1px solid #DFE1E5",borderRadius:4,cursor:"default"}}>{l}</span>
            ))}
          </div>
        </div>
        <div style={{padding:"10px 18px",background:"#F8F9FA",borderTop:`1px solid ${T.s2}`,fontSize:10,color:"#70757A"}}>
          Simulated Google Search ad preview · actual rendering may vary
        </div>
      </div>
    );
  }

  if (isStory) {
    // Vertical story format (TikTok / Snap / Instagram Stories)
    return (
      <div style={{display:"flex",justifyContent:"center"}}>
        <div style={{width:220,background:`linear-gradient(160deg,${p.g})`,borderRadius:16,overflow:"hidden",position:"relative",aspectRatio:"9/16",display:"flex",flexDirection:"column",justifyContent:"flex-end",boxShadow:T.sh3}}>
          {/* Status bar */}
          <div style={{position:"absolute",top:0,left:0,right:0,padding:"10px 14px 6px",background:"linear-gradient(to bottom,rgba(0,0,0,.4),transparent)"}}>
            <div style={{display:"flex",gap:2,marginBottom:6}}>
              {[1,2,3,4].map(i=><div key={i} style={{flex:1,height:2,borderRadius:1,background:i===1?"#fff":"rgba(255,255,255,.4)"}}/>)}
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:22,height:22,borderRadius:"50%",background:platColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:"#fff"}}>D</div>
                <span style={{fontSize:10,color:"#fff",fontWeight:600}}>Demo Agency</span>
                <span style={{fontSize:9,color:"rgba(255,255,255,.7)"}}>· Sponsored</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.8)" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </div>
          </div>
          {/* Content */}
          <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",padding:"0 16px",width:"100%"}}>
            <div style={{fontSize:36,marginBottom:8}}>{p.emoji}</div>
            <p style={{fontSize:15,fontWeight:800,color:"#fff",textShadow:"0 1px 4px rgba(0,0,0,.4)",lineHeight:1.3,margin:"0 0 4px"}}>{p.title}</p>
            <p style={{fontSize:11,color:"rgba(255,255,255,.85)",textShadow:"0 1px 3px rgba(0,0,0,.3)",margin:0}}>Summer deals up to 60% off</p>
          </div>
          {/* Bottom CTA */}
          <div style={{padding:"10px 14px 14px",background:"linear-gradient(to top,rgba(0,0,0,.5),transparent)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <button style={{background:"rgba(255,255,255,.9)",border:"none",borderRadius:6,padding:"7px 16px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",color:"#0F172A"}}>Shop Now</button>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                {[["♥","1.2k"],["💬","342"],["↗","Share"]].map(([ic,lb])=>(
                  <div key={lb} style={{textAlign:"center"}}>
                    <div style={{fontSize:18,lineHeight:1}}>{ic}</div>
                    <div style={{fontSize:9,color:"rgba(255,255,255,.9)",fontWeight:600}}>{lb}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard feed post (Meta, Instagram, LinkedIn, X, Pinterest)
  const isX = p.src === "x";
  const showSidebar = !isMobile;
  return (
    <div style={{background:"#fff",borderRadius:12,border:`1px solid ${T.s2}`,overflow:"hidden",boxShadow:T.sh1}}>
      {/* Post header */}
      <div style={{padding:"10px 13px",display:"flex",alignItems:"center",gap:9,borderBottom:isX?`1px solid ${T.s2}`:"none"}}>
        <div style={{width:34,height:34,borderRadius:isLinkedIn?"4px":"50%",background:platColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff",flexShrink:0}}>D</div>
        <div style={{flex:1}}>
          <p style={{fontSize:12,fontWeight:700,color:T.ink,margin:"0 0 1px"}}>Demo Agency</p>
          <p style={{fontSize:10,color:T.ink4,margin:0}}>
            Sponsored · {platLabel}
            {isLinkedIn&&" · Promoted"}
          </p>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
      </div>

      {/* Ad copy (above image for most platforms) */}
      <div style={{padding:"8px 13px 6px"}}>
        <p style={{fontSize:12,color:T.ink,lineHeight:1.5,margin:0}}>
          {p.type==="LeadForm"?"Connect with our team — get a free consultation and see how we can grow your business together. Limited spots available.":"Summer's here and so are the deals. Shop our biggest sale of the year — up to 60% off selected styles. Don't miss out."}
        </p>
      </div>

      {/* Creative */}
      <div style={{height:p.type==="Carousel"?150:170,background:`linear-gradient(135deg,${p.g})`,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden"}}>
        <div style={{fontSize:52,opacity:.9}}>{p.emoji}</div>
        {isVideo&&(
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.85)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 12px rgba(0,0,0,.2)"}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={T.ink} stroke="none"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        )}
        {p.type==="Carousel"&&(
          <div style={{position:"absolute",bottom:8,left:"50%",transform:"translateX(-50%)",display:"flex",gap:4}}>
            {[1,2,3].map(i=><div key={i} style={{width:i===1?16:6,height:6,borderRadius:3,background:i===1?"#fff":"rgba(255,255,255,.5)"}}/>)}
          </div>
        )}
        <div style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,.45)",borderRadius:4,padding:"2px 7px",fontSize:9,fontWeight:700,color:"#fff"}}>
          {p.paid?"SPONSORED":"PROMOTED"}
        </div>
      </div>

      {/* CTA bar */}
      <div style={{padding:"10px 13px",borderTop:`1px solid ${T.s2}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:"#FAFAFA"}}>
        <div>
          <p style={{fontSize:10,color:T.ink4,margin:"0 0 1px"}}>yourwebsite.com</p>
          <p style={{fontSize:12,fontWeight:700,color:T.ink,margin:0,lineHeight:1.2}}>{p.title}</p>
          <p style={{fontSize:10,color:T.ink4,margin:"1px 0 0"}}>Summer sale · up to 60% off</p>
        </div>
        <button style={{background:isLinkedIn?"#0A66C2":T.b2,border:"none",borderRadius:6,padding:"8px 14px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",color:"#fff",whiteSpace:"nowrap",flexShrink:0}}>
          {p.type==="LeadForm"?"Get a quote":"Shop Now"}
        </button>
      </div>

      {/* Engagement bar */}
      <div style={{padding:"7px 13px",borderTop:`1px solid ${T.s2}`,display:"flex",gap:16}}>
        {[["♥",fmt(p.likes,"n")],["💬",fmt(p.comments,"n")],["↗",fmt(p.shares,"n")]].map(([ic,v])=>(
          <button key={ic} style={{display:"flex",alignItems:"center",gap:5,background:"none",border:"none",cursor:"pointer",fontSize:12,color:T.ink4,fontFamily:"inherit",padding:0}}>
            <span style={{fontSize:14}}>{ic}</span><span style={{fontSize:11}}>{v}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PostPanel({ post: p, onClose }) {
  if (!p) return null;
  const [tab, setTab] = useState("stats"); // stats | preview | creative
  const rc = p.paid ? (p.roas >= 3 ? T.g2 : p.roas >= 1.5 ? T.a2 : T.r2) : T.g2;
  const td = Array.from({ length: 14 }, (_, i) => ({
    d: `${i + 1}`,
    impr:   Math.round(p.impressions / 14 * (0.7 + (i % 5) * .1)),
    clicks: Math.round(p.clicks / 14 * (0.7 + (i % 5) * .1)),
  }));
  const groups = [
    { name: "Reach & delivery", kpis: [{ l:"Impressions",v:fmt(p.impressions,"n"),a:T.b2},{l:"Reach",v:fmt(p.reach,"n"),a:T.b3},{l:"Frequency",v:`${p.freq}×`,a:T.ink3}] },
    { name: "Engagement", kpis: [{l:"Clicks",v:fmt(p.clicks,"n"),a:T.v2},{l:"CTR",v:`${p.ctr}%`,a:T.v2},{l:"Engagements",v:fmt(p.engagements,"n"),a:T.v1},{l:"Eng. rate",v:`${p.er}%`,a:T.v2},{l:"Likes",v:fmt(p.likes,"n"),a:"#DB2777"},{l:"Comments",v:fmt(p.comments,"n"),a:T.ink3},{l:"Shares",v:fmt(p.shares,"n"),a:T.ink3},{l:"Saves",v:fmt(p.saves,"n"),a:T.ink3}] },
    ...(p.paid ? [{ name: "Paid performance", kpis: [{l:"Spend",v:fmt(p.spend,"$"),a:T.a2},{l:"Revenue",v:fmt(p.revenue,"$"),a:T.g2},{l:"ROAS",v:fmt(p.roas,"x"),a:rc},{l:"CPM",v:`$${p.cpm}`,a:T.a2},{l:"CPC",v:`$${p.cpc}`,a:T.a1},{l:"CPA",v:`$${p.cpa}`,a:T.a2},{l:"Conversions",v:String(p.conversions),a:T.g2}] }]
             : [{ name: "Organic", kpis: [{l:"Reach",v:fmt(p.reach,"n"),a:T.g2},{l:"Conversions",v:String(p.conversions),a:T.g2},{l:"Revenue",v:fmt(p.revenue,"$"),a:T.g1}] }]),
    ...(p.video_views > 0 ? [{ name: "Video", kpis: [{l:"Views",v:fmt(p.video_views,"n"),a:T.r2},{l:"VTR",v:`${p.vtr}%`,a:T.r1},{l:"Watch time",v:`${Math.round(p.watch_time/3600*10)/10}h`,a:T.r2}] }] : []),
  ];

  return (
    <div style={{ position:"fixed", inset:0, zIndex:200, display:"flex", background:"rgba(15,23,42,.38)" }} onClick={onClose}>
      <style>{`@keyframes adtech-spin{to{transform:rotate(360deg)}} @keyframes adtech-slidein{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
      <div onClick={e=>e.stopPropagation()} style={{ width:440, height:"100%", marginLeft:"auto", background:T.canvas, borderLeft:`1px solid ${T.s2}`, display:"flex", flexDirection:"column", boxShadow:"0 12px 40px rgba(15,23,42,.14)", animation:"adtech-slidein .2s ease" }}>

        {/* Panel header */}
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${T.s2}`, background:T.bg, flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
            <div>
              <p style={{ fontSize:13, fontWeight:700, color:T.ink, margin:"0 0 3px" }}>{p.title}</p>
              <p style={{ fontSize:11, color:T.ink4, margin:0, display:"flex", alignItems:"center", gap:6 }}>
                <Pip src={p.src} sz={8}/>{SRC[p.src]?.label} · {p.type} · {p.dateStr}
                <Tag tone={p.paid?"blue":"ok"}>{p.paid?"PAID":"ORGANIC"}</Tag>
              </p>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.ink4, fontSize:18, lineHeight:1, padding:4, flexShrink:0 }}>×</button>
          </div>
          {/* Tabs */}
          <div style={{ display:"flex", gap:0 }}>
            {[["stats","Stats"],["preview","Ad Preview"],["creative","Creative"]].map(([id,lbl])=>(
              <button key={id} onClick={()=>setTab(id)} style={{ padding:"6px 14px", background:"none", border:"none", borderBottom:`2px solid ${tab===id?T.b2:"transparent"}`, color:tab===id?T.b2:T.ink3, fontSize:12, fontWeight:tab===id?700:500, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>{lbl}</button>
            ))}
          </div>
        </div>

        {/* Tab body */}
        <div style={{ flex:1, overflowY:"auto", padding:"16px 18px" }}>

          {/* ── STATS TAB ── */}
          {tab==="stats" && (
            <>
              {groups.map(g => (
                <div key={g.name} style={{ marginBottom:14 }}>
                  <p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", color:T.ink4, margin:"0 0 7px", paddingBottom:5, borderBottom:`1px solid ${T.s2}` }}>{g.name}</p>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
                    {g.kpis.map(k => (
                      <div key={k.l} style={{ background:T.bg, border:`1px solid ${T.s2}`, borderRadius:8, padding:"8px 9px", textAlign:"center" }}>
                        <p style={{ fontSize:8, color:T.ink4, fontWeight:700, margin:"0 0 3px", textTransform:"uppercase", letterSpacing:".05em" }}>{k.l}</p>
                        <p style={{ fontSize:12, fontWeight:800, color:k.a, margin:0 }}>{k.v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", color:T.ink4, margin:"0 0 7px", paddingBottom:5, borderBottom:`1px solid ${T.s2}` }}>14-day trend</p>
              <ResponsiveContainer width="100%" height={100}>
                <AreaChart data={td} margin={{ top:4, right:4, left:-24, bottom:0 }}>
                  <defs>
                    <linearGradient id="ppA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor={T.b2} stopOpacity={.14}/><stop offset="95%" stopColor={T.b2} stopOpacity={0}/></linearGradient>
                    <linearGradient id="ppB" x1="0" y1="0" x2="0" y2="1"><stop offset="5%"  stopColor={T.v2} stopOpacity={.12}/><stop offset="95%" stopColor={T.v2} stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.s2} vertical={false}/>
                  <XAxis dataKey="d" tick={{fontSize:9,fill:T.ink4}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:9,fill:T.ink4}} axisLine={false} tickLine={false}/>
                  <Tooltip {...TTP} formatter={v=>[v.toLocaleString()]}/>
                  <Area dataKey="impr"   name="Impressions" stroke={T.b2} strokeWidth={2} fill="url(#ppA)" dot={false}/>
                  <Area dataKey="clicks" name="Clicks"      stroke={T.v2} strokeWidth={2} fill="url(#ppB)" dot={false}/>
                </AreaChart>
              </ResponsiveContainer>
              <div style={{ display:"flex", gap:8, marginTop:14 }}>
                <button style={{ flex:1, padding:"10px 0", borderRadius:9, border:"none", background:T.b2, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Edit Ad</button>
                <Btn tone="ghost" style={{ padding:"10px 14px" }}>↗ Live</Btn>
              </div>
            </>
          )}

          {/* ── AD PREVIEW TAB ── */}
          {tab==="preview" && (
            <>
              <div style={{ background:T.s1, border:`1px solid ${T.s2}`, borderRadius:9, padding:"8px 12px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.b2} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <p style={{ fontSize:11, color:T.ink3, margin:0 }}>
                  Simulated <strong>{SRC[p.src]?.label}</strong> ad format · actual rendering may vary per device
                </p>
              </div>
              <AdPreviewMockup post={p}/>
              <div style={{ marginTop:14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                {[["Format",p.type],["Platform",SRC[p.src]?.label],["Status",p.paid?"Paid":"Organic"]].map(([l,v])=>(
                  <div key={l} style={{ background:T.bg, borderRadius:8, padding:"8px 10px", border:`1px solid ${T.s2}`, textAlign:"center" }}>
                    <p style={{ fontSize:8, color:T.ink4, fontWeight:700, textTransform:"uppercase", margin:"0 0 3px" }}>{l}</p>
                    <p style={{ fontSize:11, fontWeight:700, color:T.ink, margin:0 }}>{v}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── CREATIVE TAB ── */}
          {tab==="creative" && (
            <>
              <div style={{ height:180, borderRadius:12, background:`linear-gradient(135deg,${p.g})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:60, marginBottom:16, boxShadow:T.sh1, position:"relative" }}>
                {p.emoji}
                <div style={{ position:"absolute", bottom:10, right:10 }}>
                  <Tag tone={p.paid?"blue":"ok"}>{p.type}</Tag>
                </div>
              </div>
              {[["Title",p.title],["Platform",SRC[p.src]?.label],["Ad type",p.type],["Date",p.dateStr],["Status",p.paid?"Paid":"Organic"]].map(([l,v])=>(
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${T.s2}` }}>
                  <span style={{ fontSize:11, color:T.ink4, fontWeight:600 }}>{l}</span>
                  <span style={{ fontSize:11, color:T.ink, fontWeight:500 }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop:14, padding:"12px 14px", background:T.bL, border:`1px solid ${T.bM}`, borderRadius:9 }}>
                <p style={{ fontSize:11, fontWeight:700, color:T.b1, margin:"0 0 4px" }}>Ad copy</p>
                <p style={{ fontSize:12, color:T.ink2, margin:0, lineHeight:1.6 }}>
                  {p.type==="LeadForm"?"Connect with our team — get a free consultation and see how we can grow your business. Limited spots.":"Summer's here and so are the deals. Shop our biggest sale — up to 60% off. Don't miss out on limited-time offers."}
                </p>
              </div>
              <div style={{ display:"flex", gap:8, marginTop:14 }}>
                <button style={{ flex:1, padding:"10px 0", borderRadius:9, border:"none", background:T.b2, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Edit Creative</button>
                <Btn tone="ghost" style={{ padding:"10px 14px" }}>↗ Live preview</Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
// SVG icon paths keyed by nav id
const NAV_ICONS = {
  "dashboard":      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  "posts":          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
  "leads":          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  "organic-report": <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M3 3v18h18"/><path d="M18 17l-5-5-3 3-4-4"/></svg>,
  "organic-paid":   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><circle cx="9" cy="12" r="6"/><circle cx="15" cy="12" r="6"/></svg>,
  "create":         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>,
  "reports":        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  "ad-connectors":  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  "crm-connectors": <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  "clients":        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  "social-post":    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg>,
};

const NAV_ITEMS = [
  { id: "dashboard",      label: "Dashboard"         },
  { id: "posts",          label: "Posts"             },
  { id: "social-post",    label: "Social Post"       },
  { id: "leads",          label: "Leads",   badge: "12" },
  { id: "create",         label: "Create Ad"         },
  { id: "reports",        label: "Reports"           },
];
const ANALYTICS_ITEMS = [
  { id: "organic-report", label: "Organic"         },
  { id: "organic-paid",   label: "Paid vs Organic" },
  { id: "funnel",         label: "Funnel"          },
  { id: "mediamix",       label: "Media Mix"       },
  { id: "prog-report",    label: "Programmatic"    },
];
const OPTIMISE_ITEMS = [
  { id: "pacing",         label: "Budget Pacing"   },
  { id: "anomalies",      label: "Anomaly Feed"    },
  { id: "fatigue",        label: "Creative Fatigue"},
  { id: "abtests",        label: "A/B Tests"       },
];
const ADMIN_ITEMS = [
  { id: "portal",         label: "Client Portal"               },
  { id: "ad-connectors",  label: "Ad Connectors",  alert: true  },
  { id: "crm-connectors", label: "CRM Connectors"               },
  { id: "clients",        label: "Clients"                      },
  { id: "settings",       label: "Settings"                     },
];

function NavItem({ id, label, page, setPage, badge, alert }) {
  const on = page === id;
  const icon = NAV_ICONS[id];
  return (
    <button onClick={() => setPage(id)} style={{
      display:"flex", alignItems:"center", gap:9, padding:"7px 10px",
      borderRadius:6, width:"100%", textAlign:"left", border:"none",
      background: on ? T.s1 : "transparent",
      color: on ? T.ink : T.ink3,
      cursor:"pointer", transition:"background .1s", fontFamily:"inherit",
      fontSize:13, fontWeight: on ? 500 : 400,
    }}>
      <span style={{ flexShrink:0, display:"flex", opacity: on ? 1 : 0.55 }}>{icon}</span>
      <span style={{ flex:1 }}>{label}</span>
      {badge && (
        <span style={{ background:T.brand, color:"#fff", fontSize:10, fontWeight:500, padding:"1px 6px", borderRadius:10 }}>{badge}</span>
      )}
      {alert && !badge && (
        <span style={{ width:7, height:7, borderRadius:"50%", background:T.r2, flexShrink:0, display:"inline-block" }}/>
      )}
    </button>
  );
}

function Sidebar({ page, setPage, onAI, client, onSwitchClient }) {
  return (
    <aside style={{ width:216, flexShrink:0, background:T.canvas, borderRight:`1px solid ${T.s2}`, display:"flex", flexDirection:"column", height:"100%" }}>
      {/* Logo — solid dark mark, not gradient */}
      <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:28, height:28, background:`linear-gradient(135deg,${T.b2},${T.v2})`, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        </div>
        <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0, letterSpacing:"-.01em" }}>AdTech</p>
      </div>

      {/* Workspace switcher — understated */}
      <button onClick={onSwitchClient} style={{ padding:"10px 16px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", background:"transparent", border:"none", textAlign:"left", fontFamily:"inherit", width:"100%" }}>
        <div style={{ minWidth:0 }}>
          <p style={{ fontSize:10, fontWeight:400, color:T.ink4, margin:"0 0 3px", letterSpacing:".03em" }}>Workspace</p>
          {client
            ? <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{client.name}</p>
            : <p style={{ fontSize:13, color:T.ink4, margin:0 }}>Select a client…</p>
          }
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="1.75" strokeLinecap="round" style={{ flexShrink:0 }}><path d="M6 9l6 6 6-6"/></svg>
      </button>

      {/* Nav */}
      <nav style={{ flex:1, padding:"8px 8px", overflowY:"auto", display:"flex", flexDirection:"column", gap:1, opacity: client ? 1 : 0.35, pointerEvents: client ? "auto" : "none" }}>
        {NAV_ITEMS.map(item => <NavItem key={item.id} {...item} page={page} setPage={setPage}/>)}

        <div style={{ height:1, background:T.s2, margin:"8px 8px 6px" }}/>
        <p style={{ padding:"0 10px 4px", fontSize:10, fontWeight:400, color:T.ink4, margin:0, letterSpacing:".04em" }}>Analytics</p>
        {ANALYTICS_ITEMS.map(item => <NavItem key={item.id} {...item} page={page} setPage={setPage}/>)}

        <div style={{ height:1, background:T.s2, margin:"8px 8px 6px" }}/>
        <p style={{ padding:"0 10px 4px", fontSize:10, fontWeight:400, color:T.ink4, margin:0, letterSpacing:".04em" }}>Optimise</p>
        {OPTIMISE_ITEMS.map(item => <NavItem key={item.id} {...item} page={page} setPage={setPage}/>)}

        <div style={{ height:1, background:T.s2, margin:"8px 8px 6px" }}/>
        <p style={{ padding:"0 10px 4px", fontSize:10, fontWeight:400, color:T.ink4, margin:0, letterSpacing:".04em" }}>Settings</p>
        {ADMIN_ITEMS.map(item => <NavItem key={item.id} {...item} page={page} setPage={setPage}/>)}
      </nav>

      {/* AI button — tertiary, not a feature hero */}
      <div style={{ padding:"8px 8px", borderTop:`1px solid ${T.s2}` }}>
        <button onClick={onAI} style={{ display:"flex", alignItems:"center", gap:9, width:"100%", padding:"7px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:400, transition:"background .1s" }}
          onMouseEnter={e=>e.currentTarget.style.background=T.s1}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
          AI Assistant
        </button>
      </div>

      {/* User row */}
      <div style={{ padding:"10px 14px", borderTop:`1px solid ${T.s2}`, display:"flex", alignItems:"center", gap:9 }}>
        <div style={{ width:26, height:26, borderRadius:"50%", background:T.s1, border:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:500, color:T.ink3, flexShrink:0 }}>A</div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontSize:12, fontWeight:400, color:T.ink2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", margin:0 }}>admin@adtech.local</p>
        </div>
      </div>
    </aside>
  );
}

// ─── Dashboard Page ───────────────────────────────────────────────────────────
function Dashboard({ setSel, setShowReport, setShowPreview, client }) {
  const [dv,    setDv]      = useState({ y: 2026, m: 2, d: null });
  const [pre,   setPre]     = useState("30d");
  const [mode,  setMode]    = useState("single"); // "single" | "range"
  const [range, setRange]   = useState({ start: null, end: null });
  const dots = POSTS.map(p => p.date);

  const toNum = (v) => v ? v.y*10000 + v.m*100 + (v.d||0) : 0;
  const fp = POSTS.filter(p => {
    if (mode === "range" && range.start && range.end) {
      const n = p.date.getFullYear()*10000 + p.date.getMonth()*100 + p.date.getDate();
      const s = toNum(range.start), e = toNum(range.end);
      return n >= Math.min(s,e) && n <= Math.max(s,e);
    }
    if (dv.d != null)  return p.date.getFullYear() === dv.y && p.date.getMonth() === dv.m && p.date.getDate() === dv.d;
    if (dv.m != null)  return p.date.getFullYear() === dv.y && p.date.getMonth() === dv.m;
    return p.date.getFullYear() === dv.y;
  });

  const fmtShort = (v) => v ? `${MO[v.m].slice(0,3)} ${v.d}` : "?";
  const dlbl = mode === "range" && range.start
    ? `${fmtShort(range.start)} – ${fmtShort(range.end)}`
    : dv.d != null ? `${MO[dv.m].slice(0, 3)} ${dv.d}, ${dv.y}`
    : dv.m != null ? `${MO[dv.m]} ${dv.y}` : `${dv.y}`;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", background: T.bg }}>
      <style>{`@keyframes adtech-spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header + top-right controls */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: T.ink, margin: "0 0 3px", letterSpacing: "-.01em" }}>{client?.name || "Demo Agency"}</h1>
          <p style={{ fontSize: 11, color: T.ink3, margin: 0 }}>
            {dlbl} ·{" "}
            <span style={{ color: T.g2, fontWeight: 600 }}>● 4/9 sources</span>
            {fp.length > 0 && <> · <strong style={{ color: T.b2 }}>{fp.length}</strong> post{fp.length !== 1 ? "s" : ""}</>}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Date presets */}
          <div style={{ display: "flex", background: T.s1, border: `1px solid ${T.s2}`, borderRadius: 9, padding: 3, gap: 2 }}>
            {["7d", "30d", "90d"].map(p => (
              <button key={p} onClick={() => setPre(p)} style={{ padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 700, border: "none", background: pre === p ? T.b2 : "transparent", color: pre === p ? "#fff" : T.ink3, cursor: "pointer", transition: "all .12s", fontFamily: "inherit" }}>{p}</button>
            ))}
          </div>
          {/* Date mode toggle */}
          <div style={{ display:"flex", background:T.s1, border:`1px solid ${T.s2}`, borderRadius:9, padding:3, gap:2 }}>
            {["single","range"].map(m=>(
              <button key={m} onClick={()=>setMode(m)} style={{ padding:"5px 12px", borderRadius:7, fontSize:11, fontWeight:700, border:"none", background:mode===m?T.b2:"transparent", color:mode===m?"#fff":T.ink3, cursor:"pointer", transition:"all .12s", fontFamily:"inherit" }}>
                {m==="single"?"Date":"Range"}
              </button>
            ))}
          </div>
          {/* Calendar */}
          {mode==="single"
            ? <CalPicker val={dv} onChange={setDv} dots={dots}/>
            : <CalPicker rangeMode val={dv} onChange={setDv} dots={dots} range={range} onRangeChange={setRange}/>
          }
          {/* Preview — secondary */}
          <Btn tone="secondary" onClick={() => setShowPreview && setShowPreview(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            Preview
          </Btn>
          {/* PDF — secondary */}
          <PDFBtn label="Download PDF" type="weekly" />
          {/* Send — the one primary action */}
          <Btn tone="primary" onClick={() => setShowReport(true)}>Send report</Btn>
        </div>
      </div>

      {/* KPI groups — labelled sections */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 10, fontWeight: 500, color: T.ink4, textTransform: "uppercase", letterSpacing: ".08em", margin: "0 0 8px" }}>Paid performance</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 16 }}>
          <KpiTile label="Spend"       value="$84.2k" chg="+12.5%" dir="up" />
          <KpiTile label="Revenue"     value="$247k"  chg="+18.3%" dir="up" />
          <KpiTile label="ROAS"        value="2.93×"  chg="+5.2%"  dir="up" featured />
          <KpiTile label="Conversions" value="1,840"  chg="+9.1%"  dir="up" />
        </div>
        <p style={{ fontSize: 10, fontWeight: 500, color: T.ink4, textTransform: "uppercase", letterSpacing: ".08em", margin: "0 0 8px" }}>Reach &amp; efficiency</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
          <KpiTile label="Impressions"  value="7.4M"  chg="+8.2%"  dir="up" />
          <KpiTile label="CTR"          value="1.74%"                        />
          <KpiTile label="CPM"          value="$11.40" chg="−3.1%" dir="dn" />
          <KpiTile label="Engagements"  value="318k"  chg="+14.1%" dir="up" />
          <KpiTile label="Video views"  value="1.2M"  chg="+22%"   dir="up" />
        </div>
      </div>

      {/* Channel chart + date-filtered posts */}
      <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 14, marginBottom: 14 }}>
        <Card>
          <SH title="Channel performance" sub="Spend vs revenue · 30d" />
          <ResponsiveContainer width="100%" height={185}>
            <BarChart data={CH_DATA} margin={{ top: 4, right: 4, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.s2} vertical={false} />
              <XAxis dataKey="n" tick={{ fontSize: 10, fill: T.ink4 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: T.ink4 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
              <Tooltip {...TTP} formatter={v => [`$${v.toLocaleString()}`]} />
              <Legend wrapperStyle={{ fontSize: 11, color: T.ink3 }} />
              <Bar dataKey="sp" name="Spend"   fill={T.b2} radius={[4, 4, 0, 0]} />
              <Bar dataKey="rv" name="Revenue" fill={T.g2} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ display: "flex", flexDirection: "column" }}>
          <SH title={`Posts · ${dlbl}`} sub={fp.length === 0 ? "Pick a date with a blue dot" : "Click any post for full KPIs"} />
          {fp.length === 0 ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 20 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: T.bL, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.b2} strokeWidth="1.5" strokeLinecap="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <p style={{ fontSize: 12, fontWeight: 600, color: T.ink3, textAlign: "center", margin: 0 }}>No posts on this date</p>
              <p style={{ fontSize: 10, color: T.ink4, textAlign: "center", margin: 0 }}>Dates with posts show a blue dot in the calendar</p>
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 7 }}>
              {fp.map(p => (
                <button key={p.id} onClick={() => setSel(p)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", background: T.bg, border: `1px solid ${T.s2}`, borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: "inherit", width: "100%", transition: "all .15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.b2; e.currentTarget.style.background = T.bL; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.s2; e.currentTarget.style.background = T.bg; }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg,${p.g})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 16 }}>{p.emoji}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: "0 0 2px" }}>{p.title}</p>
                    <p style={{ fontSize: 9, color: T.ink4, margin: 0, display: "flex", alignItems: "center", gap: 4 }}>
                      <Pip src={p.src} sz={6} />{SRC[p.src]?.label}
                      <span style={{ color: T.b2, fontWeight: 600 }}>· {fmt(p.impressions, "n")} impr</span>
                      <span style={{ color: p.roas >= 3 ? T.g2 : p.roas >= 1.5 ? T.a2 : T.r2, fontWeight: 600 }}>· {p.paid ? `${p.roas}× ROAS` : `${p.er}% eng`}</span>
                    </p>
                  </div>
                  <Tag tone={p.paid ? "blue" : "ok"}>{p.paid ? "PAID" : "ORG"}</Tag>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* All posts grid */}
      <Card>
        <SH title="All posts & ads" sub="Full digital KPIs on every post · click to open detail panel on the right" right={<span style={{ fontSize: 11, color: T.ink4 }}>{POSTS.length} posts</span>} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
          {POSTS.map(p => <PostCard key={p.id} post={p} onClick={setSel} />)}
        </div>
      </Card>
    </div>
  );
}

// ─── Organic Report Page ──────────────────────────────────────────────────────
function OrganicReport({ setShowReport, setPage }) {
  const [shareOpen, setShareOpen] = useState(false);

  // Pre-drafted post copy from organic insights
  const organicDraft = "Our organic channels delivered this week 📈\n\n✅ 28.4k sessions from SEO & social (zero ad spend)\n✅ Organic CVR: 3.2% — outperforming paid traffic\n✅ Reach up 14.3% week-over-week\n\nProof that owned channels compound. What\'s your organic strategy?\n\n#OrganicMarketing #ContentMarketing #DigitalStrategy";
  const organic = POSTS.filter(p => !p.paid);
  const td = Array.from({ length: 30 }, (_, i) => ({
    d: `${i + 1}`,
    sessions: 900 + Math.sin(i * .35) * 150 + ((i * 13) % 100),
    revenue:  580 + Math.sin(i * .3)  * 80  + ((i * 11) % 70),
  }));
  const totImpr = organic.reduce((s, p) => s + p.impressions, 0);
  const totEng  = organic.reduce((s, p) => s + p.engagements, 0);
  const totConv = organic.reduce((s, p) => s + p.conversions, 0);
  const totRev  = organic.reduce((s, p) => s + p.revenue, 0);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", background: T.bg }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.g2, display: "inline-block" }} />
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: T.g2, margin: 0 }}>Organic Report</p>
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: T.ink, margin: "0 0 4px" }}>Organic Performance</h1>
          <p style={{ fontSize: 12, color: T.ink3, margin: 0 }}>Demo Agency · Feb 20 – Mar 21, 2026 · $0 paid spend</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <PDFBtn label="Download PDF" type="organic" />
          <Btn tone="secondary" onClick={() => setShareOpen && setShareOpen(true)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg>
            Share to social
          </Btn>
          <Btn tone="primary" onClick={() => setShowReport(true)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" /></svg>
            Send Report
          </Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 16 }}>
        <KpiTile label="Impressions"    value={fmt(totImpr, "n")}              chg="+8.2%"  dir="up" accent={T.b2} />
        <KpiTile label="Organic reach"  value={fmt(Math.round(totImpr * .68), "n")} chg="+6.1%"  dir="up" accent={T.b3} />
        <KpiTile label="Engagements"    value={fmt(totEng, "n")}               chg="+14.3%" dir="up" accent={T.v2} />
        <KpiTile label="Conversions"    value={fmt(totConv, "n")}              chg="+9.8%"  dir="up" accent={T.g2} />
        <KpiTile label="Organic revenue"value={fmt(totRev, "$")}               chg="+12.4%" dir="up" accent={T.g1} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14, marginBottom: 16 }}>
        <Card>
          <SH title="Sessions & revenue — organic only" sub="30-day trend · $0 ad spend" />
          <ResponsiveContainer width="100%" height={170}>
            <AreaChart data={td} margin={{ top: 4, right: 4, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="oS" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={T.b2} stopOpacity={.14} />
                  <stop offset="95%" stopColor={T.b2} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="oR" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={T.g2} stopOpacity={.14} />
                  <stop offset="95%" stopColor={T.g2} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.s2} vertical={false} />
              <XAxis dataKey="d" tick={{ fontSize: 9, fill: T.ink4 }} axisLine={false} tickLine={false} interval={5} />
              <YAxis tick={{ fontSize: 9, fill: T.ink4 }} axisLine={false} tickLine={false} />
              <Tooltip {...TTP} formatter={v => [Math.round(v).toLocaleString()]} />
              <Legend wrapperStyle={{ fontSize: 11, color: T.ink3 }} />
              <Area dataKey="sessions" name="Sessions"  stroke={T.b2} strokeWidth={2} fill="url(#oS)" dot={false} />
              <Area dataKey="revenue"  name="Revenue $" stroke={T.g2} strokeWidth={2} fill="url(#oR)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <SH title="Channel breakdown" sub="Organic sources only" />
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {[
              { src: "ga4",       lbl: "SEO / Google",       s: "14,200", cvr: "4.1%", rev: "$9,800" },
              { src: "instagram", lbl: "Instagram",           s: "4,800",  cvr: "3.2%", rev: "$3,900" },
              { src: "shopify",   lbl: "Email / Newsletter",  s: "4,200",  cvr: "5.8%", rev: "$3,100" },
              { src: "tiktok",    lbl: "TikTok Organic",      s: "3,800",  cvr: "2.9%", rev: "$1,600" },
              { src: "x",         lbl: "Direct / Dark social",s: "1,400",  cvr: "2.4%", rev: "$500"   },
            ].map(r => (
              <div key={r.src} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", background: T.bg, borderRadius: 9, border: `1px solid ${T.s2}` }}>
                <Pip src={r.src} sz={9} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.ink, flex: 1 }}>{r.lbl}</span>
                <span style={{ fontSize: 10, color: T.ink4, width: 48, textAlign: "right" }}>{r.s}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: T.g2, width: 36, textAlign: "right" }}>{r.cvr}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.g1, width: 46, textAlign: "right" }}>{r.rev}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <SH title="Organic posts & content" sub={`${organic.length} posts · all engagement KPIs`} right={<PDFBtn label="Export CSV" type="organic_posts" />} />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${T.s2}` }}>
                {["Content", "Platform", "Type", "Impressions", "Reach", "Clicks", "CTR", "Engagements", "Eng. rate", "Likes", "Saves", "Conversions", "Revenue"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "0 10px 10px 0", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: T.ink4, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {organic.map(p => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${T.s2}` }}
                  onMouseEnter={e => e.currentTarget.style.background = T.s1}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "9px 10px 9px 0", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: T.ink, margin: 0 }}>{p.title}</p>
                    <p style={{ fontSize: 9, color: T.ink4, margin: 0 }}>{p.dateStr}</p>
                  </td>
                  <td style={{ padding: "9px 10px 9px 0" }}><span style={{ display: "inline-flex", alignItems: "center" }}><Pip src={p.src} sz={7} /><span style={{ color: T.ink3 }}>{SRC[p.src]?.label}</span></span></td>
                  <td style={{ padding: "9px 10px 9px 0" }}><Tag tone="neutral">{p.type}</Tag></td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.b2, fontWeight: 600 }}>{fmt(p.impressions, "n")}</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.ink3 }}>{fmt(p.reach, "n")}</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.v2, fontWeight: 600 }}>{fmt(p.clicks, "n")}</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.ink3 }}>{p.ctr}%</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.v2 }}>{fmt(p.engagements, "n")}</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.ink3 }}>{p.er}%</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.ink3 }}>{fmt(p.likes, "n")}</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.ink3 }}>{fmt(p.saves, "n")}</td>
                  <td style={{ padding: "9px 10px 9px 0", color: T.g2, fontWeight: 600 }}>{p.conversions}</td>
                  <td style={{ padding: "9px 0", color: T.g1, fontWeight: 700 }}>{fmt(p.revenue, "$")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Reports Page ─────────────────────────────────────────────────────────────
// ─── Report Type 3D Icons ─────────────────────────────────────────────────────
function Icon3DBarChart({ active }) {
  const base = active ? "#1D4ED8" : "#64748B";
  const mid  = active ? "#3B82F6" : "#94A3B8";
  const top  = active ? "#BFDBFE" : "#CBD5E1";
  const face = active ? "#EFF6FF" : "#F1F5F9";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Bar 1 — tall */}
      <rect x="5"  y="10" width="8" height="22" fill={base} rx="1"/>
      <polygon points="5,10 9,7 17,7 13,10" fill={top}/>
      <polygon points="13,10 17,7 17,29 13,32" fill={mid}/>
      {/* Bar 2 — medium */}
      <rect x="16" y="18" width="8" height="14" fill={base} rx="1"/>
      <polygon points="16,18 20,15 28,15 24,18" fill={top}/>
      <polygon points="24,18 28,15 28,29 24,32" fill={mid}/>
      {/* Bar 3 — short */}
      <rect x="27" y="22" width="8" height="10" fill={base} rx="1"/>
      <polygon points="27,22 31,19 39,19 35,22" fill={top}/>
      <polygon points="35,22 39,19 39,29 35,32" fill={mid}/>
      {/* Floor */}
      <rect x="3" y="32" width="34" height="2" fill={face} rx="1"/>
    </svg>
  );
}

function Icon3DLineChart({ active }) {
  const base = active ? "#1D4ED8" : "#64748B";
  const mid  = active ? "#3B82F6" : "#94A3B8";
  const top  = active ? "#BFDBFE" : "#CBD5E1";
  const bg   = active ? "#EFF6FF" : "#F1F5F9";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Isometric chart base */}
      <polygon points="4,32 20,24 36,32 20,40" fill={bg}/>
      <polygon points="4,12 4,32 20,24 20,4" fill={mid}/>
      <polygon points="20,4 20,24 36,32 36,12" fill={top}/>
      {/* Trend line on front face */}
      <polyline points="6,28 12,22 18,24 24,18 30,14" stroke={base} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="30" cy="14" r="2.5" fill={base}/>
      <circle cx="12" cy="22" r="2" fill={base} opacity="0.7"/>
    </svg>
  );
}

function Icon3DLightning({ active }) {
  const base = active ? "#1D4ED8" : "#64748B";
  const mid  = active ? "#3B82F6" : "#94A3B8";
  const top  = active ? "#BFDBFE" : "#CBD5E1";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 3D bolt — front face */}
      <polygon points="22,4 14,20 20,20 17,36 27,18 21,18" fill={base}/>
      {/* Right shadow face */}
      <polygon points="27,18 21,18 17,36 23,33" fill={mid}/>
      {/* Top highlight */}
      <polygon points="22,4 25,6 27,18 21,18 20,20 14,20" fill={top} opacity="0.6"/>
    </svg>
  );
}

function Icon3DLeaf({ active }) {
  const base = active ? "#047857" : "#64748B";
  const mid  = active ? "#059669" : "#94A3B8";
  const top  = active ? "#A7F3D0" : "#CBD5E1";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 3D leaf shape */}
      {/* Main leaf body */}
      <path d="M20 34 C20 34 6 28 6 16 C6 8 12 4 20 6 C28 4 34 8 34 16 C34 28 20 34 20 34Z" fill={base}/>
      {/* Highlight top face */}
      <path d="M20 6 C28 4 34 8 34 16 C34 22 28 27 20 30 C20 30 26 24 26 16 C26 10 23 7 20 6Z" fill={top} opacity="0.5"/>
      {/* Stem */}
      <line x1="20" y1="34" x2="20" y2="38" stroke={mid} strokeWidth="2" strokeLinecap="round"/>
      {/* Vein */}
      <path d="M20 8 C20 20 18 28 20 34" stroke={top} strokeWidth="1" opacity="0.6" fill="none"/>
    </svg>
  );
}

function Icon3DBriefcase({ active }) {
  const base = active ? "#1D4ED8" : "#64748B";
  const mid  = active ? "#3B82F6" : "#94A3B8";
  const top  = active ? "#BFDBFE" : "#CBD5E1";
  const face = active ? "#EFF6FF" : "#F1F5F9";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 3D briefcase */}
      {/* Front face */}
      <rect x="6" y="15" width="26" height="18" fill={base} rx="2"/>
      {/* Top face */}
      <polygon points="6,15 10,11 36,11 32,15" fill={top}/>
      {/* Right face */}
      <polygon points="32,15 36,11 36,29 32,33" fill={mid}/>
      {/* Handle front */}
      <path d="M14 15 L14 12 C14 10 26 10 26 12 L26 15" stroke={face} strokeWidth="2" fill="none" strokeLinecap="round"/>
      {/* Clasp */}
      <rect x="17" y="23" width="6" height="4" fill={face} rx="1" opacity="0.8"/>
      {/* Horizontal bar */}
      <line x1="6" y1="23" x2="32" y2="23" stroke={face} strokeWidth="1" opacity="0.4"/>
    </svg>
  );
}

function Icon3DCustom({ active }) {
  const base = active ? "#7C3AED" : "#64748B";
  const mid  = active ? "#6D28D9" : "#94A3B8";
  const top  = active ? "#DDD6FE" : "#CBD5E1";
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 3D gear / settings */}
      {/* Main hex */}
      <polygon points="20,4 29,9 29,19 20,24 11,19 11,9" fill={base}/>
      <polygon points="20,4 23,5 32,10 29,9" fill={top}/>
      <polygon points="29,9 32,10 32,20 29,19" fill={mid}/>
      {/* Inner circle front */}
      <circle cx="20" cy="14" r="5" fill={top} opacity="0.8"/>
      {/* Teeth top */}
      <rect x="18" y="1" width="4" height="5" fill={base} rx="1"/>
      {/* Teeth bottom */}
      <rect x="18" y="22" width="4" height="5" fill={base} rx="1"/>
      {/* Teeth left */}
      <rect x="7" y="11" width="5" height="4" fill={base} rx="1"/>
      {/* Teeth right */}
      <rect x="28" y="11" width="5" height="4" fill={base} rx="1"/>
      {/* Plus symbol for custom */}
      <line x1="20" y1="11" x2="20" y2="17" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <line x1="17" y1="14" x2="23" y2="14" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

const REPORT_TYPE_ICONS = {
  weekly:  (active) => <Icon3DBarChart  active={active}/>,
  monthly: (active) => <Icon3DLineChart active={active}/>,
  daily:   (active) => <Icon3DLightning active={active}/>,
  organic: (active) => <Icon3DLeaf      active={active}/>,
  b2b:     (active) => <Icon3DBriefcase active={active}/>,
  custom:  (active) => <Icon3DCustom    active={active}/>,
};


// ─── Social Post Page ─────────────────────────────────────────────────────────
const SOCIAL_POST_PLATFORMS = [
  { id:"meta",      label:"Facebook",  color:"#1877F2", maxChars:63206, needsAdmin:true  },
  { id:"instagram", label:"Instagram", color:"#E1306C", maxChars:2200,  needsAdmin:true  },
  { id:"linkedin",  label:"LinkedIn",  color:"#0A66C2", maxChars:3000,  needsAdmin:true  },
  { id:"x",         label:"X",         color:"#000000", maxChars:280,   needsAdmin:false },
  { id:"tiktok",    label:"TikTok",    color:"#2DD4BF", maxChars:2200,  needsAdmin:true  },
  { id:"pinterest", label:"Pinterest", color:"#E60023", maxChars:500,   needsAdmin:true  },
];

const SCHEDULED_POSTS = [
  { id:"sp1", platforms:["linkedin","meta"],    body:"Our Q1 results are in — 2.93× ROAS across all channels, up 18% year-over-year. Here's what drove the growth…", status:"scheduled", date:"Mar 24 · 09:00", created:"Mar 22" },
  { id:"sp2", platforms:["instagram","tiktok"], body:"Behind the scenes of our latest campaign shoot 🎬 The team behind the Summer Sale creative…",                   status:"scheduled", date:"Mar 25 · 12:00", created:"Mar 22" },
  { id:"sp3", platforms:["linkedin"],           body:"We're hiring! Looking for a Senior Paid Media Manager to join our growing team in Dubai.",                       status:"published", date:"Mar 21 · 09:00", created:"Mar 20" },
  { id:"sp4", platforms:["meta","instagram"],   body:"Happy Monday! Here's your weekly performance tip: refresh creatives every 14 days to beat ad fatigue.",          status:"published", date:"Mar 17 · 10:00", created:"Mar 16" },
];

function SocialPostPage() {
  const [view, setView]             = useState("compose"); // compose | scheduled | published
  const [platforms, setPlatforms]   = useState(["linkedin","meta"]);
  const [body, setBody]             = useState("");
  const [headline, setHeadline]     = useState("");
  const [link, setLink]             = useState("");
  const [schedType, setSchedType]   = useState("now");
  const [schedDate, setSchedDate]   = useState("2026-03-24");
  const [schedTime, setSchedTime]   = useState("09:00");
  const [timezone,  setTimezone]    = useState("UTC+4 (Dubai)");
  const [publishing, setPubl]       = useState(false);
  const [published, setPub]         = useState(null);
  const [previewPlat, setPreviewP]  = useState("linkedin");
  const [posts, setPosts]           = useState(SCHEDULED_POSTS);

  const togglePlatform = id => setPlatforms(ps => ps.includes(id)?ps.filter(x=>x!==id):[...ps,id]);
  const selPlats = SOCIAL_POST_PLATFORMS.filter(p => platforms.includes(p.id));
  const activePrev = SOCIAL_POST_PLATFORMS.find(p=>p.id===previewPlat) || selPlats[0];
  const charLimit = activePrev?.maxChars||3000;

  const publish = () => {
    setPubl(true);
    setTimeout(()=>{
      setPubl(false);
      const newPost = { id:"sp"+Date.now(), platforms, body, status:schedType==="now"?"published":"scheduled", date:schedType==="now"?"Just now":`${schedDate} · ${schedTime} · ${timezone}`, created:"Mar 23" };
      setPosts(ps=>[newPost,...ps]);
      setPub({ platforms, schedType, schedDate, schedTime, timezone });
    }, 1500);
  };

  const reset = () => { setPub(null); setBody(""); setHeadline(""); setLink(""); setPlatforms(["linkedin","meta"]); setSchedType("now"); };

  const INP = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const LBL = { fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 };

  const scheduledPosts  = posts.filter(p=>p.status==="scheduled");
  const publishedPosts  = posts.filter(p=>p.status==="published");

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:T.bg}}>
      <style>{`@keyframes adtech-spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{padding:"16px 22px 0",background:T.canvas,borderBottom:`1px solid ${T.s2}`,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div>
            <h1 style={{fontSize:18,fontWeight:500,color:T.ink,margin:"0 0 3px"}}>Social post</h1>
            <p style={{fontSize:12,color:T.ink3,margin:0}}>Compose, schedule and publish across all connected platforms</p>
          </div>
          <div style={{display:"flex",gap:6}}>
            <span style={{padding:"4px 10px",borderRadius:6,background:T.bL,color:T.b1,fontSize:11,fontWeight:600}}>{scheduledPosts.length} scheduled</span>
            <span style={{padding:"4px 10px",borderRadius:6,background:T.gL,color:T.g1,fontSize:11,fontWeight:600}}>{publishedPosts.length} published</span>
          </div>
        </div>
        <div style={{display:"flex",borderBottom:`2px solid ${T.s2}`,marginBottom:-2}}>
          {[["compose","Compose"],["scheduled","Scheduled ("+scheduledPosts.length+")"],["published","Published"]].map(([t,l])=>(
            <button key={t} onClick={()=>{setView(t);reset();}} style={{padding:"8px 18px",background:"none",border:"none",borderBottom:`2px solid ${view===t?T.b2:"transparent"}`,marginBottom:-2,color:view===t?T.b2:T.ink3,fontSize:12,fontWeight:view===t?600:400,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}>{l}</button>
          ))}
        </div>
      </div>

      {/* ══ COMPOSE TAB ══ */}
      {view==="compose" && (
        <div style={{flex:1,display:"flex",overflow:"hidden"}}>
          {/* Left: form */}
          <div style={{flex:1,overflowY:"auto",padding:"20px 22px",borderRight:`1px solid ${T.s2}`}}>

            {published ? (
              /* Success */
              <div style={{textAlign:"center",padding:"60px 20px",maxWidth:480,margin:"0 auto"}}>
                <div style={{width:60,height:60,borderRadius:"50%",background:T.gL,border:`2px solid ${T.gM}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 18px",fontSize:26}}>✓</div>
                <p style={{fontSize:20,fontWeight:600,color:T.ink,margin:"0 0 8px"}}>{published.schedType==="now"?"Published!":"Scheduled!"}</p>
                <p style={{fontSize:13,color:T.ink3,margin:"0 0 6px"}}>
                  {published.schedType==="now"
                    ? `Live on ${published.platforms.map(id=>SOCIAL_POST_PLATFORMS.find(p=>p.id===id)?.label).join(", ")}`
                    : `Scheduled for ${published.schedDate} at ${published.schedTime} (${published.timezone||"UTC+4 (Dubai)"})`
                  }
                </p>
                <p style={{fontSize:12,color:T.ink4,margin:"0 0 24px"}}>Platforms: {published.platforms.map(id=>SOCIAL_POST_PLATFORMS.find(p=>p.id===id)?.label).join(", ")}</p>
                <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                  <button onClick={reset} style={{padding:"8px 20px",borderRadius:7,border:`1px solid ${T.s2}`,background:"transparent",color:T.ink2,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>New post</button>
                  <button onClick={()=>setView("scheduled")} style={{padding:"8px 20px",borderRadius:7,border:"none",background:T.brand,color:"#fff",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"inherit"}}>View scheduled</button>
                </div>
              </div>
            ) : (
            <div style={{maxWidth:600}}>
              {/* Platform selector */}
              <div style={{marginBottom:18}}>
                <label style={LBL}>Post to <span style={{color:T.ink4,fontWeight:400,fontSize:9}}>— select all that apply</span></label>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:6}}>
                  {SOCIAL_POST_PLATFORMS.map(p=>{
                    const on=platforms.includes(p.id);
                    return (
                      <button key={p.id} onClick={()=>togglePlatform(p.id)} style={{
                        display:"flex",alignItems:"center",gap:7,padding:"7px 14px",borderRadius:20,
                        border:`1.5px solid ${on?p.color:T.s2}`,background:on?p.color+"18":T.canvas,
                        color:on?p.color:T.ink3,fontSize:12,fontWeight:on?600:400,cursor:"pointer",fontFamily:"inherit",
                      }}>
                        <div style={{width:9,height:9,borderRadius:"50%",background:p.color}}/>
                        {p.label}
                        {!p.needsAdmin&&<span style={{fontSize:9,opacity:.6}}>no admin needed</span>}
                      </button>
                    );
                  })}
                </div>
                {platforms.some(id=>SOCIAL_POST_PLATFORMS.find(p=>p.id===id)?.needsAdmin) && (
                  <p style={{fontSize:11,color:T.a2,margin:"8px 0 0",background:T.aL,border:`1px solid ${T.aM}`,borderRadius:6,padding:"6px 10px"}}>
                    Admin access required on the selected pages — connect them in Ad Connectors → Social pages
                  </p>
                )}
              </div>

              {/* Headline */}
              <div style={{marginBottom:14}}>
                <label style={LBL}>Headline / hook <span style={{color:T.ink4,fontWeight:400,fontSize:9}}>optional</span></label>
                <input value={headline} onChange={e=>setHeadline(e.target.value)} placeholder="e.g. Our Q1 results are in 📊" style={INP}/>
              </div>

              {/* Body */}
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <label style={{...LBL,marginBottom:0}}>Post copy</label>
                  <span style={{fontSize:10,color:body.length>charLimit*0.9?T.r2:T.ink4}}>{body.length} / {activePrev?.id==="x"?280:charLimit}</span>
                </div>
                <textarea rows={7} value={body} onChange={e=>setBody(e.target.value)}
                  placeholder="Write your post copy here…"
                  style={{...INP,resize:"vertical",lineHeight:1.6}}/>
                {/* Per-platform char warnings */}
                {body.length > 0 && selPlats.map(p=>body.length>p.maxChars&&(
                  <p key={p.id} style={{fontSize:10,color:T.r2,margin:"3px 0 0"}}>⚠ Over {p.label} limit by {body.length-p.maxChars} chars</p>
                ))}
              </div>

              {/* Link */}
              <div style={{marginBottom:14}}>
                <label style={LBL}>Link <span style={{color:T.ink4,fontWeight:400,fontSize:9}}>optional</span></label>
                <input value={link} onChange={e=>setLink(e.target.value)} placeholder="https://yourdomain.com/..." style={INP}/>
              </div>

              {/* Media */}
              <div style={{marginBottom:18}}>
                <label style={LBL}>Creative / image <span style={{color:T.ink4,fontWeight:400,fontSize:9}}>optional</span></label>
                <div style={{border:`1px dashed ${T.s3}`,borderRadius:8,padding:"18px",textAlign:"center",cursor:"pointer",background:T.bg}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.brand;e.currentTarget.style.background=T.bL;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.s3;e.currentTarget.style.background=T.bg;}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="1.5" strokeLinecap="round" style={{display:"block",margin:"0 auto 7px"}}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <p style={{fontSize:12,color:T.ink3,margin:"0 0 2px"}}>Upload image or video</p>
                  <p style={{fontSize:10,color:T.ink4,margin:0}}>JPG · PNG · MP4 · GIF · max 50MB</p>
                </div>
              </div>

              {/* Schedule toggle */}
              <div style={{padding:"14px 16px",background:T.s1,borderRadius:10,marginBottom:18}}>
                <label style={{...LBL,marginBottom:8}}>When to post</label>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  {[["now","Post now"],["scheduled","Schedule for later"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setSchedType(v)} style={{
                      flex:1,padding:"7px 0",borderRadius:7,border:`1px solid ${schedType===v?T.b2:T.s2}`,
                      background:schedType===v?T.bL:T.canvas,color:schedType===v?T.b1:T.ink3,
                      fontSize:12,fontWeight:schedType===v?600:400,cursor:"pointer",fontFamily:"inherit",
                    }}>{l}</button>
                  ))}
                </div>
                {/* Timezone — always visible */}
                <div style={{marginBottom:schedType==="scheduled"?12:0}}>
                  <label style={LBL}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                      Timezone
                    </span>
                  </label>
                  <select value={timezone} onChange={e=>setTimezone(e.target.value)} style={INP}>
                    {["UTC−8 (Los Angeles)","UTC−5 (New York)","UTC+0 (London)","UTC+1 (Paris / Berlin)","UTC+2 (Cairo)","UTC+3 (Riyadh)","UTC+4 (Dubai)","UTC+5:30 (Mumbai)","UTC+8 (Singapore / HK)","UTC+9 (Tokyo)","UTC+10 (Sydney)"].map(tz=><option key={tz}>{tz}</option>)}
                  </select>
                </div>
                {schedType==="scheduled" && (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
                    <div><label style={LBL}>Date</label><input type="date" value={schedDate} onChange={e=>setSchedDate(e.target.value)} style={INP}/></div>
                    <div>
                      <label style={LBL}>Time <span style={{color:T.ink4,fontWeight:400,fontSize:9}}>{timezone}</span></label>
                      <input type="time" value={schedTime} onChange={e=>setSchedTime(e.target.value)} style={INP}/>
                    </div>
                  </div>
                )}
                {schedType==="scheduled" && (
                  <p style={{fontSize:10,color:T.ink4,margin:"8px 0 0"}}>
                    📅 Will post on {schedDate} at {schedTime} {timezone}
                  </p>
                )}
              </div>

              <div style={{display:"flex",gap:8}}>
                <button onClick={reset} style={{padding:"10px 18px",borderRadius:7,border:`1px solid ${T.s2}`,background:"transparent",color:T.ink3,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>
                <button onClick={publish} disabled={publishing||!body||platforms.length===0}
                  style={{flex:1,padding:"10px 0",borderRadius:7,border:"none",background:(!body||platforms.length===0||publishing)?T.s2:T.brand,color:(!body||platforms.length===0||publishing)?T.ink4:"#fff",fontSize:12,fontWeight:500,cursor:(!body||platforms.length===0||publishing)?"not-allowed":"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                  {publishing
                    ? <><span style={{width:13,height:13,border:"2px solid rgba(255,255,255,.3)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"adtech-spin 1s linear infinite"}}/> {schedType==="now"?"Publishing…":"Scheduling…"}</>
                    : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg> {schedType==="now"?`Publish to ${selPlats.length} platform${selPlats.length!==1?"s":""}`:`Schedule · ${schedDate} ${schedTime}`}</>
                  }
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Right: live preview */}
          <div style={{width:340,flexShrink:0,overflowY:"auto",background:T.s1,padding:"18px 16px"}}>
            <p style={{fontSize:10,fontWeight:600,color:T.ink4,textTransform:"uppercase",letterSpacing:".07em",margin:"0 0 12px"}}>Live preview</p>

            {selPlats.length>1&&(
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
                {selPlats.map(p=>(
                  <button key={p.id} onClick={()=>setPreviewP(p.id)} style={{
                    padding:"3px 10px",borderRadius:5,border:`1px solid ${previewPlat===p.id?p.color:T.s2}`,
                    background:previewPlat===p.id?p.color+"18":T.canvas,color:previewPlat===p.id?p.color:T.ink3,
                    fontSize:10,fontWeight:previewPlat===p.id?600:400,cursor:"pointer",fontFamily:"inherit",
                  }}>{p.label}</button>
                ))}
              </div>
            )}

            {activePrev && (
              <div style={{background:T.canvas,borderRadius:10,overflow:"hidden",border:`1px solid ${T.s2}`,boxShadow:"0 2px 10px rgba(15,23,42,.08)"}}>
                <div style={{background:activePrev.color,padding:"6px 12px",display:"flex",alignItems:"center",gap:7}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:"rgba(255,255,255,.5)"}}/>
                  <span style={{fontSize:11,fontWeight:500,color:"#fff"}}>{activePrev.label}</span>
                  <span style={{fontSize:10,color:"rgba(255,255,255,.6)",marginLeft:"auto"}}>{activePrev.maxChars.toLocaleString()} char limit</span>
                </div>
                <div style={{padding:"14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:12}}>
                    <div style={{width:36,height:36,borderRadius:activePrev.id==="linkedin"?8:"50%",background:activePrev.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff"}}>A</div>
                    <div>
                      <p style={{fontSize:13,fontWeight:600,color:T.ink,margin:0}}>Your Page</p>
                      <p style={{fontSize:10,color:T.ink4,margin:0}}>{schedType==="now"?"Just now":schedDate+" · "+schedTime} · {platforms.length} platforms</p>
                    </div>
                  </div>
                  {headline&&<p style={{fontSize:14,fontWeight:600,color:T.ink,margin:"0 0 8px"}}>{headline}</p>}
                  <p style={{fontSize:13,color:T.ink2,margin:"0 0 10px",lineHeight:1.6,whiteSpace:"pre-line"}}>
                    {body.slice(0,activePrev.id==="x"?280:300)||<span style={{color:T.ink4,fontStyle:"italic"}}>Your post copy will appear here…</span>}
                    {body.length>(activePrev.id==="x"?280:300)&&<span style={{color:T.r2}}> [{body.length-(activePrev.id==="x"?280:300)} over limit]</span>}
                  </p>
                  {link&&<p style={{fontSize:11,color:T.b2,margin:"0 0 10px"}}>{link}</p>}
                  <div style={{height:88,background:"#F1F5F9",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",gap:7,border:`1px dashed ${T.s3}`}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    <span style={{fontSize:11,color:T.ink4}}>Creative</span>
                  </div>
                  <div style={{paddingTop:10,borderTop:`1px solid ${T.s2}`,marginTop:10,display:"flex",gap:16}}>
                    {["Like","Comment","Share"].map(a=><span key={a} style={{fontSize:11,color:T.ink4}}>{a}</span>)}
                  </div>
                </div>
              </div>
            )}

            {platforms.length===0&&(
              <div style={{textAlign:"center",padding:"40px 16px",color:T.ink4}}>
                <p style={{fontSize:12,margin:0}}>Select platforms above</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ SCHEDULED TAB ══ */}
      {view==="scheduled" && (
        <div style={{flex:1,overflowY:"auto",padding:"20px 22px"}}>
          <div style={{maxWidth:720}}>
            {scheduledPosts.length===0
              ? <div style={{textAlign:"center",padding:"60px 0",color:T.ink4}}><p style={{fontSize:14,margin:"0 0 4px"}}>No scheduled posts</p><p style={{fontSize:12,margin:0}}>Compose a post and schedule it for later</p></div>
              : scheduledPosts.map(post=>{
                  const platObjs=SOCIAL_POST_PLATFORMS.filter(p=>post.platforms.includes(p.id));
                  return (
                    <div key={post.id} style={{background:T.canvas,border:`1px solid ${T.s2}`,borderRadius:10,padding:"14px 18px",marginBottom:10}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
                        <div style={{display:"flex",gap:6}}>
                          {platObjs.map(p=><span key={p.id} style={{padding:"2px 8px",borderRadius:20,background:p.color+"18",color:p.color,fontSize:11,fontWeight:600}}>{p.label}</span>)}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:11,color:T.b2,fontWeight:600}}>🕐 {post.date}</span>
                          <button onClick={()=>setPosts(ps=>ps.filter(p=>p.id!==post.id))} style={{padding:"3px 8px",borderRadius:5,border:`1px solid ${T.rM||T.s2}`,background:T.rL,color:T.r2,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                        </div>
                      </div>
                      <p style={{fontSize:12,color:T.ink2,margin:0,lineHeight:1.5}}>{post.body.slice(0,180)}{post.body.length>180?"…":""}</p>
                    </div>
                  );
                })
            }
          </div>
        </div>
      )}

      {/* ══ PUBLISHED TAB ══ */}
      {view==="published" && (
        <div style={{flex:1,overflowY:"auto",padding:"20px 22px"}}>
          <div style={{maxWidth:720}}>
            {publishedPosts.map(post=>{
              const platObjs=SOCIAL_POST_PLATFORMS.filter(p=>post.platforms.includes(p.id));
              return (
                <div key={post.id} style={{background:T.canvas,border:`1px solid ${T.s2}`,borderRadius:10,padding:"14px 18px",marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{display:"flex",gap:6}}>
                      {platObjs.map(p=><span key={p.id} style={{padding:"2px 8px",borderRadius:20,background:p.color+"18",color:p.color,fontSize:11,fontWeight:600}}>{p.label}</span>)}
                    </div>
                    <span style={{fontSize:11,color:T.g1,fontWeight:600}}>✓ Published {post.date}</span>
                  </div>
                  <p style={{fontSize:12,color:T.ink2,margin:0,lineHeight:1.5}}>{post.body.slice(0,180)}{post.body.length>180?"…":""}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Report Widget Definitions ─────────────────────────────────────────────────

const PLAT_COLORS = { meta:"#1877F2", google:"#4285F4", linkedin:"#0A66C2", tiktok:"#2DD4BF", pinterest:"#E60023", snapchat:"#FFFC00", x:"#000000",
  dv360:"#1A73E8", ttd:"#00C44F", amazon_dsp:"#FF9900", xandr:"#00A4EF" };
const PLAT_LABELS = { meta:"Meta", google:"Google", linkedin:"LinkedIn", tiktok:"TikTok", pinterest:"Pinterest", snapchat:"Snapchat", x:"X",
  dv360:"DV360", ttd:"Trade Desk", amazon_dsp:"Amazon DSP", xandr:"Xandr" };

const PER_PLATFORM = {
  kpi_spend:   { blended:"$84.2k", dir:"up", chg:"+12.5%", label:"Total spend",        sub:"Last 7 days",           platforms:[{id:"meta",value:"$32.4k",raw:32400},{id:"google",value:"$28.0k",raw:28000},{id:"linkedin",value:"$12.0k",raw:12000},{id:"tiktok",value:"$8.5k",raw:8500},{id:"pinterest",value:"$3.3k",raw:3300}] },
  kpi_revenue: { blended:"$247k",  dir:"up", chg:"+18.3%", label:"Revenue",            sub:"Attributed",            platforms:[{id:"meta",value:"$90.7k",raw:90700},{id:"google",value:"$109.2k",raw:109200},{id:"linkedin",value:"$25.2k",raw:25200},{id:"tiktok",value:"$15.3k",raw:15300},{id:"pinterest",value:"$7.9k",raw:7900}] },
  kpi_roas:    { blended:"2.93×",  dir:"up", chg:"+5.2%",  label:"ROAS",               sub:"Blended",               platforms:[{id:"meta",value:"2.80×",raw:2.80},{id:"google",value:"3.90×",raw:3.90},{id:"linkedin",value:"2.10×",raw:2.10},{id:"tiktok",value:"1.80×",raw:1.80},{id:"pinterest",value:"2.40×",raw:2.40}] },
  kpi_ctr:     { blended:"1.74%",  dir:"up", chg:"+0.2pt", label:"CTR",                sub:"Avg all channels",      platforms:[{id:"meta",value:"1.90%",raw:1.90},{id:"google",value:"3.80%",raw:3.80},{id:"linkedin",value:"0.60%",raw:0.60},{id:"tiktok",value:"1.40%",raw:1.40},{id:"pinterest",value:"0.50%",raw:0.50}] },
  kpi_cpm:     { blended:"$11.40", dir:"dn", chg:"-3.1%",  label:"CPM",                sub:"Cost per 1k impr",      platforms:[{id:"meta",value:"$11.40",raw:11.40},{id:"google",value:"$8.20",raw:8.20},{id:"linkedin",value:"$22.50",raw:22.50},{id:"tiktok",value:"$6.10",raw:6.10},{id:"pinterest",value:"$9.80",raw:9.80}] },
  kpi_cpc:     { blended:"$0.65",  dir:"dn", chg:"-5.4%",  label:"CPC",                sub:"Cost per click",        platforms:[{id:"meta",value:"$0.60",raw:0.60},{id:"google",value:"$0.22",raw:0.22},{id:"linkedin",value:"$3.75",raw:3.75},{id:"tiktok",value:"$0.44",raw:0.44},{id:"pinterest",value:"$1.96",raw:1.96}] },
  kpi_cpa:     { blended:"$45.78", dir:"dn", chg:"-8.2%",  label:"CPA",                sub:"Cost per acquisition",  platforms:[{id:"meta",value:"$47.20",raw:47.20},{id:"google",value:"$38.60",raw:38.60},{id:"linkedin",value:"$68.40",raw:68.40},{id:"tiktok",value:"$55.10",raw:55.10},{id:"pinterest",value:"$41.20",raw:41.20}] },
  kpi_cpl:     { blended:"$6.74",  dir:"dn", chg:"-4.1%",  label:"CPL",                sub:"Cost per lead",         platforms:[{id:"meta",value:"$7.20",raw:7.20},{id:"google",value:"$5.40",raw:5.40},{id:"linkedin",value:"$9.80",raw:9.80},{id:"tiktok",value:"$6.10",raw:6.10},{id:"pinterest",value:"$8.30",raw:8.30}] },
  kpi_conv:    { blended:"1,840",  dir:"up", chg:"+9.1%",  label:"Conversions",        sub:"Total actions",         platforms:[{id:"meta",value:"686",raw:686},{id:"google",value:"725",raw:725},{id:"linkedin",value:"175",raw:175},{id:"tiktok",value:"154",raw:154},{id:"pinterest",value:"80",raw:80}] },
  kpi_impr:    { blended:"7.4M",   dir:"up", chg:"+8.2%",  label:"Impressions",        sub:"Total views",           platforms:[{id:"meta",value:"3.2M",raw:3200000},{id:"google",value:"2.1M",raw:2100000},{id:"linkedin",value:"820k",raw:820000},{id:"tiktok",value:"940k",raw:940000},{id:"pinterest",value:"340k",raw:340000}] },
  kpi_reach:   { blended:"4.1M",   dir:"up", chg:"+6.4%",  label:"Reach",              sub:"Unique users",          platforms:[{id:"meta",value:"1.9M",raw:1900000},{id:"google",value:"1.2M",raw:1200000},{id:"linkedin",value:"460k",raw:460000},{id:"tiktok",value:"420k",raw:420000},{id:"pinterest",value:"120k",raw:120000}] },
  kpi_freq:    { blended:"1.81x",  dir:"up", chg:"+0.3x",  label:"Frequency",          sub:"Avg per user",          platforms:[{id:"meta",value:"1.68x",raw:1.68},{id:"google",value:"1.75x",raw:1.75},{id:"linkedin",value:"1.78x",raw:1.78},{id:"tiktok",value:"2.24x",raw:2.24},{id:"pinterest",value:"2.83x",raw:2.83}] },
  kpi_eng:     { blended:"318k",   dir:"up", chg:"+14.1%", label:"Engagements",        sub:"Likes+comments+shares", platforms:[{id:"meta",value:"142k",raw:142000},{id:"google",value:"28k",raw:28000},{id:"linkedin",value:"34k",raw:34000},{id:"tiktok",value:"96k",raw:96000},{id:"pinterest",value:"18k",raw:18000}] },
  kpi_er:      { blended:"4.3%",   dir:"up", chg:"+1.1pt", label:"Eng. Rate",          sub:"Eng / reach",           platforms:[{id:"meta",value:"7.5%",raw:7.5},{id:"google",value:"1.3%",raw:1.3},{id:"linkedin",value:"7.4%",raw:7.4},{id:"tiktok",value:"22.9%",raw:22.9},{id:"pinterest",value:"15.0%",raw:15.0}] },
  kpi_vv:      { blended:"1.2M",   dir:"up", chg:"+22%",   label:"Video Views",        sub:"3-second+",             platforms:[{id:"meta",value:"420k",raw:420000},{id:"google",value:"180k",raw:180000},{id:"linkedin",value:"95k",raw:95000},{id:"tiktok",value:"480k",raw:480000},{id:"pinterest",value:"25k",raw:25000}] },
  kpi_vtr:     { blended:"28.4%",  dir:"up", chg:"+3.2pt", label:"VTR",                sub:"View-through rate",     platforms:[{id:"meta",value:"24.8%",raw:24.8},{id:"google",value:"18.2%",raw:18.2},{id:"linkedin",value:"22.4%",raw:22.4},{id:"tiktok",value:"51.2%",raw:51.2},{id:"pinterest",value:"29.6%",raw:29.6}] },
  kpi_leads:   { blended:"486",    dir:"up", chg:"+11.8%", label:"Leads",              sub:"Form fills",            platforms:[{id:"meta",value:"142",raw:142},{id:"google",value:"168",raw:168},{id:"linkedin",value:"124",raw:124},{id:"tiktok",value:"38",raw:38},{id:"pinterest",value:"14",raw:14}] },
  kpi_roi:     { blended:"193%",   dir:"up", chg:"+15.2pt",label:"ROI",                sub:"Return on investment",  platforms:[{id:"meta",value:"180%",raw:180},{id:"google",value:"290%",raw:290},{id:"linkedin",value:"110%",raw:110},{id:"tiktok",value:"80%",raw:80},{id:"pinterest",value:"140%",raw:140}] },
};

const WIDGET_TYPES = [
  { id:"kpi_spend",    label:"Spend",              cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_revenue",  label:"Revenue",            cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_roas",     label:"ROAS",               cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_ctr",      label:"CTR",                cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_cpm",      label:"CPM",                cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_cpc",      label:"CPC",                cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_cpa",      label:"CPA",                cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_cpl",      label:"CPL",                cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_conv",     label:"Conversions",        cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_impr",     label:"Impressions",        cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_reach",    label:"Reach",              cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_freq",     label:"Frequency",          cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_eng",      label:"Engagements",        cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_er",       label:"Eng. Rate",          cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_vv",       label:"Video Views",        cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_vtr",      label:"VTR",                cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_leads",    label:"Leads",              cat:"KPI",   chartType:"kpi"   },
  { id:"kpi_roi",      label:"ROI %",              cat:"KPI",        chartType:"kpi"   },
  { id:"kpi_viewab",   label:"Viewability",        cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_ivt",      label:"IVT Rate",           cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_brand_safe",label:"Brand Safety",      cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_winrate",  label:"Win Rate",           cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_deal_cpm", label:"Deal CPM",           cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_aud_reach",label:"Audience Reach",     cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_vcr",      label:"Video Completion",   cat:"Programmatic", chartType:"kpi"   },
  { id:"kpi_ctv_impr", label:"CTV Impressions",    cat:"Programmatic", chartType:"kpi"   },
  // Programmatic-specific KPIs
  { id:"kpi_viewab",   label:"Viewability",        cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_ivt",      label:"IVT Rate",           cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_brand_safe",label:"Brand Safety",      cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_winrate",  label:"Win Rate",           cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_deal_cpm", label:"Deal CPM",           cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_aud_reach",label:"Audience Reach",     cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_vcr",      label:"Video Completion",   cat:"Programmatic", chartType:"kpi" },
  { id:"kpi_ctv_impr", label:"CTV Impressions",    cat:"Programmatic", chartType:"kpi" },
  { id:"bar_channel",  label:"Channel spend (bar)",cat:"Bar",   chartType:"bar"   },
  { id:"bar_roas",     label:"ROAS by channel",    cat:"Bar",   chartType:"bar"   },
  { id:"line_trend",   label:"Revenue trend",      cat:"Line",  chartType:"line"  },
  { id:"line_spend",   label:"Spend over time",    cat:"Line",  chartType:"line"  },
  { id:"pie_channel",  label:"Budget split (pie)", cat:"Pie",   chartType:"pie"   },
  { id:"pie_revenue",  label:"Revenue split (pie)",cat:"Pie",   chartType:"pie"   },
  { id:"donut_reach",  label:"Reach by platform",  cat:"Donut", chartType:"donut" },
  { id:"table_posts",  label:"Top posts table",    cat:"Table", chartType:"table" },
  { id:"table_ch",     label:"Channel summary",    cat:"Table", chartType:"table" },
  { id:"text_summary", label:"Executive summary",  cat:"Text",  chartType:"text"  },
  { id:"text_reco",    label:"Recommendations",    cat:"Text",  chartType:"text"  },
];

const WIDGET_DATA = {
  bar_channel:[{n:"Meta",sp:32400,rv:90720},{n:"Google",sp:28000,rv:109200},{n:"LinkedIn",sp:12000,rv:25200},{n:"TikTok",sp:8500,rv:15300},{n:"Pinterest",sp:3300,rv:7920}],
  bar_roas:[{n:"Meta",v:2.8},{n:"Google",v:3.9},{n:"LinkedIn",v:2.1},{n:"TikTok",v:1.8},{n:"Pinterest",v:2.4}],
  line_trend:[{d:"Mar 15",v:31200},{d:"Mar 16",v:29800},{d:"Mar 17",v:34500},{d:"Mar 18",v:38200},{d:"Mar 19",v:36900},{d:"Mar 20",v:42100},{d:"Mar 21",v:34300}],
  line_spend:[{d:"Mar 15",v:11200},{d:"Mar 16",v:9800},{d:"Mar 17",v:12500},{d:"Mar 18",v:13200},{d:"Mar 19",v:11900},{d:"Mar 20",v:14100},{d:"Mar 21",v:11500}],
  pie_channel:[{name:"Meta",value:38.5,color:"#1877F2"},{name:"Google",value:33.3,color:"#4285F4"},{name:"LinkedIn",value:14.3,color:"#0A66C2"},{name:"TikTok",value:10.1,color:"#2DD4BF"},{name:"Pinterest",value:3.9,color:"#E60023"}],
  pie_revenue:[{name:"Meta",value:36.7,color:"#1877F2"},{name:"Google",value:44.2,color:"#4285F4"},{name:"LinkedIn",value:10.2,color:"#0A66C2"},{name:"TikTok",value:6.2,color:"#2DD4BF"},{name:"Pinterest",value:3.2,color:"#E60023"}],
  donut_reach:[{name:"Meta",value:45,color:"#1877F2"},{name:"TikTok",value:28,color:"#2DD4BF"},{name:"Google",value:18,color:"#4285F4"},{name:"Pinterest",value:9,color:"#E60023"}],
  table_posts:[
    {title:"Summer Sale Hero Video",platform:"meta",roas:"3.2x",impr:"1.2M",ctr:"2.1%"},
    {title:"Lead Gen Enterprise",platform:"linkedin",roas:"2.4x",impr:"420k",ctr:"0.8%"},
    {title:"Brand Keywords",platform:"google",roas:"4.1x",impr:"890k",ctr:"4.2%"},
  ],
  table_ch:[
    {ch:"Meta Ads",spend:"$32.4k",rev:"$90.7k",roas:"2.8x",ctr:"1.9%",cpm:"$11.4"},
    {ch:"Google Ads",spend:"$28.0k",rev:"$109.2k",roas:"3.9x",ctr:"3.8%",cpm:"$8.2"},
    {ch:"LinkedIn",spend:"$12.0k",rev:"$25.2k",roas:"2.1x",ctr:"0.6%",cpm:"$22.5"},
    {ch:"TikTok",spend:"$8.5k",rev:"$15.3k",roas:"1.8x",ctr:"1.4%",cpm:"$6.1"},
    {ch:"Pinterest",spend:"$3.3k",rev:"$7.9k",roas:"2.4x",ctr:"0.5%",cpm:"$9.8"},
  ],
  text_summary:"This week delivered $247k revenue from $84.2k spend — blended ROAS 2.93x. Google Ads leads at 3.9x ROAS. Meta drives highest absolute revenue. Performance up 18.3% WoW.",
  text_reco:"1. Increase Google Ads budget +15% — highest ROAS at 3.9x\n2. Refresh Meta creative — frequency 6.8, CTR declining\n3. Test LinkedIn Lead Gen Forms — CPL 40% lower\n4. Pause TikTok non-video formats — video outperforms static by 2.8x",
};

function MiniPie({data,donut}){
  const total=data.reduce((a,b)=>a+b.value,0);
  let angle=-90;
  const r=donut?28:36,cx=44,cy=44;
  const paths=data.map(d=>{
    const sweep=(d.value/total)*360,start=angle;angle+=sweep;
    const s=Math.PI/180,x1=cx+r*Math.cos(start*s),y1=cy+r*Math.sin(start*s),x2=cx+r*Math.cos((start+sweep)*s),y2=cy+r*Math.sin((start+sweep)*s),large=sweep>180?1:0;
    if(donut){const ir=18,ix1=cx+ir*Math.cos(start*s),iy1=cy+ir*Math.sin(start*s),ix2=cx+ir*Math.cos((start+sweep)*s),iy2=cy+ir*Math.sin((start+sweep)*s);return{d:"M"+x1+","+y1+" A"+r+","+r+" 0 "+large+",1 "+x2+","+y2+" L"+ix2+","+iy2+" A"+ir+","+ir+" 0 "+large+",0 "+ix1+","+iy1+" Z",color:d.color,name:d.name,value:d.value};}
    return{d:"M"+cx+","+cy+" L"+x1+","+y1+" A"+r+","+r+" 0 "+large+",1 "+x2+","+y2+" Z",color:d.color,name:d.name,value:d.value};
  });
  return(<div style={{display:"flex",alignItems:"center",gap:12}}><svg width="88" height="88" viewBox="0 0 88 88">{paths.map((p,i)=><path key={i} d={p.d} fill={p.color} stroke="#fff" strokeWidth="1.5"/>)}</svg><div style={{flex:1}}>{data.slice(0,4).map((d,i)=>(<div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}><div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:7,height:7,borderRadius:"50%",background:d.color}}/><span style={{fontSize:10,color:T.ink3}}>{d.name}</span></div><span style={{fontSize:10,fontWeight:600,color:T.ink}}>{d.value}%</span></div>))}</div></div>);
}

function MiniBar({data,valueKey="sp",color}){
  color=color||T.b2;
  const max=Math.max(...data.map(d=>d[valueKey]));
  return(<div style={{display:"flex",flexDirection:"column",gap:4}}>{data.map((d,i)=>(<div key={i} style={{display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:9,color:T.ink4,width:48,flexShrink:0,textAlign:"right"}}>{d.n||d.ch}</span><div style={{flex:1,height:12,background:T.s1,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:(d[valueKey]/max*100)+"%",background:color,borderRadius:3}}/></div><span style={{fontSize:9,fontWeight:600,color:T.ink,minWidth:32}}>{typeof d[valueKey]==="number"&&d[valueKey]>1000?"$"+(d[valueKey]/1000).toFixed(0)+"k":d[valueKey]}</span></div>))}</div>);
}

function MiniLine({data}){
  const vals=data.map(d=>d.v),min=Math.min(...vals),max=Math.max(...vals),range=max-min||1,W=200,H=60,pad=6;
  const pts=data.map((d,i)=>{const x=pad+(i/(data.length-1))*(W-pad*2),y=H-pad-((d.v-min)/range)*(H-pad*2);return x+","+y;}).join(" ");
  return(<svg width={W} height={H} style={{display:"block"}}><polyline points={pts} fill="none" stroke={T.b2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>{data.map((d,i)=>{const x=pad+(i/(data.length-1))*(W-pad*2),y=H-pad-((d.v-min)/range)*(H-pad*2);return <circle key={i} cx={x} cy={y} r="3" fill={T.b2}/>;})}</svg>);
}

function PlatformBreakdown({metricId,highlight}){
  const d=PER_PLATFORM[metricId];
  if(!d||!d.platforms) return null;
  const maxRaw=Math.max(...d.platforms.map(p=>p.raw));
  return(
    <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid "+T.s2}}>
      <p style={{fontSize:9,fontWeight:600,color:T.ink4,textTransform:"uppercase",letterSpacing:".07em",margin:"0 0 7px"}}>By platform</p>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {d.platforms.map(plat=>{
          const pctW=maxRaw>0?(plat.raw/maxRaw)*100:0;
          const color=PLAT_COLORS[plat.id]||T.b2;
          const isH=highlight===plat.id;
          return(
            <div key={plat.id} style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0}}/>
              <span style={{fontSize:9,color:T.ink3,width:52,flexShrink:0}}>{PLAT_LABELS[plat.id]||plat.id}</span>
              <div style={{flex:1,height:10,background:T.s1,borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:pctW+"%",background:color,borderRadius:3,opacity:isH?1:0.75}}/>
              </div>
              <span style={{fontSize:9,fontWeight:isH?700:500,color:isH?color:T.ink,minWidth:38,textAlign:"right"}}>{plat.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReportWidget({wid,onRemove,onEdit}){
  const wt=WIDGET_TYPES.find(w=>w.id===wid.typeId);
  const data=WIDGET_DATA[wid.typeId];
  const kpiD=PER_PLATFORM[wid.typeId];
  const isKpi=wt?.chartType==="kpi",isBar=wt?.chartType==="bar",isLine=wt?.chartType==="line",isPie=wt?.chartType==="pie",isDon=wt?.chartType==="donut",isTbl=wt?.chartType==="table",isTxt=wt?.chartType==="text";
  const [highlight,setHighlight]=useState(null);
  return(
    <div style={{background:T.canvas,border:"1px solid "+T.s2,borderRadius:10,overflow:"hidden",gridColumn:isKpi?"span 1":"span 2"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 13px",borderBottom:"1px solid "+T.s2,background:T.s1}}>
        <div style={{display:"flex",alignItems:"center",gap:7}}>
          <span style={{fontSize:11,fontWeight:600,color:T.ink2}}>{wt?.label}</span>
          {isKpi&&kpiD&&<span style={{fontSize:9,color:T.ink4,background:T.s2,padding:"1px 6px",borderRadius:3}}>{wid.vizType&&wid.vizType!=="kpi"?wid.vizType+" chart":"per platform"}</span>}
        </div>
        <div style={{display:"flex",gap:4}}>
          {!isTxt&&<button onClick={()=>onEdit(wid)} style={{padding:"2px 7px",borderRadius:4,border:"1px solid "+T.s2,background:"transparent",color:T.ink4,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>}
          <button onClick={()=>onRemove(wid.id)} style={{padding:"2px 7px",borderRadius:4,border:"1px solid "+T.s2,background:"transparent",color:T.r2,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>x</button>
        </div>
      </div>
      {isKpi&&kpiD&&(
        <div style={{padding:"14px 14px 12px"}}>
          {/* KPI header — always shown */}
          <p style={{fontSize:9,color:T.ink4,margin:"0 0 2px",textTransform:"uppercase",letterSpacing:".07em"}}>{kpiD.label}</p>
          <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:4}}>
            <p style={{fontSize:26,fontWeight:700,color:T.ink,margin:0,letterSpacing:"-.01em"}}>{wid.override||kpiD.blended}</p>
            <span style={{fontSize:11,fontWeight:600,padding:"1px 6px",borderRadius:4,background:kpiD.dir==="up"?T.gL:T.rL,color:kpiD.dir==="up"?T.g1:T.r1}}>{kpiD.dir==="up"?"↑":"↓"} {kpiD.chg}</span>
          </div>
          <p style={{fontSize:9,color:T.ink4,margin:"0 0 10px"}}>{kpiD.sub}</p>

          {/* Viz based on wid.vizType */}
          {(!wid.vizType||wid.vizType==="kpi") && (
            <>
              <PlatformBreakdown metricId={wid.typeId} highlight={highlight}/>
              <div style={{display:"flex",gap:4,marginTop:8,flexWrap:"wrap"}}>
                {kpiD.platforms.map(p=>(
                  <button key={p.id} onMouseEnter={()=>setHighlight(p.id)} onMouseLeave={()=>setHighlight(null)}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"2px 7px",borderRadius:20,border:"1px solid "+(highlight===p.id?PLAT_COLORS[p.id]||T.s3:T.s2),background:highlight===p.id?(PLAT_COLORS[p.id]||T.b2)+"18":"transparent",cursor:"pointer",fontFamily:"inherit"}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:PLAT_COLORS[p.id]||T.b2}}/>
                    <span style={{fontSize:9,color:highlight===p.id?PLAT_COLORS[p.id]||T.b2:T.ink3,fontWeight:highlight===p.id?600:400}}>{PLAT_LABELS[p.id]||p.id}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {wid.vizType==="bar" && (
            <div style={{marginTop:4}}>
              <p style={{fontSize:9,color:T.ink4,margin:"0 0 8px",textTransform:"uppercase",letterSpacing:".06em"}}>By platform</p>
              {(() => {
                const maxR = Math.max(...kpiD.platforms.map(p=>p.raw));
                return kpiD.platforms.map(p=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:PLAT_COLORS[p.id]||T.b2,flexShrink:0}}/>
                    <span style={{fontSize:10,color:T.ink3,width:54,flexShrink:0}}>{PLAT_LABELS[p.id]}</span>
                    <div style={{flex:1,height:14,background:T.s1,borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:(p.raw/maxR*100)+"%",background:PLAT_COLORS[p.id]||T.b2,borderRadius:3}}/>
                    </div>
                    <span style={{fontSize:10,fontWeight:600,color:T.ink,minWidth:40,textAlign:"right"}}>{p.value}</span>
                  </div>
                ));
              })()}
            </div>
          )}

          {(wid.vizType==="pie"||wid.vizType==="donut") && (()=>{
            const total=kpiD.platforms.reduce((a,p)=>a+p.raw,0);
            let ang=-90;
            const r=wid.vizType==="donut"?32:40,cx=48,cy=48;
            const paths=kpiD.platforms.map(p=>{
              const sw=(p.raw/total)*360,st=ang;ang+=sw;
              const s=Math.PI/180,x1=cx+r*Math.cos(st*s),y1=cy+r*Math.sin(st*s),x2=cx+r*Math.cos((st+sw)*s),y2=cy+r*Math.sin((st+sw)*s),lg=sw>180?1:0;
              if(wid.vizType==="donut"){const ir=16,ix1=cx+ir*Math.cos(st*s),iy1=cy+ir*Math.sin(st*s),ix2=cx+ir*Math.cos((st+sw)*s),iy2=cy+ir*Math.sin((st+sw)*s);return{d:"M"+x1+","+y1+" A"+r+","+r+" 0 "+lg+",1 "+x2+","+y2+" L"+ix2+","+iy2+" A"+ir+","+ir+" 0 "+lg+",0 "+ix1+","+iy1+" Z",c:PLAT_COLORS[p.id]||T.b2,n:PLAT_LABELS[p.id],pct:Math.round(p.raw/total*100)};}
              return{d:"M"+cx+","+cy+" L"+x1+","+y1+" A"+r+","+r+" 0 "+lg+",1 "+x2+","+y2+" Z",c:PLAT_COLORS[p.id]||T.b2,n:PLAT_LABELS[p.id],pct:Math.round(p.raw/total*100)};
            });
            return (
              <div style={{display:"flex",alignItems:"center",gap:14,marginTop:4}}>
                <svg width="96" height="96" viewBox="0 0 96 96">
                  {paths.map((p,i)=><path key={i} d={p.d} fill={p.c} stroke="#fff" strokeWidth="1.5"/>)}
                  {wid.vizType==="donut"&&<text x="48" y="52" textAnchor="middle" fontSize="11" fontWeight="700" fill={T.ink}>{kpiD.blended}</text>}
                </svg>
                <div style={{flex:1}}>
                  {paths.map((p,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                      <div style={{display:"flex",alignItems:"center",gap:5}}>
                        <div style={{width:8,height:8,borderRadius:"50%",background:p.c}}/>
                        <span style={{fontSize:10,color:T.ink3}}>{p.n}</span>
                      </div>
                      <span style={{fontSize:10,fontWeight:600,color:T.ink}}>{p.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {wid.vizType==="line" && (()=>{
            const vals=kpiD.platforms.map(p=>p.raw);
            const min=Math.min(...vals),max=Math.max(...vals),range=max-min||1;
            const W=180,H=70,pad=8;
            const pts=kpiD.platforms.map((p,i)=>{
              const x=pad+(i/(kpiD.platforms.length-1))*(W-pad*2);
              const y=H-pad-((p.raw-min)/range)*(H-pad*2);
              return x+","+y;
            }).join(" ");
            return (
              <div style={{marginTop:4}}>
                <p style={{fontSize:9,color:T.ink4,margin:"0 0 4px",textTransform:"uppercase",letterSpacing:".06em"}}>{kpiD.label} by platform</p>
                <svg width={W} height={H} style={{display:"block",overflow:"visible"}}>
                  <polyline points={pts} fill="none" stroke={T.b2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  {kpiD.platforms.map((p,i)=>{
                    const x=pad+(i/(kpiD.platforms.length-1))*(W-pad*2);
                    const y=H-pad-((p.raw-min)/range)*(H-pad*2);
                    return <g key={i}><circle cx={x} cy={y} r="4" fill={PLAT_COLORS[p.id]||T.b2} stroke="#fff" strokeWidth="1.5"/><text x={x} y={H+12} textAnchor="middle" fontSize="8" fill={T.ink4}>{PLAT_LABELS[p.id]?.slice(0,4)}</text></g>;
                  })}
                </svg>
                <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
                  {kpiD.platforms.map(p=>(
                    <div key={p.id} style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:PLAT_COLORS[p.id]||T.b2}}/>
                      <span style={{fontSize:9,color:T.ink3}}>{PLAT_LABELS[p.id]}</span>
                      <span style={{fontSize:9,fontWeight:600,color:T.ink}}>{p.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {!isKpi&&(
        <div style={{padding:"14px"}}>
          {isBar&&Array.isArray(data)&&<MiniBar data={data} valueKey={wid.typeId==="bar_roas"?"v":"sp"} color={wid.typeId==="bar_roas"?T.g2:T.b2}/>}
          {isLine&&Array.isArray(data)&&<MiniLine data={data}/>}
          {isPie&&Array.isArray(data)&&<MiniPie data={data} donut={false}/>}
          {isDon&&Array.isArray(data)&&<MiniPie data={data} donut={true}/>}
          {isTbl&&wid.typeId==="table_posts"&&Array.isArray(data)&&(
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead><tr>{["Post","Platform","ROAS","Impr","CTR"].map(h=><th key={h} style={{padding:"4px 6px",textAlign:"left",fontSize:9,fontWeight:600,color:T.ink4,textTransform:"uppercase",letterSpacing:".06em",borderBottom:"1px solid "+T.s2}}>{h}</th>)}</tr></thead>
              <tbody>{data.map((r,i)=><tr key={i} style={{borderBottom:"1px solid "+T.s2}}><td style={{padding:"5px 6px",color:T.ink,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.title}</td><td style={{padding:"5px 6px",color:T.ink3}}>{r.platform}</td><td style={{padding:"5px 6px",color:T.g2,fontWeight:600}}>{r.roas}</td><td style={{padding:"5px 6px",color:T.ink3}}>{r.impr}</td><td style={{padding:"5px 6px",color:T.b2,fontWeight:600}}>{r.ctr}</td></tr>)}</tbody>
            </table>
          )}
          {isTbl&&wid.typeId==="table_ch"&&Array.isArray(data)&&(
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead><tr>{["Channel","Spend","Revenue","ROAS","CTR","CPM"].map(h=><th key={h} style={{padding:"4px 6px",textAlign:"left",fontSize:9,fontWeight:600,color:T.ink4,textTransform:"uppercase",letterSpacing:".06em",borderBottom:"1px solid "+T.s2}}>{h}</th>)}</tr></thead>
              <tbody>{data.map((r,i)=><tr key={i} style={{borderBottom:"1px solid "+T.s2}}><td style={{padding:"5px 6px",color:T.ink,fontWeight:500}}>{r.ch}</td><td style={{padding:"5px 6px",color:T.ink3}}>{r.spend}</td><td style={{padding:"5px 6px",color:T.g2,fontWeight:600}}>{r.rev}</td><td style={{padding:"5px 6px",color:parseFloat(r.roas)>=3?T.g2:parseFloat(r.roas)>=2?T.a2:T.r2,fontWeight:600}}>{r.roas}</td><td style={{padding:"5px 6px",color:T.ink3}}>{r.ctr}</td><td style={{padding:"5px 6px",color:T.ink3}}>{r.cpm}</td></tr>)}</tbody>
            </table>
          )}
          {isTxt&&typeof data==="string"&&<p style={{fontSize:12,color:T.ink2,margin:0,lineHeight:1.6,whiteSpace:"pre-line"}}>{wid.override||data}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Social Post Composer ─────────────────────────────────────────────────────
const POST_PLATFORMS = [
  { id:"linkedin",  label:"LinkedIn",  color:"#0A66C2", maxChars:3000 },
  { id:"meta",      label:"Facebook",  color:"#1877F2", maxChars:63206 },
  { id:"instagram", label:"Instagram", color:"#E1306C", maxChars:2200  },
  { id:"x",         label:"X",         color:"#000000", maxChars:280   },
  { id:"tiktok",    label:"TikTok",    color:"#2DD4BF", maxChars:2200  },
];

function SocialPostComposer({ onClose }) {
  const [platforms, setPlatforms] = useState(["linkedin","meta"]);
  const [body, setBody]           = useState("");
  const [headline, setHeadline]   = useState("");
  const [link, setLink]           = useState("");
  const [mediaNote, setMediaNote] = useState("");
  const [schedType, setSchedType] = useState("now"); // now | scheduled
  const [schedDate, setSchedDate] = useState("2026-03-24");
  const [schedTime, setSchedTime] = useState("09:00");
  const [publishing, setPubl]     = useState(false);
  const [published, setPub]       = useState([]);
  const [previewPlatform, setPreviewPlatform] = useState("linkedin");

  const togglePlatform = id => setPlatforms(ps => ps.includes(id) ? ps.filter(x=>x!==id) : [...ps, id]);
  const selPlat = POST_PLATFORMS.filter(p => platforms.includes(p.id));
  const activePreview = POST_PLATFORMS.find(p => p.id === previewPlatform) || selPlat[0];
  const charLimit = activePreview?.maxChars || 3000;

  const publish = () => {
    setPubl(true);
    setTimeout(() => {
      setPubl(false);
      setPub(platforms);
    }, 1500);
  };

  const INP = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const LBL = { fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.45)", zIndex:200, display:"flex", alignItems:"flex-start", justifyContent:"center", paddingTop:60, overflowY:"auto" }}>
      <div style={{ background:T.canvas, borderRadius:14, width:"min(900px,95vw)", boxShadow:"0 24px 64px rgba(15,23,42,.25)", overflow:"hidden" }}>
        {/* Header */}
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <p style={{ fontSize:15, fontWeight:600, color:T.ink, margin:"0 0 2px" }}>Post to social media</p>
            <p style={{ fontSize:11, color:T.ink3, margin:0 }}>Share this report or insight across your connected accounts</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.ink4, fontSize:22, lineHeight:1, padding:"0 4px" }}>×</button>
        </div>

        {published.length > 0 ? (
          /* Success */
          <div style={{ padding:"48px 20px", textAlign:"center" }}>
            <div style={{ width:56, height:56, borderRadius:"50%", background:T.gL, border:`2px solid ${T.gM}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", fontSize:24 }}>✓</div>
            <p style={{ fontSize:18, fontWeight:600, color:T.ink, margin:"0 0 8px" }}>
              {schedType==="now" ? "Published!" : "Scheduled!"}
            </p>
            <p style={{ fontSize:12, color:T.ink3, margin:"0 0 20px" }}>
              {schedType==="now"
                ? `Posted to ${published.map(id=>POST_PLATFORMS.find(p=>p.id===id)?.label).join(", ")}`
                : `Scheduled for ${schedDate} at ${schedTime} on ${published.map(id=>POST_PLATFORMS.find(p=>p.id===id)?.label).join(", ")}`
              }
            </p>
            <button onClick={onClose} style={{ padding:"8px 24px", borderRadius:7, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Done</button>
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 360px", gap:0 }}>
            {/* Left: composer */}
            <div style={{ padding:"20px", borderRight:`1px solid ${T.s2}` }}>
              {/* Platform selector */}
              <div style={{ marginBottom:16 }}>
                <label style={LBL}>Post to</label>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {POST_PLATFORMS.map(p => {
                    const on = platforms.includes(p.id);
                    return (
                      <button key={p.id} onClick={()=>togglePlatform(p.id)} style={{
                        display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:20,
                        border:`1.5px solid ${on?p.color:T.s2}`, background:on?p.color+"18":T.canvas,
                        color:on?p.color:T.ink3, fontSize:12, fontWeight:on?600:400, cursor:"pointer", fontFamily:"inherit",
                      }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:p.color }}/>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Headline */}
              <div style={{ marginBottom:12 }}>
                <label style={LBL}>Headline / hook <span style={{ color:T.ink4, fontWeight:400, fontSize:9 }}>optional</span></label>
                <input value={headline} onChange={e=>setHeadline(e.target.value)} placeholder="e.g. Our Q1 results are in 📊" style={INP}/>
              </div>

              {/* Body */}
              <div style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                  <label style={{ ...LBL, marginBottom:0 }}>Post copy</label>
                  <span style={{ fontSize:10, color:body.length>charLimit*0.9?T.r2:T.ink4 }}>{body.length} / {charLimit}</span>
                </div>
                <textarea rows={6} value={body} onChange={e=>setBody(e.target.value)}
                  placeholder={`Write your post copy here. Reference key metrics from the report — e.g. "We achieved a 2.93× ROAS this week, up 5% from last week. Here's what worked..."`}
                  style={{ ...INP, resize:"vertical", lineHeight:1.6 }}/>
              </div>

              {/* Link */}
              <div style={{ marginBottom:12 }}>
                <label style={LBL}>Link <span style={{ color:T.ink4, fontWeight:400, fontSize:9 }}>optional</span></label>
                <input value={link} onChange={e=>setLink(e.target.value)} placeholder="https://yourdomain.com/report" style={INP}/>
              </div>

              {/* Media */}
              <div style={{ marginBottom:16 }}>
                <label style={LBL}>Creative / image</label>
                <div style={{ border:`1px dashed ${T.s3}`, borderRadius:8, padding:"14px", textAlign:"center", cursor:"pointer", background:T.bg }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.brand;e.currentTarget.style.background=T.bL;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.s3;e.currentTarget.style.background=T.bg;}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="1.5" strokeLinecap="round" style={{ display:"block", margin:"0 auto 6px" }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <p style={{ fontSize:11, color:T.ink3, margin:"0 0 2px" }}>Upload image or video</p>
                  <p style={{ fontSize:10, color:T.ink4, margin:0 }}>JPG · PNG · MP4 · max 50MB</p>
                </div>
              </div>

              {/* Schedule */}
              <div style={{ padding:"12px 14px", background:T.s1, borderRadius:9, marginBottom:16 }}>
                <div style={{ display:"flex", gap:8, marginBottom: schedType==="scheduled"?12:0 }}>
                  {[["now","Post now"],["scheduled","Schedule"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setSchedType(v)} style={{
                      flex:1, padding:"6px 0", borderRadius:6, border:`1px solid ${schedType===v?T.b2:T.s2}`,
                      background:schedType===v?T.bL:T.canvas, color:schedType===v?T.b1:T.ink3,
                      fontSize:11, fontWeight:schedType===v?600:400, cursor:"pointer", fontFamily:"inherit",
                    }}>{l}</button>
                  ))}
                </div>
                {schedType==="scheduled" && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    <div>
                      <label style={LBL}>Date</label>
                      <input type="date" value={schedDate} onChange={e=>setSchedDate(e.target.value)} style={INP}/>
                    </div>
                    <div>
                      <label style={LBL}>Time</label>
                      <input type="time" value={schedTime} onChange={e=>setSchedTime(e.target.value)} style={INP}/>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display:"flex", gap:8 }}>
                <button onClick={onClose} style={{ flex:1, padding:"9px 0", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                <button onClick={publish} disabled={publishing||!body||platforms.length===0}
                  style={{ flex:2, padding:"9px 0", borderRadius:7, border:"none", background:(!body||platforms.length===0||publishing)?T.s2:T.brand, color:(!body||platforms.length===0||publishing)?T.ink4:"#fff", fontSize:12, fontWeight:500, cursor:(!body||platforms.length===0||publishing)?"not-allowed":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                  {publishing
                    ? <><span style={{ width:12, height:12, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"adtech-spin 1s linear infinite" }}/> {schedType==="now"?"Publishing…":"Scheduling…"}</>
                    : schedType==="now" ? "Publish now →" : "Schedule post →"
                  }
                </button>
              </div>
            </div>

            {/* Right: live preview */}
            <div style={{ background:T.s1, padding:"16px", display:"flex", flexDirection:"column", gap:10 }}>
              <p style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:0 }}>Preview</p>
              {/* Platform tabs */}
              {selPlat.length > 1 && (
                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                  {selPlat.map(p=>(
                    <button key={p.id} onClick={()=>setPreviewPlatform(p.id)} style={{
                      padding:"3px 10px", borderRadius:5, border:`1px solid ${previewPlatform===p.id?p.color:T.s2}`,
                      background:previewPlatform===p.id?p.color+"18":T.canvas, color:previewPlatform===p.id?p.color:T.ink3,
                      fontSize:10, fontWeight:previewPlatform===p.id?600:400, cursor:"pointer", fontFamily:"inherit",
                    }}>{p.label}</button>
                  ))}
                </div>
              )}
              {/* Post preview card */}
              {activePreview && (
                <div style={{ background:T.canvas, borderRadius:10, overflow:"hidden", border:`1px solid ${T.s2}`, boxShadow:"0 2px 8px rgba(15,23,42,.08)" }}>
                  <div style={{ background:activePreview.color, padding:"5px 10px", display:"flex", alignItems:"center", gap:6 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:"rgba(255,255,255,.5)" }}/>
                    <span style={{ fontSize:10, fontWeight:500, color:"#fff" }}>{activePreview.label}</span>
                  </div>
                  <div style={{ padding:"12px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                      <div style={{ width:32, height:32, borderRadius:"50%", background:activePreview.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff" }}>A</div>
                      <div>
                        <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>Agency Name</p>
                        <p style={{ fontSize:10, color:T.ink4, margin:0 }}>Just now · Sponsored</p>
                      </div>
                    </div>
                    {headline && <p style={{ fontSize:13, fontWeight:600, color:T.ink, margin:"0 0 6px" }}>{headline}</p>}
                    <p style={{ fontSize:12, color:T.ink2, margin:"0 0 10px", lineHeight:1.6, whiteSpace:"pre-line" }}>
                      {body.slice(0, activePreview.id==="x"?280:300) || <span style={{ color:T.ink4, fontStyle:"italic" }}>Your post copy will appear here…</span>}
                      {body.length > (activePreview.id==="x"?280:300) && "…"}
                    </p>
                    {link && <p style={{ fontSize:11, color:T.b2, margin:"0 0 10px" }}>{link}</p>}
                    <div style={{ height:80, background:T.s1, borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center", gap:6, border:`1px dashed ${T.s3}` }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      <span style={{ fontSize:10, color:T.ink4 }}>Creative asset</span>
                    </div>
                    <div style={{ paddingTop:10, borderTop:`1px solid ${T.s2}`, marginTop:10, display:"flex", gap:16 }}>
                      {["Like","Comment","Share","Repost"].slice(0,activePreview.id==="instagram"?3:4).map(a=>(
                        <span key={a} style={{ fontSize:11, color:T.ink4 }}>{a}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {platforms.length === 0 && (
                <div style={{ textAlign:"center", padding:"32px 16px", color:T.ink4 }}>
                  <p style={{ fontSize:12, margin:0 }}>Select at least one platform above</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Reports Page ─────────────────────────────────────────────────────────────
function ReportsPage({ setShowReport, setShowPreview }) {
  const [tab, setTab]   = useState("builder"); // builder | history | schedules
  const [showPost, setShowPost] = useState(false);

  // ── Widget canvas state ──────────────────────────────────────────────────
  const [widgets, setWidgets]     = useState([
    { id:"w1", typeId:"kpi_spend"   },
    { id:"w2", typeId:"kpi_roas"    },
    { id:"w3", typeId:"bar_channel" },
    { id:"w4", typeId:"line_trend"  },
    { id:"w5", typeId:"pie_channel" },
    { id:"w6", typeId:"text_summary"},
  ]);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [pickerSel, setPickerSel]               = useState(null);
  const [pickerViz, setPickerViz]               = useState("kpi"); // kpi|bar|pie|donut|line
  const [editWidget, setEditWidget] = useState(null);
  const [editValue,  setEditValue]  = useState("");
  const [dragId,    setDragId]      = useState(null);
  const [dragOver,  setDragOver]    = useState(null);
  const [reportName, setReportName] = useState("Weekly Performance Report");
  const [period, setPeriod]         = useState("Mar 15–21, 2026");
  const [generating, setGenerating] = useState(false);
  const [comments, setComments]     = useState([
    { id:"c1", author:"Sarah M.", avatar:"SM", color:"#7C3AED", text:"Google Ads ROAS jumped to 3.9× this week — suggest increasing budget by 15% before Friday.", ts:"Mar 22 · 09:14", resolved:false },
    { id:"c2", author:"James O.",  avatar:"JO", color:"#059669", text:"TikTok performance has been declining for 3 weeks. Should we pause In-Feed and redirect spend?", ts:"Mar 22 · 11:30", resolved:false },
  ]);
  const [newComment, setNewComment] = useState("");
  const [commentAuthor, setCommentAuthor] = useState("You");
  const [generated, setGenerated]   = useState(false);

  const removeWidget  = id => setWidgets(ws => ws.filter(w=>w.id!==id));
  const addWidget     = (typeId, vizType) => { setWidgets(ws=>[...ws,{ id:"w"+Date.now(), typeId, vizType:vizType||"kpi" }]); setShowWidgetPicker(false); };
  const saveEdit      = () => { setWidgets(ws=>ws.map(w=>w.id===editWidget.id?{...w,override:editValue}:w)); setEditWidget(null); };

  const handleDrop = (targetId) => {
    if (!dragId || dragId===targetId) return;
    setWidgets(ws => {
      const from=ws.findIndex(w=>w.id===dragId), to=ws.findIndex(w=>w.id===targetId);
      const arr=[...ws]; const [item]=arr.splice(from,1); arr.splice(to,0,item); return arr;
    });
    setDragId(null); setDragOver(null);
  };

  const generate = () => { setGenerating(true); setTimeout(()=>{ setGenerating(false); setGenerated(true); setTimeout(()=>setGenerated(false),3000); },1600); };

  const HISTORY = [
    { id:"r1", type:"Weekly",  period:"Mar 15–21, 2026", status:"delivered", sz:"612 KB", sent:"Mar 22 · 06:00", kpis:8 },
    { id:"r2", type:"Daily",   period:"Mar 21, 2026",    status:"delivered", sz:"284 KB", sent:"Mar 22 · 07:14", kpis:6 },
    { id:"r3", type:"Organic", period:"Mar 15–21, 2026", status:"generated", sz:"198 KB", sent:"—",              kpis:5 },
    { id:"r4", type:"Monthly", period:"Feb 2026",         status:"delivered", sz:"1.1 MB", sent:"Mar 1 · 06:00",  kpis:12},
    { id:"r5", type:"B2B",     period:"Mar 15–21, 2026", status:"delivered", sz:"312 KB", sent:"Mar 22 · 06:30", kpis:7 },
  ];
  const SCHEDULES = [
    { type:"Daily Performance",  freq:"Daily",  time:"06:00 UTC",     to:"team@agency.com"  },
    { type:"Weekly Review",      freq:"Weekly", time:"Mon 06:00 UTC", to:"cmo@agency.com"   },
    { type:"LinkedIn B2B",       freq:"Weekly", time:"Mon 07:00 UTC", to:"sales@agency.com" },
  ];


  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", background:T.bg }}>
      <style>{`@keyframes adtech-fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}} @keyframes adtech-spin{to{transform:rotate(360deg)}}`}</style>

      {showPost && <SocialPostComposer onClose={()=>setShowPost(false)}/>}

      {/* Header */}
      <div style={{ padding:"16px 22px 0", background:T.canvas, borderBottom:`1px solid ${T.s2}`, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div>
            <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Reports</h1>
            <p style={{ fontSize:12, color:T.ink3, margin:0 }}>Build reports with widgets · generate PDF · post to social</p>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setShowPost(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:`1px solid ${T.vM}`, background:T.vL, color:T.v1, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg>
              Post to social
            </button>
            <button onClick={()=>setShowPreview&&setShowPreview(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview report
            </button>
            <Btn tone="primary" onClick={()=>setShowReport(true)}>Send report</Btn>
          </div>
        </div>
        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`2px solid ${T.s2}`, marginBottom:-2 }}>
          {[["builder","Report builder"],["history","History"],["schedules","Schedules"]].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{ padding:"9px 18px", background:"none", border:"none", borderBottom:`2px solid ${tab===t?T.b2:"transparent"}`, marginBottom:-2, color:tab===t?T.b2:T.ink3, fontSize:12, fontWeight:tab===t?600:400, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>{l}</button>
          ))}
        </div>
      </div>

      {/* ══ BUILDER TAB ══ */}
      {tab==="builder" && (
        <div style={{ flex:1, display:"flex", overflow:"hidden" }}>

          {/* Left sidebar: report meta + widget picker */}
          <div style={{ width:260, flexShrink:0, borderRight:`1px solid ${T.s2}`, overflowY:"auto", padding:"16px", background:T.canvas }}>
            {/* Report meta */}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 }}>Report name</label>
              <input value={reportName} onChange={e=>setReportName(e.target.value)} style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"7px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}/>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 }}>Period</label>
              <input value={period} onChange={e=>setPeriod(e.target.value)} placeholder="e.g. Mar 15–21, 2026" style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"7px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}/>
            </div>

            {/* Widget picker */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <p style={{ fontSize:11, fontWeight:600, color:T.ink, margin:0 }}>Widgets ({widgets.length})</p>
              <button onClick={()=>setShowWidgetPicker(s=>!s)} style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                {showWidgetPicker ? "✕ Close" : "+ Add"}
              </button>
            </div>

            {showWidgetPicker && (() => {
              const kpiTypes   = WIDGET_TYPES.filter(w=>w.cat==="KPI");
              const chartTypes = WIDGET_TYPES.filter(w=>w.cat!=="KPI");
              const chartCats  = [...new Set(chartTypes.map(w=>w.cat))];
              const alreadyAdded = new Set(widgets.map(w=>w.typeId));
              return (
                <div style={{ background:T.bg, border:`1px solid ${T.s2}`, borderRadius:9, marginBottom:12, overflow:"hidden" }}>

                  {/* KPI grid */}
                  <div style={{ padding:"10px 10px 6px" }}>
                    <p style={{ fontSize:9, fontWeight:700, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 8px" }}>KPI metrics — click to preview</p>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4 }}>
                      {kpiTypes.map(wt => {
                        const kd = PER_PLATFORM[wt.id];
                        const added = alreadyAdded.has(wt.id);
                        const isSel = pickerSel === wt.id;
                        return (
                          <button key={wt.id} onClick={()=>setPickerSel(isSel ? null : wt.id)}
                            style={{ padding:"7px 8px", borderRadius:7, border:`1.5px solid ${isSel?T.b2:added?T.gM:T.s2}`, background:isSel?T.bL:added?T.gL:T.canvas, cursor:"pointer", textAlign:"left", fontFamily:"inherit", transition:"all .1s" }}>
                            <p style={{ fontSize:11, fontWeight:isSel?600:400, color:isSel?T.b1:T.ink, margin:"0 0 1px" }}>{wt.label}</p>
                            {kd && <p style={{ fontSize:9, color:isSel?T.b2:T.ink4, margin:0, fontWeight:isSel?600:400 }}>{kd.blended}</p>}
                            {added && !isSel && <p style={{ fontSize:9, color:T.g1, margin:0 }}>added</p>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* KPI preview pane */}
                  {pickerSel && (() => {
                    const wt = WIDGET_TYPES.find(w=>w.id===pickerSel);
                    const kd = PER_PLATFORM[pickerSel];
                    const maxRaw = kd ? Math.max(...kd.platforms.map(p=>p.raw)) : 1;

                    // viz previews
                    const VIZ_OPTS = [
                      { id:"kpi",   label:"KPI card",   desc:"Number + platform breakdown bars" },
                      { id:"bar",   label:"Bar chart",   desc:"Horizontal bars per platform"     },
                      { id:"pie",   label:"Pie chart",   desc:"Platform share as wedges"         },
                      { id:"donut", label:"Donut chart", desc:"Platform share with centre total" },
                      { id:"line",  label:"Trend line",  desc:"7-day trend over time"            },
                    ];

                    // mini previews rendered inline
                    const renderMiniViz = (vizId) => {
                      const barData = kd?.platforms||[];
                      const maxR    = maxRaw;
                      if (vizId==="kpi") return (
                        <div style={{padding:"6px 8px",background:T.bg,borderRadius:6}}>
                          <p style={{fontSize:8,color:T.ink4,margin:"0 0 1px",textTransform:"uppercase"}}>{kd?.label}</p>
                          <p style={{fontSize:18,fontWeight:700,color:T.ink,margin:"0 0 3px"}}>{kd?.blended}</p>
                          <div style={{display:"flex",flexDirection:"column",gap:2}}>
                            {barData.slice(0,3).map(p=>(
                              <div key={p.id} style={{display:"flex",alignItems:"center",gap:4}}>
                                <div style={{width:4,height:4,borderRadius:"50%",background:PLAT_COLORS[p.id]||T.b2}}/>
                                <div style={{flex:1,height:5,background:T.s1,borderRadius:1,overflow:"hidden"}}><div style={{height:"100%",width:(p.raw/maxR*100)+"%",background:PLAT_COLORS[p.id]||T.b2}}/></div>
                                <span style={{fontSize:7,color:T.ink,minWidth:22,textAlign:"right"}}>{p.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                      if (vizId==="bar") return (
                        <div style={{padding:"6px 8px",background:T.bg,borderRadius:6}}>
                          <p style={{fontSize:8,color:T.ink4,margin:"0 0 4px",textTransform:"uppercase"}}>{kd?.label} by platform</p>
                          {barData.map(p=>(
                            <div key={p.id} style={{display:"flex",alignItems:"center",gap:3,marginBottom:2}}>
                              <span style={{fontSize:7,color:T.ink4,width:36,textAlign:"right",flexShrink:0}}>{PLAT_LABELS[p.id]}</span>
                              <div style={{flex:1,height:8,background:T.s1,borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:(p.raw/maxR*100)+"%",background:PLAT_COLORS[p.id]||T.b2,borderRadius:2}}/></div>
                              <span style={{fontSize:7,fontWeight:600,color:T.ink,minWidth:24,textAlign:"right"}}>{p.value}</span>
                            </div>
                          ))}
                        </div>
                      );
                      if (vizId==="pie"||vizId==="donut") {
                        const total=barData.reduce((a,p)=>a+p.raw,0);
                        let ang=-90;
                        const r=vizId==="donut"?22:28,cx=34,cy=34;
                        const paths=barData.map(p=>{
                          const sw=(p.raw/total)*360,st=ang;ang+=sw;
                          const s=Math.PI/180,x1=cx+r*Math.cos(st*s),y1=cy+r*Math.sin(st*s),x2=cx+r*Math.cos((st+sw)*s),y2=cy+r*Math.sin((st+sw)*s),lg=sw>180?1:0;
                          if(vizId==="donut"){const ir=12,ix1=cx+ir*Math.cos(st*s),iy1=cy+ir*Math.sin(st*s),ix2=cx+ir*Math.cos((st+sw)*s),iy2=cy+ir*Math.sin((st+sw)*s);return{d:"M"+x1+","+y1+" A"+r+","+r+" 0 "+lg+",1 "+x2+","+y2+" L"+ix2+","+iy2+" A"+ir+","+ir+" 0 "+lg+",0 "+ix1+","+iy1+" Z",c:PLAT_COLORS[p.id]||T.b2};}
                          return{d:"M"+cx+","+cy+" L"+x1+","+y1+" A"+r+","+r+" 0 "+lg+",1 "+x2+","+y2+" Z",c:PLAT_COLORS[p.id]||T.b2};
                        });
                        return (
                          <div style={{padding:"6px 8px",background:T.bg,borderRadius:6,display:"flex",alignItems:"center",gap:8}}>
                            <svg width="68" height="68" viewBox="0 0 68 68">{paths.map((p,i)=><path key={i} d={p.d} fill={p.c} stroke="#fff" strokeWidth="1"/>)}{vizId==="donut"&&<text x="34" y="38" textAnchor="middle" fontSize="9" fontWeight="700" fill={T.ink}>{kd?.blended}</text>}</svg>
                            <div>{barData.slice(0,4).map(p=><div key={p.id} style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}><div style={{width:5,height:5,borderRadius:"50%",background:PLAT_COLORS[p.id]||T.b2}}/><span style={{fontSize:8,color:T.ink3}}>{PLAT_LABELS[p.id]}</span><span style={{fontSize:8,fontWeight:600,color:T.ink,marginLeft:2}}>{Math.round(p.raw/total*100)}%</span></div>)}</div>
                          </div>
                        );
                      }
                      if (vizId==="line") {
                        const pts=barData.map((p,i)=>{const x=8+(i/(barData.length-1))*104,y=50-((p.raw/maxR)*38);return x+","+y;}).join(" ");
                        return (
                          <div style={{padding:"6px 8px",background:T.bg,borderRadius:6}}>
                            <p style={{fontSize:8,color:T.ink4,margin:"0 0 3px",textTransform:"uppercase"}}>{kd?.label} trend (simulated)</p>
                            <svg width="120" height="58" style={{display:"block"}}>
                              <polyline points={pts} fill="none" stroke={T.b2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              {barData.map((p,i)=>{const x=8+(i/(barData.length-1))*104,y=50-((p.raw/maxR)*38);return <circle key={i} cx={x} cy={y} r="3" fill={T.b2}/>;  })}
                            </svg>
                          </div>
                        );
                      }
                      return null;
                    };

                    return (
                      <div style={{ margin:"0 10px 10px", padding:"12px", background:T.canvas, borderRadius:8, border:`1px solid ${T.bM}` }}>
                        {/* Metric header */}
                        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:10 }}>
                          <span style={{ fontSize:9, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em" }}>{kd?.label}</span>
                          <span style={{ fontSize:20, fontWeight:700, color:T.ink }}>{kd?.blended}</span>
                          <span style={{ fontSize:10, fontWeight:600, padding:"1px 5px", borderRadius:3, background:kd?.dir==="up"?T.gL:T.rL, color:kd?.dir==="up"?T.g1:T.r1 }}>
                            {kd?.dir==="up"?"↑":"↓"} {kd?.chg}
                          </span>
                        </div>

                        {/* Chart type selector */}
                        <p style={{ fontSize:9, fontWeight:700, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 6px" }}>Choose chart type</p>
                        <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
                          {VIZ_OPTS.map(opt => {
                            const isSel = pickerViz===opt.id;
                            return (
                              <div key={opt.id} style={{ borderRadius:8, border:`1.5px solid ${isSel?T.b2:T.s2}`, background:isSel?T.bL:T.bg, overflow:"hidden", cursor:"pointer", transition:"all .1s" }}
                                onClick={()=>setPickerViz(opt.id)}>
                                {/* Label row */}
                                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px" }}>
                                  <div style={{ width:16, height:16, borderRadius:"50%", border:`2px solid ${isSel?T.b2:T.s3}`, background:isSel?T.b2:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                                    {isSel&&<div style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>}
                                  </div>
                                  <div style={{ flex:1 }}>
                                    <p style={{ fontSize:11, fontWeight:isSel?600:400, color:isSel?T.b1:T.ink, margin:0 }}>{opt.label}</p>
                                    <p style={{ fontSize:9, color:T.ink4, margin:0 }}>{opt.desc}</p>
                                  </div>
                                </div>
                                {/* Inline mini preview — only for selected */}
                                {isSel && (
                                  <div style={{ borderTop:`1px solid ${T.bM}`, padding:"8px 10px" }}>
                                    {renderMiniViz(opt.id)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <button onClick={()=>{ addWidget(pickerSel, pickerViz); setPickerSel(null); setPickerViz("kpi"); }}
                          style={{ width:"100%", padding:"8px 0", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                          Add {wt?.label} as {VIZ_OPTS.find(v=>v.id===pickerViz)?.label} to report
                        </button>
                      </div>
                    );
                  })()}

                  {/* Charts, Tables, Text */}
                  <div style={{ padding:"0 10px 10px", borderTop:`1px solid ${T.s2}`, marginTop:4 }}>
                    {chartCats.map(cat => (
                      <div key={cat} style={{ marginTop:10 }}>
                        <p style={{ fontSize:9, fontWeight:700, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 5px" }}>{cat}</p>
                        {chartTypes.filter(w=>w.cat===cat).map(wt=>(
                          <button key={wt.id} onClick={()=>addWidget(wt.id)}
                            style={{ display:"block", width:"100%", textAlign:"left", padding:"6px 8px", borderRadius:6, border:`1px solid ${alreadyAdded.has(wt.id)?T.gM:T.s2}`, background:alreadyAdded.has(wt.id)?T.gL:T.canvas, color:T.ink2, fontSize:11, cursor:"pointer", fontFamily:"inherit", marginBottom:4 }}>
                            {alreadyAdded.has(wt.id) ? "✓ " : "+ "}{wt.label}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Widget list (drag handles) */}
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {widgets.map(w=>{
                const wt=WIDGET_TYPES.find(x=>x.id===w.typeId);
                return (
                  <div key={w.id} draggable
                    onDragStart={()=>setDragId(w.id)}
                    onDragOver={e=>{e.preventDefault();setDragOver(w.id);}}
                    onDrop={()=>handleDrop(w.id)}
                    style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 8px", borderRadius:7, border:`1px solid ${dragOver===w.id?T.b2:T.s2}`, background:dragOver===w.id?T.bL:T.canvas, cursor:"grab", userSelect:"none" }}>
                    <span style={{ fontSize:14, color:T.ink4 }}>⠿</span>
                    <span style={{ fontSize:11, color:T.ink, flex:1 }}>{wt?.label}</span>
                    <button onClick={()=>removeWidget(w.id)} style={{ background:"none", border:"none", color:T.ink4, cursor:"pointer", fontSize:12, padding:"0 2px" }}>✕</button>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop:16, display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={generate} style={{ padding:"9px 0", borderRadius:7, border:"none", background:generating||generated?T.gL:T.brand, color:generating||generated?T.g1:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                {generating ? <><span style={{ width:12, height:12, border:"2px solid rgba(255,255,255,.3)", borderTopColor:T.g2, borderRadius:"50%", display:"inline-block", animation:"adtech-spin 1s linear infinite" }}/> Generating…</> : generated ? "✓ PDF generated!" : "Generate PDF"}
              </button>
              <button onClick={()=>setShowReport(true)} style={{ padding:"9px 0", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Send to client</button>
              <button onClick={()=>setShowPost(true)} style={{ padding:"9px 0", borderRadius:7, border:`1px solid ${T.vM}`, background:T.vL, color:T.v1, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Post to social →</button>
            </div>
          </div>

          {/* Right: widget canvas */}
          <div style={{ flex:1, overflowY:"auto", padding:"20px", background:"#EEF2F7" }}>
            {/* Report header */}
            <div style={{ background:`linear-gradient(135deg,${T.b1},${T.v2})`, borderRadius:12, padding:"22px 24px", marginBottom:16, color:"#fff" }}>
              <p style={{ fontSize:11, opacity:.65, margin:"0 0 6px", textTransform:"uppercase", letterSpacing:".1em" }}>Performance Report</p>
              <p style={{ fontSize:20, fontWeight:700, margin:"0 0 4px" }}>{reportName}</p>
              <p style={{ fontSize:12, opacity:.75, margin:0 }}>{period} &nbsp;·&nbsp; Demo Agency &nbsp;·&nbsp; {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</p>
            </div>

            {/* Edit modal */}
            {editWidget && (
              <div style={{ background:T.canvas, border:`1px solid ${T.bM}`, borderRadius:10, padding:"14px", marginBottom:16 }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 8px" }}>Edit widget content</p>
                <textarea rows={3} value={editValue} onChange={e=>setEditValue(e.target.value)}
                  style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px", fontSize:12, outline:"none", boxSizing:"border-box", fontFamily:"inherit", resize:"vertical" }}/>
                <div style={{ display:"flex", gap:6, marginTop:8 }}>
                  <button onClick={saveEdit} style={{ padding:"5px 14px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Save</button>
                  <button onClick={()=>setEditWidget(null)} style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Widget grid */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {widgets.map(w=>(
                <ReportWidget key={w.id} wid={w}
                  onRemove={removeWidget}
                  onEdit={w=>{ setEditWidget(w); setEditValue(w.override || (typeof WIDGET_DATA[w.typeId]==="string"?WIDGET_DATA[w.typeId]:"") || ""); }}/>
              ))}
            </div>

            {widgets.length===0 && (
              <div style={{ textAlign:"center", padding:"60px 20px", color:T.ink4 }}>
                <p style={{ fontSize:14, margin:"0 0 8px" }}>No widgets yet</p>
                <p style={{ fontSize:12, margin:0 }}>Click "+ Add" in the left panel to add charts and KPIs</p>
              </div>
            )}

            {/* ── Comment section ── */}
            <div style={{ marginTop:20, background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden" }}>
              {/* Header */}
              <div style={{ padding:"12px 18px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:T.s1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink3} strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>Comments</p>
                  {comments.filter(c=>!c.resolved).length > 0 && (
                    <span style={{ fontSize:10, fontWeight:600, padding:"1px 7px", borderRadius:10, background:T.brand, color:"#fff" }}>
                      {comments.filter(c=>!c.resolved).length}
                    </span>
                  )}
                </div>
                <span style={{ fontSize:10, color:T.ink4 }}>{comments.length} comment{comments.length!==1?"s":""}</span>
              </div>

              {/* Existing comments */}
              <div style={{ maxHeight:340, overflowY:"auto" }}>
                {comments.length===0 && (
                  <p style={{ fontSize:12, color:T.ink4, textAlign:"center", padding:"24px 20px", margin:0 }}>No comments yet — add the first one below</p>
                )}
                {comments.map((c,i) => (
                  <div key={c.id} style={{ padding:"14px 18px", borderBottom:i<comments.length-1?`1px solid ${T.s2}`:"none", opacity:c.resolved?0.55:1 }}>
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                      {/* Avatar */}
                      <div style={{ width:32, height:32, borderRadius:"50%", background:c.color+"22", border:`2px solid ${c.color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:c.color, flexShrink:0 }}>
                        {c.avatar}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                          <span style={{ fontSize:12, fontWeight:600, color:T.ink }}>{c.author}</span>
                          <span style={{ fontSize:10, color:T.ink4 }}>{c.ts}</span>
                          {c.resolved && <span style={{ fontSize:9, fontWeight:600, padding:"1px 6px", borderRadius:4, background:T.gL, color:T.g1 }}>Resolved</span>}
                        </div>
                        <p style={{ fontSize:12, color:T.ink2, margin:"0 0 8px", lineHeight:1.6 }}>{c.text}</p>
                        <div style={{ display:"flex", gap:8 }}>
                          {!c.resolved && (
                            <button onClick={()=>setComments(cs=>cs.map(x=>x.id===c.id?{...x,resolved:true}:x))}
                              style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, border:`1px solid ${T.gM}`, background:T.gL, color:T.g1, cursor:"pointer", fontFamily:"inherit" }}>
                              ✓ Resolve
                            </button>
                          )}
                          {c.resolved && (
                            <button onClick={()=>setComments(cs=>cs.map(x=>x.id===c.id?{...x,resolved:false}:x))}
                              style={{ fontSize:10, color:T.ink4, padding:"2px 8px", borderRadius:4, border:`1px solid ${T.s2}`, background:"transparent", cursor:"pointer", fontFamily:"inherit" }}>
                              Reopen
                            </button>
                          )}
                          <button onClick={()=>setComments(cs=>cs.filter(x=>x.id!==c.id))}
                            style={{ fontSize:10, color:T.r2, padding:"2px 8px", borderRadius:4, border:`1px solid ${T.rM||T.s2}`, background:T.rL, cursor:"pointer", fontFamily:"inherit" }}>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* New comment input */}
              <div style={{ padding:"14px 18px", borderTop:`1px solid ${T.s2}`, background:T.bg }}>
                <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:T.bL, border:`2px solid ${T.bM}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:T.b1, flexShrink:0 }}>
                    {commentAuthor.slice(0,2).toUpperCase()||"YO"}
                  </div>
                  <div style={{ flex:1 }}>
                    <textarea
                      rows={2}
                      value={newComment}
                      onChange={e=>setNewComment(e.target.value)}
                      onKeyDown={e=>{ if(e.key==="Enter"&&(e.metaKey||e.ctrlKey)&&newComment.trim()){ setComments(cs=>[...cs,{ id:"c"+Date.now(), author:commentAuthor||"You", avatar:(commentAuthor||"You").slice(0,2).toUpperCase(), color:T.b2, text:newComment.trim(), ts:"Just now", resolved:false }]); setNewComment(""); }}}
                      placeholder="Add a comment… (Cmd/Ctrl + Enter to post)"
                      style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:8, padding:"8px 10px", fontSize:12, color:T.ink, background:T.canvas, outline:"none", boxSizing:"border-box", fontFamily:"inherit", resize:"none", lineHeight:1.5 }}
                    />
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:6 }}>
                      <input value={commentAuthor} onChange={e=>setCommentAuthor(e.target.value)} placeholder="Your name"
                        style={{ border:`1px solid ${T.s2}`, borderRadius:6, padding:"4px 8px", fontSize:11, color:T.ink3, background:T.canvas, outline:"none", fontFamily:"inherit", width:120 }}/>
                      <button
                        onClick={()=>{ if(!newComment.trim()) return; setComments(cs=>[...cs,{ id:"c"+Date.now(), author:commentAuthor||"You", avatar:(commentAuthor||"You").slice(0,2).toUpperCase(), color:T.b2, text:newComment.trim(), ts:"Just now", resolved:false }]); setNewComment(""); }}
                        disabled={!newComment.trim()}
                        style={{ padding:"5px 14px", borderRadius:6, border:"none", background:newComment.trim()?T.brand:T.s2, color:newComment.trim()?"#fff":T.ink4, fontSize:11, fontWeight:600, cursor:newComment.trim()?"pointer":"default", fontFamily:"inherit" }}>
                        Post comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ══ HISTORY TAB ══ */}
      {tab==="history" && (
        <div style={{ flex:1, overflowY:"auto", padding:"20px 22px" }}>
          <div style={{ maxWidth:820 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0 }}>Generated reports</p>
              <button onClick={()=>setShowPost(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:6, border:`1px solid ${T.vM}`, background:T.vL, color:T.v1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg>
                Post to social
              </button>
            </div>
            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead style={{ background:T.s1 }}>
                  <tr>{["Type","Period","Status","Size","Sent","Actions"].map(h=><th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", borderBottom:`1px solid ${T.s2}` }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {HISTORY.map((r,i)=>(
                    <tr key={r.id} style={{ borderBottom:i<HISTORY.length-1?`1px solid ${T.s2}`:"none" }}>
                      <td style={{ padding:"11px 14px", fontWeight:500, color:T.ink }}>{r.type}</td>
                      <td style={{ padding:"11px 14px", color:T.ink3 }}>{r.period}</td>
                      <td style={{ padding:"11px 14px" }}>
                        <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, background:r.status==="delivered"?T.gL:T.bL, color:r.status==="delivered"?T.g1:T.b1 }}>{r.status}</span>
                      </td>
                      <td style={{ padding:"11px 14px", color:T.ink4 }}>{r.sz}</td>
                      <td style={{ padding:"11px 14px", color:T.ink4 }}>{r.sent}</td>
                      <td style={{ padding:"11px 14px" }}>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={()=>setShowPreview&&setShowPreview(true)} style={{ padding:"4px 9px", borderRadius:5, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>Preview</button>
                          <button onClick={()=>setShowReport(true)} style={{ padding:"4px 9px", borderRadius:5, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Send</button>
                          <button onClick={()=>setShowPost(true)} style={{ padding:"4px 9px", borderRadius:5, border:`1px solid ${T.vM}`, background:T.vL, color:T.v1, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Post</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ SCHEDULES TAB ══ */}
      {tab==="schedules" && (
        <div style={{ flex:1, overflowY:"auto", padding:"20px 22px" }}>
          <div style={{ maxWidth:680 }}>
            <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:"0 0 14px" }}>Active schedules</p>
            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden", marginBottom:14 }}>
              {SCHEDULES.map((s,i)=>(
                <div key={i} style={{ padding:"14px 18px", borderBottom:i<SCHEDULES.length-1?`1px solid ${T.s2}`:"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div>
                    <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>{s.type}</p>
                    <p style={{ fontSize:11, color:T.ink4, margin:0 }}>{s.freq} · {s.time} · {s.to}</p>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                    <button style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${T.rM||T.s2}`, background:T.rL, color:T.r2, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Pause</button>
                  </div>
                </div>
              ))}
            </div>
            <Btn tone="primary">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Add schedule
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Organic vs Paid Page ─────────────────────────────────────────────────────
function OrgVsPaid() {
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", background: T.bg }}>
      <h1 style={{ fontSize: 18, fontWeight: 500, color: T.ink, margin: "0 0 4px" }}>Organic vs Paid</h1>
      <p style={{ fontSize: 12, color: T.ink3, margin: "0 0 18px" }}>Side-by-side attribution · 30-day window</p>
      <Card style={{ marginBottom: 14 }}>
        <SH title="Revenue comparison" sub="Paid vs organic · 30d" />
        <ResponsiveContainer width="100%" height={145}>
          <AreaChart data={TREND30} margin={{ top: 4, right: 4, left: -15, bottom: 0 }}>
            <defs>
              <linearGradient id="gP3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={T.b2} stopOpacity={.15} />
                <stop offset="95%" stopColor={T.b2} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gO3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={T.g2} stopOpacity={.15} />
                <stop offset="95%" stopColor={T.g2} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={T.s2} vertical={false} />
            <XAxis dataKey="d" tick={{ fontSize: 9, fill: T.ink4 }} axisLine={false} tickLine={false} interval={5} />
            <YAxis tick={{ fontSize: 9, fill: T.ink4 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip {...TTP} formatter={v => [`$${Math.round(v).toLocaleString()}`]} />
            <Legend wrapperStyle={{ fontSize: 11, color: T.ink3 }} />
            <Area dataKey="paid"    name="Paid Revenue"    stroke={T.b2} strokeWidth={2.5} fill="url(#gP3)" dot={false} />
            <Area dataKey="organic" name="Organic Revenue" stroke={T.g2} strokeWidth={2.5} fill="url(#gO3)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ background: T.canvas, border: `1px solid ${T.s2}`, borderLeft: `4px solid ${T.b2}`, borderRadius: "0 12px 12px 0", padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: T.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: T.b2, display: "inline-block" }} />Paid
            </p>
            <Tag tone="blue">$84.2k spend</Tag>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {[["Impressions","3.8M",T.b2],["Clicks","42.1k",T.v2],["CPM","$11.40",T.a2],["CPC","$2.00",T.a1],["CTR","1.74%",T.v2],["ROAS","2.93×",T.g2],["Conversions","1,840",T.g2],["Revenue","$247k",T.g1],["CPA","$45.80",T.a2],["Video views","218k",T.r2]].map(([l, v, a]) => (
              <div key={l} style={{ background: T.bg, borderRadius: 8, padding: "7px 9px", textAlign: "center", border: `1px solid ${T.s2}` }}>
                <p style={{ fontSize: 8, color: T.ink4, fontWeight: 700, margin: "0 0 2px" }}>{l}</p>
                <p style={{ fontSize: 12, fontWeight: 800, color: a, margin: 0 }}>{v}</p>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: T.canvas, border: `1px solid ${T.s2}`, borderLeft: `4px solid ${T.g2}`, borderRadius: "0 12px 12px 0", padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <p style={{ fontSize: 14, fontWeight: 800, color: T.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: T.g2, display: "inline-block" }} />Organic
            </p>
            <Tag tone="ok">$0 spend</Tag>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {[["Sessions","28.4k",T.b2],["Users","19.2k",T.b3],["Engagement","61%",T.v2],["Avg duration","2m 14s",T.ink3],["Bounce rate","34%",T.r2],["New users","11.8k",T.g2],["Revenue","$18.9k",T.g1],["Conv. rate","3.2%",T.g2],["Followers","+842",T.v2],["Post reach","148k",T.b2]].map(([l, v, a]) => (
              <div key={l} style={{ background: T.bg, borderRadius: 8, padding: "7px 9px", textAlign: "center", border: `1px solid ${T.s2}` }}>
                <p style={{ fontSize: 8, color: T.ink4, fontWeight: 700, margin: "0 0 2px" }}>{l}</p>
                <p style={{ fontSize: 12, fontWeight: 800, color: a, margin: 0 }}>{v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}



// ─── Split Creative Pane (Step 2) ────────────────────────────────────────────

// ─── Create Ad — Option B: Brief → Platform Launcher ─────────────────────────

const CA_PLATFORMS = [
  { id:"meta",      label:"Meta Ads",      color:"#1877F2", tc:"#fff", abbr:"f",  api:true,  url:"https://adsmanager.facebook.com/adsmanager/creation",
    targeting:[{id:"interests",l:"Interests",ph:"e.g. Digital marketing, SaaS, B2B"},{id:"behaviours",l:"Behaviours",ph:"e.g. Business decision makers"},{id:"lookalike",l:"Lookalike source",ph:"e.g. Existing customers"},{id:"custom",l:"Custom audience",ph:"e.g. Website visitors 30d"}],
    placements:["Feed","Stories / Reels","Messenger","Audience Network"],
    bids:["Lowest cost","Cost cap","Bid cap","Target ROAS"],
    formats:["Single image","Video","Carousel","Collection"],
  },
  { id:"google_ads",label:"Google Ads",    color:"#4285F4", tc:"#fff", abbr:"G",  api:true,  url:"https://ads.google.com/aw/campaigns/new",
    targeting:[{id:"keywords",l:"Keywords",ph:"e.g. crm software, b2b marketing"},{id:"match",l:"Match types",ph:"Broad, Phrase, Exact"},{id:"negatives",l:"Negative keywords",ph:"e.g. free, cheap"},{id:"audiences",l:"In-market audiences",ph:"e.g. Business software buyers"}],
    placements:["Search results","Display Network","YouTube","Gmail"],
    bids:["Maximise conversions","Target CPA","Target ROAS","Maximise clicks"],
    formats:["Responsive Search Ad","Responsive Display","Video (YouTube)","Performance Max"],
  },
  { id:"linkedin",  label:"LinkedIn",      color:"#0A66C2", tc:"#fff", abbr:"in", api:false, url:"https://www.linkedin.com/campaignmanager/",
    targeting:[{id:"job_title",l:"Job titles",ph:"e.g. VP Marketing, Head of Growth"},{id:"seniority",l:"Seniority",ph:"e.g. Director, VP, C-Suite"},{id:"industry",l:"Industry",ph:"e.g. SaaS, Financial Services"},{id:"company_size",l:"Company size",ph:"e.g. 50–200, 500+"},{id:"skills",l:"Member skills",ph:"e.g. Digital Marketing, B2B Sales"}],
    placements:["LinkedIn Feed","Stories","Messaging","Audience Network"],
    bids:["Maximum delivery","Target cost","Manual bid"],
    formats:["Single image","Video","Carousel","Lead Gen Form","Document ad"],
  },
  { id:"tiktok",    label:"TikTok",        color:"#010101", tc:"#fff", abbr:"TT", api:false, url:"https://ads.tiktok.com/i18n/dashboard",
    targeting:[{id:"interests",l:"Interests",ph:"e.g. Technology, Business, Marketing"},{id:"behaviours",l:"Video interactions",ph:"e.g. Liked tech videos last 7d"},{id:"hashtags",l:"Hashtag / creator",ph:"e.g. #marketingtips, #saas"}],
    placements:["TikTok In-Feed","TopView","Spark Ads","Brand Takeover"],
    bids:["Lowest cost","Cost cap","Bid cap"],
    formats:["In-Feed Video","Spark Ad","TopView","Collection"],
  },
  { id:"snapchat",  label:"Snapchat",      color:"#FFFC00", tc:"#000", abbr:"S",  api:false, url:"https://forbusiness.snapchat.com/",
    targeting:[{id:"interests",l:"Interests & categories",ph:"e.g. Technology, Business"},{id:"custom",l:"Custom audience",ph:"e.g. Customer list"},{id:"lookalike",l:"Lookalike",ph:"Based on customer list"}],
    placements:["Between Stories","Story Ads","Commercials","Collection"],
    bids:["Auto-bid","Target cost","Max bid"],
    formats:["Single video","Story Ad","Collection","Dynamic"],
  },
  { id:"pinterest", label:"Pinterest",     color:"#E60023", tc:"#fff", abbr:"P",  api:false, url:"https://ads.pinterest.com/",
    targeting:[{id:"interests",l:"Interests",ph:"e.g. Home decor, Technology"},{id:"keywords",l:"Keywords",ph:"e.g. minimalist home office"},{id:"custom",l:"Customer list",ph:"Upload email list"}],
    placements:["Home feed","Search results","Related pins","Explore"],
    bids:["Automatic bid","Custom bid"],
    formats:["Standard Pin","Video Pin","Carousel","Shopping"],
  },
  { id:"x",         label:"X / Twitter",  color:"#000000", tc:"#fff", abbr:"X",  api:false, url:"https://ads.twitter.com/",
    targeting:[{id:"interests",l:"Interests",ph:"e.g. Technology, Marketing"},{id:"keywords",l:"Keywords in tweets",ph:"e.g. CRM software, automation"},{id:"followers",l:"Follower look-alikes",ph:"e.g. @HubSpot followers"}],
    placements:["Timeline","Profile pages","Search results"],
    bids:["Automatic bid","Maximum bid","Target bid"],
    formats:["Promoted Ad","Video Ad","Carousel"],
  },
];

const CA_OBJECTIVES = [
  {id:"sales",      label:"Sales & conversions",  desc:"Drive purchases, sign-ups, or other conversions"},
  {id:"leads",      label:"Lead generation",       desc:"Collect contact info via forms or landing pages"},
  {id:"traffic",    label:"Website traffic",       desc:"Send people to your website or app"},
  {id:"awareness",  label:"Brand awareness",       desc:"Reach new audiences and build brand recall"},
  {id:"engagement", label:"Engagement",            desc:"Get likes, comments, shares and video views"},
  {id:"app",        label:"App installs",          desc:"Drive downloads and in-app actions"},
];

const CA_REC = {
  sales:["meta","google_ads","tiktok","pinterest","snapchat"],
  leads:["linkedin","meta","google_ads","tiktok"],
  traffic:["google_ads","meta","pinterest","x"],
  awareness:["meta","tiktok","snapchat","x"],
  engagement:["meta","tiktok","x","pinterest"],
  app:["meta","google_ads","tiktok","snapchat"],
};

// ─── Platform Preview Card ────────────────────────────────────────────────────
function PlatformPreview({ plat, headline, body, cta, destUrl }) {
  const hl = headline || "Your headline here";
  const bd = body     || "Your ad copy will appear here.";
  const isVertical = ["tiktok","snapchat"].includes(plat.id);
  const isSearch   = plat.id === "google_ads";
  const snapBg     = plat.id === "snapchat";
  return (
    <div style={{ borderRadius:10, overflow:"hidden", border:"1px solid rgba(0,0,0,.08)", background:"#fff", boxShadow:"0 2px 8px rgba(0,0,0,.1)" }}>
      <div style={{ background:plat.color, padding:"7px 12px", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:22, height:22, borderRadius:5, background:"rgba(255,255,255,.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:plat.tc, flexShrink:0 }}>{plat.abbr}</div>
        <span style={{ fontSize:12, fontWeight:500, color:plat.tc }}>{plat.label}</span>
        <span style={{ marginLeft:"auto", fontSize:10, color:plat.tc, opacity:.7 }}>{isVertical?"Story":isSearch?"Search":"Feed"}</span>
      </div>
      {isSearch && (
        <div style={{ padding:"12px 14px", background:"#fff" }}>
          <div style={{ fontSize:10, color:"#188038", fontWeight:500, marginBottom:3 }}>Ad · {destUrl||"yoursite.com"}</div>
          <div style={{ fontSize:17, color:"#1558D6", lineHeight:1.3, marginBottom:5 }}>{hl}</div>
          <div style={{ fontSize:12, color:"#3C4043", lineHeight:1.5, marginBottom:10 }}>{bd.slice(0,90)}{bd.length>90?"…":""}</div>
          <div style={{ display:"flex", gap:6 }}>
            {["Free trial","Pricing","How it works"].map(s=>(
              <span key={s} style={{ fontSize:11, color:"#1558D6", border:"1px solid #DADCE0", borderRadius:4, padding:"3px 8px" }}>{s}</span>
            ))}
          </div>
        </div>
      )}
      {isVertical && (
        <div style={{ height:200, background:snapBg?"#FFFC00":"#111", padding:"10px 12px 14px", display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
          <div style={{ display:"flex", gap:3 }}>
            {[1,2,3,4].map(i=><div key={i} style={{ flex:1, height:2, borderRadius:1, background:i===1?(snapBg?"#000":"#fff"):"rgba(128,128,128,.35)" }}/>)}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", background:plat.color, border:"2px solid rgba(255,255,255,.4)" }}/>
            <span style={{ fontSize:12, fontWeight:500, color:snapBg?"#000":"#fff" }}>Your Page</span>
            <span style={{ fontSize:10, color:snapBg?"rgba(0,0,0,.5)":"rgba(255,255,255,.5)" }}>· Sponsored</span>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:500, color:snapBg?"#000":"#fff", marginBottom:10, lineHeight:1.3 }}>{hl}</div>
            <button style={{ background:"rgba(255,255,255,.9)", border:"none", borderRadius:6, padding:"7px 16px", fontSize:12, fontWeight:600, cursor:"pointer", color:"#0F172A" }}>{cta}</button>
          </div>
        </div>
      )}
      {!isSearch && !isVertical && (
        <div style={{ background:"#fff" }}>
          <div style={{ padding:"9px 11px", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid #F1F5F9" }}>
            <div style={{ width:34, height:34, borderRadius:plat.id==="linkedin"?7:"50%", background:plat.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:plat.tc, flexShrink:0 }}>{plat.abbr}</div>
            <div>
              <div style={{ fontSize:12, fontWeight:500, color:"#0F172A" }}>Your Page</div>
              <div style={{ fontSize:10, color:"#94A3B8" }}>Sponsored</div>
            </div>
          </div>
          <div style={{ padding:"9px 11px 6px" }}>
            <div style={{ fontSize:12, color:"#334155", lineHeight:1.5 }}>{bd.slice(0,90)}{bd.length>90?"…":""}</div>
          </div>
          <div style={{ height:90, background:"#F1F5F9", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <span style={{ fontSize:10, color:"#CBD5E1" }}>Creative asset</span>
          </div>
          <div style={{ padding:"9px 11px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:10, color:"#94A3B8", marginBottom:1 }}>{destUrl||"yoursite.com"}</div>
              <div style={{ fontSize:12, fontWeight:500, color:"#0F172A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{hl}</div>
            </div>
            <button style={{ background:plat.id==="linkedin"?"#0A66C2":"#0F172A", border:"none", borderRadius:5, padding:"6px 12px", fontSize:11, fontWeight:500, color:"#fff", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>{cta}</button>
          </div>
          <div style={{ padding:"7px 11px", borderTop:"1px solid #F1F5F9", display:"flex", gap:16 }}>
            {["Like","Comment","Share"].map(a=><span key={a} style={{ fontSize:11, color:"#94A3B8" }}>{a}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create Ad ────────────────────────────────────────────────────────────────
function CreateAd({ setPage }) {
  const [step, setStep]     = useState(1);
  const [brief, setBrief]   = useState({ objective:"", name:"", message:"", budget:"", currency:"USD", budgetType:"daily", startDate:"2026-04-01", endDate:"2026-04-30", ageMin:"25", ageMax:"55", gender:"all", locations:"United States, United Kingdom, UAE", platforms:[] });
  const [creative, setCreative] = useState({ headline:"", body:"", cta:"Learn more", destUrl:"", mediaNote:"" });
  const [targeting, setTargeting] = useState({});
  const [launched,  setLaunched]  = useState(null);

  const updB = (k,v) => setBrief(b=>({...b,[k]:v}));
  const updC = (k,v) => setCreative(c=>({...c,[k]:v}));
  const updT = (pid,k,v) => setTargeting(t=>({...t,[pid]:{...(t[pid]||{}),[k]:v}}));
  const togPlat = (id) => setBrief(b=>({...b, platforms: b.platforms.includes(id)?b.platforms.filter(x=>x!==id):[...b.platforms,id]}));

  const selPlats = CA_PLATFORMS.filter(p=>brief.platforms.includes(p.id));
  const recIds   = CA_REC[brief.objective]||[];

  const fi = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:6, padding:"8px 11px", color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontSize:12, fontFamily:"inherit" };
  const fl = { fontSize:11, fontWeight:400, color:T.ink3, display:"block", marginBottom:5 };

  const steps = ["Campaign brief","Creative & preview","Targeting","Launch"];
  const StepBar = () => (
    <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:20 }}>
      {steps.map((label,i) => {
        const s=i+1, done=s<step, active=s===step;
        return (
          <div key={label} style={{ display:"flex", alignItems:"center" }}>
            <button onClick={()=>s<=step&&setStep(s)} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", borderRadius:6, border:`1px solid ${active?T.s3:done?T.gM:"transparent"}`, background:active?T.canvas:done?T.gL:"transparent", cursor:s<=step?"pointer":"default", fontFamily:"inherit" }}>
              <div style={{ width:20, height:20, borderRadius:"50%", background:active?T.brand:done?T.g2:T.s3, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {done ? <svg width="9" height="7" viewBox="0 0 9 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M1 3.5l2.5 2.5L8 1"/></svg> : <span style={{ fontSize:10, fontWeight:500, color:"#fff" }}>{s}</span>}
              </div>
              <span style={{ fontSize:12, fontWeight:active||done?500:400, color:active?T.ink:done?T.g1:T.ink4 }}>{label}</span>
            </button>
            {i<3 && <div style={{ width:24, height:1, background:done?T.g2:T.s2, margin:"0 2px" }}/>}
          </div>
        );
      })}
    </div>
  );

  // Success screen
  if (launched) {
    const plat = CA_PLATFORMS.find(p=>p.id===launched);
    return (
      <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
        <div style={{ maxWidth:520, margin:"60px auto", textAlign:"center" }}>
          <div style={{ width:56, height:56, borderRadius:"50%", background:T.gL, border:`1px solid ${T.gM}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:24 }}>✓</div>
          <p style={{ fontSize:20, fontWeight:500, color:T.ink, margin:"0 0 8px" }}>Brief saved</p>
          <p style={{ fontSize:13, color:T.ink3, margin:"0 0 28px", lineHeight:1.6 }}>{plat && (plat.api ? "Submitting to "+plat.label+" via API." : "Opening "+plat.label+" — brief copied to clipboard.")}</p>
          <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
            <button onClick={()=>{setLaunched(null);setStep(1);setBrief({objective:"",name:"",message:"",budget:"",currency:"USD",budgetType:"daily",startDate:"2026-04-01",endDate:"2026-04-30",ageMin:"25",ageMax:"55",gender:"all",locations:"United States, United Kingdom, UAE",platforms:[]});setCreative({headline:"",body:"",cta:"Learn more",destUrl:"",mediaNote:""});}} style={{ padding:"8px 18px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Create another</button>
            <button onClick={()=>setPage("dashboard")} style={{ padding:"8px 18px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2 — top-level return, no flex chain needed
  if (step === 2) {
    return (
      <div style={{ flex:1, display:"flex", overflow:"hidden", background:T.bg }}>
        {/* LEFT — creative form */}
        <div style={{ width:400, flexShrink:0, overflowY:"auto", padding:"18px 22px", background:T.canvas, borderRight:`1px solid ${T.s2}` }}>
          <StepBar />
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                <label style={{...fl,marginBottom:0}}>Headline</label>
                <span style={{ fontSize:10, color:creative.headline.length>63?T.r2:T.ink4 }}>{creative.headline.length}/70</span>
              </div>
              <input value={creative.headline} onChange={e=>updC("headline",e.target.value)} placeholder="e.g. Cut your CPL by 40% — free trial" maxLength={70} style={fi} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/>
              <p style={{ fontSize:10, color:T.ink4, margin:"4px 0 0" }}>Meta · LinkedIn · TikTok · Snapchat · X · Pinterest</p>
            </div>
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                <label style={{...fl,marginBottom:0}}>Body copy</label>
                <span style={{ fontSize:10, color:creative.body.length>113?T.r2:T.ink4 }}>{creative.body.length}/125</span>
              </div>
              <textarea rows={4} value={creative.body} onChange={e=>updC("body",e.target.value)} placeholder="e.g. AdTech gives teams one dashboard for every channel." maxLength={125} style={{...fi,resize:"vertical",lineHeight:1.5}} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div>
                <label style={fl}>Call to action</label>
                <select value={creative.cta} onChange={e=>updC("cta",e.target.value)} style={fi}>{["Learn more","Sign up","Get started","Download","Contact us","Apply now","Subscribe","Book now"].map(c=><option key={c}>{c}</option>)}</select>
              </div>
              <div>
                <label style={fl}>Destination URL</label>
                <input value={creative.destUrl} onChange={e=>updC("destUrl",e.target.value)} placeholder="https://yoursite.com/lp" style={fi} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/>
              </div>
            </div>
            <div>
              <label style={fl}>Creative asset</label>
              <div style={{ border:`1px dashed ${T.s3}`, borderRadius:8, padding:"20px", textAlign:"center", background:T.bg, cursor:"pointer" }} onMouseEnter={e=>{e.currentTarget.style.borderColor=T.brand;e.currentTarget.style.background=T.s1;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=T.s3;e.currentTarget.style.background=T.bg;}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="1.5" strokeLinecap="round" style={{ display:"block", margin:"0 auto 8px" }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <p style={{ fontSize:12, color:T.ink3, margin:"0 0 3px" }}>Upload image or video</p>
                <p style={{ fontSize:10, color:T.ink4, margin:0 }}>JPG · PNG · MP4 · max 30MB</p>
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", paddingTop:8, borderTop:`1px solid ${T.s2}`, marginTop:4 }}>
              <button onClick={()=>setStep(1)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Back</button>
              <button onClick={()=>setStep(3)} disabled={!creative.headline||!creative.body} style={{ padding:"7px 18px", borderRadius:6, border:"none", background:(!creative.headline||!creative.body)?T.s2:T.brand, color:(!creative.headline||!creative.body)?T.ink4:"#fff", fontSize:12, fontWeight:500, cursor:(!creative.headline||!creative.body)?"not-allowed":"pointer", fontFamily:"inherit" }}>Next</button>
            </div>
          </div>
        </div>

        {/* RIGHT — preview panel, dark grey canvas, white cards float on it */}
        <div style={{ flex:1, overflowY:"auto", background:"#C8D4DC", padding:"18px 20px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <span style={{ fontSize:11, fontWeight:500, color:"#334155", textTransform:"uppercase", letterSpacing:".08em" }}>Live preview</span>
            <span style={{ fontSize:11, color:"#64748B" }}>{selPlats.length} platform{selPlats.length!==1?"s":""}</span>
          </div>
          {selPlats.length === 0 ? (
            <div style={{ background:"rgba(255,255,255,.7)", borderRadius:10, padding:"48px 20px", textAlign:"center" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.25" strokeLinecap="round" style={{ display:"block", margin:"0 auto 12px" }}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
              <p style={{ fontSize:13, color:"#475569", margin:"0 0 4px", fontWeight:500 }}>No platforms selected</p>
              <p style={{ fontSize:11, color:"#94A3B8", margin:0 }}>Go back to Step 1 and select at least one platform</p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {selPlats.map(plat=>(
                <PlatformPreview key={plat.id} plat={plat} headline={creative.headline} body={creative.body} cta={creative.cta} destUrl={creative.destUrl}/>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Steps 1, 3, 4 — single column scrolling layout
  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <style>{`@keyframes adtech-fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}} @keyframes adtech-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth:820 }}>
        <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 4px", letterSpacing:"-.01em" }}>Create campaign</h1>
        <p style={{ fontSize:12, color:T.ink3, margin:"0 0 20px" }}>Brief once · preview across all platforms · launch to one or many</p>
        <StepBar />

        {step===1 && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, animation:"adtech-fadein .2s ease" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div><label style={fl}>Campaign name</label><input value={brief.name} onChange={e=>updB("name",e.target.value)} placeholder="e.g. Q2 Lead Gen — LinkedIn + Meta" style={fi} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/></div>
              <div>
                <label style={fl}>Objective</label>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                  {CA_OBJECTIVES.map(obj=>{const sel=brief.objective===obj.id; return (<button key={obj.id} onClick={()=>updB("objective",obj.id)} style={{ padding:"9px 12px", borderRadius:6, border:`1px solid ${sel?T.brand:T.s2}`, background:sel?T.s1:T.canvas, cursor:"pointer", textAlign:"left", fontFamily:"inherit" }}><p style={{ fontSize:12, fontWeight:sel?500:400, color:sel?T.ink:T.ink2, margin:"0 0 2px" }}>{obj.label}</p><p style={{ fontSize:10, color:T.ink4, margin:0 }}>{obj.desc}</p></button>);})}
                </div>
              </div>
              <div><label style={fl}>Key message</label><textarea rows={3} value={brief.message} onChange={e=>updB("message",e.target.value)} placeholder="What should the audience take away?" style={{...fi,resize:"vertical",lineHeight:1.5}} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div><label style={fl}>Budget</label><div style={{ display:"flex", gap:5 }}><select value={brief.budgetType} onChange={e=>updB("budgetType",e.target.value)} style={{...fi,width:80}}><option value="daily">Daily</option><option value="lifetime">Lifetime</option></select><input value={brief.budget} onChange={e=>updB("budget",e.target.value)} placeholder="500" style={{...fi,flex:1}} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/><select value={brief.currency} onChange={e=>updB("currency",e.target.value)} style={{...fi,width:65}}>{["USD","EUR","GBP","AED","SAR"].map(c=><option key={c}>{c}</option>)}</select></div></div>
                <div><label style={fl}>Locations</label><input value={brief.locations} onChange={e=>updB("locations",e.target.value)} placeholder="e.g. United States, UK" style={fi} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                <div><label style={fl}>Gender</label><select value={brief.gender} onChange={e=>updB("gender",e.target.value)} style={fi}><option value="all">All</option><option value="male">Male</option><option value="female">Female</option></select></div>
                <div><label style={fl}>Age min</label><input value={brief.ageMin} onChange={e=>updB("ageMin",e.target.value)} placeholder="25" style={fi}/></div>
                <div><label style={fl}>Age max</label><input value={brief.ageMax} onChange={e=>updB("ageMax",e.target.value)} placeholder="55" style={fi}/></div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div><label style={fl}>Start date</label><input type="date" value={brief.startDate} onChange={e=>updB("startDate",e.target.value)} style={fi}/></div>
                <div><label style={fl}>End date</label><input type="date" value={brief.endDate} onChange={e=>updB("endDate",e.target.value)} style={fi}/></div>
              </div>
            </div>
            <div>
              <label style={fl}>Platforms {brief.objective&&<span style={{ color:T.g1, fontSize:10 }}>— recommended highlighted</span>}</label>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {CA_PLATFORMS.map(plat=>{const sel=brief.platforms.includes(plat.id); const rec=recIds.includes(plat.id); return (<button key={plat.id} onClick={()=>togPlat(plat.id)} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, border:`1.5px solid ${sel?T.brand:rec?T.s3:T.s2}`, background:sel?T.s1:T.canvas, cursor:"pointer", textAlign:"left", fontFamily:"inherit" }}><div style={{ width:30, height:30, borderRadius:7, background:plat.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:plat.tc, flexShrink:0 }}>{plat.abbr}</div><div style={{ flex:1 }}><p style={{ fontSize:12, fontWeight:sel?500:400, color:T.ink, margin:"0 0 1px" }}>{plat.label}</p><p style={{ fontSize:10, color:T.ink4, margin:0 }}>{plat.api?"API launch":"Opens native"}{rec?" · recommended":""}</p></div><div style={{ width:18, height:18, borderRadius:"50%", border:`1.5px solid ${sel?T.brand:T.s3}`, background:sel?T.brand:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{sel&&<svg width="9" height="7" viewBox="0 0 9 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M1 3.5l2.5 2.5L8 1"/></svg>}</div></button>);})}
              </div>
              <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
                <button onClick={()=>setStep(2)} disabled={!brief.name||!brief.objective||brief.platforms.length===0} style={{ padding:"8px 20px", borderRadius:6, border:"none", background:(!brief.name||!brief.objective||brief.platforms.length===0)?T.s2:T.brand, color:(!brief.name||!brief.objective||brief.platforms.length===0)?T.ink4:"#fff", fontSize:12, fontWeight:500, cursor:(!brief.name||!brief.objective||brief.platforms.length===0)?"not-allowed":"pointer", fontFamily:"inherit" }}>Next</button>
              </div>
            </div>
          </div>
        )}

        {step===3 && (
          <div style={{ animation:"adtech-fadein .2s ease" }}>
            <p style={{ fontSize:12, color:T.ink3, margin:"0 0 18px" }}>Each platform uses a different targeting model. Fill in what applies — blanks use platform defaults.</p>
            {selPlats.map(plat=>(
              <div key={plat.id} style={{ marginBottom:16, border:`1px solid ${T.s2}`, borderRadius:8, overflow:"hidden" }}>
                <div style={{ background:plat.color, padding:"10px 16px", display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:28, height:28, borderRadius:6, background:"rgba(255,255,255,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:plat.tc, flexShrink:0 }}>{plat.abbr}</div>
                  <div><p style={{ fontSize:13, fontWeight:500, color:plat.tc, margin:0 }}>{plat.label}</p><p style={{ fontSize:10, color:plat.tc, opacity:.75, margin:0 }}>{plat.api?"API launch":"Opens native platform"}</p></div>
                </div>
                <div style={{ padding:"16px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, background:T.canvas }}>
                  {plat.targeting.map(field=>(<div key={field.id}><label style={fl}>{field.l}</label><input value={(targeting[plat.id]||{})[field.id]||""} onChange={e=>updT(plat.id,field.id,e.target.value)} placeholder={field.ph} style={fi} onFocus={e=>e.target.style.borderColor=T.s3} onBlur={e=>e.target.style.borderColor=T.s2}/></div>))}
                  <div style={{ gridColumn:"1/-1" }}><label style={fl}>Bid strategy</label><select value={(targeting[plat.id]||{}).bid||""} onChange={e=>updT(plat.id,"bid",e.target.value)} style={fi}><option value="">Platform default</option>{plat.bids.map(b=><option key={b}>{b}</option>)}</select></div>
                </div>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
              <button onClick={()=>setStep(2)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Back</button>
              <button onClick={()=>setStep(4)} style={{ padding:"7px 18px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Review and launch</button>
            </div>
          </div>
        )}

        {step===4 && (
          <div style={{ animation:"adtech-fadein .2s ease" }}>
            <div style={{ border:`1px solid ${T.s2}`, borderRadius:8, overflow:"hidden", marginBottom:20 }}>
              <div style={{ background:T.s1, padding:"10px 14px", borderBottom:`1px solid ${T.s2}` }}><p style={{ fontSize:12, fontWeight:500, color:T.ink, margin:0 }}>Campaign brief</p></div>
              <div style={{ padding:14 }}>
                {[["Name",brief.name],["Objective",CA_OBJECTIVES.find(o=>o.id===brief.objective)?.label||"—"],["Budget",brief.currency+" "+brief.budget+" / "+brief.budgetType],["Dates",brief.startDate+" to "+brief.endDate],["Locations",brief.locations],["Headline",creative.headline],["CTA",creative.cta]].map(([k,v])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${T.s2}` }}><span style={{ fontSize:11, color:T.ink4 }}>{k}</span><span style={{ fontSize:11, color:T.ink, maxWidth:"60%", textAlign:"right", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{v||"—"}</span></div>
                ))}
              </div>
            </div>
            <p style={{ fontSize:11, fontWeight:500, color:T.ink3, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 10px" }}>Launch to {selPlats.length} platform{selPlats.length!==1?"s":""}</p>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
              {selPlats.map(plat=>(
                <div key={plat.id} style={{ border:`1px solid ${T.s2}`, borderRadius:8, padding:"12px 16px", display:"flex", alignItems:"center", gap:14, background:T.canvas }}>
                  <div style={{ width:36, height:36, borderRadius:8, background:plat.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:plat.tc, flexShrink:0 }}>{plat.abbr}</div>
                  <div style={{ flex:1 }}><p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:"0 0 2px" }}>{plat.label}</p><p style={{ fontSize:11, color:T.ink4, margin:0 }}>{plat.api?"Creates campaign via API":"Opens native platform"}</p></div>
                  <button onClick={()=>{if(!plat.api){const parts=["Campaign: "+brief.name,"Objective: "+brief.objective,"Budget: "+brief.currency+" "+brief.budget+"/"+brief.budgetType,"Headline: "+creative.headline,"Body: "+creative.body,"CTA: "+creative.cta,"URL: "+creative.destUrl];navigator.clipboard?.writeText(parts.join("\n")).catch(()=>{});window.open(plat.url,"_blank");}setLaunched(plat.id);}} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:6, border:"none", background:plat.color, color:plat.tc, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>{plat.api?"Submit via API":"Open "+plat.label}</button>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <button onClick={()=>setStep(3)} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Back</button>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>setPage("dashboard")} style={{ padding:"7px 16px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Save as draft</button>
                <button onClick={()=>setLaunched(selPlats[0]?.id)} style={{ padding:"7px 18px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Launch all</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Post Library Page ────────────────────────────────────────────────────────
// ─── Post Library Page ────────────────────────────────────────────────────────
// ─── Lead Dashboard ───────────────────────────────────────────────────────────
const MOCK_LEADS = [
  { id:"l1",  name:"Sarah Mitchell",   email:"s.mitchell@acme.com",      company:"Acme Corp",          title:"VP of Marketing",      source:"linkedin",   campaign:"Lead Gen — Enterprise",    score:92, stage:"qualified",   created:"Mar 21 · 14:32", phone:"+1 (415) 555-0182", pushed:true,  pushed_to:"HubSpot CRM" },
  { id:"l2",  name:"James Okafor",     email:"j.okafor@techcorp.io",     company:"TechCorp",           title:"Head of Growth",       source:"meta",       campaign:"Summer Sale — Hero Video",  score:78, stage:"contacted",   created:"Mar 21 · 11:15", phone:"+1 (212) 555-0341", pushed:true,  pushed_to:"Salesforce" },
  { id:"l3",  name:"Priya Sharma",     email:"priya@innovate.co",        company:"Innovate.co",        title:"Founder & CEO",        source:"google_ads", campaign:"Brand Keywords Campaign",   score:88, stage:"qualified",   created:"Mar 20 · 16:44", phone:"+44 20 7946 0831",  pushed:false, pushed_to:null },
  { id:"l4",  name:"Carlos Mendez",    email:"c.mendez@globex.com",      company:"Globex Solutions",   title:"IT Director",          source:"linkedin",   campaign:"Lead Gen — Enterprise",    score:71, stage:"new",         created:"Mar 20 · 09:22", phone:"+34 91 555 0183",   pushed:false, pushed_to:null },
  { id:"l5",  name:"Emma Johansson",   email:"emma.j@nordic.se",         company:"Nordic Industries",  title:"CMO",                  source:"meta",       campaign:"Retargeting Carousel 30d",  score:85, stage:"negotiation", created:"Mar 19 · 13:08", phone:"+46 8 555 0192",    pushed:true,  pushed_to:"HubSpot CRM" },
  { id:"l6",  name:"Ravi Patel",       email:"ravi.p@infosys.com",       company:"Infosys",            title:"Digital Strategy Lead",source:"linkedin",   campaign:"Lead Gen — Enterprise",    score:67, stage:"new",         created:"Mar 19 · 10:41", phone:"+91 98765 43210",   pushed:false, pushed_to:null },
  { id:"l7",  name:"Amelia Chen",      email:"a.chen@designco.hk",       company:"DesignCo",           title:"Marketing Director",   source:"tiktok",     campaign:"Trending UGC — Dance",     score:74, stage:"contacted",   created:"Mar 18 · 15:22", phone:"+852 9876 5432",    pushed:true,  pushed_to:"Pipedrive" },
  { id:"l8",  name:"Luca Ferrari",     email:"l.ferrari@luxbrand.it",    company:"LuxBrand Italia",    title:"Brand Manager",        source:"instagram",  campaign:"Summer Collection Post",    score:61, stage:"new",         created:"Mar 18 · 09:50", phone:"+39 02 1234 5678",  pushed:false, pushed_to:null },
  { id:"l9",  name:"Fatima Al-Hassan", email:"fatima@visiongroup.ae",    company:"Vision Group",       title:"Chief Digital Officer",source:"linkedin",   campaign:"Lead Gen — Enterprise",    score:95, stage:"qualified",   created:"Mar 17 · 16:30", phone:"+971 50 123 4567",  pushed:true,  pushed_to:"Salesforce" },
  { id:"l10", name:"Tom Wright",       email:"t.wright@agencies.co.uk",  company:"Wright Agencies",    title:"Founder",              source:"google_ads", campaign:"Brand Keywords Campaign",   score:80, stage:"contacted",   created:"Mar 17 · 11:05", phone:"+44 7700 900123",   pushed:true,  pushed_to:"HubSpot CRM" },
  { id:"l11", name:"Sofia Martins",    email:"sofia@crescer.pt",         company:"Crescer Digital",    title:"Growth Manager",       source:"meta",       campaign:"Summer Sale — Hero Video",  score:69, stage:"new",         created:"Mar 16 · 14:18", phone:"+351 912 345 678",  pushed:false, pushed_to:null },
  { id:"l12", name:"Daniel Kim",       email:"d.kim@seoultech.kr",       company:"Seoul Tech Co.",     title:"VP Engineering",       source:"linkedin",   campaign:"Lead Gen — Enterprise",    score:83, stage:"negotiation", created:"Mar 16 · 08:44", phone:"+82 10 1234 5678",  pushed:true,  pushed_to:"Pipedrive" },
];

const STAGE_COLORS = {
  new:         { bg:"#F1F5F9", c:"#475569" },
  contacted:   { bg:"#EFF6FF", c:"#1D4ED8" },
  qualified:   { bg:"#F0FDF4", c:"#166534" },
  negotiation: { bg:"#FFF7ED", c:"#C2410C" },
  closed:      { bg:"#F5F3FF", c:"#6D28D9" },
};

const CRM_OPTIONS = ["HubSpot CRM","Salesforce","Pipedrive","Zoho CRM","Close CRM"];

function ScoreBar({ score }) {
  const color = score >= 80 ? "#16A34A" : score >= 60 ? "#D97706" : "#DC2626";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:4, borderRadius:2, background:"#E2E8F0", overflow:"hidden" }}>
        <div style={{ width:score+"%", height:"100%", borderRadius:2, background:color }}/>
      </div>
      <span style={{ fontSize:10, fontWeight:500, color, minWidth:24 }}>{score}</span>
    </div>
  );
}

function LeadDashboard({ setPage }) {
  const [leads, setLeads]         = useState(MOCK_LEADS);
  const [sel, setSel]             = useState(null);
  const [stageFilter, setFilter]  = useState("all");
  const [srcFilter, setSrcFilter] = useState("all");
  const [selected, setSelected]   = useState([]);
  const [bulkCRM, setBulkCRM]     = useState("HubSpot CRM");
  const [pushingId, setPushingId] = useState(null);
  const [toasts, setToasts]       = useState([]);

  const addToast = (msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  };

  const pushLead = (leadId, crm) => {
    setPushingId(leadId);
    setTimeout(() => {
      setLeads(ls => ls.map(l => l.id === leadId ? { ...l, pushed:true, pushed_to:crm } : l));
      setPushingId(null);
      addToast("Pushed to " + crm);
    }, 1200);
  };

  const pushBulk = () => {
    const ids = selected.filter(id => !leads.find(l => l.id === id)?.pushed);
    if (!ids.length) return;
    ids.forEach((id, i) => setTimeout(() => pushLead(id, bulkCRM), i * 400));
    setSelected([]);
  };

  const updateStage = (leadId, stage) => {
    setLeads(ls => ls.map(l => l.id === leadId ? { ...l, stage } : l));
    if (sel?.id === leadId) setSel(s => ({ ...s, stage }));
  };

  const toggleSel = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const filtered = leads.filter(l =>
    (stageFilter === "all" || l.stage === stageFilter) &&
    (srcFilter   === "all" || l.source === srcFilter)
  );

  const srcLabel = (src) => ({ linkedin:"LinkedIn", meta:"Meta Ads", google_ads:"Google Ads", tiktok:"TikTok", instagram:"Instagram" }[src] || src);

  return (
    <div style={{ flex:1, display:"flex", overflow:"hidden", background:T.bg }}>
      <style>{`@keyframes adtech-fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}} @keyframes adtech-spin{to{transform:rotate(360deg)}}`}</style>

      {/* Toast stack */}
      <div style={{ position:"fixed", bottom:24, right:24, zIndex:999, display:"flex", flexDirection:"column", gap:8, pointerEvents:"none" }}>
        {toasts.map(t => (
          <div key={t.id} style={{ background:T.brand, color:"#fff", fontSize:12, fontWeight:500, padding:"10px 16px", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,.2)", animation:"adtech-fadein .2s ease" }}>
            ✓ {t.msg}
          </div>
        ))}
      </div>

      {/* Main panel */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {/* Header */}
        <div style={{ padding:"18px 22px 14px", flexShrink:0, borderBottom:`1px solid ${T.s2}`, background:T.canvas }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div>
              <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Lead Dashboard</h1>
              <p style={{ fontSize:12, color:T.ink3, margin:0 }}>{leads.length} leads · {leads.filter(l=>l.pushed).length} pushed to CRM · {leads.filter(l=>!l.pushed).length} pending</p>
            </div>
            <button onClick={()=>setPage("create")} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Create lead campaign
            </button>
          </div>

          {/* Filters + bulk push */}
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <select value={stageFilter} onChange={e=>setFilter(e.target.value)} style={{ border:`1px solid ${T.s2}`, borderRadius:6, padding:"6px 10px", fontSize:11, color:T.ink, background:T.bg, outline:"none", cursor:"pointer", fontFamily:"inherit" }}>
              <option value="all">All stages</option>
              {["new","contacted","qualified","negotiation"].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
            <select value={srcFilter} onChange={e=>setSrcFilter(e.target.value)} style={{ border:`1px solid ${T.s2}`, borderRadius:6, padding:"6px 10px", fontSize:11, color:T.ink, background:T.bg, outline:"none", cursor:"pointer", fontFamily:"inherit" }}>
              <option value="all">All sources</option>
              {["linkedin","meta","google_ads","tiktok","instagram"].map(s=><option key={s} value={s}>{srcLabel(s)}</option>)}
            </select>
            {selected.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto", background:T.s1, border:`1px solid ${T.s2}`, borderRadius:8, padding:"5px 10px" }}>
                <span style={{ fontSize:11, color:T.ink3 }}>{selected.length} selected</span>
                <select value={bulkCRM} onChange={e=>setBulkCRM(e.target.value)} style={{ border:`1px solid ${T.s2}`, borderRadius:5, padding:"4px 8px", fontSize:11, color:T.ink, background:T.canvas, outline:"none", fontFamily:"inherit" }}>
                  {CRM_OPTIONS.map(c=><option key={c}>{c}</option>)}
                </select>
                <button onClick={pushBulk} style={{ padding:"5px 12px", borderRadius:5, border:"none", background:T.brand, color:"#fff", fontSize:11, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                  Push {selected.length} leads
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ flex:1, overflowY:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead style={{ position:"sticky", top:0, background:T.s1, zIndex:5 }}>
              <tr style={{ borderBottom:`1px solid ${T.s2}` }}>
                <th style={{ padding:"10px 14px", textAlign:"left", fontSize:10, fontWeight:500, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", width:36 }}>
                  <input type="checkbox" checked={selected.length===filtered.length&&filtered.length>0} onChange={e=>setSelected(e.target.checked?filtered.map(l=>l.id):[])} style={{ accentColor:T.brand, cursor:"pointer" }}/>
                </th>
                {["Lead","Company","Source","Score","Stage","Pushed to","Created","Actions"].map(h=>(
                  <th key={h} style={{ padding:"10px 14px 10px 0", textAlign:"left", fontSize:10, fontWeight:500, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => {
                const sc = STAGE_COLORS[lead.stage] || STAGE_COLORS.new;
                const isPushing = pushingId === lead.id;
                const isSel = selected.includes(lead.id);
                return (
                  <tr key={lead.id}
                    style={{ borderBottom:`1px solid ${T.s2}`, background:isSel?T.bL:sel?.id===lead.id?T.s1:"transparent", cursor:"pointer", transition:"background .1s" }}
                    onMouseEnter={e=>{ if(!isSel&&sel?.id!==lead.id) e.currentTarget.style.background=T.s1; }}
                    onMouseLeave={e=>{ if(!isSel&&sel?.id!==lead.id) e.currentTarget.style.background="transparent"; }}>
                    <td style={{ padding:"10px 14px" }} onClick={e=>e.stopPropagation()}>
                      <input type="checkbox" checked={isSel} onChange={()=>toggleSel(lead.id)} style={{ accentColor:T.brand, cursor:"pointer" }}/>
                    </td>
                    <td style={{ padding:"10px 14px 10px 0" }} onClick={()=>setSel(lead)}>
                      <p style={{ fontSize:12, fontWeight:500, color:T.ink, margin:"0 0 1px", whiteSpace:"nowrap" }}>{lead.name}</p>
                      <p style={{ fontSize:10, color:T.ink4, margin:0 }}>{lead.email}</p>
                    </td>
                    <td style={{ padding:"10px 14px 10px 0", color:T.ink3, whiteSpace:"nowrap" }} onClick={()=>setSel(lead)}>{lead.company}</td>
                    <td style={{ padding:"10px 14px 10px 0", whiteSpace:"nowrap" }} onClick={()=>setSel(lead)}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <Pip src={lead.source} sz={7}/>
                        <span style={{ fontSize:11, color:T.ink3 }}>{srcLabel(lead.source)}</span>
                      </div>
                    </td>
                    <td style={{ padding:"10px 14px 10px 0", width:100 }} onClick={()=>setSel(lead)}>
                      <ScoreBar score={lead.score}/>
                    </td>
                    <td style={{ padding:"10px 14px 10px 0" }} onClick={()=>setSel(lead)}>
                      <span style={{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:4, background:sc.bg, color:sc.c, whiteSpace:"nowrap" }}>
                        {lead.stage.charAt(0).toUpperCase()+lead.stage.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px 10px 0", fontSize:11, color:lead.pushed?T.g1:T.ink4, whiteSpace:"nowrap" }} onClick={()=>setSel(lead)}>
                      {lead.pushed ? (lead.pushed_to||"CRM") : "—"}
                    </td>
                    <td style={{ padding:"10px 14px 10px 0", fontSize:10, color:T.ink4, whiteSpace:"nowrap" }} onClick={()=>setSel(lead)}>{lead.created}</td>
                    <td style={{ padding:"10px 0", whiteSpace:"nowrap" }} onClick={e=>e.stopPropagation()}>
                      {lead.pushed ? (
                        <span style={{ fontSize:10, color:T.g1, padding:"4px 8px", borderRadius:5, background:T.gL, fontWeight:500 }}>✓ Pushed</span>
                      ) : (
                        <button onClick={()=>pushLead(lead.id, CRM_OPTIONS[0])} disabled={isPushing}
                          style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:5, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:11, cursor:isPushing?"wait":"pointer", fontFamily:"inherit" }}>
                          {isPushing
                            ? <><span style={{ width:10, height:10, border:`1.5px solid ${T.ink4}`, borderTopColor:"transparent", borderRadius:"50%", animation:"adtech-spin 1s linear infinite", display:"inline-block" }}/> Pushing…</>
                            : "Push to CRM"
                          }
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right detail panel */}
      {sel && (
        <div style={{ width:320, flexShrink:0, borderLeft:`1px solid ${T.s2}`, background:T.canvas, overflowY:"auto", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"16px 18px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0 }}>{sel.name}</p>
            <button onClick={()=>setSel(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.ink4, fontSize:18, lineHeight:1, padding:"0 2px" }}>×</button>
          </div>
          <div style={{ padding:"16px 18px", flex:1 }}>
            {/* Score */}
            <div style={{ marginBottom:16 }}>
              <p style={{ fontSize:10, fontWeight:500, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 6px" }}>Lead score</p>
              <ScoreBar score={sel.score}/>
            </div>
            {/* Contact info */}
            <div style={{ marginBottom:16 }}>
              <p style={{ fontSize:10, fontWeight:500, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 8px" }}>Contact</p>
              {[["Email",sel.email],["Phone",sel.phone],["Title",sel.title],["Company",sel.company],["Source",srcLabel(sel.source)],["Campaign",sel.campaign],["Created",sel.created]].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:`1px solid ${T.s2}` }}>
                  <span style={{ fontSize:11, color:T.ink4 }}>{k}</span>
                  <span style={{ fontSize:11, color:T.ink, textAlign:"right", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{v||"—"}</span>
                </div>
              ))}
            </div>
            {/* Stage */}
            <div style={{ marginBottom:16 }}>
              <p style={{ fontSize:10, fontWeight:500, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 8px" }}>Stage</p>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["new","contacted","qualified","negotiation"].map(s=>{
                  const active=sel.stage===s;
                  const sc=STAGE_COLORS[s];
                  return (
                    <button key={s} onClick={()=>updateStage(sel.id,s)} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${active?sc.c:T.s2}`, background:active?sc.bg:T.canvas, color:active?sc.c:T.ink3, fontSize:11, fontWeight:active?500:400, cursor:"pointer", fontFamily:"inherit" }}>
                      {s.charAt(0).toUpperCase()+s.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* CRM push */}
            <div>
              <p style={{ fontSize:10, fontWeight:500, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 8px" }}>CRM push</p>
              {sel.pushed ? (
                <div style={{ padding:"10px 12px", borderRadius:7, background:T.gL, border:`1px solid ${T.gM}` }}>
                  <p style={{ fontSize:12, fontWeight:500, color:T.g1, margin:"0 0 2px" }}>✓ Pushed to {sel.pushed_to}</p>
                  <p style={{ fontSize:10, color:T.g2, margin:0 }}>Contact record created successfully</p>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <select defaultValue={CRM_OPTIONS[0]} id="detail-crm" style={{ border:`1px solid ${T.s2}`, borderRadius:6, padding:"7px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", fontFamily:"inherit" }}>
                    {CRM_OPTIONS.map(c=><option key={c}>{c}</option>)}
                  </select>
                  <button onClick={()=>{const crm=document.getElementById("detail-crm")?.value||CRM_OPTIONS[0];pushLead(sel.id,crm);setSel(s=>({...s,pushed:true,pushed_to:crm}));}}
                    disabled={pushingId===sel.id}
                    style={{ padding:"8px 0", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    {pushingId===sel.id
                      ? <><span style={{ width:12, height:12, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"adtech-spin 1s linear infinite", display:"inline-block" }}/> Pushing…</>
                      : "Push to CRM"
                    }
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PostLibrary({ setSel, setPage }) {
  const [src,  setSrc]  = useState("");
  const [type, setType] = useState("all");
  const fp = POSTS.filter(p => (!src || p.src === src) && (type === "all" || (type === "paid" && p.paid) || (type === "organic" && !p.paid)));
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", background: T.bg }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: T.ink, margin: "0 0 3px" }}>Post Library</h1>
          <p style={{ fontSize: 12, color: T.ink3, margin: 0 }}>Every post across all platforms · click for full KPI detail panel</p>
        </div>
        <Btn tone="primary" onClick={() => setPage("create")}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          Create Ad
        </Btn>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <select value={src} onChange={e => setSrc(e.target.value)} style={{ background: T.canvas, border: `1px solid ${T.s2}`, borderRadius: 8, padding: "7px 12px", color: T.ink, fontSize: 11, cursor: "pointer", outline: "none", fontFamily: "inherit" }}>
          <option value="">All platforms</option>
          {Object.entries(SRC).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {["all", "paid", "organic"].map(t => (
          <button key={t} onClick={() => setType(t)} style={{ padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, border: `1px solid ${type === t ? T.b2 : T.s2}`, background: type === t ? T.bL : "transparent", color: type === t ? T.b1 : T.ink3, cursor: "pointer", textTransform: "capitalize", fontFamily: "inherit", transition: "all .12s" }}>
            {t === "all" ? `All (${POSTS.length})` : t === "paid" ? `Paid (${POSTS.filter(p => p.paid).length})` : `Organic (${POSTS.filter(p => !p.paid).length})`}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        {fp.map(p => <PostCard key={p.id} post={p} onClick={setSel} />)}
      </div>
    </div>
  );
}


// ─── Ad Platform Connectors Page ──────────────────────────────────────────────
const AD_PLATFORMS = [
  {
    id:"meta", label:"Meta Ads", color:"#1877F2", icon:"f", textColor:"#fff",
    desc:"Facebook, Instagram, Messenger & Audience Network · v21.0 API",
    authMethod:"OAuth 2.0 — System User Token",
    status:"ok", accounts:"3 ad accounts", lastSync:"Mar 21 · 06:00",
    token:"System User Access Token (60-day) · auto-refresh via Meta Business Manager",
    fields:[
      { k:"access_token", l:"System User Access Token", ph:"EAAxxxxxx...", hint:"Meta Business Manager → System Users → Generate Token (60-day)" },
      { k:"ad_account_id",l:"Ad Account ID",            ph:"act_123456789", hint:"Format: act_XXXXXXXXX · found in Ads Manager URL or Business Settings" },
      { k:"app_id",       l:"App ID",                   ph:"1234567890",   hint:"From developers.facebook.com → Your App → Dashboard" },
      { k:"app_secret",   l:"App Secret",               ph:"xxxxxxxxxxxxxxx",hint:"From developers.facebook.com → App Settings → Basic" },
      { k:"pixel_id",     l:"Pixel ID (optional)",      ph:"123456789012345",hint:"From Events Manager → Data Sources → your Pixel" },
    ],
    scopes:["ads_read","ads_management","business_management","pages_read_engagement"],
    rateLimit:"200 calls/hour per ad account",
    docs:"https://developers.facebook.com/docs/marketing-api",
  },
  {
    id:"google_ads", label:"Google Ads", color:"#4285F4", icon:"G", textColor:"#fff",
    desc:"Search, Shopping, Display, YouTube & Performance Max · API v17",
    authMethod:"OAuth 2.0 — Manager Account (MCC)",
    status:"ok", accounts:"MCC: 123-456-7890 · 6 clients", lastSync:"Mar 21 · 06:05",
    token:"OAuth 2.0 access token (1h) · refreshed via refresh token",
    fields:[
      { k:"developer_token",l:"Developer Token",     ph:"ABcdEfGhIjKlMnOp",hint:"Google Ads API Center → Applied for Developer Token" },
      { k:"client_id",      l:"OAuth Client ID",    ph:"123456.apps.googleusercontent.com",hint:"Google Cloud Console → Credentials → OAuth Client ID" },
      { k:"client_secret",  l:"OAuth Client Secret",ph:"GOCSPX-...",       hint:"Google Cloud Console → Credentials → OAuth Client Secret" },
      { k:"refresh_token",  l:"OAuth Refresh Token",ph:"1//0gXx...",       hint:"Generated after user completes Google OAuth consent flow" },
      { k:"mcc_id",         l:"Manager Account ID (MCC)",ph:"123-456-7890",hint:"Top-level account ID — find in Google Ads top-left dropdown" },
    ],
    scopes:["https://www.googleapis.com/auth/adwords"],
    rateLimit:"15,000 operations/day (Standard) · higher on allowlist",
    docs:"https://developers.google.com/google-ads/api/docs",
  },
  {
    id:"tiktok", label:"TikTok Ads", color:"#2DD4BF", icon:"TT", textColor:"#000",
    desc:"In-Feed, TopView, Spark Ads, TikTok Shopping · Marketing API",
    authMethod:"OAuth 2.0 — Short-lived tokens (24h)",
    status:"error", accounts:null, lastSync:null,
    token:"Access tokens expire every 24h — re-auth required or use long-term token via App",
    error:"Access token expired 2 days ago — reconnect below",
    fields:[
      { k:"app_id",      l:"App ID",           ph:"1234567890123456789", hint:"TikTok for Business → Apps → your app → Basic Info" },
      { k:"app_secret",  l:"App Secret",       ph:"xxxxxxxxxxxxxxxxxxxx", hint:"TikTok for Business → Apps → your app → Basic Info" },
      { k:"access_token",l:"Access Token",     ph:"xxxxxxxxxxxxxxxxxxxx", hint:"Generated via OAuth flow at business-api.tiktok.com/portal/auth" },
      { k:"advertiser_id",l:"Advertiser ID",   ph:"1234567890123456789", hint:"TikTok Ads Manager → top-left account dropdown → Account ID" },
    ],
    scopes:["tt.advertiser.read","ad.create","ad.read","report.ad.get"],
    rateLimit:"600 req/min · 1,000 req/min for reporting",
    docs:"https://business-api.tiktok.com/portal/docs",
  },
  {
    id:"linkedin", label:"LinkedIn Ads", color:"#0A66C2", icon:"in", textColor:"#fff",
    desc:"Sponsored Content, Message Ads, Lead Gen Forms · Marketing API",
    authMethod:"OAuth 2.0 — 60-day tokens · 500 req/day limit",
    status:"none", accounts:null, lastSync:null,
    token:"Access tokens last 60 days · refresh token is permanent but single-use",
    fields:[
      { k:"client_id",    l:"Client ID",       ph:"78xxxxxxxxxx",    hint:"LinkedIn Developer App → Auth → Client ID" },
      { k:"client_secret",l:"Client Secret",   ph:"xxxxxxxxxxxx",    hint:"LinkedIn Developer App → Auth → Client Secret" },
      { k:"access_token", l:"Access Token",    ph:"AQXx...",         hint:"Generated via OAuth 2.0 flow at linkedin.com/oauth/v2/authorization" },
      { k:"refresh_token",l:"Refresh Token",   ph:"AQVx...",         hint:"Permanent refresh token — regenerate if revoked" },
      { k:"account_id",   l:"Ad Account ID",   ph:"509000000",       hint:"LinkedIn Campaign Manager → Account Assets → account number in URL" },
    ],
    scopes:["r_ads","w_organization_social","r_ads_reporting","rw_ads"],
    rateLimit:"500 req/day (Lite) · 5,000 req/day (Restrictive)",
    docs:"https://learn.microsoft.com/linkedin/marketing",
  },
  {
    id:"snapchat", label:"Snapchat Ads", color:"#F59E0B", icon:"S", textColor:"#000",
    desc:"Snap Ads, Story Ads, Collection Ads · Marketing API v1",
    authMethod:"OAuth 2.0 — short-lived (60 min) access tokens",
    status:"none", accounts:null, lastSync:null,
    token:"Access tokens expire every 60 min — use refresh token for auto-renewal",
    fields:[
      { k:"client_id",    l:"Client ID",       ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"Snapchat Business → Apps → your app → Client ID" },
      { k:"client_secret",l:"Client Secret",   ph:"xxxxxxxxxxxxxxxxxxxx",                  hint:"Snapchat Business → Apps → your app → Client Secret" },
      { k:"refresh_token",l:"Refresh Token",   ph:"xxxxxxxxxxxxxxxxxxxx",                  hint:"Generated after OAuth consent. Long-lived." },
      { k:"ad_account_id",l:"Ad Account ID",   ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"Snapchat Ads Manager → Business Details → Ad Account ID" },
    ],
    scopes:["snapchat-marketing-api"],
    rateLimit:"1,000 req/min",
    docs:"https://marketingapi.snapchat.com/docs",
  },
  {
    id:"pinterest", label:"Pinterest Ads", color:"#E60023", icon:"P", textColor:"#fff",
    desc:"Standard Pins, Video Pins, Shopping · Pinterest Ads API v5",
    authMethod:"OAuth 2.0",
    status:"none", accounts:null, lastSync:null,
    token:"Access tokens expire in 30 days · refresh via refresh token",
    fields:[
      { k:"app_id",      l:"App ID",           ph:"1234567",           hint:"Pinterest Developer → My Apps → your app → App ID" },
      { k:"app_secret",  l:"App Secret",       ph:"xxxxxxxxxxxx",      hint:"Pinterest Developer → My Apps → your app → App Secret" },
      { k:"access_token",l:"Access Token",     ph:"pina_xxxxx...",     hint:"Generated via Pinterest OAuth 2.0 flow" },
      { k:"advertiser_id",l:"Advertiser ID",   ph:"549755885175",      hint:"Pinterest Ads → Billing → Account ID" },
    ],
    scopes:["ads:read","ads:write"],
    rateLimit:"300 req/min",
    docs:"https://developers.pinterest.com/docs/api/v5",
  },
  {
    id:"x", label:"X / Twitter Ads", color:"#1DA1F2", icon:"X", textColor:"#fff",
    desc:"Promoted Tweets, Follower Ads, App Install · Twitter Ads API v12",
    authMethod:"OAuth 1.0a — permanent tokens",
    status:"none", accounts:null, lastSync:null,
    token:"OAuth 1.0a tokens do not expire — revoke from Twitter Dev Portal",
    fields:[
      { k:"api_key",       l:"API Key",          ph:"xxxxxxxxxxxxxxxxxxxx",     hint:"Twitter Developer Portal → Projects & Apps → Keys & Tokens" },
      { k:"api_secret",    l:"API Secret",       ph:"xxxxxxxxxxxxxxxxxxxx",     hint:"Twitter Developer Portal → Projects & Apps → Keys & Tokens" },
      { k:"access_token",  l:"Access Token",     ph:"000000000-xxxxxxxx",       hint:"Twitter Developer Portal → Access Token & Secret" },
      { k:"access_secret", l:"Access Token Secret",ph:"xxxxxxxxxxxxxxxxxxxx",   hint:"Twitter Developer Portal → Access Token & Secret" },
      { k:"account_id",    l:"Ad Account ID",    ph:"abc123",                   hint:"Short alphanumeric ID — Twitter Ads Manager → Account dropdown" },
    ],
    scopes:["tweet.read","tweet.write","ads:read","ads:write"],
    rateLimit:"50 req/15 min (Basic) · 300 req/15 min (Pro)",
    docs:"https://developer.twitter.com/en/docs/twitter-ads-api",
  },
  {
    id:"ga4", label:"Google Analytics 4", color:"#F97316", icon:"GA", textColor:"#fff",
    desc:"Website & app analytics · conversion tracking · audience sync · Data API v1",
    authMethod:"Service Account JSON key",
    status:"ok", accounts:"2 properties", lastSync:"Mar 21 · 06:10",
    token:"Service Account — no token expiry. Rotate key periodically for security.",
    fields:[
      { k:"property_id",  l:"GA4 Property ID",          ph:"123456789",       hint:"Google Analytics → Admin → Property Settings → Property ID" },
      { k:"service_email",l:"Service Account Email",     ph:"adtech@project.iam.gserviceaccount.com", hint:"Google Cloud Console → IAM → Service Accounts" },
      { k:"private_key",  l:"Private Key (JSON)",        ph:"-----BEGIN PRIVATE KEY-----\nMIIE...", hint:"Download service account JSON → copy private_key field" },
    ],
    scopes:["https://www.googleapis.com/auth/analytics.readonly"],
    rateLimit:"10 req/s · 10,000 req/day per property",
    docs:"https://developers.google.com/analytics/devguides/reporting/data/v1",
  },
  {
    id:"shopify", label:"Shopify", color:"#5BA43C", icon:"SH", textColor:"#fff",
    desc:"Order attribution · product catalogs · customer sync · Admin API 2024-10",
    authMethod:"Custom App — Admin API Access Token",
    status:"none", accounts:null, lastSync:null,
    token:"Admin API access tokens do not expire (for Custom Apps)",
    fields:[
      { k:"shop_domain",   l:"Shop domain",       ph:"your-store.myshopify.com",    hint:"Your Shopify store URL — find in Shopify Admin settings" },
      { k:"access_token",  l:"Admin API Access Token",ph:"shpat_xxxxxxxxxxxx",      hint:"Shopify Admin → Apps → Develop apps → your app → API credentials" },
      { k:"api_version",   l:"API version",       ph:"2024-10",                      hint:"Use latest stable version e.g. 2024-10" },
    ],
    scopes:["read_orders","read_customers","read_products","write_marketing_events"],
    rateLimit:"2 req/s (Shopify standard) · 40 req/s (Shopify Plus)",
    docs:"https://shopify.dev/docs/api/admin-rest",
  },

  {
    id:"dv360", label:"DV360", color:"#1A73E8", icon:"DV", textColor:"#fff",
    desc:"Google Display & Video 360 — programmatic display, video, audio & connected TV",
    authMethod:"OAuth 2.0 — Service Account or User OAuth",
    status:"none", accounts:null, lastSync:null,
    token:"Service Account JSON key OR OAuth 2.0 refresh token via Google Cloud Console",
    fields:[
      { k:"client_id",      l:"OAuth Client ID",     ph:"123456.apps.googleusercontent.com", hint:"Google Cloud Console → APIs & Services → Credentials" },
      { k:"client_secret",  l:"OAuth Client Secret",  ph:"GOCSPX-...",    hint:"Google Cloud Console → Credentials → OAuth Client Secret" },
      { k:"refresh_token",  l:"OAuth Refresh Token",  ph:"1//0gXx...",    hint:"Generated after user completes DV360 OAuth consent flow" },
      { k:"advertiser_id",  l:"DV360 Advertiser ID",  ph:"123456789",     hint:"DV360 → Admin → Advertiser Settings → Advertiser ID" },
      { k:"partner_id",     l:"Partner ID",           ph:"1234567",       hint:"DV360 → Admin → Partner Settings → Partner ID" },
    ],
    scopes:["https://www.googleapis.com/auth/display-video","https://www.googleapis.com/auth/doubleclickbidmanager"],
    rateLimit:"500 req/min for Bid Manager API",
    docs:"https://developers.google.com/display-video/api/guides/getting-started",
  },
  {
    id:"ttd", label:"The Trade Desk", color:"#00C44F", icon:"TTD", textColor:"#fff",
    desc:"The Trade Desk DSP — programmatic display, video, audio, CTV and DOOH",
    authMethod:"API Key (long-lived) + Advertiser ID",
    status:"none", accounts:null, lastSync:null,
    token:"API tokens are long-lived — generate in TTD Platform under My Account → API Tokens",
    fields:[
      { k:"api_key",        l:"API Token",           ph:"xxxxxxxxxxxxxxxxxxxxxxxxxxxx", hint:"The Trade Desk UI → My Account → API Tokens → Create Token" },
      { k:"advertiser_id",  l:"Advertiser ID",       ph:"abc123de",                    hint:"URL when viewing advertiser: tradedesk.com/advertiser/{id}/..." },
      { k:"partner_id",     l:"Partner ID",          ph:"partner123",                  hint:"Contact your TTD account manager for Partner ID" },
    ],
    scopes:["reporting","campaigns","audiences"],
    rateLimit:"10 req/s per API token",
    docs:"https://api.thetradedesk.com/v3",
  },
  {
    id:"amazon_dsp", label:"Amazon DSP", color:"#FF9900", icon:"AMZ", textColor:"#fff",
    desc:"Amazon Demand Side Platform — display, video and streaming TV ads",
    authMethod:"Login with Amazon (LwA) — OAuth 2.0",
    status:"none", accounts:null, lastSync:null,
    token:"LwA access tokens expire in 1 hour — refresh via refresh_token stored securely",
    fields:[
      { k:"client_id",      l:"LwA Client ID",       ph:"amzn1.application-oa2-client.xxxx", hint:"Amazon Developer Console → Security Profile → OAuth2 Credentials" },
      { k:"client_secret",  l:"LwA Client Secret",   ph:"xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",   hint:"Amazon Developer Console → Security Profile → OAuth2 Credentials" },
      { k:"refresh_token",  l:"Refresh Token",       ph:"Atzr|IwEB...",                       hint:"Generated after merchant completes LwA OAuth consent flow" },
      { k:"advertiser_id",  l:"Advertiser ID",       ph:"ENTITY1234567890",                   hint:"Amazon DSP UI → Advertiser Settings → Advertiser ID" },
      { k:"region",         l:"Region",              ph:"NA",                                  hint:"NA (North America), EU (Europe), FE (Far East)" },
    ],
    scopes:["advertising::campaign_management","advertising::audiences"],
    rateLimit:"Varies by endpoint — typically 2 req/s with burst of 10",
    docs:"https://advertising.amazon.com/API/docs/en-us/dsp-advertiser-campaigns",
  },
  {
    id:"xandr", label:"Xandr / Microsoft", color:"#00A4EF", icon:"XN", textColor:"#fff",
    desc:"Xandr Invest DSP (Microsoft Advertising) — programmatic display, video and native",
    authMethod:"OAuth 2.0 — Microsoft identity platform",
    status:"none", accounts:null, lastSync:null,
    token:"Microsoft access tokens expire in 1 hour — uses standard MSAL refresh flow",
    fields:[
      { k:"client_id",      l:"Azure App Client ID",  ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"Azure Portal → App Registrations → your app → Application (client) ID" },
      { k:"client_secret",  l:"Client Secret",        ph:"xxxxxxxxxxxxxxxxxxxxxxxxxxxxx",         hint:"Azure Portal → App Registrations → Certificates & Secrets → New client secret" },
      { k:"tenant_id",      l:"Tenant ID",            ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"Azure Portal → Azure Active Directory → Tenant ID" },
      { k:"advertiser_id",  l:"Xandr Advertiser ID",  ph:"1234567",                              hint:"Xandr Invest UI → Advertiser Settings → Advertiser ID" },
      { k:"member_id",      l:"Member ID",            ph:"12345",                                 hint:"Your Xandr seat Member ID — contact account manager" },
    ],
    scopes:["https://api.xandr.com/read","https://api.xandr.com/reporting"],
    rateLimit:"300 req/min per member",
    docs:"https://learn.microsoft.com/en-us/advertising/guides/xandr-api",
  },
  {
    id:"stackadapt", label:"StackAdapt", color:"#0052CC", icon:"SA", textColor:"#fff",
    desc:"StackAdapt DSP — native, display, video, CTV and audio programmatic advertising",
    authMethod:"API Key — long-lived token issued per account",
    status:"none", accounts:null, lastSync:null,
    token:"StackAdapt API keys do not expire — store securely and rotate if compromised",
    fields:[
      { k:"api_key", l:"API Key", ph:"xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", hint:"StackAdapt UI → Settings → API Keys → Generate Key" },
    ],
    scopes:["campaigns","reporting","audiences","creatives"],
    rateLimit:"100 req/min",
    docs:"https://api.stackadapt.com",
  },
  {
    id:"yahoo_dsp", label:"Yahoo DSP", color:"#720E9E", icon:"YH", textColor:"#fff",
    desc:"Yahoo DSP — programmatic display, native, video and CTV via OneConnect",
    authMethod:"OAuth 2.0 — Yahoo client credentials flow",
    status:"none", accounts:null, lastSync:null,
    token:"Access tokens valid for 3600s — auto-refreshed via client_credentials grant",
    fields:[
      { k:"client_id",     l:"Client ID",     ph:"dj0yJmk9...", hint:"Yahoo Developer Network → My Apps → your app → Client ID" },
      { k:"client_secret", l:"Client Secret", ph:"xxxxxxxx",    hint:"Yahoo Developer Network → My Apps → your app → Client Secret" },
      { k:"account_id",    l:"Account ID",    ph:"XXXXXXXX",    hint:"Yahoo DSP → Account Settings → Account ID" },
    ],
    scopes:["reporting","campaigns","audiences"],
    rateLimit:"1,000 req/hour",
    docs:"https://developer.yahooinc.com/dsp/api",
  },
  {
    id:"mediamath", label:"MediaMath", color:"#E63946", icon:"MM", textColor:"#fff",
    desc:"MediaMath TerminalOne DSP — programmatic display, video and native",
    authMethod:"API Key + Session Token (short-lived)",
    status:"none", accounts:null, lastSync:null,
    token:"Session tokens expire in 24h — re-authenticate via /session endpoint with API key",
    fields:[
      { k:"api_key",       l:"API Key",        ph:"xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", hint:"MediaMath Platform → My Account → API Keys" },
      { k:"username",      l:"Username",        ph:"your@email.com",                  hint:"Your MediaMath login email" },
      { k:"password",      l:"Password",        ph:"••••••••",                         hint:"MediaMath login password — used only for session token generation" },
      { k:"advertiser_id", l:"Advertiser ID",  ph:"1234567",                          hint:"MediaMath UI → Advertisers → select advertiser → ID in URL" },
    ],
    scopes:["reporting","campaigns","strategy","audiences"],
    rateLimit:"10 req/s per session",
    docs:"https://apidocs.mediamath.com",
  },
  {
    id:"adform", label:"Adform", color:"#003087", icon:"AF", textColor:"#fff",
    desc:"Adform DSP — EU-focused programmatic display, video and rich media",
    authMethod:"OAuth 2.0 — client credentials with scope-based access",
    status:"none", accounts:null, lastSync:null,
    token:"Access tokens expire in 3600s — refresh via client_credentials grant",
    fields:[
      { k:"client_id",     l:"Client ID",     ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"Adform DSP → Administration → API Management → Create client" },
      { k:"client_secret", l:"Client Secret", ph:"xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",      hint:"Adform DSP → Administration → API Management → client secret" },
      { k:"agency_id",     l:"Agency ID",     ph:"123456",                                hint:"Adform DSP → Administration → Agency Settings → Agency ID" },
    ],
    scopes:["dmp.profiles.readonly","dsp.campaigns.readonly","dsp.reporting.readonly"],
    rateLimit:"100 req/min per client",
    docs:"https://api.adform.com",
  },
];

function AdConnStatusDot({ status }) {
  const c = status==="ok"?T.g2:status==="error"?T.r2:T.ink4;
  const label = status==="ok"?"Connected":status==="error"?"Error":"Not connected";
  return <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:600,color:c}}><span style={{width:7,height:7,borderRadius:"50%",background:c,display:"inline-block"}}/>{label}</span>;
}

function AdConnectorsPage() {
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(AD_PLATFORMS.map(p => [p.id, { status:p.status, vals:{}, error:p.error||null, accounts:p.accounts, lastSync:p.lastSync }]))
  );
  const [open, setOpen]       = useState(null);
  const [testState, setTestState] = useState({});
  const [adTab, setAdTab]     = useState("platforms"); // platforms | social-pages

  // Social pages state
  const [socialPages, setSocialPages] = useState([
    { id:"sp1", name:"Demo Agency",         platform:"meta",      type:"Facebook Page",   role:"Admin", fans:"28.4k", status:"active",   adAccount:true  },
    { id:"sp2", name:"Demo Agency",         platform:"instagram", type:"Instagram Business",role:"Admin",followers:"14.2k",status:"active",adAccount:true },
    { id:"sp3", name:"Demo Agency Official",platform:"linkedin",  type:"Company Page",    role:"Admin", followers:"6.8k", status:"active",   adAccount:false },
    { id:"sp4", name:"@DemoAgency",         platform:"x",         type:"X Profile",       role:"Admin", followers:"3.1k", status:"active",   adAccount:false },
    { id:"sp5", name:"Demo Agency",         platform:"tiktok",    type:"TikTok Business", role:"Admin", followers:"1.2k", status:"inactive", adAccount:false },
    { id:"sp6", name:"Demo Agency Shop",    platform:"pinterest", type:"Pinterest Board",  role:"Admin", followers:"890",  status:"inactive", adAccount:false },
  ]);
  const [addPageForm, setAddPageForm] = useState(null);
  const [newPage, setNewPage]         = useState({ platform:"meta", name:"", url:"", role:"Admin" });

  const connected = Object.values(statuses).filter(s=>s.status==="ok").length;
  const errored   = Object.values(statuses).filter(s=>s.status==="error").length;

  const testConn = (id) => {
    setTestState(t=>({...t,[id]:"testing"}));
    setTimeout(()=>setTestState(t=>({...t,[id]:"ok"})), 1400);
    setTimeout(()=>setTestState(t=>({...t,[id]:"idle"})), 3500);
  };

  const save = (id, vals) => {
    setStatuses(s=>({...s,[id]:{...s[id],status:"ok",vals,error:null,accounts:"Connected",lastSync:"Just now"}}));
    setOpen(null);
  };

  const disconnect = (id) => {
    setStatuses(s=>({...s,[id]:{...s[id],status:"none",vals:{},error:null,accounts:null,lastSync:null}}));
  };

  const cfg = open ? AD_PLATFORMS.find(p=>p.id===open) : null;
  const st  = open ? statuses[open] : null;

  const addSocialPage = () => {
    if (!newPage.name) return;
    const platInfo = { meta:"Facebook Page", instagram:"Instagram Business", linkedin:"Company Page", x:"X Profile", tiktok:"TikTok Business", pinterest:"Pinterest Board" };
    setSocialPages(ps=>[...ps,{ id:"sp"+Date.now(), name:newPage.name, platform:newPage.platform, type:platInfo[newPage.platform]||"Page", role:newPage.role, followers:"0", status:"active", adAccount:false }]);
    setAddPageForm(null); setNewPage({ platform:"meta", name:"", url:"", role:"Admin" });
  };
  const toggleSocialPage = (id) => setSocialPages(ps=>ps.map(p=>p.id===id?{...p,status:p.status==="active"?"inactive":"active"}:p));
  const removeSocialPage = (id) => setSocialPages(ps=>ps.filter(p=>p.id!==id));

  const PLAT_INFO = { meta:{c:"#1877F2",l:"Facebook"}, instagram:{c:"#E1306C",l:"Instagram"}, linkedin:{c:"#0A66C2",l:"LinkedIn"}, x:{c:"#000000",l:"X"}, tiktok:{c:"#2DD4BF",l:"TikTok"}, pinterest:{c:"#E60023",l:"Pinterest"} };
  const INP = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"7px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const LBL = { fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 };

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:T.bg}}>
      <style>{`@keyframes adtech-spin{to{transform:rotate(360deg)}} @keyframes adtech-fadein{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Header + tabs */}
      <div style={{padding:"16px 22px 0",background:T.canvas,borderBottom:`1px solid ${T.s2}`,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
          <div>
            <h1 style={{fontSize:18,fontWeight:500,color:T.ink,margin:"0 0 3px"}}>Ad Connectors</h1>
            <p style={{fontSize:12,color:T.ink3,margin:0}}>
              {adTab==="platforms" ? <><strong style={{color:T.b2}}>{connected}/9</strong> platforms connected{errored>0&&<> · <strong style={{color:T.r2}}>{errored} need attention</strong></>}</> : <><strong style={{color:T.g2}}>{socialPages.filter(p=>p.status==="active").length}</strong> social pages active</>}
            </p>
          </div>
          {adTab==="social-pages" && (
            <button onClick={()=>setAddPageForm(true)} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:7,border:"none",background:T.brand,color:"#fff",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"inherit"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Add page
            </button>
          )}
        </div>
        <div style={{display:"flex",borderBottom:`2px solid ${T.s2}`,marginBottom:-2}}>
          {[["platforms","Ad platforms"],["social-pages","Social pages"]].map(([t,l])=>(
            <button key={t} onClick={()=>setAdTab(t)} style={{padding:"8px 18px",background:"none",border:"none",borderBottom:`2px solid ${adTab===t?T.b2:"transparent"}`,marginBottom:-2,color:adTab===t?T.b2:T.ink3,fontSize:12,fontWeight:adTab===t?600:400,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}>{l}</button>
          ))}
        </div>
      </div>

      {/* ══ AD PLATFORMS TAB ══ */}
      {adTab==="platforms" && (
      <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>

      {/* Summary row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:18}}>
        {[
          {l:"Connected",      v:String(connected),                                    a:T.b2},
          {l:"Needs attention",v:String(errored),                                       a:T.r2},
          {l:"Data sources",   v:`${connected} platforms`,                              a:T.g2},
          {l:"Last sync",      v:"Mar 21 · 06:10",                                     a:T.ink3},
        ].map(k=>(
          <div key={k.l} style={{background:T.canvas,border:`1px solid ${T.s2}`,borderRadius:10,padding:"12px 14px",borderTop:`3px solid ${k.a}`,boxShadow:"0 1px 3px rgba(15,23,42,.05)"}}>
            <p style={{fontSize:8,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",color:T.ink4,margin:"0 0 5px"}}>{k.l}</p>
            <p style={{fontSize:18,fontWeight:800,color:k.a,margin:0,lineHeight:1}}>{k.v}</p>
          </div>
        ))}
      </div>

      {/* Platform grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {AD_PLATFORMS.map(p=>{
          const s = statuses[p.id];
          const ts = testState[p.id];
          return (
            <div key={p.id}
              style={{background:T.canvas,border:`1px solid ${s.status==="ok"?T.gM:s.status==="error"?T.rM:T.s2}`,borderRadius:12,padding:"16px",transition:"all .15s",boxShadow:"0 1px 3px rgba(15,23,42,.05)",animation:"adtech-fadein .2s ease"}}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(15,23,42,.1)"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 3px rgba(15,23,42,.05)"}
            >
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:40,height:40,borderRadius:10,background:p.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:p.textColor,flexShrink:0,letterSpacing:"-.5px"}}>{p.icon}</div>
                  <div>
                    <p style={{fontSize:13,fontWeight:700,color:T.ink,margin:"0 0 2px"}}>{p.label}</p>
                    <AdConnStatusDot status={s.status}/>
                  </div>
                </div>
              </div>

              {/* Desc */}
              <p style={{fontSize:11,color:T.ink3,margin:"0 0 8px",lineHeight:1.5}}>{p.desc}</p>

              {/* Error */}
              {s.error&&<div style={{background:T.rL,border:`1px solid ${T.rM}`,borderRadius:7,padding:"7px 10px",marginBottom:8,fontSize:10,color:T.r1,fontWeight:600}}>⚠ {s.error}</div>}

              {/* Stats */}
              {s.status==="ok"&&(
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
                  <div style={{background:T.bg,borderRadius:7,padding:"6px 9px",border:`1px solid ${T.s2}`}}>
                    <p style={{fontSize:8,color:T.ink4,fontWeight:700,margin:"0 0 2px"}}>ACCOUNTS</p>
                    <p style={{fontSize:11,fontWeight:700,color:T.g2,margin:0}}>{s.accounts}</p>
                  </div>
                  <div style={{background:T.bg,borderRadius:7,padding:"6px 9px",border:`1px solid ${T.s2}`}}>
                    <p style={{fontSize:8,color:T.ink4,fontWeight:700,margin:"0 0 2px"}}>LAST SYNC</p>
                    <p style={{fontSize:11,fontWeight:600,color:T.ink3,margin:0}}>{s.lastSync||"—"}</p>
                  </div>
                </div>
              )}

              {/* Auth method + rate limit */}
              <p style={{fontSize:9,color:T.ink4,margin:"0 0 4px",fontWeight:600}}>Auth: {p.authMethod}</p>
              <p style={{fontSize:9,color:T.ink4,margin:"0 0 12px"}}>{p.rateLimit}</p>

              {/* Actions */}
              <div style={{display:"flex",gap:6}}>
                {s.status==="ok"&&(
                  <button onClick={()=>testConn(p.id)} style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"6px 0",borderRadius:7,border:`1px solid ${T.s2}`,background:"transparent",color:T.ink3,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    {ts==="testing"?<><span style={{width:11,height:11,border:`2px solid ${T.b3}`,borderTopColor:"transparent",borderRadius:"50%",animation:"adtech-spin 1s linear infinite",display:"inline-block"}}/> Testing…</>:ts==="ok"?<><span style={{color:T.g2}}>✓</span> Connected!</>:"↻ Test sync"}
                  </button>
                )}
                <button onClick={()=>setOpen(p.id)} style={{flex:s.status==="ok"?1:2,display:"flex",alignItems:"center",justifyContent:"center",gap:5,padding:"6px 0",borderRadius:7,border:`1px solid ${s.status!=="none"?T.s2:T.bM}`,background:s.status==="none"?T.bL:"transparent",color:s.status==="none"?T.b1:T.ink3,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  {s.status==="ok"?"⚙ Settings":s.status==="error"?"↺ Reconnect":"+ Connect"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Ad Platform Setup Modal ── */}
      {open && cfg && st && (
        <div style={{position:"fixed",inset:0,zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(15,23,42,.5)"}} onClick={()=>setOpen(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:T.canvas,borderRadius:16,width:580,maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(15,23,42,.2)"}}>
            {/* Header */}
            <div style={{padding:"18px 22px",borderBottom:`1px solid ${T.s2}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:38,height:38,borderRadius:10,background:cfg.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:cfg.textColor,letterSpacing:"-.5px"}}>{cfg.icon}</div>
                <div>
                  <p style={{fontSize:15,fontWeight:800,color:T.ink,margin:0}}>{cfg.label}</p>
                  <p style={{fontSize:10,color:T.ink4,margin:"2px 0 0"}}>{cfg.authMethod} · {cfg.rateLimit.split("·")[0].trim()}</p>
                </div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <a href={cfg.docs} target="_blank" rel="noreferrer" style={{fontSize:11,color:T.b2,textDecoration:"none",fontWeight:600}}>Docs ↗</a>
                <button onClick={()=>setOpen(null)} style={{background:"none",border:"none",cursor:"pointer",color:T.ink4,fontSize:20,lineHeight:1,padding:"0 4px"}}>×</button>
              </div>
            </div>

            <div style={{flex:1,overflowY:"auto",padding:"20px 22px"}}>
              {/* Token info */}
              <div style={{background:T.aL,border:`1px solid ${T.aM}`,borderRadius:8,padding:"9px 12px",marginBottom:18,display:"flex",gap:8,alignItems:"flex-start"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.a1} strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                <p style={{fontSize:11,color:T.a1,margin:0,fontWeight:600}}>{cfg.token}</p>
              </div>

              {/* Required scopes */}
              <div style={{marginBottom:18}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:T.ink4,margin:"0 0 8px"}}>Required OAuth scopes</p>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {cfg.scopes.map(s=>(
                    <code key={s} style={{fontSize:10,background:T.s1,padding:"3px 8px",borderRadius:5,color:T.v2,border:`1px solid ${T.s2}`}}>{s}</code>
                  ))}
                </div>
              </div>

              {/* Credential fields */}
              <div style={{display:"flex",flexDirection:"column",gap:13,marginBottom:20}}>
                {cfg.fields.map(f=>{
                  const fi = {width:"100%",border:`1px solid ${T.s2}`,borderRadius:8,padding:"9px 11px",color:T.ink,background:T.bg,outline:"none",boxSizing:"border-box",fontSize:12,fontFamily:"inherit"};
                  return (
                    <div key={f.k}>
                      <label style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",color:T.ink4,display:"block",marginBottom:6}}>{f.l}</label>
                      <input type={f.k.includes("secret")||f.k.includes("token")||f.k.includes("key")||f.k.includes("private")?"password":"text"}
                        placeholder={f.ph} defaultValue={st.vals?.[f.k]||""}
                        style={fi} onFocus={e=>e.target.style.borderColor=T.b2} onBlur={e=>e.target.style.borderColor=T.s2}/>
                      <p style={{fontSize:10,color:T.ink4,margin:"4px 0 0",lineHeight:1.4}}>{f.hint}</p>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>save(cfg.id, {})} style={{flex:1,padding:"10px 0",borderRadius:9,border:"none",background:T.b2,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                  Save & Connect
                </button>
                {st.status==="ok"&&(
                  <button onClick={()=>{disconnect(cfg.id);setOpen(null);}} style={{padding:"10px 16px",borderRadius:9,border:`1px solid ${T.rM}`,background:T.rL,color:T.r1,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                    Disconnect
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      )}

      {/* ══ SOCIAL PAGES TAB ══ */}
      {adTab==="social-pages" && (
      <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>

        {/* Add page form */}
        {addPageForm && (
          <div style={{background:T.bL,border:`1px solid ${T.bM}`,borderRadius:10,padding:"16px 18px",marginBottom:16}}>
            <p style={{fontSize:12,fontWeight:600,color:T.b1,margin:"0 0 12px"}}>Add social page or account</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={LBL}>Platform</label>
                <select value={newPage.platform} onChange={e=>setNewPage(p=>({...p,platform:e.target.value}))} style={INP}>
                  {Object.entries(PLAT_INFO).map(([k,v])=><option key={k} value={k}>{v.l}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Page name</label>
                <input value={newPage.name} onChange={e=>setNewPage(p=>({...p,name:e.target.value}))} placeholder="e.g. Demo Agency" style={INP}/>
              </div>
              <div>
                <label style={LBL}>Your role</label>
                <select value={newPage.role} onChange={e=>setNewPage(p=>({...p,role:e.target.value}))} style={INP}>
                  {["Admin","Editor","Analyst","Viewer"].map(r=><option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Page URL (optional)</label>
                <input value={newPage.url} onChange={e=>setNewPage(p=>({...p,url:e.target.value}))} placeholder="https://..." style={INP}/>
              </div>
            </div>
            <p style={{fontSize:11,color:T.b1,margin:"0 0 10px"}}>
              ⚠ You must be an Admin on this page to post content and pull insights via the API.
            </p>
            <div style={{display:"flex",gap:8}}>
              <button onClick={addSocialPage} style={{padding:"6px 16px",borderRadius:6,border:"none",background:T.brand,color:"#fff",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"inherit"}}>Connect page</button>
              <button onClick={()=>setAddPageForm(null)} style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${T.s2}`,background:"transparent",color:T.ink3,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        )}

        {/* Summary pills */}
        <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
          {Object.values(
            socialPages.reduce((acc,p)=>{ if(!acc[p.platform]) acc[p.platform]={platform:p.platform,count:0,active:0}; acc[p.platform].count++; if(p.status==="active") acc[p.platform].active++; return acc; },{})
          ).map(({platform,count,active})=>{
            const pi=PLAT_INFO[platform]||{c:T.b2,l:platform};
            return (
              <div key={platform} style={{display:"flex",alignItems:"center",gap:7,padding:"6px 12px",borderRadius:8,background:T.canvas,border:`1px solid ${T.s2}`}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:pi.c}}/>
                <span style={{fontSize:12,color:T.ink}}>{pi.l}</span>
                <span style={{fontSize:11,color:T.ink4}}>{active}/{count} active</span>
              </div>
            );
          })}
        </div>

        {/* Pages list */}
        <div style={{background:T.canvas,border:`1px solid ${T.s2}`,borderRadius:12,overflow:"hidden"}}>
          {socialPages.map((pg,i)=>{
            const pi=PLAT_INFO[pg.platform]||{c:T.b2,l:pg.platform};
            return (
              <div key={pg.id} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 18px",borderBottom:i<socialPages.length-1?`1px solid ${T.s2}`:"none",opacity:pg.status==="active"?1:0.55}}>
                {/* Platform icon */}
                <div style={{width:40,height:40,borderRadius:10,background:pi.c+"18",border:`1.5px solid ${pi.c}33`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:pi.c}}/>
                </div>
                {/* Info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                    <p style={{fontSize:13,fontWeight:500,color:T.ink,margin:0}}>{pg.name}</p>
                    <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:4,background:pi.c+"18",color:pi.c}}>{pi.l}</span>
                    {pg.role==="Admin" && <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:4,background:T.gL,color:T.g1}}>Admin ✓</span>}
                    {pg.role!=="Admin" && <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:4,background:T.aL,color:T.a1}}>⚠ {pg.role} — posting limited</span>}
                  </div>
                  <p style={{fontSize:11,color:T.ink4,margin:0}}>{pg.type} · {pg.followers||"0"} followers · {pg.adAccount?"✓ Ad account linked":"No ad account"}</p>
                </div>
                {/* Status + actions */}
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:pg.status==="active"?T.g2:T.ink4}}/>
                  <span style={{fontSize:11,color:pg.status==="active"?T.g1:T.ink4,fontWeight:600}}>{pg.status==="active"?"Active":"Inactive"}</span>
                  <button onClick={()=>toggleSocialPage(pg.id)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.s2}`,background:"transparent",color:T.ink3,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                    {pg.status==="active"?"Disconnect":"Reconnect"}
                  </button>
                  <button onClick={()=>removeSocialPage(pg.id)} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.rM||T.s2}`,background:T.rL,color:T.r2,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{marginTop:14,padding:"12px 16px",background:T.bL,border:`1px solid ${T.bM}`,borderRadius:9}}>
          <p style={{fontSize:11,color:T.b1,margin:0,lineHeight:1.6}}>
            <strong>Admin access required</strong> — you must be an Admin on each page to publish posts, schedule content, pull organic reach data, and link ad accounts. Pages where you have Viewer or Analyst access are shown but posting is disabled.
          </p>
        </div>
      </div>
      )}
    </div>
  );
}


// ─── CRM Connectors Page ─────────────────────────────────────────────────────
/*
  Full CRM integration hub — researched OAuth flows for each platform:
  HubSpot   : OAuth 2.0 · api.hubapi.com · scopes: crm.objects.contacts.write, crm.objects.leads.write
  Salesforce: OAuth 2.0 + Instance URL · REST API /services/data/v59.0/sobjects/Lead
  Pipedrive : API Token OR OAuth 2.0 · api.pipedrive.com/v1/persons
  Zoho CRM  : OAuth 2.0 · accounts.zoho.com · scope: ZohoCRM.modules.leads.CREATE
  MS Dynamics: OAuth 2.0 AAD · {org}.api.crm.dynamics.com/api/data/v9.2
  Close CRM : API Key (no OAuth) · api.close.com/api/v1/lead
  Freshsales: API Key · {subdomain}.freshsales.io/api/leads
  Webhook   : Custom HTTP POST endpoint
*/

const CRM_CONFIGS = {
  hubspot: {
    id: "hubspot", label: "HubSpot CRM", color: "#FF7A59", textColor: "#fff",
    icon: "H", category: "crm",
    desc: "Sync leads as Contacts in HubSpot with lifecycle stage, source, and campaign attribution.",
    authMethod: "oauth2",
    docsUrl: "https://developers.hubspot.com/docs/api/crm/contacts",
    rateLimit: "100 req/10s · 40,000 req/day",
    tokenExpiry: "Access tokens expire in ~30 min — auto-refreshed via refresh token",
    setupSteps: [
      "Go to app.hubspot.com/developer → Create a Public App",
      "Under Scopes add: crm.objects.contacts.write · crm.objects.leads.write · crm.objects.deals.write",
      "Add your redirect URI: https://your-adtech.com/oauth/hubspot/callback",
      "Copy the App ID (Client ID) and Client Secret from the Auth tab",
    ],
    fields: [
      { key:"client_id",     label:"App ID (Client ID)",    type:"text",     ph:"12345678",            hint:"Found in App Settings → Auth tab" },
      { key:"client_secret", label:"Client Secret",         type:"password", ph:"xxxx-xxxx-xxxx",       hint:"Keep secret — rotate if exposed" },
      { key:"refresh_token", label:"OAuth Refresh Token",   type:"password", ph:"na1-xxxx-xxxx-xxxx",   hint:"Generated after user completes OAuth flow at /oauth/authorize" },
      { key:"portal_id",     label:"HubSpot Portal (Hub) ID",type:"text",   ph:"12345678",            hint:"Found in Settings → Account Defaults → Hub ID" },
    ],
    fieldMap: [
      { from:"email",     to:"email",           required:true  },
      { from:"first_name",to:"firstname",       required:true  },
      { from:"last_name", to:"lastname",        required:true  },
      { from:"company",   to:"company",         required:false },
      { from:"phone",     to:"phone",           required:false },
      { from:"job_title", to:"jobtitle",        required:false },
      { from:"source",    to:"hs_lead_source",  required:false },
      { from:"campaign",  to:"hs_analytics_last_referrer",required:false },
    ],
    object: "Contact",
    endpoint: "POST https://api.hubapi.com/crm/v3/objects/contacts",
  },
  salesforce: {
    id: "salesforce", label: "Salesforce", color: "#00A1E0", textColor: "#fff",
    icon: "SF", category: "crm",
    desc: "Push leads as Lead records in Salesforce with full source tracking and campaign membership.",
    authMethod: "oauth2",
    docsUrl: "https://developer.salesforce.com/docs/apis",
    rateLimit: "15,000 API calls/day (Developer) · higher on Enterprise",
    tokenExpiry: "Access tokens expire in ~2h — use Connected App refresh token",
    setupSteps: [
      "Setup → App Manager → New Connected App in Salesforce",
      "Enable OAuth Settings · set callback URL to your adtech redirect",
      "Select scopes: api · refresh_token · offline_access",
      "Copy Consumer Key (Client ID) and Consumer Secret",
    ],
    fields: [
      { key:"client_id",     label:"Consumer Key (Client ID)", type:"text",     ph:"3MVG9....",         hint:"From Salesforce Connected App → Manage Consumer Details" },
      { key:"client_secret", label:"Consumer Secret",          type:"password", ph:"1234567890ABCDEF",  hint:"Keep secret" },
      { key:"refresh_token", label:"OAuth Refresh Token",      type:"password", ph:"5Aep861...",        hint:"Generated after user completes OAuth via /services/oauth2/authorize" },
      { key:"instance_url",  label:"Instance URL",             type:"text",     ph:"https://yourorg.my.salesforce.com", hint:"Your Salesforce org URL — found in browser address bar" },
    ],
    fieldMap: [
      { from:"email",       to:"Email",       required:true  },
      { from:"first_name",  to:"FirstName",   required:true  },
      { from:"last_name",   to:"LastName",    required:true  },
      { from:"company",     to:"Company",     required:true  },
      { from:"phone",       to:"Phone",       required:false },
      { from:"job_title",   to:"Title",       required:false },
      { from:"source",      to:"LeadSource",  required:false },
    ],
    object: "Lead",
    endpoint: "POST {instance_url}/services/data/v59.0/sobjects/Lead",
  },
  pipedrive: {
    id: "pipedrive", label: "Pipedrive", color: "#1A1A1A", textColor: "#fff",
    icon: "P", category: "crm",
    desc: "Create Persons and Deals in Pipedrive with full pipeline stage and source attribution.",
    authMethod: "apikey",
    docsUrl: "https://developers.pipedrive.com/docs/api/v1",
    rateLimit: "100 req/10s · no daily hard limit",
    tokenExpiry: "API tokens do not expire",
    setupSteps: [
      "Log in to Pipedrive → click your name → Personal Preferences",
      "Navigate to API section → copy your Personal API Token",
      "Alternatively use OAuth 2.0 app for multi-account setup via developer.pipedrive.com",
      "Ensure your user has admin or leads-write permissions",
    ],
    fields: [
      { key:"api_token",  label:"Personal API Token", type:"password", ph:"a1b2c3d4e5f6...",  hint:"From Pipedrive → Your Name → Personal Preferences → API" },
      { key:"company_domain", label:"Company Domain", type:"text",     ph:"yourcompany",      hint:"The subdomain of your Pipedrive account (e.g. yourcompany.pipedrive.com)" },
    ],
    fieldMap: [
      { from:"email",     to:"email[0].value",  required:true  },
      { from:"first_name",to:"name (first)",     required:true  },
      { from:"last_name", to:"name (last)",      required:true  },
      { from:"phone",     to:"phone[0].value",   required:false },
      { from:"company",   to:"org_id",           required:false },
    ],
    object: "Person + Deal",
    endpoint: "POST https://api.pipedrive.com/v1/persons",
  },
  zoho: {
    id: "zoho", label: "Zoho CRM", color: "#E42527", textColor: "#fff",
    icon: "Z", category: "crm",
    desc: "Push leads to the Zoho CRM Leads module with full field mapping and assignment rules.",
    authMethod: "oauth2",
    docsUrl: "https://www.zoho.com/crm/developer/docs/api/v8",
    rateLimit: "200 req/min · 5,000 credits/day (Standard)",
    tokenExpiry: "Access tokens expire in 1 hour — refresh via refresh_token",
    setupSteps: [
      "Go to api-console.zoho.com → Add Client → Server-based Applications",
      "Set redirect URI · copy Client ID and Client Secret",
      "Authorize at: accounts.zoho.com/oauth/v2/auth?scope=ZohoCRM.modules.leads.CREATE",
      "Exchange code for access_token + refresh_token (offline access)",
    ],
    fields: [
      { key:"client_id",     label:"Client ID",      type:"text",     ph:"1000.XXXX...",      hint:"From api-console.zoho.com → your app → Client ID" },
      { key:"client_secret", label:"Client Secret",  type:"password", ph:"xxxx-xxxx...",      hint:"Keep secret" },
      { key:"refresh_token", label:"Refresh Token",  type:"password", ph:"1000.xxxx...",      hint:"Permanent token for refreshing access · store securely" },
      { key:"data_center",   label:"Data center",    type:"text",     ph:"com",               hint:"Your Zoho region: com · eu · in · com.au · jp" },
    ],
    fieldMap: [
      { from:"email",     to:"Email",        required:true  },
      { from:"first_name",to:"First_Name",   required:false },
      { from:"last_name", to:"Last_Name",    required:true  },
      { from:"company",   to:"Company",      required:false },
      { from:"phone",     to:"Phone",        required:false },
      { from:"job_title", to:"Designation",  required:false },
      { from:"source",    to:"Lead_Source",  required:false },
    ],
    object: "Lead",
    endpoint: "POST https://www.zohoapis.{dc}/crm/v2/Leads",
  },
  dynamics: {
    id: "dynamics", label: "Microsoft Dynamics 365", color: "#0078D4", textColor: "#fff",
    icon: "MS", category: "crm",
    desc: "Sync leads as Lead records in Dynamics 365 Sales using the Web API.",
    authMethod: "oauth2",
    docsUrl: "https://docs.microsoft.com/dynamics365/customer-engagement/web-api",
    rateLimit: "Service protection limits · 6,000 req/5 min",
    tokenExpiry: "Access tokens expire in 1h — refresh via Azure AD refresh token",
    setupSteps: [
      "Azure Portal → App Registrations → New Registration",
      "Add Redirect URI → create Client Secret under Certificates & Secrets",
      "In Dynamics: Settings → Security → Users → assign API user role",
      "API Permission: Dynamics CRM → user_impersonation",
    ],
    fields: [
      { key:"client_id",     label:"Application (Client) ID", type:"text",     ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"From Azure App Registration → Overview" },
      { key:"client_secret", label:"Client Secret Value",     type:"password", ph:"xxxx~xxxx...",                         hint:"From Azure → Certificates & Secrets" },
      { key:"tenant_id",     label:"Directory (Tenant) ID",   type:"text",     ph:"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", hint:"From Azure → App Registration → Overview" },
      { key:"org_url",       label:"Organization URL",        type:"text",     ph:"https://yourorg.crm.dynamics.com",     hint:"Your Dynamics org URL" },
    ],
    fieldMap: [
      { from:"email",     to:"emailaddress1", required:true  },
      { from:"first_name",to:"firstname",     required:true  },
      { from:"last_name", to:"lastname",      required:true  },
      { from:"company",   to:"companyname",   required:true  },
      { from:"phone",     to:"telephone1",    required:false },
      { from:"job_title", to:"jobtitle",      required:false },
    ],
    object: "Lead",
    endpoint: "POST {org_url}/api/data/v9.2/leads",
  },
  close: {
    id: "close", label: "Close CRM", color: "#4CAF50", textColor: "#fff",
    icon: "CL", category: "crm",
    desc: "Push leads as Leads with Contacts in Close CRM, optimized for outbound sales teams.",
    authMethod: "apikey",
    docsUrl: "https://developer.close.com",
    rateLimit: "60 req/min · no daily limit",
    tokenExpiry: "API keys do not expire",
    setupSteps: [
      "Log in to Close CRM → Settings → API Keys",
      "Click Generate API Key → copy the key immediately (shown only once)",
      "Ensure the user has admin or leads-write permissions",
    ],
    fields: [
      { key:"api_key",  label:"API Key", type:"password", ph:"api_xxxxxxxxxxxxxxxxxxxx", hint:"From Close CRM → Settings → API Keys · shown only once — save immediately" },
    ],
    fieldMap: [
      { from:"email",     to:"contacts[0].emails[0].email", required:true  },
      { from:"first_name",to:"contacts[0].name (first)",    required:false },
      { from:"last_name", to:"contacts[0].name (last)",     required:false },
      { from:"phone",     to:"contacts[0].phones[0].phone", required:false },
      { from:"company",   to:"name",                        required:false },
    ],
    object: "Lead + Contact",
    endpoint: "POST https://api.close.com/api/v1/lead/",
  },
  freshsales: {
    id: "freshsales", label: "Freshsales", color: "#FF5733", textColor: "#fff",
    icon: "FS", category: "crm",
    desc: "Create Contacts and Deals in Freshsales Suite with AI-powered lead scoring.",
    authMethod: "apikey",
    docsUrl: "https://developers.freshworks.com/crm/api",
    rateLimit: "400 req/min · higher on Enterprise plans",
    tokenExpiry: "API tokens do not expire",
    setupSteps: [
      "Log in to Freshsales → click profile icon → Settings → API Settings",
      "Copy your API Key (under API Settings tab)",
      "Note your Freshsales subdomain (e.g. yourcompany.freshsales.io)",
    ],
    fields: [
      { key:"api_key",   label:"API Key",   type:"password", ph:"xxxxxxxxxxxxxxxxxxxx",          hint:"From Freshsales → Profile → Settings → API Settings" },
      { key:"subdomain", label:"Subdomain", type:"text",     ph:"yourcompany",                  hint:"The subdomain part of yourcompany.freshsales.io" },
    ],
    fieldMap: [
      { from:"email",     to:"email",       required:true  },
      { from:"first_name",to:"first_name",  required:true  },
      { from:"last_name", to:"last_name",   required:true  },
      { from:"phone",     to:"work_number", required:false },
      { from:"job_title", to:"job_title",   required:false },
    ],
    object: "Contact",
    endpoint: "POST https://{subdomain}.freshsales.io/api/contacts",
  },
  webhook: {
    id: "webhook", label: "Custom Webhook", color: "#6D28D9", textColor: "#fff",
    icon: "WH", category: "webhook",
    desc: "Send lead data as JSON POST to any endpoint — Zapier, Make, n8n, or your own backend.",
    authMethod: "custom",
    docsUrl: "",
    rateLimit: "No platform limit — depends on your endpoint",
    tokenExpiry: "Credentials do not expire",
    setupSteps: [
      "Create a webhook endpoint that accepts POST requests with JSON body",
      "Configure optional authentication header (Bearer token, API key, or Basic Auth)",
      "Test with the Send Test Payload button below",
      "Map lead fields to your expected JSON structure",
    ],
    fields: [
      { key:"url",           label:"Webhook URL",    type:"text",     ph:"https://hooks.zapier.com/hooks/catch/...", hint:"Must accept POST with Content-Type: application/json" },
      { key:"auth_header",   label:"Auth header (optional)", type:"text", ph:"Authorization: Bearer your-token",  hint:"Full header string. Leave blank if no auth required." },
      { key:"secret",        label:"Signing secret (optional)",type:"password",ph:"whsec_...",                   hint:"Used to sign payloads so your endpoint can verify they came from AdTech" },
    ],
    fieldMap: [],
    object: "JSON payload",
    endpoint: "POST {your webhook URL}",
  },
  leadforge: {
    id: "leadforge", label: "LeadForge", color: "#7C3AED", textColor: "#fff",
    icon: "LF", category: "crm",
    desc: "Push leads directly into LeadForge's pipeline with full campaign attribution and AI lead scoring.",
    authMethod: "apikey",
    docsUrl: "https://docs.leadforge.io/api",
    rateLimit: "500 req/min · unlimited leads/month",
    tokenExpiry: "API keys do not expire — rotate from dashboard anytime",
    setupSteps: [
      "Log in to LeadForge → Settings → API & Integrations",
      "Click Generate API Key → copy the key (shown once)",
      "Copy your Workspace ID from Settings → Workspace → General",
      "Optionally set a Webhook Secret to verify payloads from AdTech",
    ],
    fields: [
      { key:"api_key",      label:"API Key",        type:"password", ph:"lf_live_xxxxxxxxxxxxxxxxxxxx",  hint:"From LeadForge → Settings → API & Integrations · shown once" },
      { key:"workspace_id", label:"Workspace ID",   type:"text",     ph:"ws_xxxxxxxxxx",                hint:"From Settings → Workspace → General → Workspace ID" },
      { key:"pipeline_id",  label:"Pipeline ID",    type:"text",     ph:"pip_xxxxxxxxxx",               hint:"Optional — assign leads to a specific pipeline. Leave blank for default." },
      { key:"webhook_secret",label:"Webhook secret",type:"password", ph:"whsec_xxxxxxxxxx",             hint:"Optional signing secret to verify payloads at your LeadForge endpoint" },
    ],
    fieldMap: [
      { from:"email",      to:"email",           required:true  },
      { from:"first_name", to:"first_name",      required:true  },
      { from:"last_name",  to:"last_name",       required:true  },
      { from:"company",    to:"company_name",    required:false },
      { from:"phone",      to:"phone",           required:false },
      { from:"job_title",  to:"job_title",       required:false },
      { from:"source",     to:"lead_source",     required:false },
      { from:"campaign",   to:"campaign_name",   required:false },
      { from:"score",      to:"lead_score",      required:false },
    ],
    object: "Lead",
    endpoint: "POST https://api.leadforge.io/v1/leads",
  },
  csv: {
    id: "csv", label: "CSV / Google Sheets", color: "#34A853", textColor: "#fff",
    icon: "CSV", category: "export",
    desc: "Export leads to CSV or sync directly to a Google Sheet via Google Sheets API.",
    authMethod: "oauth2",
    docsUrl: "https://developers.google.com/sheets/api",
    rateLimit: "60 req/min (Google Sheets API)",
    tokenExpiry: "Google OAuth access tokens expire in 1h — auto-refreshed",
    setupSteps: [
      "Google Cloud Console → Enable Google Sheets API for your project",
      "Create OAuth 2.0 credentials → download the JSON key",
      "Authenticate and copy the Spreadsheet ID from the Google Sheet URL",
    ],
    fields: [
      { key:"spreadsheet_id", label:"Google Sheet ID", type:"text",     ph:"1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms", hint:"The long ID from your Google Sheet URL" },
      { key:"refresh_token",  label:"OAuth Refresh Token", type:"password", ph:"1//0g...",     hint:"From Google OAuth flow with scope: spreadsheets" },
      { key:"client_id",      label:"OAuth Client ID", type:"text",     ph:"123456.apps.googleusercontent.com",         hint:"From Google Cloud Console → Credentials" },
      { key:"client_secret",  label:"OAuth Client Secret",type:"password",ph:"GOCSPX-...",    hint:"From Google Cloud Console → Credentials" },
    ],
    fieldMap: [],
    object: "Sheet row",
    endpoint: "POST https://sheets.googleapis.com/v4/spreadsheets/{id}/values/{range}:append",
  },
};

const INIT_STATUS = {
  hubspot:    { connected:true,  leadsCount:247, lastSync:"Mar 21 · 06:14", error:null,    vals:{ client_id:"12345678", client_secret:"••••••••••", refresh_token:"••••••••••", portal_id:"98765432" } },
  salesforce: { connected:true,  leadsCount:183, lastSync:"Mar 21 · 06:08", error:null,    vals:{ client_id:"3MVG9...", client_secret:"••••••••••", refresh_token:"••••••••••", instance_url:"https://acme.my.salesforce.com" } },
  pipedrive:  { connected:false, leadsCount:0,   lastSync:null,             error:null,    vals:{} },
  zoho:       { connected:false, leadsCount:0,   lastSync:null,             error:"Token expired — refresh needed", vals:{ client_id:"1000.XXXX", data_center:"com" } },
  dynamics:   { connected:false, leadsCount:0,   lastSync:null,             error:null,    vals:{} },
  close:      { connected:false, leadsCount:0,   lastSync:null,             error:null,    vals:{} },
  freshsales: { connected:false, leadsCount:0,   lastSync:null,             error:null,    vals:{} },
  webhook:    { connected:true,  leadsCount:89,  lastSync:"Mar 21 · 14:22", error:null,    vals:{ url:"https://hooks.zapier.com/hooks/catch/12345/abcde/", auth_header:"", secret:"whsec_test123" } },
  csv:        { connected:false, leadsCount:0,   lastSync:null,             error:null,    vals:{} },
  leadforge:  { connected:true,  leadsCount:61,  lastSync:"Mar 21 · 15:33", error:null,    vals:{ api_key:"lf_live_••••••••••", workspace_id:"ws_demo1234", pipeline_id:"pip_inbound", webhook_secret:"••••••••••" } },
};

function CRMConnectorsPage() {
  const [statuses, setStatuses] = useState(INIT_STATUS);
  const [openModal, setOpenModal] = useState(null);  // crm id
  const [activeTab, setActiveTab] = useState("all"); // all | connected | available
  const [testState, setTestState] = useState({});    // { crmId: "idle"|"testing"|"ok"|"fail" }

  const cfg = openModal ? CRM_CONFIGS[openModal] : null;
  const st  = openModal ? statuses[openModal] : null;

  const connected   = Object.values(statuses).filter(s => s.connected).length;
  const totalLeads  = Object.values(statuses).reduce((s, x) => s + (x.leadsCount||0), 0);

  const filtered = Object.values(CRM_CONFIGS).filter(c => {
    if (activeTab === "connected") return statuses[c.id]?.connected;
    if (activeTab === "available") return !statuses[c.id]?.connected;
    return true;
  });

  const save = (id, vals) => {
    setStatuses(s => ({ ...s, [id]: { ...s[id], connected:true, leadsCount:s[id].leadsCount, lastSync:"Just now", error:null, vals } }));
    setOpenModal(null);
  };

  const disconnect = (id) => {
    setStatuses(s => ({ ...s, [id]: { ...s[id], connected:false, leadsCount:0, lastSync:null, error:null, vals:{} } }));
  };

  const testConn = (id) => {
    setTestState(t => ({...t, [id]:"testing"}));
    setTimeout(() => setTestState(t => ({...t, [id]:"ok"})), 1400);
    setTimeout(() => setTestState(t => ({...t, [id]:"idle"})), 3500);
  };

  return (
    <div style={{flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg}}>
      <style>{`@keyframes adtech-spin{to{transform:rotate(360deg)}} @keyframes adtech-slidein{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}} @keyframes adtech-fadein{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Header */}
      <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20}}>
        <div>
          <h1 style={{fontSize:20, fontWeight:800, color:T.ink, margin:"0 0 4px"}}>CRM Connectors</h1>
          <p style={{fontSize:12, color:T.ink3, margin:0}}>
            Push leads from campaigns directly into your CRM — <strong style={{color:T.b2}}>{connected} connected</strong> · {totalLeads.toLocaleString()} leads pushed total
          </p>
        </div>
        <Btn tone="ghost" size="sm">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          Request connector
        </Btn>
      </div>

      {/* Summary tiles */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18}}>
        {[
          {l:"Connected CRMs",    v:String(connected),                a:T.b2},
          {l:"Total leads pushed",v:totalLeads.toLocaleString(),       a:T.g2},
          {l:"Synced today",      v:"142",                             a:T.v2},
          {l:"Needs attention",   v:String(Object.values(statuses).filter(s=>s.error).length), a:T.r2},
        ].map(k => (
          <div key={k.l} style={{background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:10, padding:"12px 14px", borderTop:`3px solid ${k.a}`, boxShadow:"0 1px 3px rgba(15,23,42,.05)"}}>
            <p style={{fontSize:8, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", color:T.ink4, margin:"0 0 5px"}}>{k.l}</p>
            <p style={{fontSize:22, fontWeight:800, color:k.a, margin:0, lineHeight:1}}>{k.v}</p>
          </div>
        ))}
      </div>

      {/* Tab filter */}
      <div style={{display:"flex", gap:2, background:T.s1, border:`1px solid ${T.s2}`, borderRadius:9, padding:3, marginBottom:16, alignSelf:"flex-start", width:"fit-content"}}>
        {[["all","All CRMs"],["connected","Connected"],["available","Available"]].map(([t,l]) => (
          <button key={t} onClick={()=>setActiveTab(t)} style={{padding:"6px 16px", borderRadius:7, fontSize:11, fontWeight:700, border:"none", background:activeTab===t?T.b2:"transparent", color:activeTab===t?"#fff":T.ink3, cursor:"pointer", fontFamily:"inherit", transition:"all .12s"}}>{l}</button>
        ))}
      </div>

      {/* CRM grid */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12}}>
        {filtered.map(crm => {
          const s = statuses[crm.id];
          const ts = testState[crm.id];
          return (
            <div key={crm.id}
              style={{background:T.canvas, border:`1px solid ${s.connected ? (s.error ? T.rM : T.gM) : T.s2}`, borderRadius:12, padding:"16px", cursor:"pointer", transition:"all .15s", boxShadow:"0 1px 3px rgba(15,23,42,.05)", animation:"adtech-fadein .2s ease"}}
              onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 16px rgba(15,23,42,.1)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow="0 1px 3px rgba(15,23,42,.05)"}
            >
              {/* Card header */}
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
                <div style={{display:"flex", alignItems:"center", gap:10}}>
                  <div style={{width:40, height:40, borderRadius:10, background:crm.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:crm.textColor, flexShrink:0, letterSpacing:"-.5px"}}>{crm.icon}</div>
                  <div>
                    <p style={{fontSize:13, fontWeight:700, color:T.ink, margin:"0 0 2px"}}>{crm.label}</p>
                    <p style={{fontSize:9, color:T.ink4, margin:0, textTransform:"uppercase", letterSpacing:".06em", fontWeight:600}}>
                      {crm.authMethod === "oauth2" ? "OAuth 2.0" : crm.authMethod === "apikey" ? "API Key" : "Custom"}
                    </p>
                  </div>
                </div>
                <div>
                  {s.error
                    ? <Tag tone="bad">Error</Tag>
                    : s.connected
                    ? <Tag tone="ok">Connected</Tag>
                    : <Tag tone="neutral">Available</Tag>
                  }
                </div>
              </div>

              {/* Description */}
              <p style={{fontSize:11, color:T.ink3, margin:"0 0 10px", lineHeight:1.5}}>{crm.desc}</p>

              {/* Error banner */}
              {s.error && (
                <div style={{background:T.rL, border:`1px solid ${T.rM}`, borderRadius:7, padding:"7px 10px", marginBottom:10, fontSize:10, color:T.r1, fontWeight:600}}>
                  ⚠ {s.error}
                </div>
              )}

              {/* Stats (if connected) */}
              {s.connected && !s.error && (
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:12}}>
                  <div style={{background:T.bg, borderRadius:7, padding:"7px 9px", border:`1px solid ${T.s2}`}}>
                    <p style={{fontSize:8, color:T.ink4, fontWeight:700, margin:"0 0 2px"}}>LEADS PUSHED</p>
                    <p style={{fontSize:14, fontWeight:800, color:T.g2, margin:0}}>{s.leadsCount.toLocaleString()}</p>
                  </div>
                  <div style={{background:T.bg, borderRadius:7, padding:"7px 9px", border:`1px solid ${T.s2}`}}>
                    <p style={{fontSize:8, color:T.ink4, fontWeight:700, margin:"0 0 2px"}}>LAST SYNC</p>
                    <p style={{fontSize:11, fontWeight:600, color:T.ink3, margin:0}}>{s.lastSync || "—"}</p>
                  </div>
                </div>
              )}

              {/* Rate limit info */}
              <p style={{fontSize:9, color:T.ink4, margin:"0 0 12px"}}>{crm.rateLimit}</p>

              {/* Actions */}
              <div style={{display:"flex", gap:6}}>
                {s.connected && (
                  <button onClick={() => testConn(crm.id)} style={{display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", flex:1, justifyContent:"center"}}>
                    {ts === "testing"
                      ? <><span style={{width:11,height:11,border:`2px solid ${T.b3}`,borderTopColor:"transparent",borderRadius:"50%",animation:"adtech-spin 1s linear infinite",display:"inline-block"}}/> Testing…</>
                      : ts === "ok"
                      ? <><span style={{color:T.g2}}>✓</span> Connected!</>
                      : "↻ Test sync"
                    }
                  </button>
                )}
                <button onClick={() => setOpenModal(crm.id)} style={{display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, border:`1px solid ${s.connected ? T.s2 : T.bM}`, background:s.connected ? "transparent" : T.bL, color:s.connected ? T.ink3 : T.b1, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", flex:s.connected?1:2, justifyContent:"center"}}>
                  {s.connected
                    ? <>⚙ Configure</>
                    : s.error
                    ? <>↺ Reconnect</>
                    : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg> Connect</>
                  }
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Setup Modal ── */}
      {openModal && cfg && st && (
        <CRMModal cfg={cfg} st={st} onClose={()=>setOpenModal(null)} onSave={save} onDisconnect={disconnect}/>
      )}
    </div>
  );
}

/* ─── CRM Setup Modal ────────────────────────────────────────────────────────── */
function CRMModal({ cfg, st, onClose, onSave, onDisconnect }) {
  const [tab,      setTab]      = useState("setup");     // setup | fieldmap | test | logs
  const [vals,     setVals]     = useState({ ...st.vals });
  const [saved,    setSaved]    = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [testRes,  setTestRes]  = useState(null);
  const [sending,  setSending]  = useState(false);
  const [sendRes,  setSendRes]  = useState(null);

  const upd = (k, v) => setVals(x => ({ ...x, [k]: v }));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => { onSave(cfg.id, vals); }, 1400);
  };

  const handleTest = () => {
    setTesting(true); setTestRes(null);
    setTimeout(() => { setTesting(false); setTestRes({ ok:true, message:"Connection verified · 1 test lead created and deleted · Response: 201 Created", ms:342 }); }, 1800);
  };

  const handleSendTest = () => {
    setSending(true); setSendRes(null);
    setTimeout(() => { setSending(false); setSendRes({ ok:true, id:"lead_test_"+Math.random().toString(36).slice(2,8), ms:218 }); }, 1400);
  };

  const fi  = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:8, padding:"9px 11px", color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontSize:12, fontFamily:"inherit" };
  const fl  = { fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, display:"block", marginBottom:6 };
  const tabs = ["setup","fieldmap","test","logs"];

  const testPayload = JSON.stringify({
    email: "sarah.test@example.com",
    first_name: "Sarah", last_name: "Test",
    company: "Example Corp", phone: "+1 555 0100",
    job_title: "VP of Marketing",
    source: "LinkedIn Ads", campaign: "Lead Gen — Enterprise",
    score: 88, stage: "qualified",
    adtech_lead_id: "lead_test_abc123",
    pushed_at: new Date().toISOString(),
  }, null, 2);

  return (
    <div style={{position:"fixed", inset:0, zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(15,23,42,.5)"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:T.canvas, borderRadius:16, width:640, maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(15,23,42,.2)", animation:"adtech-fadein .18s ease"}}>

        {/* Modal header */}
        <div style={{padding:"18px 22px 0", borderBottom:`1px solid ${T.s2}`, flexShrink:0}}>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
            <div style={{display:"flex", alignItems:"center", gap:12}}>
              <div style={{width:38, height:38, borderRadius:10, background:cfg.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:cfg.textColor, letterSpacing:"-.5px"}}>{cfg.icon}</div>
              <div>
                <p style={{fontSize:15, fontWeight:800, color:T.ink, margin:0}}>{cfg.label}</p>
                <p style={{fontSize:10, color:T.ink4, margin:"2px 0 0"}}>
                  {cfg.authMethod==="oauth2"?"OAuth 2.0 · ":cfg.authMethod==="apikey"?"API Key · ":"Custom · "}
                  {cfg.object} · {cfg.rateLimit.split("·")[0].trim()}
                </p>
              </div>
            </div>
            <div style={{display:"flex", alignItems:"center", gap:8}}>
              {st.connected && <Tag tone="ok">Connected</Tag>}
              {st.error && <Tag tone="bad">Error</Tag>}
              {cfg.docsUrl && <a href={cfg.docsUrl} target="_blank" rel="noreferrer" style={{fontSize:11, color:T.b2, textDecoration:"none", fontWeight:600}}>Docs ↗</a>}
              <button onClick={onClose} style={{background:"none", border:"none", cursor:"pointer", color:T.ink4, fontSize:20, lineHeight:1, padding:"0 4px"}}>×</button>
            </div>
          </div>
          {/* Tab bar */}
          <div style={{display:"flex", gap:0}}>
            {tabs.map(t => (
              <button key={t} onClick={()=>setTab(t)} style={{padding:"8px 18px", background:"none", border:"none", borderBottom:`2px solid ${tab===t?T.b2:"transparent"}`, marginBottom:-1, color:tab===t?T.b2:T.ink3, fontSize:12, fontWeight:tab===t?700:500, cursor:"pointer", textTransform:"capitalize", fontFamily:"inherit", transition:"all .15s"}}>{t === "fieldmap" ? "Field mapping" : t}</button>
            ))}
          </div>
        </div>

        {/* Modal body */}
        <div style={{flex:1, overflowY:"auto", padding:"20px 22px"}}>

          {/* Setup tab */}
          {tab==="setup" && (
            <>
              {/* Setup steps */}
              <div style={{background:T.s1, borderRadius:10, padding:"13px 15px", marginBottom:18, border:`1px solid ${T.s2}`}}>
                <p style={{fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, margin:"0 0 10px"}}>Setup guide</p>
                {cfg.setupSteps.map((step, i) => (
                  <div key={i} style={{display:"flex", gap:10, marginBottom: i < cfg.setupSteps.length-1 ? 8 : 0}}>
                    <div style={{width:20, height:20, borderRadius:"50%", background:T.b2, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#fff", flexShrink:0, marginTop:1}}>{i+1}</div>
                    <p style={{fontSize:11, color:T.ink2, margin:0, lineHeight:1.5}}>{step}</p>
                  </div>
                ))}
              </div>

              {/* Credential fields */}
              <div style={{display:"flex", flexDirection:"column", gap:13, marginBottom:18}}>
                {cfg.fields.map(f => (
                  <div key={f.key}>
                    <label style={fl}>{f.label}</label>
                    <input type={f.type} placeholder={f.ph} value={vals[f.key]||""} onChange={e=>upd(f.key,e.target.value)} style={fi}
                      onFocus={e=>e.target.style.borderColor=T.b2} onBlur={e=>e.target.style.borderColor=T.s2}/>
                    <p style={{fontSize:10, color:T.ink4, margin:"4px 0 0", lineHeight:1.4}}>{f.hint}</p>
                  </div>
                ))}
              </div>

              {/* Token expiry note */}
              <div style={{background:T.aL, border:`1px solid ${T.aM}`, borderRadius:8, padding:"9px 12px", marginBottom:18, display:"flex", gap:8, alignItems:"flex-start"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.a1} strokeWidth="2" strokeLinecap="round" style={{flexShrink:0, marginTop:1}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                <p style={{fontSize:11, color:T.a1, margin:0, fontWeight:600}}>{cfg.tokenExpiry}</p>
              </div>

              {/* Action buttons */}
              <div style={{display:"flex", gap:8}}>
                {saved
                  ? <div style={{flex:1, padding:"10px 0", borderRadius:9, background:T.gL, border:`1px solid ${T.gM}`, display:"flex", alignItems:"center", justifyContent:"center", gap:6}}>
                      <svg width="14" height="12" viewBox="0 0 14 12" fill="none" stroke={T.g2} strokeWidth="2.5" strokeLinecap="round"><path d="M1 6l4 4L13 1"/></svg>
                      <span style={{fontSize:13, fontWeight:700, color:T.g1}}>Saved & connected!</span>
                    </div>
                  : <button onClick={handleSave} style={{flex:1, padding:"10px 0", borderRadius:9, border:"none", background:T.b2, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                      Save & Connect
                    </button>
                }
                {st.connected && (
                  <button onClick={()=>onDisconnect(cfg.id)} style={{padding:"10px 16px", borderRadius:9, border:`1px solid ${T.rM}`, background:T.rL, color:T.r1, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit"}}>
                    Disconnect
                  </button>
                )}
              </div>
            </>
          )}

          {/* Field mapping tab */}
          {tab==="fieldmap" && (
            <>
              <p style={{fontSize:12, color:T.ink3, margin:"0 0 16px", lineHeight:1.5}}>
                Map AdTech lead fields to <strong>{cfg.label}</strong> {cfg.object} fields. Required fields must be mapped for the sync to succeed.
              </p>
              {cfg.fieldMap.length === 0 ? (
                <div style={{background:T.s1, borderRadius:10, padding:20, textAlign:"center", border:`1px solid ${T.s2}`}}>
                  <p style={{fontSize:12, color:T.ink3, margin:"0 0 6px"}}>Field mapping is automatic for {cfg.label}.</p>
                  <p style={{fontSize:11, color:T.ink4, margin:0}}>Lead data is sent as a JSON payload to your webhook URL.</p>
                </div>
              ) : (
                <>
                  <div style={{background:T.bg, border:`1px solid ${T.s2}`, borderRadius:10, overflow:"hidden"}}>
                    <table style={{width:"100%", borderCollapse:"collapse", fontSize:11}}>
                      <thead>
                        <tr style={{borderBottom:`2px solid ${T.s2}`, background:T.s1}}>
                          <th style={{padding:"10px 14px", textAlign:"left", fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:T.ink4}}>AdTech Field</th>
                          <th style={{padding:"10px 14px", textAlign:"center", fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:T.ink4}}>→</th>
                          <th style={{padding:"10px 14px", textAlign:"left", fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:T.ink4}}>{cfg.label} Field</th>
                          <th style={{padding:"10px 14px", textAlign:"center", fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:T.ink4}}>Required</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cfg.fieldMap.map((m, i) => (
                          <tr key={m.from} style={{borderBottom: i < cfg.fieldMap.length-1 ? `1px solid ${T.s2}` : "none"}}>
                            <td style={{padding:"10px 14px", fontWeight:600, color:T.ink}}>{m.from}</td>
                            <td style={{padding:"10px 14px", textAlign:"center", color:T.ink4, fontSize:14}}>→</td>
                            <td style={{padding:"10px 14px"}}>
                              <code style={{fontSize:11, background:T.s1, padding:"2px 7px", borderRadius:4, color:T.v2, border:`1px solid ${T.s2}`}}>{m.to}</code>
                            </td>
                            <td style={{padding:"10px 14px", textAlign:"center"}}>
                              {m.required ? <Tag tone="bad">Required</Tag> : <Tag tone="neutral">Optional</Tag>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{marginTop:14, padding:"12px 14px", background:T.bL, border:`1px solid ${T.bM}`, borderRadius:9}}>
                    <p style={{fontSize:11, color:T.b1, fontWeight:600, margin:"0 0 3px"}}>Endpoint</p>
                    <code style={{fontSize:10, color:T.b2}}>{cfg.endpoint}</code>
                  </div>
                </>
              )}
            </>
          )}

          {/* Test tab */}
          {tab==="test" && (
            <>
              <p style={{fontSize:12, color:T.ink3, margin:"0 0 16px", lineHeight:1.5}}>
                Send a test lead to verify your connection. The test record will be created and immediately deleted (or marked as test in {cfg.label}).
              </p>
              <div style={{marginBottom:14}}>
                <p style={{...fl, marginBottom:8}}>Test payload</p>
                <pre style={{background:T.s1, border:`1px solid ${T.s2}`, borderRadius:8, padding:"12px 14px", fontSize:10, color:T.ink, fontFamily:"monospace", overflow:"auto", maxHeight:220, margin:0}}>{testPayload}</pre>
              </div>
              {testRes && (
                <div style={{background:testRes.ok?T.gL:T.rL, border:`1px solid ${testRes.ok?T.gM:T.rM}`, borderRadius:9, padding:"12px 14px", marginBottom:14}}>
                  <p style={{fontSize:12, fontWeight:700, color:testRes.ok?T.g1:T.r1, margin:"0 0 4px"}}>{testRes.ok?"✓ Connection verified":"✕ Test failed"}</p>
                  <p style={{fontSize:11, color:testRes.ok?T.g2:T.r2, margin:0}}>{testRes.message}</p>
                  {testRes.ms && <p style={{fontSize:10, color:T.ink4, margin:"4px 0 0"}}>Response time: {testRes.ms}ms</p>}
                </div>
              )}
              {sendRes && (
                <div style={{background:T.gL, border:`1px solid ${T.gM}`, borderRadius:9, padding:"12px 14px", marginBottom:14}}>
                  <p style={{fontSize:12, fontWeight:700, color:T.g1, margin:"0 0 3px"}}>✓ Test lead pushed</p>
                  <p style={{fontSize:11, color:T.g2, margin:0}}>Record created: <code style={{fontSize:10,background:T.s1,padding:"1px 6px",borderRadius:4}}>{sendRes.id}</code> · {sendRes.ms}ms</p>
                </div>
              )}
              <div style={{display:"flex", gap:8}}>
                <button onClick={handleTest} disabled={testing} style={{flex:1, padding:"10px 0", borderRadius:9, border:"none", background:T.b2, color:"#fff", fontSize:13, fontWeight:700, cursor:testing?"wait":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7}}>
                  {testing
                    ? <><span style={{width:14,height:14,border:"2px solid rgba(255,255,255,.4)",borderTopColor:"#fff",borderRadius:"50%",animation:"adtech-spin 1s linear infinite",display:"inline-block"}}/> Testing connection…</>
                    : "Test connection"
                  }
                </button>
                <button onClick={handleSendTest} disabled={sending} style={{flex:1, padding:"10px 0", borderRadius:9, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:13, fontWeight:700, cursor:sending?"wait":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7}}>
                  {sending
                    ? <><span style={{width:14,height:14,border:`2px solid ${T.bM}`,borderTopColor:T.b2,borderRadius:"50%",animation:"adtech-spin 1s linear infinite",display:"inline-block"}}/> Sending…</>
                    : <>Send test lead →</>
                  }
                </button>
              </div>
            </>
          )}

          {/* Logs tab */}
          {tab==="logs" && (
            <>
              <p style={{fontSize:12, color:T.ink3, margin:"0 0 14px"}}>Recent sync activity for {cfg.label}</p>
              {[
                {t:"Mar 21 · 14:32", ok:true,  msg:"Lead pushed: Sarah Mitchell (sarah.mitchell@acme.com) → Contact #12847"},
                {t:"Mar 21 · 11:15", ok:true,  msg:"Lead pushed: James Okafor (j.okafor@techcorp.io) → Contact #12831"},
                {t:"Mar 21 · 09:44", ok:false, msg:"Push failed: rate limit exceeded (429) — retried after 10s · 2 retries"},
                {t:"Mar 20 · 16:44", ok:true,  msg:"Lead pushed: Priya Sharma (priya@innovate.co) → Contact #12816"},
                {t:"Mar 20 · 14:22", ok:true,  msg:"Bulk push: 8 leads → 8 contacts created · 0 duplicates · 312ms avg"},
                {t:"Mar 20 · 09:22", ok:true,  msg:"Lead pushed: Carlos Mendez (c.mendez@globex.com) → Contact #12809"},
                {t:"Mar 19 · 13:08", ok:true,  msg:"Lead pushed: Emma Johansson (emma.j@nordic.se) → Contact #12798"},
                {t:"Mar 19 · 08:51", ok:false, msg:"Push failed: invalid email format for David Nakamura — skipped, logged"},
              ].map((log, i) => (
                <div key={i} style={{display:"flex", gap:10, padding:"8px 0", borderBottom:`1px solid ${T.s2}`}}>
                  <div style={{width:16, height:16, borderRadius:"50%", background:log.ok?T.gL:T.rL, border:`1px solid ${log.ok?T.gM:T.rM}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1}}>
                    {log.ok
                      ? <svg width="8" height="6" viewBox="0 0 8 6" fill="none" stroke={T.g2} strokeWidth="2" strokeLinecap="round"><path d="M1 3l2 2L7 1"/></svg>
                      : <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke={T.r2} strokeWidth="2" strokeLinecap="round"><path d="M1 1l6 6M7 1L1 7"/></svg>
                    }
                  </div>
                  <div style={{flex:1}}>
                    <p style={{fontSize:11, color:T.ink, margin:"0 0 2px"}}>{log.msg}</p>
                    <p style={{fontSize:9, color:T.ink4, margin:0}}>{log.t}</p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
// ─── Connectors Page ─────────────────────────────────────────────────────────

// ─── Client data factory ─────────────────────────────────────────────────────
// Each client gets its own seed so KPIs / posts feel unique
function makeClientData(seed) {
  const m = (seed % 5) + 1;
  return {
    spend:    `$${(42 + seed * 11.3).toFixed(1)}k`,
    revenue:  `$${(124 + seed * 28.7).toFixed(1)}k`,
    roas:     `${(1.9 + seed * 0.22).toFixed(2)}×`,
    cpm:      `$${(8.4 + seed * 1.1).toFixed(2)}`,
    cpc:      `$${(1.2 + seed * 0.3).toFixed(2)}`,
    ctr:      `${(1.1 + seed * 0.18).toFixed(2)}%`,
    conv:     String(840 + seed * 180),
    impr:     `${(3.2 + seed * 0.9).toFixed(1)}M`,
    reach:    `${(2.1 + seed * 0.7).toFixed(1)}M`,
    eng:      `${(140 + seed * 35).toFixed(0)}k`,
    vv:       `${(0.4 + seed * 0.2).toFixed(1)}M`,
    sources:  m + "/9",
    leads:    seed * 23 + 12,
  };
}

// ─── Client Switcher Modal ────────────────────────────────────────────────────
function ClientSwitcher({ clients, activeId, onSelect, onClose, onNew }) {
  const [search, setSearch] = useState("");
  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.industry.toLowerCase().includes(search.toLowerCase()));

  const industryColor = {
    "E-commerce":"#2563EB","SaaS":"#7C3AED","Finance":"#059669",
    "Healthcare":"#DC2626","Real Estate":"#D97706","Agency":"#F97316",
    "Retail":"#0A66C2","Media":"#E1306C","Travel":"#2DD4BF","B2B":"#1877F2",
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, display:"flex", alignItems:"flex-start", justifyContent:"flex-start", background:"rgba(15,23,42,.4)", paddingTop:0 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:300, height:"100%", background:T.canvas, borderRight:`1px solid ${T.s2}`, display:"flex", flexDirection:"column", boxShadow:"4px 0 24px rgba(15,23,42,.12)", animation:"adtech-slidein .18s ease" }}>
        <style>{`@keyframes adtech-slidein{from{transform:translateX(-20px);opacity:0}to{transform:translateX(0);opacity:1}} @keyframes adtech-fadein{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {/* Header */}
        <div style={{ padding:"16px 18px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <p style={{ fontSize:14, fontWeight:800, color:T.ink, margin:0 }}>All Clients</p>
            <p style={{ fontSize:11, color:T.ink4, margin:"2px 0 0" }}>{clients.length} clients</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.ink4, fontSize:20, lineHeight:1 }}>×</button>
        </div>

        {/* Search */}
        <div style={{ padding:"10px 14px", borderBottom:`1px solid ${T.s2}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, background:T.bg, border:`1px solid ${T.s2}`, borderRadius:8, padding:"7px 11px" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients…"
              style={{ flex:1, border:"none", outline:"none", fontSize:12, color:T.ink, background:"transparent", fontFamily:"inherit" }}/>
          </div>
        </div>

        {/* Client list */}
        <div style={{ flex:1, overflowY:"auto", padding:"8px 10px" }}>
          {filtered.map(c => {
            const active = c.id === activeId;
            const ic = industryColor[c.industry] || T.b2;
            return (
              <button key={c.id} onClick={()=>{ onSelect(c); onClose(); }}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 11px", borderRadius:10, width:"100%", textAlign:"left", border:`1.5px solid ${active?T.b2:T.s2}`, background:active?T.bL:T.canvas, cursor:"pointer", fontFamily:"inherit", marginBottom:6, transition:"all .12s", boxShadow:active?`0 0 0 3px ${T.bM}`:"0 1px 3px rgba(15,23,42,.04)" }}>
                {/* Avatar */}
                <div style={{ width:36, height:36, borderRadius:9, background:ic, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:"#fff", flexShrink:0 }}>
                  {c.name.slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:active?T.b1:T.ink, margin:"0 0 2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</p>
                  <p style={{ fontSize:10, color:T.ink4, margin:0 }}>{c.industry} · {makeClientData(c.seed).sources} sources</p>
                </div>
                {active && <div style={{ width:8, height:8, borderRadius:"50%", background:T.b2, flexShrink:0 }}/>}
              </button>
            );
          })}
        </div>

        {/* New client */}
        <div style={{ padding:"10px 14px", borderTop:`1px solid ${T.s2}` }}>
          <button onClick={onNew} style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"10px 13px", borderRadius:9, border:`1.5px dashed ${T.bM}`, background:T.bL, color:T.b1, cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:700, transition:"all .15s" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            New client
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Client Modal ─────────────────────────────────────────────────────────
function NewClientModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ name:"", industry:"E-commerce", currency:"USD", timezone:"UTC", website:"", contactEmail:"", budget:"", notes:"" });
  const [step, setStep] = useState(1);
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const fi = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:8, padding:"9px 11px", color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontSize:12, fontFamily:"inherit" };
  const fl = { fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, display:"block", marginBottom:6 };

  const industries = ["E-commerce","SaaS","Finance","Healthcare","Real Estate","Agency","Retail","Media","Travel","B2B"];
  const currencies = ["USD","EUR","GBP","AED","SAR","CAD","AUD"];
  const timezones  = ["UTC","UTC+1 (London)","UTC+2 (Paris)","UTC+3 (Riyadh)","UTC+4 (Dubai)","UTC-5 (New York)","UTC-8 (Los Angeles)"];

  const canCreate = form.name.trim().length >= 2;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:600, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(15,23,42,.5)" }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:T.canvas, borderRadius:16, width:500, maxHeight:"88vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(15,23,42,.2)" }}>
        {/* Header */}
        <div style={{ padding:"20px 24px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <p style={{ fontSize:16, fontWeight:800, color:T.ink, margin:0 }}>Create new client</p>
            <p style={{ fontSize:11, color:T.ink4, margin:"3px 0 0" }}>Step {step} of 2 — {step===1?"Details":"Settings"}</p>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.ink3, fontSize:20, lineHeight:1 }}>×</button>
        </div>

        <div style={{ padding:"22px 24px" }}>
          {step===1 ? (
            <>
              <div style={{ marginBottom:14 }}>
                <label style={fl}>Client / Agency name <span style={{ color:T.r2 }}>*</span></label>
                <input value={form.name} onChange={e=>upd("name",e.target.value)} placeholder="Acme Corp" style={fi}
                  onFocus={e=>e.target.style.borderColor=T.b2} onBlur={e=>e.target.style.borderColor=T.s2} autoFocus/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                <div>
                  <label style={fl}>Industry</label>
                  <select value={form.industry} onChange={e=>upd("industry",e.target.value)} style={fi}>
                    {industries.map(i=><option key={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fl}>Monthly budget</label>
                  <input value={form.budget} onChange={e=>upd("budget",e.target.value)} placeholder="$10,000" style={fi}/>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                <div>
                  <label style={fl}>Website</label>
                  <input value={form.website} onChange={e=>upd("website",e.target.value)} placeholder="https://acme.com" style={fi}/>
                </div>
                <div>
                  <label style={fl}>Contact email</label>
                  <input type="email" value={form.contactEmail} onChange={e=>upd("contactEmail",e.target.value)} placeholder="cmo@acme.com" style={fi}/>
                </div>
              </div>
              <div style={{ marginBottom:20 }}>
                <label style={fl}>Notes</label>
                <textarea rows={2} value={form.notes} onChange={e=>upd("notes",e.target.value)} placeholder="Client brief, focus areas, key objectives…" style={{...fi,resize:"vertical"}}/>
              </div>
              <Btn tone="primary" disabled={!canCreate} onClick={()=>setStep(2)} style={{ width:"100%", justifyContent:"center", padding:"11px 0" }}>
                Next — Settings →
              </Btn>
            </>
          ) : (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                <div>
                  <label style={fl}>Currency</label>
                  <select value={form.currency} onChange={e=>upd("currency",e.target.value)} style={fi}>
                    {currencies.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fl}>Timezone</label>
                  <select value={form.timezone} onChange={e=>upd("timezone",e.target.value)} style={fi}>
                    {timezones.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {/* Feature toggles */}
              <div style={{ marginBottom:20 }}>
                <label style={{ ...fl, marginBottom:10 }}>Enable features</label>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {[["Lead Dashboard","Capture and push leads to CRM",true],["Organic Report","SEO + social organic tracking",true],["AI Assistant","Claude-powered analytics assistant",true],["Report Sending","Email PDF reports to client",true],["Ad Creation","Create and publish ads",true]].map(([f2,d,def])=>(
                    <label key={f2} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:9, border:`1px solid ${T.s2}`, background:T.bg, cursor:"pointer" }}>
                      <input type="checkbox" defaultChecked={def} style={{ accentColor:T.b2, flexShrink:0 }}/>
                      <div>
                        <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>{f2}</p>
                        <p style={{ fontSize:10, color:T.ink4, margin:0 }}>{d}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn tone="ghost" onClick={()=>setStep(1)}>← Back</Btn>
                <button onClick={()=>{ onCreate({ ...form, id:`c_${Date.now()}`, seed:Math.floor(Math.random()*20), created:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}), connectedSources:0, status:"active" }); onClose(); }}
                  style={{ flex:1, padding:"11px 0", borderRadius:9, border:"none", background:T.b2, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Create client workspace
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Clients overview page ────────────────────────────────────────────────────
function ClientsPage({ clients, activeClient, onSelect, onNew }) {
  const [search, setSearch] = useState("");
  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  const industryColor = {
    "E-commerce":"#2563EB","SaaS":"#7C3AED","Finance":"#059669",
    "Healthcare":"#DC2626","Real Estate":"#D97706","Agency":"#F97316",
    "Retail":"#0A66C2","Media":"#E1306C","Travel":"#2DD4BF","B2B":"#1877F2",
  };

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <style>{`@keyframes adtech-fadein{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:T.ink, margin:"0 0 4px" }}>Clients</h1>
          <p style={{ fontSize:12, color:T.ink3, margin:0 }}>{clients.length} workspaces · each with full isolated analytics, leads, and reports</p>
        </div>
        <Btn tone="primary" onClick={onNew}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          New client
        </Btn>
      </div>

      {/* Search */}
      <div style={{ display:"flex", alignItems:"center", gap:8, background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:10, padding:"9px 14px", marginBottom:18, maxWidth:340 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.ink4} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients…"
          style={{ flex:1, border:"none", outline:"none", fontSize:12, color:T.ink, background:"transparent", fontFamily:"inherit" }}/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
        {filtered.map(c => {
          const d = makeClientData(c.seed);
          const ic = industryColor[c.industry] || T.b2;
          const active = c.id === activeClient?.id;
          return (
            <div key={c.id} onClick={()=>onSelect(c)}
              style={{ background:T.canvas, border:`1.5px solid ${active?T.b2:T.s2}`, borderRadius:14, overflow:"hidden", cursor:"pointer", transition:"all .15s", boxShadow:active?`0 0 0 3px ${T.bM},0 4px 16px rgba(15,23,42,.08)`:"0 1px 4px rgba(15,23,42,.06)", animation:"adtech-fadein .2s ease" }}
              onMouseEnter={e=>{ if(!active){ e.currentTarget.style.borderColor=T.b2; e.currentTarget.style.boxShadow="0 4px 16px rgba(15,23,42,.1)"; }}}
              onMouseLeave={e=>{ if(!active){ e.currentTarget.style.borderColor=T.s2; e.currentTarget.style.boxShadow="0 1px 4px rgba(15,23,42,.06)"; }}}>
              {/* Card top bar */}
              <div style={{ height:6, background:ic }}/>
              <div style={{ padding:"14px 16px" }}>
                {/* Header */}
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:ic, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:"#fff", flexShrink:0 }}>
                    {c.name.slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:13, fontWeight:800, color:T.ink, margin:"0 0 2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</p>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:10, color:T.ink4 }}>{c.industry}</span>
                      {active && <Tag tone="blue">Active</Tag>}
                    </div>
                  </div>
                </div>

                {/* KPI mini-grid */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6, marginBottom:12 }}>
                  {[["Spend",d.spend,T.b2],["Revenue",d.revenue,T.g2],["ROAS",d.roas,T.v2],["Sources",d.sources,T.ink3],["Leads",String(d.leads),T.a2],["CTR",d.ctr,T.b3]].map(([l,v,a])=>(
                    <div key={l} style={{ background:T.bg, borderRadius:7, padding:"6px 8px", border:`1px solid ${T.s2}` }}>
                      <p style={{ fontSize:8, color:T.ink4, fontWeight:700, margin:"0 0 2px", textTransform:"uppercase", letterSpacing:".05em" }}>{l}</p>
                      <p style={{ fontSize:11, fontWeight:800, color:a, margin:0 }}>{v}</p>
                    </div>
                  ))}
                </div>

                {/* Footer meta */}
                <div style={{ display:"flex", justifyContent:"space-between", paddingTop:10, borderTop:`1px solid ${T.s2}` }}>
                  <span style={{ fontSize:10, color:T.ink4 }}>{c.budget ? `${c.currency||"USD"} ${c.budget}/mo` : "No budget set"}</span>
                  <span style={{ fontSize:10, color:T.ink4 }}>Since {c.created}</span>
                </div>
              </div>
            </div>
          );
        })}
        {/* New client card */}
        <button onClick={onNew} style={{ background:"transparent", border:`1.5px dashed ${T.s3}`, borderRadius:14, padding:"30px 20px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=T.b2;e.currentTarget.style.background=T.bL;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.s3;e.currentTarget.style.background="transparent";}}>
          <div style={{ width:44, height:44, borderRadius:12, background:T.bL, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.b2} strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          </div>
          <p style={{ fontSize:12, fontWeight:700, color:T.b2, margin:0 }}>Add new client</p>
          <p style={{ fontSize:10, color:T.ink4, margin:0, textAlign:"center" }}>Create an isolated workspace with full dashboards</p>
        </button>
      </div>
    </div>
  );
}

// ─── Report Preview ───────────────────────────────────────────────────────────
// ─── All available KPIs for report customization ─────────────────────────────
const ALL_KPIS = [
  // Paid
  { id:"spend",       label:"Spend",           cat:"paid",    accent:"#2563EB" },
  { id:"revenue",     label:"Revenue",         cat:"paid",    accent:"#059669" },
  { id:"roas",        label:"ROAS",            cat:"paid",    accent:"#7C3AED" },
  { id:"cpm",         label:"CPM",             cat:"paid",    accent:"#D97706" },
  { id:"cpc",         label:"CPC",             cat:"paid",    accent:"#B45309" },
  { id:"cpa",         label:"CPA",             cat:"paid",    accent:"#D97706" },
  { id:"ctr",         label:"CTR",             cat:"paid",    accent:"#3B82F6" },
  { id:"conv",        label:"Conversions",     cat:"paid",    accent:"#047857" },
  { id:"roi",         label:"ROI %",           cat:"paid",    accent:"#059669" },
  { id:"cvr",         label:"Conv. Rate",      cat:"paid",    accent:"#059669" },
  // Reach
  { id:"impr",        label:"Impressions",     cat:"reach",   accent:"#2563EB" },
  { id:"reach",       label:"Reach",           cat:"reach",   accent:"#3B82F6" },
  { id:"freq",        label:"Frequency",       cat:"reach",   accent:"#94A3B8" },
  // Engagement
  { id:"eng",         label:"Engagements",     cat:"engage",  accent:"#7C3AED" },
  { id:"er",          label:"Eng. Rate",       cat:"engage",  accent:"#7C3AED" },
  { id:"likes",       label:"Likes",           cat:"engage",  accent:"#DB2777" },
  { id:"comments",    label:"Comments",        cat:"engage",  accent:"#475569" },
  { id:"shares",      label:"Shares",          cat:"engage",  accent:"#475569" },
  { id:"saves",       label:"Saves",           cat:"engage",  accent:"#475569" },
  // Video
  { id:"vv",          label:"Video Views",     cat:"video",   accent:"#DC2626" },
  { id:"vtr",         label:"VTR",             cat:"video",   accent:"#B91C1C" },
  { id:"watch_time",  label:"Watch Time",      cat:"video",   accent:"#DC2626" },
  // Organic
  { id:"sessions",    label:"Sessions",        cat:"organic", accent:"#2563EB" },
  { id:"new_users",   label:"New Users",       cat:"organic", accent:"#3B82F6" },
  { id:"bounce",      label:"Bounce Rate",     cat:"organic", accent:"#DC2626" },
  { id:"org_rev",     label:"Organic Revenue", cat:"organic", accent:"#059669" },
  { id:"org_cvr",     label:"Organic CVR",     cat:"organic", accent:"#059669" },
  // Leads
  { id:"leads",       label:"Leads",           cat:"leads",   accent:"#7C3AED" },
  { id:"cpl",         label:"CPL",             cat:"leads",   accent:"#B45309" },
  { id:"lead_score",  label:"Avg Lead Score",  cat:"leads",   accent:"#7C3AED" },
  { id:"qualified",   label:"Qualified Leads", cat:"leads",   accent:"#059669" },
];

const DEFAULT_KPI_IDS = ["spend","revenue","roas","ctr","conv","impr","reach","eng"];

const REPORT_SECTIONS = [
  { id:"summary",      label:"Executive Summary",    on:true  },
  { id:"kpis",         label:"KPI tiles",            on:true  },
  { id:"trend",        label:"Revenue trend chart",  on:true  },
  { id:"channels",     label:"Channel breakdown",    on:true  },
  { id:"posts",        label:"Posts / creatives",    on:false },
  { id:"leads",        label:"Lead pipeline",        on:false },
  { id:"organic",      label:"Organic breakdown",    on:false },
  { id:"recommendations",label:"Recommendations",   on:true  },
  { id:"footer",       label:"Footer / branding",   on:true  },
];

function ReportPreview({ client, onClose, onSend }) {
  const [type,      setType]     = useState("weekly");
  const [showCust,  setShowCust] = useState(false);
  const [kpiIds,    setKpiIds]   = useState(DEFAULT_KPI_IDS);
  const [sections,  setSections] = useState(REPORT_SECTIONS);
  const [brandColor,setBrandColor]=useState("#2563EB");
  const [reportTitle,setReportTitle]=useState("Performance Report");
  const [dragOver,  setDragOver] = useState(null);

  const d = makeClientData(client?.seed||0);
  const organic = POSTS.filter(p=>!p.paid);

  const kpiValueMap = {
    spend:d.spend, revenue:d.revenue, roas:d.roas, cpm:d.cpm, cpc:d.cpc,
    cpa:"$45.80", ctr:d.ctr, conv:d.conv, roi:"193%", cvr:"4.4%",
    impr:d.impr, reach:d.reach, freq:"2.3×",
    eng:d.eng, er:"4.3%", likes:"218k", comments:"41k", shares:"28k", saves:"31k",
    vv:d.vv, vtr:"62.1%", watch_time:"3,840h",
    sessions:"28.4k", new_users:"11.8k", bounce:"34%", org_rev:"$18.9k", org_cvr:"3.2%",
    leads:String(d.leads), cpl:"$38.40", lead_score:"82", qualified:String(Math.round(d.leads*.6)),
  };

  const activeKpis = ALL_KPIS.filter(k=>kpiIds.includes(k.id));
  const secOn = (id) => sections.find(s=>s.id===id)?.on;
  const toggleSec = (id) => setSections(ss=>ss.map(s=>s.id===id?{...s,on:!s.on}:s));
  const toggleKpi = (id) => setKpiIds(ks=>ks.includes(id)?ks.filter(k=>k!==id):[...ks,id]);
  const moveKpi   = (fromIdx,toIdx) => {
    const arr=[...kpiIds];
    const [moved]=arr.splice(fromIdx,1);
    arr.splice(toIdx,0,moved);
    setKpiIds(arr);
  };

  const trend30 = Array.from({length:30},(_,i)=>({
    d:`${i+1}`,
    paid:2400+Math.sin(i*.4)*400+((i*17+3)%200),
    organic:580+Math.sin(i*.3)*80+((i*11)%120),
  }));

  const CAT_COLORS = { paid:"#2563EB", reach:"#3B82F6", engage:"#7C3AED", video:"#DC2626", organic:"#059669", leads:"#6D28D9" };

  const types=[{id:"weekly",label:"Weekly"},{id:"monthly",label:"Monthly"},{id:"organic",label:"Organic"},{id:"b2b",label:"B2B"}];

  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, display:"flex", background:"rgba(15,23,42,.55)" }} onClick={onClose}>
      <style>{`
        @keyframes adtech-slidein-right{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}
        @keyframes adtech-fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* ── Customizer side panel ── */}
      {showCust && (
        <div onClick={e=>e.stopPropagation()} style={{ width:280, height:"100%", background:T.canvas, borderRight:`1px solid ${T.s2}`, display:"flex", flexDirection:"column", overflowY:"auto", flexShrink:0, marginLeft:"auto" }}>
          <div style={{ padding:"14px 16px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <p style={{ fontSize:13, fontWeight:800, color:T.ink, margin:0 }}>Customise Report</p>
            <button onClick={()=>setShowCust(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.ink4, fontSize:18, lineHeight:1 }}>×</button>
          </div>

          <div style={{ flex:1, overflowY:"auto", padding:"14px 16px" }}>

            {/* Report title */}
            <div style={{ marginBottom:18 }}>
              <p style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, margin:"0 0 7px" }}>Report title</p>
              <input value={reportTitle} onChange={e=>setReportTitle(e.target.value)}
                style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:8, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
                onFocus={e=>e.target.style.borderColor=T.b2} onBlur={e=>e.target.style.borderColor=T.s2}/>
            </div>

            {/* Brand color */}
            <div style={{ marginBottom:18 }}>
              <p style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, margin:"0 0 7px" }}>Accent colour</p>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["#2563EB","#7C3AED","#059669","#D97706","#DC2626","#0A66C2","#E1306C","#0F172A"].map(c=>(
                  <button key={c} onClick={()=>setBrandColor(c)} style={{ width:28, height:28, borderRadius:7, background:c, border:`2.5px solid ${brandColor===c?"#fff":"transparent"}`, outline:brandColor===c?`2.5px solid ${c}`:none, cursor:"pointer", boxShadow:brandColor===c?"0 0 0 2px "+c:"none", transition:"all .12s" }}/>
                ))}
                <input type="color" value={brandColor} onChange={e=>setBrandColor(e.target.value)}
                  style={{ width:28, height:28, borderRadius:7, border:`1px solid ${T.s2}`, padding:2, cursor:"pointer", background:"transparent" }}/>
              </div>
            </div>

            {/* Sections */}
            <div style={{ marginBottom:18 }}>
              <p style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, margin:"0 0 10px" }}>Report sections</p>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {sections.map(s=>(
                  <label key={s.id} style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:8, border:`1px solid ${s.on?T.bM:T.s2}`, background:s.on?T.bL:"transparent", cursor:"pointer", transition:"all .12s" }}>
                    <input type="checkbox" checked={s.on} onChange={()=>toggleSec(s.id)} style={{ accentColor:T.b2, flexShrink:0 }}/>
                    <span style={{ fontSize:12, fontWeight:s.on?600:400, color:s.on?T.b1:T.ink3 }}>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* KPI tiles */}
            <div style={{ marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <p style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:T.ink4, margin:0 }}>KPI tiles ({kpiIds.length} selected)</p>
                <button onClick={()=>setKpiIds(DEFAULT_KPI_IDS)} style={{ fontSize:10, color:T.b2, background:"none", border:"none", cursor:"pointer", fontWeight:600, fontFamily:"inherit" }}>Reset</button>
              </div>

              {/* Selected — reorderable */}
              {kpiIds.length>0&&(
                <div style={{ marginBottom:10 }}>
                  <p style={{ fontSize:9, color:T.ink4, margin:"0 0 6px", fontWeight:600 }}>Selected (drag to reorder)</p>
                  <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    {kpiIds.map((id,i)=>{
                      const kpi=ALL_KPIS.find(k=>k.id===id);
                      if(!kpi)return null;
                      return (
                        <div key={id} draggable
                          onDragStart={e=>{e.dataTransfer.setData("idx",String(i));setDragOver(null);}}
                          onDragOver={e=>{e.preventDefault();setDragOver(i);}}
                          onDrop={e=>{e.preventDefault();moveKpi(Number(e.dataTransfer.getData("idx")),i);setDragOver(null);}}
                          onDragLeave={()=>setDragOver(null)}
                          style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 9px", borderRadius:8, border:`1px solid ${dragOver===i?T.b2:T.s2}`, background:dragOver===i?T.bL:T.bg, cursor:"grab", transition:"all .1s" }}>
                          <svg width="10" height="12" viewBox="0 0 10 12" fill={T.ink4}><circle cx="3" cy="2" r="1.2"/><circle cx="7" cy="2" r="1.2"/><circle cx="3" cy="6" r="1.2"/><circle cx="7" cy="6" r="1.2"/><circle cx="3" cy="10" r="1.2"/><circle cx="7" cy="10" r="1.2"/></svg>
                          <span style={{ width:10, height:10, borderRadius:"50%", background:kpi.accent, flexShrink:0, display:"inline-block" }}/>
                          <span style={{ fontSize:11, fontWeight:600, color:T.ink, flex:1 }}>{kpi.label}</span>
                          <span style={{ fontSize:10, color:T.ink4, fontFamily:"monospace" }}>{kpiValueMap[id]||"—"}</span>
                          <button onClick={()=>toggleKpi(id)} style={{ background:"none", border:"none", cursor:"pointer", color:T.r2, fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available to add — grouped by category */}
              {Object.entries({paid:"Paid",reach:"Reach",engage:"Engagement",video:"Video",organic:"Organic",leads:"Leads"}).map(([cat,catLabel])=>{
                const available = ALL_KPIS.filter(k=>k.cat===cat&&!kpiIds.includes(k.id));
                if(available.length===0)return null;
                return (
                  <div key={cat} style={{ marginBottom:10 }}>
                    <p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:CAT_COLORS[cat], margin:"0 0 5px" }}>{catLabel}</p>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                      {available.map(k=>(
                        <button key={k.id} onClick={()=>toggleKpi(k.id)} style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 9px", borderRadius:6, border:`1px solid ${T.s2}`, background:T.canvas, color:T.ink3, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all .12s" }}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor=k.accent;e.currentTarget.style.color=k.accent;e.currentTarget.style.background=k.accent+"12";}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.s2;e.currentTarget.style.color=T.ink3;e.currentTarget.style.background=T.canvas;}}>
                          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke={k.accent} strokeWidth="2" strokeLinecap="round"><path d="M4.5 1v7M1 4.5h7"/></svg>
                          {k.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Report preview panel ── */}
      <div onClick={e=>e.stopPropagation()} style={{ width:showCust?"calc(100% - 280px)":"72%", maxWidth:showCust?9999:900, height:"100%", marginLeft:showCust?0:"auto", background:"#fff", display:"flex", flexDirection:"column", boxShadow:"0 0 60px rgba(15,23,42,.2)", animation:"adtech-slidein-right .22s ease", transition:"width .2s" }}>

        {/* Toolbar */}
        <div style={{ background:T.ink, padding:"12px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ display:"flex", gap:6 }}>
              {["#DC2626","#D97706","#059669"].map(c=><div key={c} style={{width:12,height:12,borderRadius:"50%",background:c}}/>)}
            </div>
            <p style={{ fontSize:12, color:"rgba(255,255,255,.7)", margin:0 }}>Report preview · {client?.name}</p>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {/* Type tabs */}
            <div style={{ display:"flex", gap:2 }}>
              {types.map(t=>(
                <button key={t.id} onClick={()=>setType(t.id)} style={{ padding:"5px 12px", borderRadius:6, border:"none", background:type===t.id?"rgba(255,255,255,.2)":"transparent", color:type===t.id?"#fff":"rgba(255,255,255,.5)", fontSize:11, fontWeight:type===t.id?700:400, cursor:"pointer", fontFamily:"inherit" }}>{t.label}</button>
              ))}
            </div>
            <div style={{ width:1, height:20, background:"rgba(255,255,255,.2)", margin:"0 4px" }}/>
            {/* Customise toggle */}
            <button onClick={()=>setShowCust(c=>!c)} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:6, border:`1px solid ${showCust?"rgba(255,255,255,.5)":"rgba(255,255,255,.2)"}`, background:showCust?"rgba(255,255,255,.15)":"transparent", color:showCust?"#fff":"rgba(255,255,255,.7)", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              Customise
            </button>
            <Btn tone="green" size="sm" onClick={onSend}>Send →</Btn>
            <PDFBtn label="↓ PDF" type={type}/>
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,.6)", fontSize:18, lineHeight:1, padding:"0 4px" }}>×</button>
          </div>
        </div>

        {/* Report body */}
        <div style={{ flex:1, overflowY:"auto", padding:"40px 52px", fontFamily:"Georgia, serif", fontSize:14, color:"#1E293B", lineHeight:1.6, background:"#fff" }}>

          {/* Brand header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:36, paddingBottom:22, borderBottom:`3px solid ${brandColor}` }}>
            <div>
              <div style={{ width:40, height:40, borderRadius:10, background:brandColor, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:14 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </div>
              <p style={{ fontSize:30, fontWeight:700, color:"#0F172A", margin:"0 0 5px", fontFamily:"Georgia, serif" }}>{reportTitle}</p>
              <p style={{ fontSize:15, color:"#64748B", margin:0, fontFamily:"system-ui,sans-serif" }}>{type.charAt(0).toUpperCase()+type.slice(1)} Report · {client?.name||"Demo Agency"}</p>
            </div>
            <div style={{ textAlign:"right" }}>
              <p style={{ fontSize:12, color:"#94A3B8", margin:"0 0 4px", fontFamily:"system-ui,sans-serif" }}>Reporting period</p>
              <p style={{ fontSize:14, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif" }}>Feb 20 – Mar 21, 2026</p>
              <p style={{ fontSize:12, color:"#94A3B8", margin:"0 0 4px", fontFamily:"system-ui,sans-serif" }}>Prepared by</p>
              <p style={{ fontSize:13, fontWeight:700, color:brandColor, margin:0, fontFamily:"system-ui,sans-serif" }}>AdTech Platform</p>
            </div>
          </div>

          {/* Executive summary */}
          {secOn("summary")&&(
            <div style={{ background:"#EFF6FF", border:`1px solid #BFDBFE`, borderLeft:`4px solid ${brandColor}`, borderRadius:"0 8px 8px 0", padding:"16px 18px", marginBottom:30 }}>
              <p style={{ fontSize:13, fontWeight:700, color:brandColor, margin:"0 0 7px", fontFamily:"system-ui,sans-serif", textTransform:"uppercase", letterSpacing:".07em" }}>Executive Summary</p>
              <p style={{ margin:0, fontSize:14, color:"#1E293B", lineHeight:1.7 }}>
                {client?.name||"The client"} achieved a blended ROAS of <strong>{d.roas}</strong> on {d.spend} spend this period, generating <strong>{d.revenue}</strong> in attributed revenue.
                {type==="organic"?` Organic channels drove ${organic.length} posts across ${[...new Set(organic.map(p=>p.src))].length} platforms at $0 paid spend.`:` Campaign performance delivered ${d.conv} conversions at a CTR of ${d.ctr} across ${d.sources} connected platforms.`}
                {" "}Impressions reached <strong>{d.impr}</strong> with a reach of {d.reach}.
              </p>
            </div>
          )}

          {/* KPI tiles — fully customizable */}
          {secOn("kpis")&&activeKpis.length>0&&(
            <>
              <p style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif", borderBottom:"1px solid #E3E8EF", paddingBottom:8 }}>
                Key Performance Indicators
                <span style={{ fontSize:11, color:"#94A3B8", fontWeight:400, marginLeft:8 }}>{activeKpis.length} metrics</span>
              </p>
              <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(activeKpis.length,4)},1fr)`, gap:12, marginBottom:30 }}>
                {activeKpis.map(k=>(
                  <div key={k.id} style={{ background:"#F8FAFC", borderRadius:8, padding:"12px 14px", border:"1px solid #E3E8EF", borderTopWidth:3, borderTopColor:k.accent, borderTopStyle:"solid" }}>
                    <p style={{ fontSize:10, fontWeight:700, color:"#94A3B8", margin:"0 0 5px", fontFamily:"system-ui,sans-serif", textTransform:"uppercase", letterSpacing:".07em" }}>{k.label}</p>
                    <p style={{ fontSize:22, fontWeight:800, color:k.accent, margin:0, fontFamily:"system-ui,sans-serif", lineHeight:1 }}>{kpiValueMap[k.id]||"—"}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Trend chart */}
          {secOn("trend")&&(
            <>
              <p style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif", borderBottom:"1px solid #E3E8EF", paddingBottom:8 }}>Revenue Trend</p>
              <div style={{ marginBottom:30 }}>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={trend30} margin={{top:4,right:4,left:-15,bottom:0}}>
                    <defs>
                      <linearGradient id="rptP" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={brandColor} stopOpacity={.15}/><stop offset="95%" stopColor={brandColor} stopOpacity={0}/></linearGradient>
                      <linearGradient id="rptO" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#059669" stopOpacity={.15}/><stop offset="95%" stopColor="#059669" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E3E8EF" vertical={false}/>
                    <XAxis dataKey="d" tick={{fontSize:9,fill:"#94A3B8"}} axisLine={false} tickLine={false} interval={6}/>
                    <YAxis tick={{fontSize:9,fill:"#94A3B8"}} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/>
                    <Tooltip contentStyle={{background:"#fff",border:"1px solid #E3E8EF",borderRadius:8,fontSize:11}} formatter={v=>[`$${Math.round(v).toLocaleString()}`]}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    {type!=="organic"&&<Area dataKey="paid"    name="Paid Revenue"    stroke={brandColor} strokeWidth={2} fill="url(#rptP)" dot={false}/>}
                    <Area dataKey="organic" name="Organic Revenue" stroke="#059669" strokeWidth={2} fill="url(#rptO)" dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* Channel breakdown */}
          {secOn("channels")&&type!=="organic"&&(
            <>
              <p style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif", borderBottom:"1px solid #E3E8EF", paddingBottom:8 }}>Channel Breakdown</p>
              <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:30, fontFamily:"system-ui,sans-serif", fontSize:12 }}>
                <thead><tr style={{ borderBottom:"2px solid #E3E8EF" }}>
                  {["Channel","Spend","Revenue","ROAS","Impressions","CTR","Conversions"].map(h=>(
                    <th key={h} style={{ textAlign:"left", padding:"0 0 10px", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[["Meta Ads","$32.4k","$91k","2.8×","3.2M","1.9%","612"],["Google Ads","$26.8k","$84.2k","3.9×","1.8M","2.4%","480"],["TikTok","$10.2k","$21.4k","1.2×","1.4M","0.8%","180"],["LinkedIn","$8.8k","$14.1k","1.6×","0.4M","0.6%","318"],["Snapchat","$4.6k","$7.8k","1.7×","0.6M","1.1%","250"]].map(row=>(
                    <tr key={row[0]} style={{ borderBottom:"1px solid #F1F5F9" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      {row.map((cell,i)=>(
                        <td key={i} style={{ padding:"8px 0", color:i===3?(parseFloat(cell)>=3?"#059669":parseFloat(cell)>=1.5?"#D97706":"#DC2626"):i===0?"#0F172A":"#475569", fontWeight:i===0?600:i===3?700:400 }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Organic posts */}
          {secOn("organic")&&type==="organic"&&(
            <>
              <p style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif", borderBottom:"1px solid #E3E8EF", paddingBottom:8 }}>Organic Posts</p>
              <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:30, fontFamily:"system-ui,sans-serif", fontSize:12 }}>
                <thead><tr style={{ borderBottom:"2px solid #E3E8EF" }}>
                  {["Post","Platform","Impressions","CTR","Engagements","Revenue"].map(h=><th key={h} style={{ textAlign:"left", padding:"0 0 10px", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {organic.map(p=>(
                    <tr key={p.id} style={{ borderBottom:"1px solid #F1F5F9" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"8px 0", fontWeight:600, color:"#0F172A", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.title}</td>
                      <td style={{ padding:"8px 0", color:"#475569" }}>{SRC[p.src]?.label}</td>
                      <td style={{ padding:"8px 0", color:brandColor, fontWeight:600 }}>{String(Math.round(p.impressions/1000))+"k"}</td>
                      <td style={{ padding:"8px 0", color:"#475569" }}>{p.ctr}%</td>
                      <td style={{ padding:"8px 0", color:"#7C3AED" }}>{Math.round(p.engagements/1000)+"k"}</td>
                      <td style={{ padding:"8px 0", color:"#059669", fontWeight:700 }}>${Math.round(p.revenue/100)*100}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Leads section */}
          {secOn("leads")&&(
            <>
              <p style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif", borderBottom:"1px solid #E3E8EF", paddingBottom:8 }}>Lead Pipeline</p>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:30 }}>
                {[["Total leads",d.leads,"#7C3AED"],["Qualified",String(Math.round(d.leads*.6)),"#059669"],["Avg score","82","#2563EB"],["Pushed to CRM","61","#D97706"]].map(([l,v,a])=>(
                  <div key={l} style={{ background:"#F8FAFC", borderRadius:8, padding:"12px 14px", border:"1px solid #E3E8EF", borderTopWidth:3, borderTopColor:a, borderTopStyle:"solid" }}>
                    <p style={{ fontSize:10, fontWeight:700, color:"#94A3B8", margin:"0 0 5px", fontFamily:"system-ui,sans-serif", textTransform:"uppercase", letterSpacing:".07em" }}>{l}</p>
                    <p style={{ fontSize:22, fontWeight:800, color:a, margin:0, fontFamily:"system-ui,sans-serif" }}>{v}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Recommendations */}
          {secOn("recommendations")&&(
            <>
              <p style={{ fontSize:16, fontWeight:700, color:"#0F172A", margin:"0 0 14px", fontFamily:"system-ui,sans-serif", borderBottom:"1px solid #E3E8EF", paddingBottom:8 }}>Recommendations</p>
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:30 }}>
                {[["Increase Google Ads budget by 15%","Google Ads has the highest ROAS (3.9×) with headroom before saturation. Shifting $3–5k from TikTok could yield ~$12k incremental revenue.","high"],["Refresh TikTok creative","CTR dropped 42% vs 14-day baseline (frequency 5.8). New UGC-style videos under 10 seconds recommended.","med"],["Scale organic SEO","Organic SEO drives 14.2k sessions at $0 spend with 4.1% CVR — better than most paid channels. Double content output for +$6k/mo.","high"]].map(([title,body,pri])=>(
                  <div key={title} style={{ background:pri==="high"?"#ECFDF5":"#FFFBEB", border:`1px solid ${pri==="high"?"#A7F3D0":"#FDE68A"}`, borderLeft:`4px solid ${pri==="high"?"#059669":"#D97706"}`, borderRadius:"0 8px 8px 0", padding:"12px 16px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                      <p style={{ fontSize:13, fontWeight:700, color:pri==="high"?"#047857":"#B45309", margin:0, fontFamily:"system-ui,sans-serif" }}>{title}</p>
                      <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:4, background:pri==="high"?"#A7F3D0":"#FDE68A", color:pri==="high"?"#047857":"#B45309", textTransform:"uppercase", letterSpacing:".06em" }}>{pri==="high"?"High priority":"Medium"}</span>
                    </div>
                    <p style={{ fontSize:13, color:"#475569", margin:0, fontFamily:"system-ui,sans-serif" }}>{body}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Footer */}
          {secOn("footer")&&(
            <div style={{ borderTop:`2px solid ${brandColor}`, paddingTop:20, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <p style={{ fontSize:11, color:"#94A3B8", margin:0, fontFamily:"system-ui,sans-serif" }}>Generated by AdTech Platform · {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</p>
              <p style={{ fontSize:11, color:brandColor, fontWeight:700, margin:0, fontFamily:"system-ui,sans-serif" }}>Confidential · {client?.name||"Demo Agency"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Budget Pacing Page ───────────────────────────────────────────────────────
const PACING_DATA = [
  { id:"c1", name:"Lead Gen — Enterprise",   platform:"linkedin", budget:5000,  spent:3820, days_left:9,  target_pacing:76,  actual_pacing:86, status:"over"    },
  { id:"c2", name:"Summer Sale — Hero Video",platform:"meta",     budget:8500,  spent:4100, days_left:14, target_pacing:50,  actual_pacing:48, status:"ok"      },
  { id:"c3", name:"Brand Keywords Campaign", platform:"google_ads",budget:3200, spent:980,  days_left:18, target_pacing:38,  actual_pacing:31, status:"under"   },
  { id:"c4", name:"Retargeting Carousel 30d",platform:"meta",     budget:2000,  spent:1870, days_left:3,  target_pacing:90,  actual_pacing:94, status:"over"    },
  { id:"c5", name:"Pinterest Shopping",      platform:"pinterest", budget:1500, spent:420,  days_left:22, target_pacing:27,  actual_pacing:28, status:"ok"      },
  { id:"c6", name:"TikTok UGC Series",       platform:"tiktok",   budget:4000,  spent:880,  days_left:20, target_pacing:33,  actual_pacing:22, status:"under"   },
];

function PacingBar({ actual, target, status }) {
  const clr = status==="over" ? T.r2 : status==="under" ? T.a2 : T.g2;
  const bg  = status==="over" ? T.rL  : status==="under" ? T.aL  : T.gL;
  return (
    <div style={{ position:"relative", height:28, background:T.s1, borderRadius:6, overflow:"hidden" }}>
      {/* target line */}
      <div style={{ position:"absolute", left:`${target}%`, top:0, bottom:0, width:2, background:T.s3, zIndex:2 }}/>
      {/* actual bar */}
      <div style={{ position:"absolute", left:0, top:4, bottom:4, width:`${Math.min(actual,100)}%`, background:clr, borderRadius:4, transition:"width .4s", zIndex:1 }}/>
      <span style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", fontSize:10, fontWeight:600, color:clr, zIndex:3 }}>{actual}%</span>
    </div>
  );
}

function BudgetPacingPage() {
  const [campaigns, setCampaigns] = useState(PACING_DATA);
  const [adjustId, setAdjustId]  = useState(null);
  const [newBudget, setNewBudget] = useState("");

  const applyAdjust = (id) => {
    setCampaigns(cs => cs.map(c => c.id===id ? {...c, budget: Number(newBudget), actual_pacing: Math.round(c.spent/Number(newBudget)*100), status: Math.round(c.spent/Number(newBudget)*100) > c.target_pacing+10 ? "over" : Math.round(c.spent/Number(newBudget)*100) < c.target_pacing-10 ? "under" : "ok"} : c));
    setAdjustId(null); setNewBudget("");
  };

  const over  = campaigns.filter(c=>c.status==="over").length;
  const under = campaigns.filter(c=>c.status==="under").length;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Budget pacing</h1>
          <p style={{ fontSize:12, color:T.ink3, margin:0 }}>Daily burn vs target across all active campaigns</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {over>0  && <span style={{ padding:"5px 12px", borderRadius:6, background:T.rL, color:T.r1, fontSize:11, fontWeight:600 }}>⚠ {over} overpacing</span>}
          {under>0 && <span style={{ padding:"5px 12px", borderRadius:6, background:T.aL, color:T.a1, fontSize:11, fontWeight:600 }}>↓ {under} underpacing</span>}
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {campaigns.map(c => {
          const clr = c.status==="over" ? T.r2 : c.status==="under" ? T.a2 : T.g2;
          const bgL = c.status==="over" ? T.rL  : c.status==="under" ? T.aL  : T.gL;
          const platClr = SRC[c.platform]?.c || T.b2;
          return (
            <div key={c.id} style={{ background:T.canvas, border:`1px solid ${c.status!=="ok"?clr:T.s2}`, borderRadius:10, padding:"14px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:platClr, flexShrink:0 }}/>
                <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0, flex:1 }}>{c.name}</p>
                <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, background:bgL, color:clr }}>
                  {c.status==="over"?"Overpacing":c.status==="under"?"Underpacing":"On track"}
                </span>
                <button onClick={()=>{ setAdjustId(c.id); setNewBudget(String(c.budget)); }}
                  style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>
                  Adjust budget
                </button>
              </div>

              <PacingBar actual={c.actual_pacing} target={c.target_pacing} status={c.status}/>

              <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
                <div style={{ display:"flex", gap:20 }}>
                  <span style={{ fontSize:11, color:T.ink4 }}>Spent: <strong style={{ color:T.ink }}>${c.spent.toLocaleString()}</strong></span>
                  <span style={{ fontSize:11, color:T.ink4 }}>Budget: <strong style={{ color:T.ink }}>${c.budget.toLocaleString()}</strong></span>
                  <span style={{ fontSize:11, color:T.ink4 }}>Days left: <strong style={{ color:T.ink }}>{c.days_left}</strong></span>
                </div>
                <span style={{ fontSize:11, color:T.ink4 }}>Target: {c.target_pacing}% · Actual: <strong style={{ color:clr }}>{c.actual_pacing}%</strong></span>
              </div>

              {/* Daily forecast */}
              <div style={{ marginTop:10, padding:"8px 12px", background:T.s1, borderRadius:7 }}>
                <p style={{ fontSize:10, color:T.ink4, margin:0 }}>
                  Forecast: <strong style={{ color:T.ink }}>${Math.round(c.spent/((31-c.days_left)||1)).toLocaleString()}/day</strong> avg spend ·{" "}
                  {c.status==="over"
                    ? <span style={{ color:T.r2 }}>Cap at ${Math.round(c.budget/31).toLocaleString()}/day to stay on budget</span>
                    : c.status==="under"
                    ? <span style={{ color:T.a2 }}>Increase to ${Math.round((c.budget-c.spent)/c.days_left).toLocaleString()}/day to spend in full</span>
                    : <span style={{ color:T.g2 }}>On track to finish ${Math.round((c.budget-c.spent)).toLocaleString()} remaining</span>
                  }
                </p>
              </div>

              {/* Adjust modal inline */}
              {adjustId===c.id && (
                <div style={{ marginTop:10, padding:"12px", background:T.bL, border:`1px solid ${T.bM}`, borderRadius:8, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:11, color:T.b1 }}>New budget (USD):</span>
                  <input type="number" value={newBudget} onChange={e=>setNewBudget(e.target.value)} style={{ width:110, border:`1px solid ${T.bM}`, borderRadius:6, padding:"5px 8px", fontSize:12, outline:"none", fontFamily:"inherit" }}/>
                  <button onClick={()=>applyAdjust(c.id)} style={{ padding:"5px 14px", borderRadius:6, border:"none", background:T.b2, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Apply</button>
                  <button onClick={()=>setAdjustId(null)} style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Anomaly Feed Page ────────────────────────────────────────────────────────
const ANOMALIES = [
  { id:"a1", sev:"critical", metric:"CPA",    campaign:"Lead Gen — Enterprise",   platform:"linkedin",   msg:"CPA doubled overnight — $84 → $168",         delta:"+100%", time:"2h ago",   explained:false },
  { id:"a2", sev:"warning",  metric:"CTR",    campaign:"Summer Sale — Hero Video",platform:"meta",       msg:"CTR dropped 28% over 3 days",                delta:"−28%",  time:"5h ago",   explained:false },
  { id:"a3", sev:"critical", metric:"ROAS",   campaign:"Brand Keywords Campaign",  platform:"google_ads", msg:"ROAS fell below 1.0× floor — pausing risk",   delta:"−42%",  time:"8h ago",   explained:true  },
  { id:"a4", sev:"info",     metric:"CPM",    campaign:"TikTok UGC Series",        platform:"tiktok",    msg:"CPM spike +65% — possible auction pressure",   delta:"+65%",  time:"12h ago",  explained:false },
  { id:"a5", sev:"warning",  metric:"Freq",   campaign:"Retargeting Carousel 30d", platform:"meta",       msg:"Frequency hit 8.2 — creative fatigue likely",  delta:"8.2×",  time:"1d ago",   explained:true  },
  { id:"a6", sev:"info",     metric:"Conv",   campaign:"Pinterest Shopping",        platform:"pinterest",  msg:"Conversion rate up 34% — investigate positive",delta:"+34%",  time:"1d ago",   explained:false },
];

const AI_EXPLANATIONS = {
  a3: "Google Ads quality score dropped from 7 to 3 for your top 3 keywords — likely due to landing page changes. Recommend reviewing LP load speed and ad-to-page relevance.",
  a5: "Audience segment fully saturated. Same 42k users have seen this creative an average of 8× in 14 days. Recommend refreshing creative or expanding audience by 30%.",
};

function AnomalyFeedPage() {
  const [items, setItems]       = useState(ANOMALIES);
  const [explaining, setExpl]   = useState(null);
  const [explanation, setExpln] = useState({});
  const [filter, setFilter]     = useState("all");

  const explain = (id) => {
    if (explanation[id]) { setExpl(id); return; }
    setExpl(id);
    setTimeout(() => {
      setExpln(ex => ({...ex, [id]: AI_EXPLANATIONS[id] || "Based on the last 7-day trend, this anomaly is likely caused by increased competition in your target segment, combined with creative wear-out. Recommend refreshing the top creative and adjusting bid strategy to Target CPA."}));
      setItems(its => its.map(i => i.id===id ? {...i, explained:true} : i));
    }, 900);
  };

  const dismiss = (id) => setItems(its => its.filter(i => i.id!==id));
  const filtered = filter==="all" ? items : items.filter(i=>i.sev===filter);
  const sevColor = (s) => s==="critical"?T.r2:s==="warning"?T.a2:T.b2;
  const sevBg    = (s) => s==="critical"?T.rL:s==="warning"?T.aL:T.bL;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Anomaly feed</h1>
          <p style={{ fontSize:12, color:T.ink3, margin:0 }}>{items.length} active alerts · AI-powered root cause analysis</p>
        </div>
        <div style={{ display:"flex", gap:6 }}>
          {["all","critical","warning","info"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${filter===f?sevColor(f):T.s2}`, background:filter===f?sevBg(f):"transparent", color:filter===f?sevColor(f):T.ink3, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit", textTransform:"capitalize" }}>{f}</button>
          ))}
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {filtered.length===0 && (
          <div style={{ textAlign:"center", padding:"48px 0", color:T.ink4 }}>
            <p style={{ fontSize:14, margin:"0 0 4px" }}>No {filter!=="all"?filter:""} alerts</p>
            <p style={{ fontSize:12, margin:0 }}>All campaigns are performing within expected ranges</p>
          </div>
        )}
        {filtered.map(item=>{
          const clr = sevColor(item.sev);
          const bg  = sevBg(item.sev);
          const platClr = SRC[item.platform]?.c || T.b2;
          return (
            <div key={item.id} style={{ background:T.canvas, border:`1px solid ${clr}`, borderRadius:10, padding:"14px 16px" }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <div style={{ width:36, height:36, borderRadius:9, background:bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <span style={{ fontSize:16, color:clr, fontWeight:800 }}>{item.sev==="critical"?"!":item.sev==="warning"?"↓":"↑"}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:4, background:bg, color:clr, textTransform:"uppercase" }}>{item.sev}</span>
                    <span style={{ fontSize:11, fontWeight:600, color:T.ink }}>{item.metric}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:clr }}>{item.delta}</span>
                    <span style={{ fontSize:10, color:T.ink4, marginLeft:"auto" }}>{item.time}</span>
                  </div>
                  <p style={{ fontSize:12, color:T.ink2, margin:"0 0 4px" }}>{item.msg}</p>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <div style={{ width:6, height:6, borderRadius:"50%", background:platClr }}/>
                    <span style={{ fontSize:11, color:T.ink4 }}>{item.campaign}</span>
                  </div>
                </div>
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  <button onClick={()=>explain(item.id)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                    {explaining===item.id&&!explanation[item.id] ? "Analyzing…" : item.explained ? "View AI analysis" : "Explain with AI"}
                  </button>
                  <button onClick={()=>dismiss(item.id)} style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink4, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Dismiss</button>
                </div>
              </div>

              {explaining===item.id && explanation[item.id] && (
                <div style={{ marginTop:12, padding:"12px 14px", background:T.bL, border:`1px solid ${T.bM}`, borderRadius:8 }}>
                  <p style={{ fontSize:11, fontWeight:700, color:T.b1, margin:"0 0 4px" }}>AI root cause analysis</p>
                  <p style={{ fontSize:12, color:T.b1, margin:0, lineHeight:1.6 }}>{explanation[item.id]}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Media Mix Model Page ─────────────────────────────────────────────────────
const MMM_BASE = [
  { id:"meta",     ch:"Meta Ads",   budget:32400, roas:2.8, cpm:11.4, ctr:1.9, color:"#1877F2" },
  { id:"google",   ch:"Google Ads", budget:28000, roas:3.9, cpm:8.2,  ctr:3.8, color:"#4285F4" },
  { id:"linkedin", ch:"LinkedIn",   budget:12000, roas:2.1, cpm:22.5, ctr:0.6, color:"#0A66C2" },
  { id:"tiktok",   ch:"TikTok",     budget:8500,  roas:1.8, cpm:6.1,  ctr:1.4, color:"#2DD4BF" },
  { id:"pinterest",ch:"Pinterest",  budget:3300,  roas:2.4, cpm:9.8,  ctr:0.5, color:"#E60023" },
];

const PLAN_OBJECTIVES = ["Brand awareness","Lead generation","E-commerce sales","App installs","Website traffic","Retargeting","Brand consideration"];
const PLAN_DURATIONS  = ["1 week","2 weeks","1 month","2 months","3 months","6 months","Custom"];
const COUNTRIES = [
  "Global","United States","United Kingdom","United Arab Emirates","Saudi Arabia",
  "Germany","France","Netherlands","Australia","Canada","India","Singapore",
  "Brazil","Mexico","South Africa","Egypt","Kuwait","Qatar","Bahrain","Japan",
];
const MMM_PLATFORMS_ALL = [
  { id:"meta",      label:"Meta Ads",   color:"#1877F2" },
  { id:"google",    label:"Google Ads", color:"#4285F4" },
  { id:"linkedin",  label:"LinkedIn",   color:"#0A66C2" },
  { id:"tiktok",    label:"TikTok",     color:"#2DD4BF" },
  { id:"pinterest", label:"Pinterest",  color:"#E60023" },
  { id:"snapchat",  label:"Snapchat",   color:"#FFFC00" },
  { id:"x",         label:"X / Twitter",color:"#000000" },
  { id:"youtube",   label:"YouTube",    color:"#FF0000" },
];

// Simulated PDF plan HTML for the print window
function buildPlanHTML(plan, budgets, revenues, blendedRoas, totalRev, total) {
  const rows = MMM_BASE.map((c,i) => `
    <tr>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;font-weight:500">${c.ch}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;text-align:right">$${Math.round(budgets[i]/1000)}k</td>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;text-align:right">${Math.round(budgets[i]/total*100)}%</td>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;text-align:right;color:#2563EB">${c.roas}×</td>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;text-align:right">$${c.cpm}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;text-align:right">${c.ctr}%</td>
      <td style="padding:10px 14px;border-bottom:1px solid #E2E8F0;text-align:right;color:#059669;font-weight:600">$${Math.round(revenues[i]/1000)}k</td>
    </tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>Media Plan — ${plan.name||"AdTech"}</title>
  <style>
    body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#0F172A;margin:0;padding:0;background:#fff}
    @media print{body{margin:0}}
    .cover{background:linear-gradient(135deg,#1D4ED8,#7C3AED);color:#fff;padding:48px 56px;min-height:200px}
    .cover h1{font-size:32px;font-weight:700;margin:0 0 8px}
    .cover p{font-size:14px;opacity:.8;margin:0 0 6px}
    .section{padding:32px 56px}
    .section h2{font-size:18px;font-weight:600;margin:0 0 16px;color:#0F172A;border-bottom:2px solid #2563EB;padding-bottom:8px}
    .kpi-row{display:flex;gap:16px;margin-bottom:24px}
    .kpi{flex:1;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:10px;padding:16px}
    .kpi-label{font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:#64748B;margin:0 0 6px}
    .kpi-value{font-size:26px;font-weight:700;margin:0}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th{padding:10px 14px;background:#F8FAFC;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:#64748B;font-weight:600;border-bottom:2px solid #E2E8F0}
    th:first-child{text-align:left}
    .bar-wrap{display:flex;height:20px;border-radius:6px;overflow:hidden;margin:12px 0}
    .note{background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:14px 18px;font-size:13px;color:#1D4ED8;margin-top:16px}
    .footer{background:#0F172A;color:rgba(255,255,255,.6);padding:20px 56px;font-size:11px;display:flex;justify-content:space-between}
  </style>
  </head><body>
  <div class="cover">
    <p style="font-size:12px;opacity:.6;margin:0 0 24px;text-transform:uppercase;letter-spacing:.1em">Media Plan</p>
    <h1>${plan.name||"Q2 Media Plan"}</h1>
    <p>Client: ${plan.client||"Demo Agency"} &nbsp;·&nbsp; Objective: ${plan.objective||"Lead generation"} &nbsp;·&nbsp; Duration: ${plan.duration||"1 month"}</p>
    <p>Prepared: ${new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</p>
  </div>

  <div class="section">
    <h2>Executive summary</h2>
    <div class="kpi-row">
      <div class="kpi"><p class="kpi-label">Total budget</p><p class="kpi-value" style="color:#2563EB">$${Math.round(total/1000)}k</p></div>
      <div class="kpi"><p class="kpi-label">Forecast revenue</p><p class="kpi-value" style="color:#059669">$${Math.round(totalRev/1000)}k</p></div>
      <div class="kpi"><p class="kpi-label">Blended ROAS</p><p class="kpi-value" style="color:${Number(blendedRoas)>=3?"#059669":Number(blendedRoas)>=2?"#D97706":"#DC2626"}">${blendedRoas}×</p></div>
      <div class="kpi"><p class="kpi-label">Channels</p><p class="kpi-value">${MMM_BASE.filter((_,i)=>budgets[i]>0).length}</p></div>
    </div>
    ${plan.notes ? `<div class="note">${plan.notes}</div>` : ""}
  </div>

  <div class="section" style="padding-top:0">
    <h2>Channel allocation</h2>
    <table>
      <thead><tr>
        <th style="text-align:left">Channel</th>
        <th>Budget</th><th>Share</th><th>ROAS</th><th>CPM</th><th>CTR</th><th>Rev. forecast</th>
      </tr></thead>
      <tbody>${rows}
      <tr style="font-weight:700;background:#F8FAFC">
        <td style="padding:10px 14px">Total</td>
        <td style="padding:10px 14px;text-align:right">$${Math.round(total/1000)}k</td>
        <td style="padding:10px 14px;text-align:right">100%</td>
        <td style="padding:10px 14px;text-align:right;color:#2563EB">${blendedRoas}×</td>
        <td style="padding:10px 14px;text-align:right">—</td>
        <td style="padding:10px 14px;text-align:right">—</td>
        <td style="padding:10px 14px;text-align:right;color:#059669">$${Math.round(totalRev/1000)}k</td>
      </tr></tbody>
    </table>

    <div class="bar-wrap">
      ${MMM_BASE.map((c,i)=>{ const pct=totalRev>0?revenues[i]/totalRev*100:0; return pct>0?`<div style="width:${pct.toFixed(1)}%;background:${c.color}"></div>`:""}).join("")}
    </div>
    <div style="display:flex;gap:16px;flex-wrap:wrap;font-size:12px">
      ${MMM_BASE.map((c,i)=>{ const pct=totalRev>0?Math.round(revenues[i]/totalRev*100):0; return pct>0?`<span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${c.color};margin-right:4px"></span>${c.ch} ${pct}%</span>`:""}).join("")}
    </div>
  </div>

  <div class="footer">
    <span>Prepared with AdTech Analytics Platform</span>
    <span>${plan.client||"Demo Agency"} · Confidential</span>
  </div>
  </body></html>`;
}

function MediaMixPage() {
  const [budgets, setBudgets]   = useState(MMM_BASE.map(c=>c.budget));
  const [tab, setTab]           = useState("model"); // "model" | "plan"
  const [plan, setPlan]         = useState({
    name:"Q2 Media Plan", client:"Demo Agency",
    objectives:[PLAN_OBJECTIVES[1]],
    platforms: MMM_BASE.map(c=>c.id),
    countries: ["United States","United Kingdom","United Arab Emirates"],
    duration:PLAN_DURATIONS[2], notes:"", recipients:"",
  });
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [shareLink, setShare]   = useState(null);
  const [copied, setCopied]     = useState(false);
  const [printing, setPrinting] = useState(false);

  const total       = budgets.reduce((a,b)=>a+b,0);
  const revenues    = MMM_BASE.map((c,i)=>budgets[i]*c.roas);
  const totalRev    = revenues.reduce((a,b)=>a+b,0);
  const blendedRoas = total>0 ? (totalRev/total).toFixed(2) : "0.00";
  const maxBudget   = 80000;

  const upd = (k,v) => setPlan(p=>({...p,[k]:v}));
  const toggleArr = (k,v) => setPlan(p=>({ ...p, [k]: p[k].includes(v) ? p[k].filter(x=>x!==v) : [...p[k],v] }));

  const sendPlan = () => {
    setSending(true);
    setTimeout(() => { setSending(false); setSent(true); setTimeout(()=>setSent(false),3000); }, 1400);
  };

  const generateLink = () => {
    const id = Math.random().toString(36).slice(2,8);
    setShare(`https://adtech.io/plan/${id}`);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(shareLink).catch(()=>{});
    setCopied(true); setTimeout(()=>setCopied(false),2000);
  };

  const downloadPDF = () => {
    setPrinting(true);
    const html = buildPlanHTML(plan, budgets, revenues, blendedRoas, totalRev, total);
    const win = window.open("","_blank","width=900,height=700");
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => { win.print(); setPrinting(false); }, 600);
    } else { setPrinting(false); }
  };

  const tabStyle = (t) => ({
    padding:"8px 20px", borderRadius:8, border:"none", fontSize:12, fontWeight:tab===t?600:400,
    background:tab===t?T.canvas:"transparent", color:tab===t?T.ink:T.ink3,
    cursor:"pointer", fontFamily:"inherit", boxShadow:tab===t?`0 1px 4px rgba(15,23,42,.08)`:"none",
    transition:"all .15s",
  });

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", background:T.bg }}>

      {/* Header */}
      <div style={{ padding:"18px 22px 0", flexShrink:0, background:T.canvas, borderBottom:`1px solid ${T.s2}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div>
            <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Media mix model</h1>
            <p style={{ fontSize:12, color:T.ink3, margin:0 }}>Model your budget allocation, then build and share a media plan</p>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {tab==="plan" && <>
              <button onClick={downloadPDF} disabled={printing}
                style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {printing ? "Opening…" : "Download PDF"}
              </button>
              {!shareLink
                ? <button onClick={generateLink}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    Get share link
                  </button>
                : <button onClick={copyLink}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:`1px solid ${T.gM}`, background:T.gL, color:T.g1, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">{copied?<polyline points="20 6 9 17 4 12"/>:<><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>}</svg>
                    {copied ? "Copied!" : shareLink.replace("https://","")}
                  </button>
              }
              <button onClick={sendPlan} disabled={sending||!plan.recipients}
                style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:7, border:"none", background:(!plan.recipients||sending)?T.s2:T.brand, color:(!plan.recipients||sending)?T.ink4:"#fff", fontSize:12, fontWeight:500, cursor:(!plan.recipients||sending)?"not-allowed":"pointer", fontFamily:"inherit" }}>
                {sending
                  ? <><span style={{ width:12, height:12, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"adtech-spin 1s linear infinite" }}/> Sending…</>
                  : sent ? "✓ Sent!" : <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg>
                    Send to client
                  </>
                }
              </button>
            </>}
          </div>
        </div>
        {/* Tabs */}
        <div style={{ display:"flex", background:T.s1, borderRadius:10, padding:3, gap:2, width:"fit-content", marginBottom:-1 }}>
          <button style={tabStyle("model")} onClick={()=>setTab("model")}>Model</button>
          <button style={tabStyle("plan")}  onClick={()=>setTab("plan")}>Media plan</button>
        </div>
      </div>

      {/* ═══ MODEL TAB ═══ */}
      {tab==="model" && (
        <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>
          {/* KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
            {[
              ["Total budget",     "$"+Math.round(total/1000)+"k",     T.b2],
              ["Forecast revenue", "$"+Math.round(totalRev/1000)+"k",  T.g2],
              ["Blended ROAS",     blendedRoas+"×",                     Number(blendedRoas)>=3?T.g2:Number(blendedRoas)>=2?T.a2:T.r2],
              ["Active channels",  String(budgets.filter(b=>b>0).length)+" / "+MMM_BASE.length, T.v2],
            ].map(([l,v,c])=>(
              <div key={l} style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:10, padding:"14px 16px" }}>
                <p style={{ fontSize:10, color:T.ink4, margin:"0 0 5px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                <p style={{ fontSize:22, fontWeight:700, color:c, margin:0 }}>{v}</p>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr", gap:14 }}>
            {/* Sliders */}
            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"18px 20px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>Channel budget allocation</p>
                <button onClick={()=>setBudgets(MMM_BASE.map(c=>c.budget))} style={{ padding:"4px 12px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink4, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Reset</button>
              </div>
              {MMM_BASE.map((c,i)=>{
                const pct = total>0?Math.round(budgets[i]/total*100):0;
                return (
                  <div key={c.ch} style={{ marginBottom:18 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:c.color }}/>
                        <span style={{ fontSize:12, fontWeight:500, color:T.ink }}>{c.ch}</span>
                        <span style={{ fontSize:10, color:T.ink4 }}>{pct}%</span>
                      </div>
                      <div style={{ display:"flex", gap:14 }}>
                        <span style={{ fontSize:11, color:T.ink4 }}>$<strong style={{ color:T.ink }}>{Math.round(budgets[i]/1000)}k</strong></span>
                        <span style={{ fontSize:11, color:T.ink4 }}>→ <strong style={{ color:T.g2 }}>${Math.round(revenues[i]/1000)}k</strong></span>
                        <span style={{ fontSize:11, fontWeight:600, color:c.roas>=3?T.g2:c.roas>=2?T.a2:T.r2 }}>{c.roas}× ROAS</span>
                      </div>
                    </div>
                    <div style={{ position:"relative", height:16 }}>
                      <div style={{ position:"absolute", top:4, left:0, right:0, height:8, background:T.s1, borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${Math.min(budgets[i]/maxBudget*100,100)}%`, background:c.color, borderRadius:4, transition:"width .1s" }}/>
                      </div>
                      <input type="range" min={0} max={maxBudget} step={500} value={budgets[i]}
                        onChange={e=>setBudgets(bs=>bs.map((b,j)=>j===i?Number(e.target.value):b))}
                        style={{ position:"absolute", top:0, left:0, width:"100%", height:16, opacity:0, cursor:"pointer", margin:0 }}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Revenue breakdown + metrics */}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"16px 18px" }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 12px" }}>Revenue contribution</p>
                <div style={{ display:"flex", height:28, borderRadius:6, overflow:"hidden", marginBottom:10 }}>
                  {MMM_BASE.map((c,i)=>{ const pct=totalRev>0?revenues[i]/totalRev*100:0; return pct>0&&(
                    <div key={c.ch} style={{ width:`${pct}%`, background:c.color, transition:"width .3s" }}/>
                  );})}
                </div>
                {MMM_BASE.filter((_,i)=>budgets[i]>0).map((c,ci)=>{ const i=MMM_BASE.indexOf(c); const pct=totalRev>0?Math.round(revenues[i]/totalRev*100):0; return (
                  <div key={c.ch} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 0", borderBottom:`1px solid ${T.s2}` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:c.color }}/>
                      <span style={{ fontSize:11, color:T.ink }}>{c.ch}</span>
                    </div>
                    <span style={{ fontSize:11, fontWeight:600, color:T.ink }}>{pct}% · ${Math.round(revenues[i]/1000)}k</span>
                  </div>
                );})}
              </div>

              {/* Benchmark table */}
              <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"14px 18px" }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 10px" }}>Channel benchmarks</p>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                  <thead>
                    <tr style={{ borderBottom:`1px solid ${T.s2}` }}>
                      {["Channel","CPM","CTR","ROAS"].map(h=><th key={h} style={{ padding:"5px 6px", textAlign:h==="Channel"?"left":"right", fontSize:9, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em" }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {MMM_BASE.map((c,i)=>(
                      <tr key={c.ch} style={{ borderBottom:`1px solid ${T.s2}`, opacity:budgets[i]===0?.4:1 }}>
                        <td style={{ padding:"6px 6px" }}><div style={{ display:"flex", alignItems:"center", gap:6 }}><div style={{ width:7,height:7,borderRadius:"50%",background:c.color }}/><span style={{ color:T.ink }}>{c.ch}</span></div></td>
                        <td style={{ padding:"6px 6px", textAlign:"right", color:T.ink3 }}>${c.cpm}</td>
                        <td style={{ padding:"6px 6px", textAlign:"right", color:T.ink3 }}>{c.ctr}%</td>
                        <td style={{ padding:"6px 6px", textAlign:"right", fontWeight:600, color:c.roas>=3?T.g2:c.roas>=2?T.a2:T.r2 }}>{c.roas}×</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button onClick={()=>setTab("plan")} style={{ padding:"10px 0", borderRadius:8, border:"none", background:T.brand, color:"#fff", fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                Build media plan →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PLAN TAB ═══ */}
      {tab==="plan" && (
        <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"340px 1fr", gap:18 }}>

            {/* Left: plan settings */}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"16px 18px" }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 14px" }}>Plan details</p>

                {[["Plan name","name","e.g. Q2 2026 Lead Gen"],["Client","client","e.g. Demo Agency"]].map(([lbl,key,ph])=>(
                  <div key={key} style={{ marginBottom:12 }}>
                    <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 }}>{lbl}</label>
                    <input value={plan[key]} onChange={e=>upd(key,e.target.value)} placeholder={ph}
                      style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}/>
                  </div>
                ))}

                {/* Objectives — multi pill */}
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:6 }}>
                    Objectives <span style={{ color:T.ink4, fontWeight:400, fontSize:9 }}>select multiple</span>
                  </label>
                  <div>
                    {PLAN_OBJECTIVES.map(o => {
                      const on = plan.objectives.includes(o);
                      return (
                        <button key={o} onClick={()=>toggleArr("objectives",o)} style={{
                          display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:20, margin:"0 5px 5px 0",
                          border:`1.5px solid ${on?T.b2:T.s2}`, background:on?T.bL:T.canvas,
                          color:on?T.b1:T.ink3, fontSize:11, fontWeight:on?600:400, cursor:"pointer", fontFamily:"inherit",
                        }}>
                          {on && <svg width="8" height="7" viewBox="0 0 9 7" fill="none" stroke={T.b1} strokeWidth="2.5" strokeLinecap="round"><path d="M1 3.5l2.5 2.5L8 1"/></svg>}
                          {o}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Platforms — multi pill with colour */}
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:6 }}>
                    Platforms <span style={{ color:T.ink4, fontWeight:400, fontSize:9 }}>select multiple</span>
                  </label>
                  <div>
                    {MMM_PLATFORMS_ALL.map(p => {
                      const on = plan.platforms.includes(p.id);
                      return (
                        <button key={p.id} onClick={()=>toggleArr("platforms",p.id)} style={{
                          display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:20, margin:"0 5px 5px 0",
                          border:`1.5px solid ${on?p.color:T.s2}`, background:on?p.color+"22":T.canvas,
                          color:on?p.color:T.ink3, fontSize:11, fontWeight:on?600:400, cursor:"pointer", fontFamily:"inherit",
                        }}>
                          <span style={{ width:7, height:7, borderRadius:"50%", background:p.color, display:"inline-block", flexShrink:0 }}/>
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Countries — multi pill */}
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:6 }}>
                    Target countries <span style={{ color:T.ink4, fontWeight:400, fontSize:9 }}>select multiple</span>
                  </label>
                  <div style={{ maxHeight:120, overflowY:"auto", padding:"2px 0" }}>
                    {COUNTRIES.map(c => {
                      const on = plan.countries.includes(c);
                      return (
                        <button key={c} onClick={()=>toggleArr("countries",c)} style={{
                          display:"inline-flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:20, margin:"0 5px 5px 0",
                          border:`1.5px solid ${on?T.b2:T.s2}`, background:on?T.bL:T.canvas,
                          color:on?T.b1:T.ink3, fontSize:11, fontWeight:on?600:400, cursor:"pointer", fontFamily:"inherit",
                        }}>
                          {on && <svg width="8" height="7" viewBox="0 0 9 7" fill="none" stroke={T.b1} strokeWidth="2.5" strokeLinecap="round"><path d="M1 3.5l2.5 2.5L8 1"/></svg>}
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Duration */}
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 }}>Duration</label>
                  <select value={plan.duration} onChange={e=>upd("duration",e.target.value)} style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", fontFamily:"inherit" }}>
                    {PLAN_DURATIONS.map(d=><option key={d}>{d}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 }}>Strategy notes</label>
                  <textarea rows={3} value={plan.notes} onChange={e=>upd("notes",e.target.value)}
                    placeholder="e.g. Focus on LinkedIn for enterprise leads in UAE + KSA. Use Meta for retargeting. Pause Pinterest if CTR drops below 0.3%."
                    style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit", resize:"vertical", lineHeight:1.5 }}/>
                </div>
              </div>

              {/* Send form */}
              <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"16px 18px" }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 12px" }}>Send to client</p>
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 }}>Recipients</label>
                  <input value={plan.recipients} onChange={e=>upd("recipients",e.target.value)} placeholder="client@example.com, cmo@example.com"
                    style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}/>
                </div>

                {/* Share link section */}
                <div style={{ padding:"10px 12px", background:T.s1, borderRadius:8, marginBottom:10 }}>
                  <p style={{ fontSize:11, fontWeight:600, color:T.ink, margin:"0 0 6px" }}>Or share as a link</p>
                  {!shareLink
                    ? <button onClick={generateLink} style={{ padding:"6px 14px", borderRadius:6, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Generate link</button>
                    : <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                        <span style={{ fontSize:11, fontFamily:"monospace", color:T.b2, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{shareLink}</span>
                        <button onClick={copyLink} style={{ padding:"4px 10px", borderRadius:5, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>
                          {copied?"✓ Copied":"Copy"}
                        </button>
                      </div>
                  }
                </div>

                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={downloadPDF} style={{ flex:1, padding:"8px 0", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink2, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                    ↓ PDF
                  </button>
                  <button onClick={sendPlan} disabled={sending||!plan.recipients}
                    style={{ flex:2, padding:"8px 0", borderRadius:7, border:"none", background:(!plan.recipients||sending)?T.s2:T.brand, color:(!plan.recipients||sending)?T.ink4:"#fff", fontSize:12, fontWeight:500, cursor:(!plan.recipients||sending)?"not-allowed":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    {sending ? "Sending…" : sent ? "✓ Sent!" : "Send plan"}
                  </button>
                </div>
              </div>
            </div>

            {/* Right: plan preview */}
            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden" }}>
              {/* Cover */}
              <div style={{ background:`linear-gradient(135deg,${T.b1},${T.v2})`, padding:"28px 28px 24px", color:"#fff" }}>
                <p style={{ fontSize:10, opacity:.65, margin:"0 0 12px", textTransform:"uppercase", letterSpacing:".12em" }}>Media Plan</p>
                <p style={{ fontSize:22, fontWeight:700, margin:"0 0 6px" }}>{plan.name||"Untitled Plan"}</p>
                <p style={{ fontSize:12, opacity:.8, margin:"0 0 3px" }}>Client: {plan.client||"—"} &nbsp;·&nbsp; {plan.objectives.join(", ")||"—"}</p>
                <p style={{ fontSize:12, opacity:.75, margin:"0 0 3px" }}>Platforms: {MMM_PLATFORMS_ALL.filter(p=>plan.platforms.includes(p.id)).map(p=>p.label).join(", ")||"—"}</p>
                <p style={{ fontSize:12, opacity:.75, margin:"0 0 3px" }}>Countries: {plan.countries.slice(0,4).join(", ")}{plan.countries.length>4?" · +"+(plan.countries.length-4)+" more":""}</p>
                <p style={{ fontSize:12, opacity:.8, margin:0 }}>{plan.duration} &nbsp;·&nbsp; {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</p>
              </div>

              {/* Summary KPIs */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:0, borderBottom:`1px solid ${T.s2}` }}>
                {[
                  ["Total budget","$"+Math.round(total/1000)+"k",T.b2],
                  ["Forecast rev","$"+Math.round(totalRev/1000)+"k",T.g2],
                  ["Blended ROAS",blendedRoas+"×",Number(blendedRoas)>=3?T.g2:Number(blendedRoas)>=2?T.a2:T.r2],
                  ["Channels",String(budgets.filter(b=>b>0).length)+" active",T.v2],
                ].map(([l,v,c],ii)=>(
                  <div key={l} style={{ padding:"14px 16px", borderRight:ii<3?`1px solid ${T.s2}`:"none" }}>
                    <p style={{ fontSize:10, color:T.ink4, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                    <p style={{ fontSize:18, fontWeight:700, color:c, margin:0 }}>{v}</p>
                  </div>
                ))}
              </div>

              {/* Notes */}
              {plan.notes && (
                <div style={{ padding:"14px 20px", background:T.bL, borderBottom:`1px solid ${T.bM}` }}>
                  <p style={{ fontSize:11, fontWeight:600, color:T.b1, margin:"0 0 3px" }}>Strategy notes</p>
                  <p style={{ fontSize:12, color:T.b1, margin:0, lineHeight:1.6 }}>{plan.notes}</p>
                </div>
              )}

              {/* Channel table */}
              <div style={{ padding:"16px 20px" }}>
                <p style={{ fontSize:11, fontWeight:600, color:T.ink, margin:"0 0 12px", textTransform:"uppercase", letterSpacing:".06em" }}>Channel allocation</p>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead>
                    <tr style={{ borderBottom:`2px solid ${T.s2}` }}>
                      {["Channel","Budget","Share","ROAS","CPM","CTR","Rev. forecast"].map(h=>(
                        <th key={h} style={{ padding:"8px 10px", textAlign:h==="Channel"?"left":"right", fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MMM_BASE.map((c,i)=>budgets[i]>0&&(
                      <tr key={c.ch} style={{ borderBottom:`1px solid ${T.s2}` }}>
                        <td style={{ padding:"10px 10px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                            <div style={{ width:8, height:8, borderRadius:"50%", background:c.color }}/>
                            <span style={{ fontWeight:500, color:T.ink }}>{c.ch}</span>
                          </div>
                        </td>
                        <td style={{ padding:"10px 10px", textAlign:"right", color:T.ink }}>${Math.round(budgets[i]/1000)}k</td>
                        <td style={{ padding:"10px 10px", textAlign:"right", color:T.ink3 }}>{Math.round(budgets[i]/total*100)}%</td>
                        <td style={{ padding:"10px 10px", textAlign:"right", fontWeight:600, color:c.roas>=3?T.g2:c.roas>=2?T.a2:T.r2 }}>{c.roas}×</td>
                        <td style={{ padding:"10px 10px", textAlign:"right", color:T.ink3 }}>${c.cpm}</td>
                        <td style={{ padding:"10px 10px", textAlign:"right", color:T.ink3 }}>{c.ctr}%</td>
                        <td style={{ padding:"10px 10px", textAlign:"right", fontWeight:600, color:T.g2 }}>${Math.round(revenues[i]/1000)}k</td>
                      </tr>
                    ))}
                    <tr style={{ background:T.s1 }}>
                      <td style={{ padding:"10px 10px", fontWeight:600, color:T.ink }}>Total</td>
                      <td style={{ padding:"10px 10px", textAlign:"right", fontWeight:700, color:T.b2 }}>${Math.round(total/1000)}k</td>
                      <td style={{ padding:"10px 10px", textAlign:"right", color:T.ink3 }}>100%</td>
                      <td style={{ padding:"10px 10px", textAlign:"right", fontWeight:700, color:Number(blendedRoas)>=3?T.g2:T.a2 }}>{blendedRoas}×</td>
                      <td colSpan={2} style={{ padding:"10px 10px", textAlign:"right", color:T.ink4 }}>—</td>
                      <td style={{ padding:"10px 10px", textAlign:"right", fontWeight:700, color:T.g2 }}>${Math.round(totalRev/1000)}k</td>
                    </tr>
                  </tbody>
                </table>

                {/* Revenue bar */}
                <div style={{ marginTop:16 }}>
                  <p style={{ fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", margin:"0 0 8px" }}>Revenue contribution</p>
                  <div style={{ display:"flex", height:18, borderRadius:5, overflow:"hidden", marginBottom:8 }}>
                    {MMM_BASE.map((c,i)=>{ const pct=totalRev>0?revenues[i]/totalRev*100:0; return pct>0&&budgets[i]>0&&(
                      <div key={c.ch} style={{ width:`${pct}%`, background:c.color, transition:"width .3s" }}/>
                    );})}
                  </div>
                  <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                    {MMM_BASE.map((c,i)=>{ const pct=totalRev>0?Math.round(revenues[i]/totalRev*100):0; return budgets[i]>0&&(
                      <div key={c.ch} style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <div style={{ width:7, height:7, borderRadius:"50%", background:c.color }}/>
                        <span style={{ fontSize:11, color:T.ink4 }}>{c.ch} <strong style={{ color:T.ink }}>{pct}%</strong></span>
                      </div>
                    );})}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Creative Fatigue Scanner ─────────────────────────────────────────────────
const FATIGUE_ADS = [
  { id:"f1", title:"Summer Sale — Hero Video",  platform:"meta",     ctr_now:0.8,  ctr_peak:2.1, freq:6.8,  days:18, status:"fatigued",  impressions:3200000 },
  { id:"f2", title:"Lead Gen — Enterprise",     platform:"linkedin",  ctr_now:0.4,  ctr_peak:0.9, freq:4.2,  days:14, status:"warning",   impressions:820000  },
  { id:"f3", title:"Brand Keywords Campaign",   platform:"google_ads",ctr_now:3.8,  ctr_peak:4.1, freq:1.0,  days:7,  status:"healthy",   impressions:640000  },
  { id:"f4", title:"Retargeting Carousel 30d",  platform:"meta",     ctr_now:0.6,  ctr_peak:2.4, freq:9.2,  days:28, status:"fatigued",  impressions:1800000 },
  { id:"f5", title:"TikTok UGC Dance Series",   platform:"tiktok",   ctr_now:1.4,  ctr_peak:1.6, freq:2.1,  days:5,  status:"healthy",   impressions:5100000 },
  { id:"f6", title:"Pinterest Shopping",        platform:"pinterest", ctr_now:0.5,  ctr_peak:1.2, freq:3.8,  days:12, status:"warning",   impressions:290000  },
];

function FatiguePage({ setPage }) {
  const [ads, setAds] = useState(FATIGUE_ADS);
  const [pausing, setPausing] = useState(null);

  const pauseAd = (id) => {
    setPausing(id);
    setTimeout(() => { setAds(as => as.map(a => a.id===id ? {...a, status:"paused"} : a)); setPausing(null); }, 1000);
  };

  const statusColor = (s) => s==="fatigued"?T.r2:s==="warning"?T.a2:s==="paused"?T.ink4:T.g2;
  const statusBg    = (s) => s==="fatigued"?T.rL:s==="warning"?T.aL:s==="paused"?T.s1:T.gL;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Creative fatigue scanner</h1>
          <p style={{ fontSize:12, color:T.ink3, margin:0 }}>CTR decay and frequency analysis across all active creatives</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <span style={{ padding:"5px 12px", borderRadius:6, background:T.rL, color:T.r1, fontSize:11, fontWeight:600 }}>{ads.filter(a=>a.status==="fatigued").length} fatigued</span>
          <span style={{ padding:"5px 12px", borderRadius:6, background:T.aL, color:T.a1, fontSize:11, fontWeight:600 }}>{ads.filter(a=>a.status==="warning").length} at risk</span>
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {ads.map(ad => {
          const decay = Math.round((1 - ad.ctr_now/ad.ctr_peak)*100);
          const clr   = statusColor(ad.status);
          const bg    = statusBg(ad.status);
          const platClr = SRC[ad.platform]?.c || T.b2;
          return (
            <div key={ad.id} style={{ background:T.canvas, border:`1px solid ${ad.status==="fatigued"||ad.status==="warning"?clr:T.s2}`, borderRadius:10, padding:"14px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:platClr, flexShrink:0 }}/>
                <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0, flex:1 }}>{ad.title}</p>
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, background:bg, color:clr, textTransform:"capitalize" }}>{ad.status}</span>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:10 }}>
                {[["Current CTR",ad.ctr_now+"%",decay>30?T.r2:T.ink],["Peak CTR",ad.ctr_peak+"%",T.ink],["CTR decay",decay+"%",decay>30?T.r2:decay>15?T.a2:T.g2],["Frequency",ad.freq+"×",ad.freq>7?T.r2:ad.freq>4?T.a2:T.g2]].map(([l,v,c])=>(
                  <div key={l} style={{ textAlign:"center", padding:"8px", background:T.s1, borderRadius:7 }}>
                    <p style={{ fontSize:10, color:T.ink4, margin:"0 0 2px" }}>{l}</p>
                    <p style={{ fontSize:15, fontWeight:700, color:c, margin:0 }}>{v}</p>
                  </div>
                ))}
              </div>

              {/* CTR decay bar */}
              <div style={{ height:6, background:T.s1, borderRadius:3, overflow:"hidden", marginBottom:10 }}>
                <div style={{ height:"100%", width:`${Math.min(decay,100)}%`, background:decay>30?T.r2:decay>15?T.a2:T.g2, borderRadius:3 }}/>
              </div>

              {(ad.status==="fatigued"||ad.status==="warning") && ad.status!=="paused" && (
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>{ setPage("create"); }} style={{ padding:"5px 14px", borderRadius:6, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                    Refresh creative →
                  </button>
                  <button onClick={()=>pauseAd(ad.id)} style={{ padding:"5px 14px", borderRadius:6, border:`1px solid ${T.rM||T.s2}`, background:T.rL, color:T.r1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                    {pausing===ad.id ? "Pausing…" : "Auto-pause"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── A/B Test Manager ─────────────────────────────────────────────────────────
const AB_TESTS = [
  { id:"ab1", name:"Hero Video vs. Static Image", campaign:"Summer Sale", platform:"meta", status:"running", days_left:4,
    a:{ label:"Hero Video",    impr:180000, ctr:2.1, conv:342, cpa:48.2, conf:72 },
    b:{ label:"Static Image",  impr:178000, ctr:1.4, conv:198, cpa:71.4, conf:72 },
  },
  { id:"ab2", name:"CTA: Learn More vs. Sign Up", campaign:"Lead Gen — Enterprise", platform:"linkedin", status:"running", days_left:2,
    a:{ label:"Learn more",    impr:42000,  ctr:0.6, conv:88,  cpa:94.1, conf:88 },
    b:{ label:"Sign up",       impr:41800,  ctr:0.9, conv:127, cpa:65.3, conf:88 },
  },
  { id:"ab3", name:"Audience: Broad vs. LAL 2%",  campaign:"Brand Keywords", platform:"google_ads", status:"completed", days_left:0,
    a:{ label:"Broad match",   impr:92000,  ctr:3.8, conv:210, cpa:31.0, conf:96 },
    b:{ label:"LAL 2%",        impr:88000,  ctr:4.4, conv:267, cpa:24.2, conf:96 },
    winner:"b",
  },
];

function ABTestPage() {
  const [tests, setTests] = useState(AB_TESTS);
  const [killing, setKilling] = useState(null);

  const killLoser = (id, loser) => {
    setKilling(id);
    setTimeout(() => {
      setTests(ts => ts.map(t => t.id===id ? {...t, status:"completed", winner:loser==="a"?"b":"a"} : t));
      setKilling(null);
    }, 1200);
  };

  const winnerStyle = { padding:"2px 8px", borderRadius:4, background:T.gL, color:T.g1, fontSize:10, fontWeight:700 };
  const loserStyle  = { padding:"2px 8px", borderRadius:4, background:T.rL, color:T.r1, fontSize:10, fontWeight:700 };

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>A/B test manager</h1>
        <p style={{ fontSize:12, color:T.ink3, margin:0 }}>Side-by-side creative and targeting experiments with statistical significance</p>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {tests.map(test=>{
          const winner = test.winner;
          const aWins  = winner==="a";
          const bWins  = winner==="b";
          const bBetter = test.b.cpa < test.a.cpa;
          const platClr = SRC[test.platform]?.c || T.b2;

          return (
            <div key={test.id} style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden" }}>
              {/* Header */}
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.s2}`, display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:platClr }}/>
                <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:0, flex:1 }}>{test.name}</p>
                <span style={{ fontSize:10, color:T.ink4 }}>{test.campaign}</span>
                <span style={{ padding:"3px 9px", borderRadius:5, fontSize:10, fontWeight:700, background:test.status==="running"?T.bL:T.gL, color:test.status==="running"?T.b1:T.g1 }}>
                  {test.status==="running" ? `${test.days_left}d left` : "Completed"}
                </span>
                <div style={{ width:8, height:8, borderRadius:4, background:T.s2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${test.a.conf}%`, background:T.g2 }}/>
                </div>
                <span style={{ fontSize:10, color:T.g1, fontWeight:600 }}>{test.a.conf}% confidence</span>
              </div>

              {/* A vs B */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                {["a","b"].map(v=>{
                  const variant = test[v];
                  const isW = winner===v;
                  const isL = winner && winner!==v;
                  return (
                    <div key={v} style={{ padding:"16px", borderRight:v==="a"?`1px solid ${T.s2}`:"none", background:isW?T.gL:isL?T.rL:"transparent" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                        <div style={{ width:22, height:22, borderRadius:6, background:isW?T.g2:isL?T.r2:T.b2, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff" }}>{v.toUpperCase()}</div>
                        <span style={{ fontSize:12, fontWeight:600, color:T.ink }}>{variant.label}</span>
                        {isW && <span style={winnerStyle}>Winner</span>}
                        {isL && <span style={loserStyle}>Loser</span>}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                        {[["Impressions",variant.impr.toLocaleString(),""],["CTR",variant.ctr+"%",""],["Conversions",variant.conv,""],["CPA","$"+variant.cpa,v==="a"?(test.b.cpa<test.a.cpa?"↑":"↓"):(test.b.cpa<test.a.cpa?"↓":"↑")]].map(([l,val,arrow])=>(
                          <div key={l} style={{ padding:"8px", background:"rgba(255,255,255,.6)", borderRadius:7 }}>
                            <p style={{ fontSize:10, color:T.ink4, margin:"0 0 2px" }}>{l}</p>
                            <p style={{ fontSize:14, fontWeight:700, color:l==="CPA"?(v==="b"&&bBetter?T.g2:v==="a"&&!bBetter?T.g2:T.r2):T.ink, margin:0 }}>{val}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              {test.status==="running" && (
                <div style={{ padding:"10px 16px", borderTop:`1px solid ${T.s2}`, display:"flex", gap:8, background:T.s1 }}>
                  <button onClick={()=>killLoser(test.id, bBetter?"a":"b")} style={{ padding:"6px 14px", borderRadius:6, border:"none", background:T.r2, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                    {killing===test.id ? "Pausing…" : `Kill ${bBetter?"A":"B"} (weaker variant)`}
                  </button>
                  <span style={{ fontSize:11, color:T.ink4, alignSelf:"center" }}>Declare winner early at {test.a.conf}% confidence</span>
                </div>
              )}
              {test.status==="completed" && winner && (
                <div style={{ padding:"10px 16px", borderTop:`1px solid ${T.gM}`, background:T.gL, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, fontWeight:600, color:T.g1 }}>✓ Variant {winner.toUpperCase()} won — {winner==="b"&&bBetter?"CPA improved by "+Math.round((1-test.b.cpa/test.a.cpa)*100)+"%":"CTR improved by "+Math.round((test[winner].ctr/test[winner==="b"?"a":"b"].ctr-1)*100)+"%"}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Conversion Funnel Page ───────────────────────────────────────────────────
const FUNNEL_STAGES = [
  { id:"impr",    label:"Impressions",   value:7400000, prev:null,       color:T.b3  },
  { id:"click",   label:"Clicks",        value:128760,  prev:7400000,    color:T.b2  },
  { id:"landing", label:"Landing page",  value:98420,   prev:128760,     color:T.b1  },
  { id:"engage",  label:"Engaged",       value:31940,   prev:98420,      color:T.v2  },
  { id:"lead",    label:"Lead captured", value:12480,   prev:31940,      color:T.v1  },
  { id:"qual",    label:"Qualified",     value:3840,    prev:12480,      color:T.g2  },
  { id:"closed",  label:"Closed / won",  value:892,     prev:3840,       color:T.g1  },
];

function FunnelPage() {
  const [selected, setSelected] = useState(null);
  const maxW = 540;

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Conversion funnel</h1>
        <p style={{ fontSize:12, color:T.ink3, margin:0 }}>Full-path attribution from impression to closed revenue</p>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:16 }}>
        {/* Funnel viz */}
        <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"20px" }}>
          {FUNNEL_STAGES.map((stage, i) => {
            const width = Math.round(stage.value / FUNNEL_STAGES[0].value * maxW);
            const dropPct = stage.prev ? Math.round((1 - stage.value/stage.prev)*100) : null;
            const isSel = selected===stage.id;
            return (
              <div key={stage.id} style={{ marginBottom:4 }}>
                {dropPct !== null && (
                  <div style={{ fontSize:10, color:T.ink4, textAlign:"center", padding:"2px 0", marginBottom:2 }}>
                    ↓ {dropPct}% drop-off
                  </div>
                )}
                <div style={{ display:"flex", alignItems:"center", gap:12, cursor:"pointer" }} onClick={()=>setSelected(isSel?null:stage.id)}>
                  <div style={{ width:maxW, display:"flex", justifyContent:"center" }}>
                    <div style={{ width:width, height:36, borderRadius:6, background:isSel?stage.color:stage.color+"99", display:"flex", alignItems:"center", justifyContent:"center", transition:"all .2s", border:isSel?`2px solid ${stage.color}`:"2px solid transparent" }}>
                      <span style={{ fontSize:11, fontWeight:700, color:"#fff", whiteSpace:"nowrap" }}>{stage.label}</span>
                    </div>
                  </div>
                  <div style={{ minWidth:120 }}>
                    <p style={{ fontSize:13, fontWeight:700, color:T.ink, margin:0 }}>{stage.value >= 1000000 ? (stage.value/1000000).toFixed(1)+"M" : stage.value >= 1000 ? (stage.value/1000).toFixed(1)+"k" : stage.value}</p>
                    {dropPct !== null && <p style={{ fontSize:10, color:dropPct>60?T.r2:dropPct>30?T.a2:T.g2, margin:0, fontWeight:600 }}>{100-dropPct}% converted</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stage detail */}
        <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"18px" }}>
          {selected ? (() => {
            const stage = FUNNEL_STAGES.find(s=>s.id===selected);
            const dropPct = stage.prev ? Math.round((1 - stage.value/stage.prev)*100) : 0;
            const campaignBreakdown = [
              { name:"Lead Gen — Enterprise",  share:38, platform:"linkedin" },
              { name:"Summer Sale — Hero",      share:27, platform:"meta"     },
              { name:"Brand Keywords",          share:21, platform:"google_ads"},
              { name:"Retargeting Carousel",    share:14, platform:"meta"     },
            ];
            return (
              <div>
                <div style={{ width:32, height:32, borderRadius:8, background:stage.color, marginBottom:12 }}/>
                <p style={{ fontSize:15, fontWeight:600, color:T.ink, margin:"0 0 4px" }}>{stage.label}</p>
                <p style={{ fontSize:22, fontWeight:700, color:stage.color, margin:"0 0 16px" }}>
                  {stage.value >= 1000000 ? (stage.value/1000000).toFixed(2)+"M" : stage.value >= 1000 ? Math.round(stage.value/1000)+"k" : stage.value}
                </p>
                {stage.prev && <p style={{ fontSize:12, color:dropPct>50?T.r2:T.ink3, margin:"0 0 16px" }}>{dropPct}% dropped off from previous stage</p>}
                <p style={{ fontSize:11, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".07em", margin:"0 0 10px" }}>By campaign</p>
                {campaignBreakdown.map(c=>(
                  <div key={c.name} style={{ marginBottom:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:11, color:T.ink }}>{c.name}</span>
                      <span style={{ fontSize:11, fontWeight:600, color:T.ink }}>{c.share}%</span>
                    </div>
                    <div style={{ height:4, background:T.s1, borderRadius:2, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${c.share}%`, background:SRC[c.platform]?.c||T.b2, borderRadius:2 }}/>
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", gap:10, color:T.ink4 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 3l18 18M3 8l6 6M9 21V9"/></svg>
              <p style={{ fontSize:12, textAlign:"center", margin:0 }}>Click a funnel stage to see campaign breakdown</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary stats row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginTop:14 }}>
        {[
          ["Overall CVR","0.012%","Impression to closed"],
          ["Lead close rate","7.1%","Qualified to closed"],
          ["Avg cost per lead","$6.74","Based on $84.2k spend"],
          ["Revenue generated","$247k","From 892 closed deals"],
        ].map(([v,sub,note])=>(
          <div key={v} style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:10, padding:"12px 14px" }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.ink, margin:"0 0 2px" }}>{v}</p>
            <p style={{ fontSize:11, color:T.b2, fontWeight:600, margin:"0 0 2px" }}>{sub}</p>
            <p style={{ fontSize:10, color:T.ink4, margin:0 }}>{note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Client Portal Page ───────────────────────────────────────────────────────
function ClientPortalPage({ activeClient }) {
  const [links, setLinks]     = useState([
    { id:"l1", client:"Demo Agency",       scope:"dashboard,reports", expires:"Apr 22, 2026", views:14, created:"Mar 22", active:true  },
    { id:"l2", client:"TechCorp Inc.",     scope:"reports",           expires:"Mar 30, 2026", views:3,  created:"Mar 15", active:true  },
    { id:"l3", client:"Nordic Retail",     scope:"dashboard",         expires:"Mar 10, 2026", views:28, created:"Feb 10", active:false },
  ]);
  const [creating, setCreating] = useState(false);
  const [form, setForm]         = useState({ scope:["dashboard"], expiry:"30", password:"" });
  const [copied, setCopied]     = useState(null);

  const toggleScope = (s) => setForm(f => ({ ...f, scope: f.scope.includes(s) ? f.scope.filter(x=>x!==s) : [...f.scope, s] }));

  const createLink = () => {
    const exp = new Date(); exp.setDate(exp.getDate()+Number(form.expiry));
    const newLink = {
      id: "l"+Date.now(), client: activeClient?.name||"Demo Agency",
      scope: form.scope.join(", "), expires: exp.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
      views:0, created:"Mar 23", active:true,
    };
    setLinks(ls => [newLink, ...ls]);
    setCreating(false);
  };

  const copyLink = (id) => {
    navigator.clipboard?.writeText(`https://adtech.io/share/${id}?token=tok_${id}`).catch(()=>{});
    setCopied(id); setTimeout(()=>setCopied(null),2000);
  };

  const revokeLink = (id) => setLinks(ls => ls.map(l => l.id===id ? {...l, active:false} : l));

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"18px 22px", background:T.bg }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Client portal</h1>
          <p style={{ fontSize:12, color:T.ink3, margin:0 }}>Generate read-only shareable links for clients — no login required</p>
        </div>
        <button onClick={()=>setCreating(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          New share link
        </button>
      </div>

      {creating && (
        <div style={{ background:T.canvas, border:`1px solid ${T.bM}`, borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, color:T.ink, margin:"0 0 14px" }}>Create share link for {activeClient?.name||"Demo Agency"}</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
            <div>
              <p style={{ fontSize:11, color:T.ink4, margin:"0 0 8px", textTransform:"uppercase", letterSpacing:".06em" }}>Include pages</p>
              {["dashboard","reports","leads","organic-report"].map(s=>(
                <label key={s} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, cursor:"pointer" }}>
                  <input type="checkbox" checked={form.scope.includes(s)} onChange={()=>toggleScope(s)} style={{ accentColor:T.brand }}/>
                  <span style={{ fontSize:12, color:T.ink, textTransform:"capitalize" }}>{s.replace("-"," ")}</span>
                </label>
              ))}
            </div>
            <div>
              <p style={{ fontSize:11, color:T.ink4, margin:"0 0 8px", textTransform:"uppercase", letterSpacing:".06em" }}>Expires in</p>
              {[["7","7 days"],["14","14 days"],["30","30 days"],["90","90 days"]].map(([v,l])=>(
                <label key={v} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, cursor:"pointer" }}>
                  <input type="radio" name="expiry" value={v} checked={form.expiry===v} onChange={()=>setForm(f=>({...f,expiry:v}))} style={{ accentColor:T.brand }}/>
                  <span style={{ fontSize:12, color:T.ink }}>{l}</span>
                </label>
              ))}
              <div style={{ marginTop:10 }}>
                <p style={{ fontSize:11, color:T.ink4, margin:"0 0 6px", textTransform:"uppercase", letterSpacing:".06em" }}>Optional password</p>
                <input value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Leave blank for no password" style={{ width:"100%", border:`1px solid ${T.s2}`, borderRadius:6, padding:"7px 10px", fontSize:12, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}/>
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={createLink} style={{ padding:"7px 18px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Generate link</button>
            <button onClick={()=>setCreating(false)} style={{ padding:"7px 14px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {links.map(link=>(
          <div key={link.id} style={{ background:T.canvas, border:`1px solid ${link.active?T.s2:T.s3}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12, opacity:link.active?1:0.55 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:link.active?T.g2:T.ink4, flexShrink:0 }}/>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontSize:12, fontWeight:500, color:T.ink, margin:"0 0 2px" }}>{link.client}</p>
              <p style={{ fontSize:11, color:T.ink4, margin:0 }}>Pages: {link.scope} · {link.views} views · Created {link.created} · Expires {link.expires}</p>
            </div>
            <span style={{ fontSize:10, fontFamily:"monospace", color:T.ink4, background:T.s1, padding:"3px 8px", borderRadius:4 }}>adtech.io/share/{link.id}</span>
            {link.active && <>
              <button onClick={()=>copyLink(link.id)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                {copied===link.id ? "Copied!" : "Copy link"}
              </button>
              <button onClick={()=>revokeLink(link.id)} style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.r2, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Revoke</button>
            </>}
            {!link.active && <span style={{ fontSize:10, color:T.ink4, fontWeight:600 }}>Expired</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Settings Page ────────────────────────────────────────────────────────────
const INIT_ACCOUNTS = [
  { id:"acc1", name:"Demo Agency Global",    email:"admin@demo-agency.com",   role:"Owner",  avatar:"DA", color:T.b2   },
  { id:"acc2", name:"Sarah Mitchell",        email:"s.mitchell@demo.com",     role:"Admin",  avatar:"SM", color:T.v2   },
  { id:"acc3", name:"James Okafor",          email:"j.okafor@demo.com",       role:"Analyst",avatar:"JO", color:T.g2   },
  { id:"acc4", name:"Priya Sharma",          email:"priya@demo.com",          role:"Viewer", avatar:"PS", color:T.a2   },
];

const INIT_PAGES = [
  { id:"pg1", name:"Demo Agency",           platform:"meta",      type:"Facebook Page",  fans:"28.4k", connected:true,  status:"active"   },
  { id:"pg2", name:"Demo Agency",           platform:"instagram", type:"Instagram Account",followers:"14.2k",connected:true,status:"active"  },
  { id:"pg3", name:"Demo Agency Official",  platform:"linkedin",  type:"Company Page",   followers:"6.8k", connected:true,  status:"active"   },
  { id:"pg4", name:"@DemoAgency",           platform:"x",         type:"X Profile",      followers:"3.1k", connected:true,  status:"active"   },
  { id:"pg5", name:"Demo Agency",           platform:"tiktok",    type:"TikTok Business",followers:"1.2k", connected:false, status:"inactive" },
  { id:"pg6", name:"Demo Agency Shop",      platform:"pinterest", type:"Pinterest Board", followers:"890",  connected:false, status:"inactive" },
];

const ROLES = ["Owner","Admin","Analyst","Viewer"];
const PLATFORM_ICONS = {
  meta:      { c:"#1877F2", l:"Facebook" },
  instagram: { c:"#E1306C", l:"Instagram" },
  linkedin:  { c:"#0A66C2", l:"LinkedIn"  },
  x:         { c:"#000000", l:"X"         },
  tiktok:    { c:"#2DD4BF", l:"TikTok"    },
  pinterest: { c:"#E60023", l:"Pinterest" },
};

function SettingsPage({ activeClient }) {
  const [stab, setStab]         = useState("accounts"); // accounts | pages | workspace | billing
  const [accounts, setAccounts] = useState(INIT_ACCOUNTS);
  const [pages, setPages]       = useState(INIT_PAGES);
  const [inviteOpen, setInvite] = useState(false);
  const [addPageOpen, setAddPage]= useState(false);
  const [invite, setInvite2]    = useState({ name:"", email:"", role:"Analyst" });
  const [newPage, setNewPage]   = useState({ platform:"meta", name:"", url:"" });
  const [editId, setEditId]     = useState(null);
  const [editRole, setEditRole] = useState("");
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [workspace, setWorkspace]= useState({
    name: activeClient?.name || "Demo Agency",
    website: "https://demo-agency.com",
    industry: "Advertising Agency",
    currency: "USD",
    timezone: "UTC+0 (London)",
    logo: "",
  });

  const INP = { width:"100%", border:`1px solid ${T.s2}`, borderRadius:7, padding:"8px 10px", fontSize:12, color:T.ink, background:T.bg, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const LBL = { fontSize:10, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", display:"block", marginBottom:5 };
  const SEL = { ...INP, cursor:"pointer" };

  const sendInvite = () => {
    if (!invite.name || !invite.email) return;
    const newAcc = { id:"acc"+Date.now(), name:invite.name, email:invite.email, role:invite.role, avatar:invite.name.split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2), color:[T.b2,T.v2,T.g2,T.a2][accounts.length%4] };
    setAccounts(a=>[...a, newAcc]);
    setInvite(false); setInvite2({ name:"", email:"", role:"Analyst" });
  };

  const removeAccount = (id) => setAccounts(a=>a.filter(x=>x.id!==id));
  const saveRole = (id) => { setAccounts(a=>a.map(x=>x.id===id?{...x,role:editRole}:x)); setEditId(null); };

  const connectPage = (id) => setPages(ps=>ps.map(p=>p.id===id?{...p,connected:true,status:"active"}:p));
  const disconnectPage = (id) => setPages(ps=>ps.map(p=>p.id===id?{...p,connected:false,status:"inactive"}:p));

  const addPage = () => {
    if (!newPage.name) return;
    const pg = { id:"pg"+Date.now(), name:newPage.name, platform:newPage.platform, type:PLATFORM_ICONS[newPage.platform]?.l+" Page", followers:"0", connected:true, status:"active" };
    setPages(ps=>[...ps, pg]);
    setAddPage(false); setNewPage({ platform:"meta", name:"", url:"" });
  };

  const saveWorkspace = () => { setSaving(true); setTimeout(()=>{ setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2000); },1000); };

  const TABS = [["accounts","Accounts"],["pages","Pages & accounts"],["workspace","Workspace"],["billing","Billing"]];

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", background:T.bg }}>

      {/* Header */}
      <div style={{ padding:"18px 22px 0", background:T.canvas, borderBottom:`1px solid ${T.s2}`, flexShrink:0 }}>
        <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 14px" }}>Settings</h1>
        <div style={{ display:"flex", gap:0, borderBottom:`2px solid ${T.s2}`, marginBottom:-2 }}>
          {TABS.map(([t,l])=>(
            <button key={t} onClick={()=>setStab(t)} style={{
              padding:"9px 18px", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit",
              borderBottom:`2px solid ${stab===t?T.b2:"transparent"}`, marginBottom:-2,
              color:stab===t?T.b2:T.ink3, fontSize:12, fontWeight:stab===t?600:400, transition:"all .15s",
            }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"22px 22px" }}>

        {/* ══ ACCOUNTS TAB ══ */}
        {stab==="accounts" && (
          <div style={{ maxWidth:740 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <p style={{ fontSize:14, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Team members</p>
                <p style={{ fontSize:12, color:T.ink3, margin:0 }}>{accounts.length} member{accounts.length!==1?"s":""} · Manage access and roles</p>
              </div>
              <button onClick={()=>setInvite(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:7, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                Invite member
              </button>
            </div>

            {/* Invite form */}
            {inviteOpen && (
              <div style={{ background:T.bL, border:`1px solid ${T.bM}`, borderRadius:10, padding:"16px 18px", marginBottom:16 }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.b1, margin:"0 0 12px" }}>Invite new team member</p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:10, alignItems:"flex-end" }}>
                  <div>
                    <label style={LBL}>Full name</label>
                    <input value={invite.name} onChange={e=>setInvite2(i=>({...i,name:e.target.value}))} placeholder="e.g. Emma Johansson" style={INP}/>
                  </div>
                  <div>
                    <label style={LBL}>Email</label>
                    <input value={invite.email} onChange={e=>setInvite2(i=>({...i,email:e.target.value}))} placeholder="emma@company.com" style={INP}/>
                  </div>
                  <div>
                    <label style={LBL}>Role</label>
                    <select value={invite.role} onChange={e=>setInvite2(i=>({...i,role:e.target.value}))} style={SEL}>
                      {ROLES.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, marginTop:12 }}>
                  <button onClick={sendInvite} style={{ padding:"6px 16px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Send invite</button>
                  <button onClick={()=>setInvite(false)} style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Accounts list */}
            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden" }}>
              {accounts.map((acc, i) => (
                <div key={acc.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 18px", borderBottom:i<accounts.length-1?`1px solid ${T.s2}`:"none" }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", background:acc.color+"22", border:`2px solid ${acc.color}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:acc.color, flexShrink:0 }}>{acc.avatar}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:"0 0 2px" }}>{acc.name}</p>
                    <p style={{ fontSize:11, color:T.ink4, margin:0 }}>{acc.email}</p>
                  </div>
                  {editId===acc.id ? (
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <select value={editRole} onChange={e=>setEditRole(e.target.value)} style={{ border:`1px solid ${T.bM}`, borderRadius:6, padding:"5px 8px", fontSize:11, background:T.bL, color:T.b1, outline:"none", fontFamily:"inherit" }}>
                        {ROLES.map(r=><option key={r}>{r}</option>)}
                      </select>
                      <button onClick={()=>saveRole(acc.id)} style={{ padding:"5px 12px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Save</button>
                      <button onClick={()=>setEditId(null)} style={{ padding:"5px 8px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20,
                        background:acc.role==="Owner"?T.vL:acc.role==="Admin"?T.bL:acc.role==="Analyst"?T.gL:T.s1,
                        color:acc.role==="Owner"?T.v1:acc.role==="Admin"?T.b1:acc.role==="Analyst"?T.g1:T.ink3,
                      }}>{acc.role}</span>
                      {acc.role!=="Owner" && <>
                        <button onClick={()=>{ setEditId(acc.id); setEditRole(acc.role); }} style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                        <button onClick={()=>removeAccount(acc.id)} style={{ padding:"4px 8px", borderRadius:6, border:`1px solid ${T.rM||T.s2}`, background:T.rL, color:T.r2, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Remove</button>
                      </>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ PAGES TAB ══ */}
        {stab==="pages" && (
          <div style={{ maxWidth:740 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <p style={{ fontSize:14, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Connected pages & social accounts</p>
                <p style={{ fontSize:12, color:T.ink3, margin:0 }}>{pages.filter(p=>p.connected).length} connected · {pages.filter(p=>!p.connected).length} disconnected</p>
              </div>
              <button onClick={()=>setAddPage(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:7, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                Add page
              </button>
            </div>

            {/* Add page form */}
            {addPageOpen && (
              <div style={{ background:T.bL, border:`1px solid ${T.bM}`, borderRadius:10, padding:"16px 18px", marginBottom:16 }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.b1, margin:"0 0 12px" }}>Add social page or account</p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                  <div>
                    <label style={LBL}>Platform</label>
                    <select value={newPage.platform} onChange={e=>setNewPage(p=>({...p,platform:e.target.value}))} style={SEL}>
                      {Object.entries(PLATFORM_ICONS).map(([k,v])=><option key={k} value={k}>{v.l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={LBL}>Page / account name</label>
                    <input value={newPage.name} onChange={e=>setNewPage(p=>({...p,name:e.target.value}))} placeholder="e.g. Demo Agency" style={INP}/>
                  </div>
                  <div>
                    <label style={LBL}>URL (optional)</label>
                    <input value={newPage.url} onChange={e=>setNewPage(p=>({...p,url:e.target.value}))} placeholder="https://facebook.com/..." style={INP}/>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={addPage} style={{ padding:"6px 16px", borderRadius:6, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Connect page</button>
                  <button onClick={()=>setAddPage(false)} style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                </div>
              </div>
            )}

            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, overflow:"hidden" }}>
              {pages.map((pg, i) => {
                const platInfo = PLATFORM_ICONS[pg.platform] || { c:T.b2, l:pg.platform };
                return (
                  <div key={pg.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 18px", borderBottom:i<pages.length-1?`1px solid ${T.s2}`:"none" }}>
                    <div style={{ width:38, height:38, borderRadius:9, background:platInfo.c+"22", border:`2px solid ${platInfo.c}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <div style={{ width:16, height:16, borderRadius:"50%", background:platInfo.c }}/>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:500, color:T.ink, margin:"0 0 2px" }}>{pg.name}</p>
                      <p style={{ fontSize:11, color:T.ink4, margin:0 }}>{platInfo.l} · {pg.type} · {pg.followers} followers</p>
                    </div>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:pg.connected?T.g2:T.ink4, flexShrink:0 }}/>
                    <span style={{ fontSize:11, fontWeight:600, padding:"3px 9px", borderRadius:20, background:pg.connected?T.gL:T.s1, color:pg.connected?T.g1:T.ink4 }}>
                      {pg.connected?"Active":"Inactive"}
                    </span>
                    {pg.connected
                      ? <button onClick={()=>disconnectPage(pg.id)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${T.s2}`, background:"transparent", color:T.r2, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Disconnect</button>
                      : <button onClick={()=>connectPage(pg.id)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Reconnect</button>
                    }
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ WORKSPACE TAB ══ */}
        {stab==="workspace" && (
          <div style={{ maxWidth:600 }}>
            <p style={{ fontSize:14, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Workspace settings</p>
            <p style={{ fontSize:12, color:T.ink3, margin:"0 0 20px" }}>General settings for this workspace</p>
            <div style={{ background:T.canvas, border:`1px solid ${T.s2}`, borderRadius:12, padding:"20px 22px", display:"flex", flexDirection:"column", gap:14 }}>
              {[
                ["Workspace name","name","e.g. Demo Agency"],
                ["Website","website","https://..."],
                ["Industry","industry","e.g. Advertising Agency"],
              ].map(([lbl,key,ph])=>(
                <div key={key}>
                  <label style={LBL}>{lbl}</label>
                  <input value={workspace[key]} onChange={e=>setWorkspace(w=>({...w,[key]:e.target.value}))} placeholder={ph} style={INP}/>
                </div>
              ))}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={LBL}>Currency</label>
                  <select value={workspace.currency} onChange={e=>setWorkspace(w=>({...w,currency:e.target.value}))} style={SEL}>
                    {["USD","EUR","GBP","AED","SAR","SGD","AUD","CAD"].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LBL}>Timezone</label>
                  <select value={workspace.timezone} onChange={e=>setWorkspace(w=>({...w,timezone:e.target.value}))} style={SEL}>
                    {["UTC+0 (London)","UTC+1 (Paris)","UTC+2 (Cairo)","UTC+3 (Riyadh)","UTC+4 (Dubai)","UTC+5:30 (Mumbai)","UTC+8 (Singapore)","UTC-5 (New York)","UTC-8 (LA)"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={LBL}>Workspace logo URL</label>
                <input value={workspace.logo} onChange={e=>setWorkspace(w=>({...w,logo:e.target.value}))} placeholder="https://yoursite.com/logo.png" style={INP}/>
              </div>
              <button onClick={saveWorkspace} style={{ padding:"8px 0", borderRadius:7, border:"none", background:saving||saved?T.gL:T.brand, color:saving||saved?T.g1:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                {saving ? <><span style={{ width:12, height:12, border:"2px solid rgba(255,255,255,.3)", borderTopColor:T.g2, borderRadius:"50%", display:"inline-block", animation:"adtech-spin 1s linear infinite" }}/> Saving…</> : saved ? "✓ Saved!" : "Save changes"}
              </button>
            </div>
          </div>
        )}

        {/* ══ BILLING TAB ══ */}
        {stab==="billing" && (
          <div style={{ maxWidth:600 }}>
            <p style={{ fontSize:14, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Billing & plan</p>
            <p style={{ fontSize:12, color:T.ink3, margin:"0 0 20px" }}>Current plan and payment details</p>
            <div style={{ background:`linear-gradient(135deg,${T.b1},${T.v2})`, borderRadius:12, padding:"20px 22px", color:"#fff", marginBottom:14 }}>
              <p style={{ fontSize:10, opacity:.7, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:".1em" }}>Current plan</p>
              <p style={{ fontSize:22, fontWeight:700, margin:"0 0 6px" }}>Agency Pro</p>
              <p style={{ fontSize:12, opacity:.8, margin:0 }}>10 clients · Unlimited users · All features · Priority support</p>
              <p style={{ fontSize:11, opacity:.7, margin:"8px 0 0" }}>Next billing: Apr 1, 2026 · $299/month</p>
            </div>
            {[
              ["Seats used","4 / unlimited"],
              ["Clients","5 / 10"],
              ["API calls this month","12,430 / unlimited"],
              ["Storage","2.1 GB / 50 GB"],
            ].map(([l,v])=>(
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"12px 0", borderBottom:`1px solid ${T.s2}` }}>
                <span style={{ fontSize:12, color:T.ink3 }}>{l}</span>
                <span style={{ fontSize:12, fontWeight:600, color:T.ink }}>{v}</span>
              </div>
            ))}
            <div style={{ display:"flex", gap:8, marginTop:16 }}>
              <button style={{ padding:"8px 18px", borderRadius:7, border:`1px solid ${T.bM}`, background:T.bL, color:T.b1, fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Upgrade plan</button>
              <button style={{ padding:"8px 14px", borderRadius:7, border:`1px solid ${T.s2}`, background:"transparent", color:T.ink3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>View invoices</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


// ─── Programmatic Report Page ─────────────────────────────────────────────────
const PROG_DSPS = [
  { id:"dv360",      label:"DV360",          color:"#1A73E8", short:"DV360"   },
  { id:"ttd",        label:"Trade Desk",     color:"#00C44F", short:"TTD"     },
  { id:"amazon_dsp", label:"Amazon DSP",     color:"#FF9900", short:"Amazon"  },
  { id:"xandr",      label:"Xandr",          color:"#00A4EF", short:"Xandr"   },
  { id:"mediamath",  label:"MediaMath",      color:"#E63946", short:"MM"      },
  { id:"yahoo_dsp",  label:"Yahoo DSP",      color:"#720E9E", short:"Yahoo"   },
  { id:"stackadapt", label:"StackAdapt",     color:"#0052CC", short:"SA"      },
  { id:"adform",     label:"Adform",         color:"#003087", short:"AF"      },
  { id:"verizon",    label:"Yahoo Verizon",  color:"#5F259F", short:"VZ"      },
  { id:"display_video","label":"DV Open Mkt",color:"#34A853", short:"DV-OA"  },
];

const PROG_DEALS = [
  // DV360
  { id:"d1",  name:"Premium News PMP",         dsp:"dv360",      type:"PMP",  cpm:"$5.40",  impr:1200000, viewab:78, ivt:1.1, freq:2.4, reach:500000,  conv:184, cpa:"$31.5",  budget:"$6.5k",  spend:"$5.8k",  pacing:89  },
  { id:"d2",  name:"Open Exchange Display",    dsp:"dv360",      type:"Open", cpm:"$1.80",  impr:8400000, viewab:58, ivt:3.1, freq:4.1, reach:2050000, conv:312, cpa:"$42.3",  budget:"$15.1k", spend:"$13.2k", pacing:87  },
  // Trade Desk
  { id:"d3",  name:"Automotive In-Market",     dsp:"ttd",        type:"PMP",  cpm:"$4.10",  impr:980000,  viewab:71, ivt:2.2, freq:1.8, reach:544000,  conv:147, cpa:"$21.8",  budget:"$4.0k",  spend:"$3.2k",  pacing:80  },
  { id:"d4",  name:"Sports CTV Package",       dsp:"ttd",        type:"PG",   cpm:"$22.00", impr:190000,  viewab:96, ivt:0.2, freq:1.2, reach:158000,  conv:89,  cpa:"$47.2",  budget:"$4.2k",  spend:"$4.2k",  pacing:100 },
  // Amazon DSP
  { id:"d5",  name:"CTV Prime Time Package",   dsp:"amazon_dsp", type:"PG",   cpm:"$18.50", impr:320000,  viewab:94, ivt:0.4, freq:1.6, reach:200000,  conv:218, cpa:"$27.1",  budget:"$6.0k",  spend:"$5.9k",  pacing:98  },
  { id:"d6",  name:"Streaming TV — Fire",      dsp:"amazon_dsp", type:"PMP",  cpm:"$14.20", impr:480000,  viewab:91, ivt:0.6, freq:2.1, reach:228000,  conv:156, cpa:"$38.5",  budget:"$6.8k",  spend:"$5.2k",  pacing:76  },
  // Xandr
  { id:"d7",  name:"Finance Decision-Maker",   dsp:"xandr",      type:"PMP",  cpm:"$6.80",  impr:560000,  viewab:69, ivt:1.8, freq:2.8, reach:200000,  conv:98,  cpa:"$29.6",  budget:"$3.8k",  spend:"$2.9k",  pacing:76  },
  // StackAdapt
  { id:"d8",  name:"B2B Native Display",       dsp:"stackadapt", type:"Open", cpm:"$8.40",  impr:720000,  viewab:74, ivt:1.4, freq:3.2, reach:225000,  conv:134, cpa:"$44.8",  budget:"$6.0k",  spend:"$5.1k",  pacing:85  },
  // Yahoo DSP
  { id:"d9",  name:"Yahoo Finance Takeover",   dsp:"yahoo_dsp",  type:"PMP",  cpm:"$9.20",  impr:340000,  viewab:82, ivt:1.1, freq:1.4, reach:242000,  conv:72,  cpa:"$56.9",  budget:"$3.1k",  spend:"$2.4k",  pacing:77  },
  // MediaMath
  { id:"d10", name:"Retail Data Segment",      dsp:"mediamath",  type:"Open", cpm:"$3.20",  impr:2100000, viewab:62, ivt:2.8, freq:3.8, reach:552000,  conv:201, cpa:"$33.2",  budget:"$6.7k",  spend:"$5.9k",  pacing:88  },
];

const PROG_AUDIENCES = [
  { name:"In-market: Business Software",   size:"2.4M", cpm:"$8.20", ctr:"0.34%", viewab:"76%", freq:2.8, reach:"860k",  conv:142, cpa:"$42.10", attrib:"Last-click" },
  { name:"Affinity: Finance Enthusiasts",  size:"5.1M", cpm:"$4.80", ctr:"0.21%", viewab:"69%", freq:3.4, reach:"1.5M",  conv:98,  cpa:"$61.20", attrib:"Data-driven" },
  { name:"Retargeting: Site visitors 30d", size:"180k", cpm:"$6.40", ctr:"1.82%", viewab:"81%", freq:4.2, reach:"43k",   conv:312, cpa:"$14.80", attrib:"View-through"},
  { name:"Lookalike: CRM upload",          size:"920k", cpm:"$5.90", ctr:"0.68%", viewab:"73%", freq:2.1, reach:"438k",  conv:187, cpa:"$38.50", attrib:"Data-driven" },
  { name:"Contextual: Tech news",          size:"3.8M", cpm:"$3.20", ctr:"0.28%", viewab:"64%", freq:1.8, reach:"2.1M",  conv:74,  cpa:"$54.30", attrib:"Last-click"  },
  { name:"Custom intent: CRM match",       size:"340k", cpm:"$9.10", ctr:"0.91%", viewab:"78%", freq:3.1, reach:"109k",  conv:224, cpa:"$24.60", attrib:"Data-driven" },
  { name:"Third-party: HH income $150k+",  size:"1.2M", cpm:"$7.40", ctr:"0.44%", viewab:"71%", freq:2.6, reach:"462k",  conv:118, cpa:"$47.80", attrib:"Last-click"  },
];

const PROG_TREND = [
  { d:"Mar 15", viewab:68.2, ivt:2.4, winrate:31.2 },
  { d:"Mar 16", viewab:69.8, ivt:2.1, winrate:32.4 },
  { d:"Mar 17", viewab:71.4, ivt:1.9, winrate:33.8 },
  { d:"Mar 18", viewab:70.9, ivt:2.0, winrate:34.1 },
  { d:"Mar 19", viewab:72.1, ivt:1.8, winrate:35.2 },
  { d:"Mar 20", viewab:73.4, ivt:1.7, winrate:34.8 },
  { d:"Mar 21", viewab:71.4, ivt:1.8, winrate:34.2 },
];

function ProgrammaticReportPage({ setShowReport }) {
  const [dspFilter, setDspFilter] = useState("all");
  const [tab, setTab]             = useState("overview");
  const [sortDeals, setSortDeals] = useState("pacing");

  const filtered = dspFilter==="all" ? PROG_DEALS : PROG_DEALS.filter(d=>d.dsp===dspFilter);
  const sorted   = [...filtered].sort((a,b)=>{
    if (sortDeals==="pacing")  return b.pacing - a.pacing;
    if (sortDeals==="viewab")  return b.viewab - a.viewab;
    if (sortDeals==="ivt")     return a.ivt - b.ivt;
    if (sortDeals==="conv")    return b.conv - a.conv;
    return 0;
  });

  const totalSpend  = filtered.reduce((a,d)=>a+parseFloat(d.spend.replace(/[^0-9.]/g,""))*1000,0);
  const totalImprN  = filtered.reduce((a,d)=>a+d.impr,0);
  const totalReach  = filtered.reduce((a,d)=>a+d.reach,0);
  const totalConv   = filtered.reduce((a,d)=>a+d.conv,0);
  const avgViewab   = filtered.length ? (filtered.reduce((a,d)=>a+d.viewab,0)/filtered.length).toFixed(1) : "0";
  const avgIvt      = filtered.length ? (filtered.reduce((a,d)=>a+d.ivt,0)/filtered.length).toFixed(1) : "0";
  const avgFreq     = filtered.length ? (filtered.reduce((a,d)=>a+d.freq,0)/filtered.length).toFixed(1) : "0";
  const blendCpa    = totalConv > 0 ? "$"+(totalSpend/totalConv).toFixed(0) : "—";
  const fmtN        = n => n>=1000000?(n/1000000).toFixed(1)+"M":n>=1000?(n/1000).toFixed(0)+"k":String(n);

  // Trend SVG
  const W=220, H=56, pad=6;
  const mkLine = (key, color) => {
    const vals=PROG_TREND.map(d=>d[key]);
    const min=Math.min(...vals),max=Math.max(...vals),range=max-min||1;
    const pts=PROG_TREND.map((d,i)=>{
      const x=pad+(i/(PROG_TREND.length-1))*(W-pad*2);
      const y=H-pad-((d[key]-min)/range)*(H-pad*2);
      return x+","+y;
    }).join(" ");
    return <polyline key={key} points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
  };

  const SEL = { border:"1px solid "+T.s2, borderRadius:6, padding:"6px 10px", fontSize:11, color:T.ink, background:T.bg, outline:"none", fontFamily:"inherit" };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", background:T.bg }}>
      {/* Header */}
      <div style={{ padding:"16px 22px 0", background:T.canvas, borderBottom:"1px solid "+T.s2, flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:T.v2 }}/>
              <span style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", color:T.v2 }}>Programmatic</span>
              <span style={{ fontSize:10, color:T.ink4 }}>{PROG_DSPS.length} DSPs connected · {PROG_DEALS.length} active deals</span>
            </div>
            <h1 style={{ fontSize:18, fontWeight:500, color:T.ink, margin:"0 0 3px" }}>Programmatic Ads Report</h1>
            <p style={{ fontSize:12, color:T.ink3, margin:0 }}>DV360 · Trade Desk · Amazon DSP · Xandr · StackAdapt · Yahoo · MediaMath · Adform · Mar 15–21, 2026</p>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <select value={dspFilter} onChange={e=>setDspFilter(e.target.value)} style={SEL}>
              <option value="all">All DSPs ({PROG_DSPS.length})</option>
              {PROG_DSPS.map(d=><option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <button onClick={()=>setShowReport(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:"none", background:T.brand, color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/></svg>
              Send report
            </button>
          </div>
        </div>
        <div style={{ display:"flex", borderBottom:"2px solid "+T.s2, marginBottom:-2 }}>
          {[["overview","Overview"],["deals","Deals & PMPs"],["audiences","Audiences"],["attribution","Attribution"],["brand-safety","Brand Safety"]].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{ padding:"8px 18px", background:"none", border:"none", borderBottom:"2px solid "+(tab===t?T.v2:"transparent"), marginBottom:-2, color:tab===t?T.v2:T.ink3, fontSize:12, fontWeight:tab===t?600:400, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>

        {/* ══ OVERVIEW ══ */}
        {tab==="overview" && (<>
          {/* 8 KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
            {[
              ["Total spend",    "$"+Math.round(totalSpend/1000)+"k", T.b2,  ""],
              ["Impressions",    fmtN(totalImprN),                    T.v2,  ""],
              ["Unique reach",   fmtN(totalReach),                    T.b3,  "People reached"],
              ["Conversions",    String(totalConv),                   T.g2,  "All attribution"],
              ["Avg viewability",avgViewab+"%",                       parseFloat(avgViewab)>=70?T.g2:T.a2,"MRC standard"],
              ["Avg IVT rate",   avgIvt+"%",                          parseFloat(avgIvt)<=2?T.g2:T.r2,"Lower = better"],
              ["Avg frequency",  avgFreq+"x",                         parseFloat(avgFreq)<=3?T.g2:T.a2,"Per unique user"],
              ["Blended CPA",    blendCpa,                            T.b2,  "Cost per conversion"],
            ].map(([l,v,c,sub])=>(
              <div key={l} style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:10, padding:"12px 14px" }}>
                <p style={{ fontSize:9, color:T.ink4, margin:"0 0 3px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                <p style={{ fontSize:20, fontWeight:700, color:c, margin:"0 0 1px" }}>{v}</p>
                {sub&&<p style={{ fontSize:9, color:T.ink4, margin:0 }}>{sub}</p>}
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1.4fr", gap:14, marginBottom:16 }}>
            {/* DSP breakdown cards */}
            <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, padding:"16px 18px" }}>
              <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 14px" }}>By DSP</p>
              {PROG_DSPS.map(dsp => {
                const dd   = PROG_DEALS.filter(d=>d.dsp===dsp.id);
                if (!dd.length) return null;
                const spnd = dd.reduce((a,d)=>a+parseFloat(d.spend.replace(/[^0-9.]/g,""))*1000,0);
                const conv = dd.reduce((a,d)=>a+d.conv,0);
                const vwb  = (dd.reduce((a,d)=>a+d.viewab,0)/dd.length).toFixed(0);
                const ivt  = (dd.reduce((a,d)=>a+d.ivt,0)/dd.length).toFixed(1);
                return (
                  <div key={dsp.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:"1px solid "+T.s2 }}>
                    <div style={{ width:34, height:34, borderRadius:8, background:dsp.color+"18", border:"1.5px solid "+dsp.color+"44", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <span style={{ fontSize:9, fontWeight:700, color:dsp.color }}>{dsp.short}</span>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:12, fontWeight:500, color:T.ink, margin:"0 0 1px" }}>{dsp.label}</p>
                      <p style={{ fontSize:10, color:T.ink4, margin:0 }}>${Math.round(spnd/1000)}k · {conv} conv · {vwb}% viewab · {ivt}% IVT</p>
                    </div>
                    <span style={{ fontSize:10, fontWeight:600, color:T.g1, flexShrink:0 }}>{dd.length} deal{dd.length!==1?"s":""}</span>
                  </div>
                );
              })}
            </div>

            {/* Trend */}
            <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, padding:"16px 18px" }}>
              <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 4px" }}>7-day trends</p>
              <p style={{ fontSize:10, color:T.ink4, margin:"0 0 12px" }}>Viewability · Win rate · IVT rate</p>
              <svg width={W} height={H+18} style={{ display:"block", overflow:"visible" }}>
                {mkLine("viewab", T.g2)}
                {mkLine("winrate", T.b2)}
                {mkLine("ivt", T.r2)}
                {PROG_TREND.map((d,i)=>{
                  const x=pad+(i/(PROG_TREND.length-1))*(W-pad*2);
                  return <text key={i} x={x} y={H+14} textAnchor="middle" fontSize="8" fill={T.ink4}>{d.d.split(" ")[1]}</text>;
                })}
              </svg>
              <div style={{ display:"flex", gap:14, marginTop:6 }}>
                {[["Viewability",T.g2],["Win rate",T.b2],["IVT rate",T.r2]].map(([l,c])=>(
                  <div key={l} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <div style={{ width:18, height:3, borderRadius:2, background:c }}/>
                    <span style={{ fontSize:10, color:T.ink4 }}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Overview deals table */}
          <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, overflow:"hidden" }}>
            <div style={{ padding:"11px 18px", borderBottom:"1px solid "+T.s2, background:T.s1, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>All deals · {filtered.length} active</p>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:10, color:T.ink4 }}>Sort by</span>
                <select value={sortDeals} onChange={e=>setSortDeals(e.target.value)} style={{ ...SEL, padding:"3px 8px", fontSize:10 }}>
                  <option value="pacing">Pacing</option>
                  <option value="viewab">Viewability</option>
                  <option value="ivt">IVT (best first)</option>
                  <option value="conv">Conversions</option>
                </select>
              </div>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                <thead>
                  <tr style={{ borderBottom:"2px solid "+T.s2 }}>
                    {["Deal","DSP","Type","CPM","Impr","Reach","Freq","Viewab","IVT","Conv","CPA","Pacing"].map(h=>(
                      <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:9, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(d => {
                    const dsp = PROG_DSPS.find(x=>x.id===d.dsp);
                    return (
                      <tr key={d.id} style={{ borderBottom:"1px solid "+T.s2 }}
                        onMouseEnter={e=>e.currentTarget.style.background=T.s1}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{ padding:"9px 10px", fontWeight:500, color:T.ink, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{d.name}</td>
                        <td style={{ padding:"9px 10px" }}><span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:3, background:(dsp?.color||T.b2)+"18", color:dsp?.color||T.b2 }}>{dsp?.short}</span></td>
                        <td style={{ padding:"9px 10px" }}><span style={{ fontSize:9, fontWeight:600, padding:"1px 6px", borderRadius:3, background:d.type==="PG"?T.vL:d.type==="PMP"?T.bL:T.s1, color:d.type==="PG"?T.v1:d.type==="PMP"?T.b1:T.ink3 }}>{d.type}</span></td>
                        <td style={{ padding:"9px 10px", color:T.ink }}>{d.cpm}</td>
                        <td style={{ padding:"9px 10px", color:T.ink }}>{fmtN(d.impr)}</td>
                        <td style={{ padding:"9px 10px", color:T.ink3 }}>{fmtN(d.reach)}</td>
                        <td style={{ padding:"9px 10px", color:d.freq<=3?T.g2:T.a2 }}>{d.freq}x</td>
                        <td style={{ padding:"9px 10px", fontWeight:600, color:d.viewab>=70?T.g2:d.viewab>=60?T.a2:T.r2 }}>{d.viewab}%</td>
                        <td style={{ padding:"9px 10px", fontWeight:600, color:d.ivt<=2?T.g2:d.ivt<=3?T.a2:T.r2 }}>{d.ivt}%</td>
                        <td style={{ padding:"9px 10px", fontWeight:600, color:T.g2 }}>{d.conv}</td>
                        <td style={{ padding:"9px 10px", color:T.ink }}>{d.cpa}</td>
                        <td style={{ padding:"9px 10px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <div style={{ width:46, height:5, background:T.s2, borderRadius:3, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:Math.min(d.pacing,100)+"%", background:d.pacing>=95?T.g2:d.pacing>=75?T.b2:T.a2, borderRadius:3 }}/>
                            </div>
                            <span style={{ fontSize:9, fontWeight:600, color:d.pacing>=95?T.g2:d.pacing>=75?T.b2:T.a2 }}>{d.pacing}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

        {/* ══ DEALS / PMP ══ */}
        {tab==="deals" && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
              {[
                ["PMP deals",    String(PROG_DEALS.filter(d=>d.type==="PMP").length), T.b2],
                ["PG deals",     String(PROG_DEALS.filter(d=>d.type==="PG").length),  T.v2],
                ["Open Exchange",String(PROG_DEALS.filter(d=>d.type==="Open").length),T.ink3],
                ["Total deals",  String(PROG_DEALS.length),                           T.ink],
              ].map(([l,v,c])=>(
                <div key={l} style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:10, padding:"13px 16px" }}>
                  <p style={{ fontSize:10, color:T.ink4, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                  <p style={{ fontSize:22, fontWeight:700, color:c, margin:0 }}>{v}</p>
                </div>
              ))}
            </div>
            {PROG_DEALS.map(deal => {
              const dsp = PROG_DSPS.find(x=>x.id===deal.dsp);
              return (
                <div key={deal.id} style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:10, padding:"14px 18px", marginBottom:10 }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                    <div>
                      <div style={{ display:"flex", gap:6, marginBottom:4 }}>
                        <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, background:(dsp?.color||T.b2)+"18", color:dsp?.color||T.b2 }}>{dsp?.label}</span>
                        <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, background:deal.type==="PG"?T.vL:deal.type==="PMP"?T.bL:T.s1, color:deal.type==="PG"?T.v1:deal.type==="PMP"?T.b1:T.ink3 }}>{deal.type}</span>
                      </div>
                      <h3 style={{ fontSize:14, fontWeight:600, color:T.ink, margin:0 }}>{deal.name}</h3>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:7, flexShrink:0 }}>
                      <div style={{ width:70, height:5, background:T.s2, borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:Math.min(deal.pacing,100)+"%", background:deal.pacing>=95?T.g2:T.b2, borderRadius:3 }}/>
                      </div>
                      <span style={{ fontSize:11, fontWeight:600, color:deal.pacing>=95?T.g2:T.b2 }}>{deal.pacing}% paced</span>
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(8,1fr)", gap:10 }}>
                    {[["CPM",deal.cpm,T.ink],["Impressions",fmtN(deal.impr),T.b2],["Reach",fmtN(deal.reach),T.v2],["Frequency",deal.freq+"x",deal.freq<=3?T.g2:T.a2],["Viewability",deal.viewab+"%",deal.viewab>=70?T.g2:T.a2],["IVT",deal.ivt+"%",deal.ivt<=2?T.g2:T.r2],["Conversions",String(deal.conv),T.g2],["CPA",deal.cpa,T.ink]].map(([l,v,c])=>(
                      <div key={l}>
                        <p style={{ fontSize:9, color:T.ink4, margin:"0 0 2px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                        <p style={{ fontSize:13, fontWeight:600, color:c, margin:0 }}>{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══ AUDIENCES ══ */}
        {tab==="audiences" && (
          <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, overflow:"hidden" }}>
            <div style={{ padding:"12px 18px", borderBottom:"1px solid "+T.s2, background:T.s1 }}>
              <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>Audience segment performance — all DSPs</p>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                <thead>
                  <tr style={{ borderBottom:"2px solid "+T.s2 }}>
                    {["Segment","Size","CPM","CTR","Viewability","Frequency","Reach","Conversions","CPA","Attribution",""].map(h=>(
                      <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:9, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PROG_AUDIENCES.map((a,i)=>(
                    <tr key={i} style={{ borderBottom:"1px solid "+T.s2 }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.s1}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"10px 12px", fontWeight:500, color:T.ink, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.name}</td>
                      <td style={{ padding:"10px 12px", color:T.ink3 }}>{a.size}</td>
                      <td style={{ padding:"10px 12px", color:T.ink }}>{a.cpm}</td>
                      <td style={{ padding:"10px 12px", fontWeight:600, color:parseFloat(a.ctr)>=1?T.g2:T.b2 }}>{a.ctr}</td>
                      <td style={{ padding:"10px 12px", fontWeight:600, color:parseFloat(a.viewab)>=70?T.g2:T.a2 }}>{a.viewab}</td>
                      <td style={{ padding:"10px 12px", color:a.freq<=3?T.g2:T.a2 }}>{a.freq}x</td>
                      <td style={{ padding:"10px 12px", color:T.ink3 }}>{a.reach}</td>
                      <td style={{ padding:"10px 12px", fontWeight:600, color:T.g2 }}>{a.conv}</td>
                      <td style={{ padding:"10px 12px", color:T.ink }}>{a.cpa}</td>
                      <td style={{ padding:"10px 12px" }}><span style={{ fontSize:9, fontWeight:600, padding:"2px 7px", borderRadius:4, background:T.vL, color:T.v1 }}>{a.attrib}</span></td>
                      <td style={{ padding:"10px 12px" }}><button style={{ padding:"3px 10px", borderRadius:5, border:"1px solid "+T.bM, background:T.bL, color:T.b1, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Boost</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ ATTRIBUTION ══ */}
        {tab==="attribution" && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
              {[
                ["Total conversions", String(totalConv),  T.g2, "All models combined"],
                ["Blended CPA",       blendCpa,           T.b2, "Cost per conversion"],
                ["View-through conv", "624",              T.v2, "Post-impression"],
                ["Click-through conv","1,549",            T.g2, "Post-click"],
              ].map(([l,v,c,sub])=>(
                <div key={l} style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:10, padding:"13px 16px" }}>
                  <p style={{ fontSize:9, color:T.ink4, margin:"0 0 3px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                  <p style={{ fontSize:22, fontWeight:700, color:c, margin:"0 0 2px" }}>{v}</p>
                  <p style={{ fontSize:9, color:T.ink4, margin:0 }}>{sub}</p>
                </div>
              ))}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
              {/* Attribution model breakdown */}
              <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, padding:"16px 18px" }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 14px" }}>Attribution model breakdown</p>
                {[
                  { model:"Data-driven", conv:1082, pct:50, color:T.b2 },
                  { model:"Last-click",  conv:534,  pct:25, color:T.v2 },
                  { model:"View-through",conv:624,  pct:25, color:T.g2 },
                ].map(row=>(
                  <div key={row.model} style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:12, color:T.ink }}>{row.model}</span>
                      <span style={{ fontSize:12, fontWeight:600, color:row.color }}>{row.conv} conv · {row.pct}%</span>
                    </div>
                    <div style={{ height:8, background:T.s2, borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:row.pct+"%", background:row.color, borderRadius:4 }}/>
                    </div>
                  </div>
                ))}
              </div>

              {/* Conv by DSP */}
              <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, padding:"16px 18px" }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 14px" }}>Conversions by DSP</p>
                {PROG_DSPS.map(dsp=>{
                  const dd   = PROG_DEALS.filter(d=>d.dsp===dsp.id);
                  if (!dd.length) return null;
                  const conv = dd.reduce((a,d)=>a+d.conv,0);
                  const maxConv = Math.max(...PROG_DSPS.map(dx=>PROG_DEALS.filter(d=>d.dsp===dx.id).reduce((a,d)=>a+d.conv,0)));
                  return (
                    <div key={dsp.id} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                        <span style={{ fontSize:11, color:T.ink }}>{dsp.label}</span>
                        <span style={{ fontSize:11, fontWeight:600, color:dsp.color }}>{conv}</span>
                      </div>
                      <div style={{ height:7, background:T.s2, borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:(conv/maxConv*100)+"%", background:dsp.color, borderRadius:3 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Per-segment attribution */}
            <div style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, overflow:"hidden" }}>
              <div style={{ padding:"11px 18px", borderBottom:"1px solid "+T.s2, background:T.s1 }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:0 }}>Attribution by audience segment</p>
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                <thead>
                  <tr style={{ borderBottom:"2px solid "+T.s2 }}>
                    {["Segment","Conversions","CPA","Model","Reach","Frequency"].map(h=>(
                      <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:9, fontWeight:600, color:T.ink4, textTransform:"uppercase", letterSpacing:".06em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PROG_AUDIENCES.map((a,i)=>(
                    <tr key={i} style={{ borderBottom:"1px solid "+T.s2 }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.s1}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"10px 14px", fontWeight:500, color:T.ink }}>{a.name}</td>
                      <td style={{ padding:"10px 14px", fontWeight:600, color:T.g2 }}>{a.conv}</td>
                      <td style={{ padding:"10px 14px", color:T.ink }}>{a.cpa}</td>
                      <td style={{ padding:"10px 14px" }}><span style={{ fontSize:9, fontWeight:600, padding:"2px 7px", borderRadius:4, background:T.vL, color:T.v1 }}>{a.attrib}</span></td>
                      <td style={{ padding:"10px 14px", color:T.ink3 }}>{a.reach}</td>
                      <td style={{ padding:"10px 14px", color:a.freq<=3?T.g2:T.a2 }}>{a.freq}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ BRAND SAFETY ══ */}
        {tab==="brand-safety" && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
              {[
                ["Brand safety score","98.7%", T.g2,  "% impr on safe inventory"],
                ["IVT rate",          avgIvt+"%", parseFloat(avgIvt)<=2?T.g2:T.r2,"Industry avg 3.2%"],
                ["Blocked categories","12",    T.b2,  "Adult, alcohol, political…"],
                ["Blocked domains",   "4,218", T.ink3,"Made-for-ad sites excluded"],
              ].map(([l,v,c,sub])=>(
                <div key={l} style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:10, padding:"13px 16px" }}>
                  <p style={{ fontSize:9, color:T.ink4, margin:"0 0 3px", textTransform:"uppercase", letterSpacing:".06em" }}>{l}</p>
                  <p style={{ fontSize:22, fontWeight:700, color:c, margin:"0 0 2px" }}>{v}</p>
                  <p style={{ fontSize:10, color:T.ink4, margin:0 }}>{sub}</p>
                </div>
              ))}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              {[["IVT rate","ivt",v=>v<=2?T.g2:v<=3?T.a2:T.r2,v=>Math.min(v*8,100)],["Viewability","viewab",v=>v>=70?T.g2:v>=60?T.a2:T.r2,v=>v]].map(([title,key,colorFn,widthFn])=>(
                <div key={title} style={{ background:T.canvas, border:"1px solid "+T.s2, borderRadius:12, padding:"16px 18px" }}>
                  <p style={{ fontSize:12, fontWeight:600, color:T.ink, margin:"0 0 14px" }}>{title} by DSP</p>
                  {PROG_DSPS.map(dsp=>{
                    const dd  = PROG_DEALS.filter(d=>d.dsp===dsp.id);
                    if (!dd.length) return null;
                    const avg = dd.reduce((a,d)=>a+d[key],0)/dd.length;
                    return (
                      <div key={dsp.id} style={{ marginBottom:11 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                            <div style={{ width:7, height:7, borderRadius:"50%", background:dsp.color }}/>
                            <span style={{ fontSize:11, color:T.ink }}>{dsp.label}</span>
                          </div>
                          <span style={{ fontSize:11, fontWeight:600, color:colorFn(avg) }}>{avg.toFixed(1)}%</span>
                        </div>
                        <div style={{ height:8, background:T.s2, borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:widthFn(avg)+"%", background:dsp.color, borderRadius:4 }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Root App ─────────────────────────────────────────────────────────────────
const INITIAL_CLIENTS = [
  { id:"c1", name:"Demo Agency",       industry:"Agency",     currency:"USD", timezone:"UTC",           budget:"$15,000", website:"https://demo.agency",   contactEmail:"hello@demo.agency",     seed:0,  created:"Jan 3, 2026",  status:"active" },
  { id:"c2", name:"TechCorp Inc.",      industry:"SaaS",       currency:"USD", timezone:"UTC-5 (New York)", budget:"$22,000", website:"https://techcorp.io",   contactEmail:"marketing@techcorp.io", seed:3,  created:"Jan 18, 2026", status:"active" },
  { id:"c3", name:"Nordic Retail Group",industry:"E-commerce", currency:"EUR", timezone:"UTC+1 (London)", budget:"$8,500",  website:"https://nordic-rg.com", contactEmail:"ads@nordic-rg.com",     seed:7,  created:"Feb 2, 2026",  status:"active" },
  { id:"c4", name:"Lumina Finance",     industry:"Finance",    currency:"GBP", timezone:"UTC+1 (London)", budget:"$18,000", website:"https://luminafin.co.uk",contactEmail:"cmo@luminafin.co.uk",   seed:11, created:"Feb 14, 2026", status:"active" },
  { id:"c5", name:"Savanna Health",     industry:"Healthcare", currency:"USD", timezone:"UTC-8 (Los Angeles)", budget:"$6,000", website:"https://savannahealth.com",contactEmail:"ops@savannahealth.com", seed:15, created:"Mar 5, 2026", status:"active" },
];

export default function AdTechPlatform() {
  const [clients,      setClients]     = useState(INITIAL_CLIENTS);
  const [activeClient, setActiveClient]= useState(INITIAL_CLIENTS[0]);
  const [page,         setPage]        = useState("dashboard");
  const [sel,          setSel]         = useState(null);
  const [showAI,       setShowAI]      = useState(false);
  const [showReport,   setShowReport]  = useState(false);
  const [showSwitcher, setShowSwitcher]= useState(false);
  const [showNewClient,setShowNewClient]=useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);

  const addClient = (c) => {
    setClients(cs=>[...cs,c]);
    setActiveClient(c);
    setPage("dashboard");
  };

  const switchClient = (c) => {
    setActiveClient(c);
    setPage("dashboard");
    setSel(null);
  };

  return (
    <div style={{ display:"flex", height:"100vh", background:T.bg, overflow:"hidden", fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif" }}>
      <Sidebar page={page} setPage={setPage} onAI={()=>setShowAI(true)} client={activeClient} onSwitchClient={()=>setShowSwitcher(true)}/>
      <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
        {/* Each page stays mounted — no state reset on re-render */}
        <div style={{ flex:1, display:page==="dashboard"      ?"flex":"none", overflow:"hidden" }}><Dashboard setSel={setSel} setShowReport={()=>setShowReport(true)} setShowPreview={()=>setShowReportPreview(true)} client={activeClient}/></div>
        <div style={{ flex:1, display:page==="posts"          ?"flex":"none", overflow:"hidden" }}><PostLibrary setSel={setSel} setPage={setPage}/></div>
        <div style={{ flex:1, display:page==="social-post"    ?"flex":"none", overflow:"hidden" }}><SocialPostPage/></div>
        <div style={{ flex:1, display:page==="leads"          ?"flex":"none", overflow:"hidden" }}><LeadDashboard setPage={setPage}/></div>
        <div style={{ flex:1, display:page==="organic-report" ?"flex":"none", overflow:"hidden" }}><OrganicReport setShowReport={()=>setShowReport(true)} setPage={setPage}/></div>
        <div style={{ flex:1, display:page==="organic-paid"   ?"flex":"none", overflow:"hidden" }}><OrgVsPaid/></div>
        <div style={{ flex:1, display:page==="create"         ?"flex":"none", overflow:"hidden" }}><CreateAd setPage={setPage}/></div>
        <div style={{ flex:1, display:page==="reports"        ?"flex":"none", overflow:"hidden" }}><ReportsPage setShowReport={()=>setShowReport(true)} setShowPreview={()=>setShowReportPreview(true)}/></div>
        <div style={{ flex:1, display:page==="pacing"         ?"flex":"none", overflow:"hidden" }}><BudgetPacingPage/></div>
        <div style={{ flex:1, display:page==="anomalies"      ?"flex":"none", overflow:"hidden" }}><AnomalyFeedPage/></div>
        <div style={{ flex:1, display:page==="mediamix"       ?"flex":"none", overflow:"hidden" }}><MediaMixPage/></div>
        <div style={{ flex:1, display:page==="prog-report"    ?"flex":"none", overflow:"hidden" }}><ProgrammaticReportPage setShowReport={()=>setShowReport(true)}/></div>
        <div style={{ flex:1, display:page==="fatigue"        ?"flex":"none", overflow:"hidden" }}><FatiguePage setPage={setPage}/></div>
        <div style={{ flex:1, display:page==="abtests"        ?"flex":"none", overflow:"hidden" }}><ABTestPage/></div>
        <div style={{ flex:1, display:page==="funnel"         ?"flex":"none", overflow:"hidden" }}><FunnelPage/></div>
        <div style={{ flex:1, display:page==="portal"         ?"flex":"none", overflow:"hidden" }}><ClientPortalPage activeClient={activeClient}/></div>
        <div style={{ flex:1, display:page==="settings"      ?"flex":"none", overflow:"hidden" }}><SettingsPage activeClient={activeClient}/></div>
        <div style={{ flex:1, display:page==="ad-connectors"  ?"flex":"none", overflow:"hidden" }}><AdConnectorsPage/></div>
        <div style={{ flex:1, display:page==="crm-connectors" ?"flex":"none", overflow:"hidden" }}><CRMConnectorsPage/></div>
        <div style={{ flex:1, display:page==="clients"        ?"flex":"none", overflow:"hidden" }}><ClientsPage clients={clients} activeClient={activeClient} onSelect={switchClient} onNew={()=>setShowNewClient(true)}/></div>
      </div>

      {sel              && <PostPanel post={sel} onClose={()=>setSel(null)}/>}
      {showAI           && <AIPanel onClose={()=>setShowAI(false)}/>}
      {showReport       && <ReportModal onClose={()=>setShowReport(false)}/>}
      {showReportPreview && <ReportPreview client={activeClient} onClose={()=>setShowReportPreview(false)} onSend={()=>{setShowReportPreview(false);setShowReport(true);}}/>}
      {showSwitcher     && <ClientSwitcher clients={clients} activeId={activeClient?.id} onSelect={switchClient} onClose={()=>setShowSwitcher(false)} onNew={()=>{setShowSwitcher(false);setShowNewClient(true);}}/>}
      {showNewClient    && <NewClientModal onClose={()=>setShowNewClient(false)} onCreate={addClient}/>}
    </div>
  );
}
